import React, { useState, useRef, useEffect, useCallback, Children } from 'react';
import Carousel from '../../../components/common/EmblaCarousel';
import { createPendingJobStore, makeThumbDataUrl } from '../../../utils/pendingGenJobs';
import { Popover as ShadcnPopover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import {
  DropdownMenu as ShadcnDropdownMenu,
  DropdownMenuTrigger as ShadcnDropdownMenuTrigger,
  DropdownMenuContent as ShadcnDropdownMenuContent,
  DropdownMenuItem as ShadcnDropdownMenuItem,
  DropdownMenuLabel as ShadcnDropdownMenuLabel,
  DropdownMenuSeparator as ShadcnDropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Checkbox } from '@/components/ui/checkbox';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { DressPreviewSvg } from '../../../components/common/DressPreviewSvg';
import CropStage from './CropStage';
import { useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDragWheelScroll, useMediaQuery } from '../../../lib/hooks';
import { AI_PHOTO_ULTRA_HD } from '../../../constants/aiPhotoQuality';
import { notifications } from '../../../lib/notifications';
import {
    IconUpload,
    IconPhoto,
    IconPhotoUp,
    IconX,
    IconDownload,
    IconRefresh,
    IconMinus,
    IconPalette,
    IconShare,
    IconMail,
    IconBrandWhatsapp,
    IconLink,
    IconEdit,
    IconTrash,
    IconAlertCircle,
    IconPlus,
    IconEye,
    IconPrinter,
    IconChevronLeft,
    IconChevronRight,
    IconCheck,
    IconSunglasses,
    IconCrown, IconCoins,
    IconRepeat,
    IconArrowsHorizontal,
    IconAdjustmentsHorizontal,
    IconAspectRatio,
    IconChevronDown,
    IconShirt,
    IconLoader2,
    IconThumbDown,
    IconThumbUp,
    IconArrowsMaximize,
    IconLayoutColumns,
    IconCircleOff,
    IconQrcode,
    IconHourglass,
    IconDeviceMobile,
    IconAlertTriangle,
    IconColumns2,
    IconArrowsExchange,
    IconArrowsLeftRight,
    IconStack2,
    IconUsers,
    IconCopy,
    IconSparkles,
    IconRazor,
    IconScissors,
    IconArrowBarUp,
    IconEyeCheck,
    IconSchool,
    IconCrop,
    IconRotate2,
    IconRotateClockwise,
    IconId,
    IconNfc,
    IconPlaneTilt,
    IconFingerprint,
    IconCertificate,
    IconFriends,
    IconUserSquare,
    IconExternalLink,
    IconSettings,
    IconArrowBack,
    IconWand,
    IconInfoCircle,
} from '@tabler/icons-react';
import QRCode from 'qrcode';
import QrUploadModal from '@/components/common/QrUploadModal';
import PhotoCollectorPanel from './PhotoCollectorPanel';
import photoCollectorService from '../../../services/photoCollectorService';
import { registerPrivateResult, markPrivateResultDelivered } from '../../../utils/privateResults';
import imageGenerationService from '../../../services/imageGenerationService';
import { shareImageToTarget, copyImageToClipboard } from '../../../utils/imageShare';
import { buildPhotoDisplayName, nextPhotoSerial } from '../../../utils/photoFileName';
import authApi from '../../../services/authApi';
import qrSessionService from '../../../services/qrSessionService';
import usageService from '../../../services/usageService';
import { useOfficeSpaceStore } from '../../../store/officeSpaceStore';
import { useAuthStore } from '../../../store/authStore';
import { useIncomeExpenseStore } from '../../../store/incomeExpenseStore';
import { moduleAutoFill } from '../../../lib/incomeExpenseDefaults';
import { ALL_DRESS_COLORS } from '../../../constants/dressColors';
import countryLabels from 'react-phone-number-input/locale/en.json';
import PrintModal from './PrintModal';
import CustomDownloadModal from './CustomDownloadModal';
import { VISA_GROUPS } from './VisaCountryModal';
import LimitReachedModal from '../../../components/common/LimitReachedModal';
import LowCreditBanner from '../../../components/common/LowCreditBanner';
import ImagePreviewModal from '../../../components/common/ImagePreviewModal';
import EraserModal from './EraserModal';
import styles from './GenerateImage.module.scss';
import AiTemplateFeedbackModal, { IMAGE_FEEDBACK_REASONS } from '../../../components/common/AiTemplateFeedbackModal';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import './print.css';
import { playSuccessChime } from '../../../utils/notificationSound.js';
import { toBnDigits } from '../../../utils/bnFormat';
import { hasTransparentSubject, compositeBackground } from './bgPresets';
import { PHOTO_TOOL_ICONS } from '../../../constants/photoToolIcons';
import { isDressStyleEnabled, toggleDressStyle } from '../../../utils/dressStyles';
import { GUIDE_MAX_CHARS, GUIDE_MAX_WORDS, sanitizeText, sanitizeExtraGuide } from '../../../utils/sanitize';
import { NEUTRAL_EDITS, isEdited, renderEdits, useFilterThumbs, usePhotoEditPreview } from '../../../utils/photoEdits';
import PhotoEditDrawer from '../../../components/common/PhotoEditDrawer';
import CompareSlider from '../../../components/common/CompareSlider';
import ResultActions from './ResultActions';

// SVG Icons from assets (imported to resolve 404s on server)
import editIcon from '../../../assets/images/edit.svg';

// Perceived lightness of a #rrggbb color — used to keep the check-pattern lines
// visible on both light and dark swatches.
function isLightHex(hex) {
    const h = (hex || '').replace('#', '');
    if (h.length < 6) return false;
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    return 0.299 * r + 0.587 * g + 0.114 * b > 180;
}

// Gingham/check preview fill for a color swatch — shown when "চেক শার্ট" is on so
// the user sees the colour will be applied as a check pattern, not a solid.
function checkSwatchStyle(hex) {
    const line = isLightHex(hex) ? 'rgba(0,0,0,0.22)' : 'rgba(255,255,255,0.55)';
    return {
        backgroundColor: hex,
        backgroundImage: `linear-gradient(0deg, ${line} 1px, transparent 1px), linear-gradient(90deg, ${line} 1px, transparent 1px)`,
        backgroundSize: '6px 6px',
    };
}

// Pinstripe preview fill for a colour swatch — shown when "স্ট্রাইপ পাঞ্জাবি" is on (the stripe counterpart of the check fill above).
function stripeSwatchStyle(hex) {
    const line = isLightHex(hex) ? 'rgba(0,0,0,0.22)' : 'rgba(255,255,255,0.55)';
    return {
        backgroundColor: hex,
        backgroundImage: `repeating-linear-gradient(90deg, ${line} 0, ${line} 1px, transparent 1px, transparent 5px)`,
    };
}

// Selected-swatch check mark. On a busy check pattern a bare tick disappears, so
// there we sit it on a solid dark chip with a white ring; on a solid swatch we
// keep the original bare tick (grey on light colours, white otherwise).
function SwatchCheck({ patterned, colorId }) {
    if (patterned) {
        return (
            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 15, height: 15, borderRadius: '50%', background: 'rgba(0,0,0,0.62)', boxShadow: '0 0 0 1.5px #fff' }}>
                <IconCheck size={9} color="#fff" strokeWidth={3.5} />
            </span>
        );
    }
    return <IconCheck size={10} color={['white', 'yellow'].includes(colorId) ? '#9ca3af' : 'white'} strokeWidth={3} />;
}

// ── Head-cover toggle button with styled tooltip ──────────────────────────────
function HeadCoverToggle({ active, onToggle, label, tooltip }) {
    return (
        <Tooltip>
                <TooltipTrigger asChild>
                    <button
                        type="button"
                        onClick={onToggle}
                        style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 4, padding: '3px 7px', borderRadius: 12, cursor: 'pointer', border: active ? '1.5px solid #fe9a00' : '1.5px solid #6b7280', background: active ? 'rgba(254,154,0,0.15)' : 'transparent', userSelect: 'none', flexShrink: 0 }}
                    >
                        <div style={{ width: 13, height: 13, borderRadius: 3, border: active ? '2px solid #fe9a00' : '2px solid #9ca3af', background: active ? '#fe9a00' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            {active && <IconCheck size={8} color="white" strokeWidth={4} />}
                        </div>
                        <span className={active ? '' : 'text-zinc-500 dark:text-zinc-300'} style={{ fontSize: 10, color: active ? '#fe9a00' : undefined, lineHeight: 1, whiteSpace: 'nowrap' }}>{label}</span>
                    </button>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-[180px] text-center z-[2000]">
                    {tooltip}
                </TooltipContent>
            </Tooltip>
    );
}

// ── Chip-style checkbox — same pill look as the ghomta toggle (no tooltip) ────
function CheckChip({ active, onToggle, label, disabled, style }) {
    return (
        <button
            type="button"
            onClick={onToggle}
            disabled={disabled}
            style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '3px 7px', borderRadius: 12, cursor: disabled ? 'not-allowed' : 'pointer', border: active ? '1.5px solid #fe9a00' : '1.5px solid #6b7280', background: active ? 'rgba(254,154,0,0.15)' : 'transparent', userSelect: 'none', flexShrink: 0, ...style }}
        >
            <div style={{ width: 13, height: 13, borderRadius: 3, border: active ? '2px solid #fe9a00' : '2px solid #9ca3af', background: active ? '#fe9a00' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                {active && <IconCheck size={8} color="white" strokeWidth={4} />}
            </div>
            <span className={active ? '' : 'text-zinc-500 dark:text-zinc-300'} style={{ fontSize: 10, color: active ? '#fe9a00' : undefined, lineHeight: 1, whiteSpace: 'nowrap' }}>{label}</span>
        </button>
    );
}

// ── Complex wrappers backed by shadcn/Embla (kept from original) ──────────────
const MANTINE_SIDE = { right: 'right', left: 'left', top: 'top', bottom: 'bottom' };
const MANTINE_P = { xs: 8, sm: 12, md: 16, lg: 20, xl: 24 };
const resolveP = (p) => p == null ? 12 : typeof p === 'number' ? p : (MANTINE_P[p] ?? 12);

// Popover — backed by shadcn/Radix (portal + outside-click built-in)
const Popover = ({ children, opened, onChange, position, disabled, targetStyle, sideOffset }) => {
  const childArr = React.Children.toArray(children);
  const target = childArr.find(c => c?.type === Popover.Target);
  const dropdownEl = childArr.find(c => c?.type === Popover.Dropdown);
  useEffect(() => {
    if (!opened || !onChange) return;
    // Close on scroll — but ARMED after a beat: opening the popover can itself
    // trigger a scroll (browser scrolling the focused dropdown into view), which
    // used to close it the instant it appeared (e.g. the lipstick picker at the
    // bottom of the tools grid). Scrolls inside the dropdown don't close either.
    let armed = false;
    const arm = setTimeout(() => { armed = true; }, 350);
    const close = (e) => {
      if (!armed) return;
      const wrap = document.querySelector('[data-radix-popper-content-wrapper]');
      if (wrap && e.target instanceof Node && wrap.contains(e.target)) return;
      onChange(false);
    };
    window.addEventListener('scroll', close, true);
    return () => { clearTimeout(arm); window.removeEventListener('scroll', close, true); };
  }, [opened, onChange]);
  if (disabled) return <>{target?.props?.children}</>;
  return (
    <ShadcnPopover open={opened} onOpenChange={onChange}>
      <PopoverTrigger asChild>
        <span style={{ display: 'inline-flex', alignItems: 'center', ...targetStyle }}>{target?.props?.children}</span>
      </PopoverTrigger>
      {dropdownEl && (
        <PopoverContent side={MANTINE_SIDE[position] || 'bottom'} sideOffset={sideOffset ?? 4} className="p-0 w-auto" style={dropdownEl.props.style}
          onOpenAutoFocus={(e) => e.preventDefault()}>
          <div style={{ padding: resolveP(dropdownEl.props.p) }}>{dropdownEl.props.children}</div>
        </PopoverContent>
      )}
    </ShadcnPopover>
  );
};
Popover.Target = ({ children }) => <>{children}</>;
Popover.Dropdown = ({ children, p, style }) => null;

// Menu — backed by shadcn DropdownMenu (portal + outside-click built-in)
const Menu = ({ children, shadow, width, targetStyle }) => {
  const childArr = React.Children.toArray(children);
  const target = childArr.find(c => c?.type === Menu.Target);
  const dropdownEl = childArr.find(c => c?.type === Menu.Dropdown);
  return (
    <ShadcnDropdownMenu>
      <ShadcnDropdownMenuTrigger asChild>
        <span style={{ display: 'inline-flex', alignItems: 'center', ...targetStyle }}>{target?.props?.children}</span>
      </ShadcnDropdownMenuTrigger>
      {dropdownEl && (
        <ShadcnDropdownMenuContent align="end" style={{ minWidth: width || 160, ...dropdownEl.props.style }}>
          {dropdownEl.props.children}
        </ShadcnDropdownMenuContent>
      )}
    </ShadcnDropdownMenu>
  );
};
Menu.Target = ({ children }) => <>{children}</>;
Menu.Dropdown = ({ children, style }) => null;
Menu.Label = ({ children }) => <ShadcnDropdownMenuLabel className="text-xs uppercase text-zinc-400">{children}</ShadcnDropdownMenuLabel>;
Menu.Item = ({ children, leftSection, onClick }) => (
  <ShadcnDropdownMenuItem onClick={onClick} className="cursor-pointer gap-2">
    {leftSection}{children}
  </ShadcnDropdownMenuItem>
);
Menu.Divider = () => <ShadcnDropdownMenuSeparator />;

// Modal — custom HTML implementation (no Mantine dependency). Opens with a smooth
// fade + gentle zoom (same soft ease-out-expo easing as the shadcn dialogs) instead of
// popping in instantly; delays unmount so the close animates out too.
const Modal = ({ children, opened, onClose, title, centered, size, fullScreen, styles: mStyles }) => {
  const [visible, setVisible] = React.useState(false);
  const [animating, setAnimating] = React.useState(false);
  React.useEffect(() => {
    if (opened) {
      setVisible(true);
      const id = requestAnimationFrame(() => requestAnimationFrame(() => setAnimating(true)));
      return () => cancelAnimationFrame(id);
    }
    setAnimating(false);
    const t = setTimeout(() => setVisible(false), 300);
    return () => clearTimeout(t);
  }, [opened]);
  if (!visible) return null;
  const startScale = fullScreen ? 0.985 : 0.95; // fullscreen barely zooms; boxes zoom a touch more
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 400, display: 'flex', alignItems: centered ? 'center' : 'flex-start', justifyContent: 'center', background: 'rgba(0,0,0,0.5)', padding: fullScreen ? 0 : 16, opacity: animating ? 1 : 0, transition: 'opacity 0.26s ease' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="font-hind" style={{ background: 'var(--color-body, #fff)', borderRadius: fullScreen ? 0 : 12, width: fullScreen ? '100vw' : size === 'sm' ? 440 : size === 620 ? 620 : typeof size === 'number' ? size : 520, maxWidth: '100%', maxHeight: fullScreen ? '100vh' : '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', ...mStyles?.content, opacity: animating ? 1 : 0, transform: animating ? 'scale(1)' : `scale(${startScale})`, transformOrigin: 'center', transition: 'opacity 0.26s ease, transform 0.3s cubic-bezier(0.16, 1, 0.3, 1)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid var(--color-border, #f0f0f0)', flexShrink: 0, ...mStyles?.header }}>
          <div style={{ flex: 1 }}>{title}</div>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', cursor: 'pointer', padding: 6, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', color: mStyles?.header?.background === '#111' ? '#fff' : 'inherit', ...mStyles?.closeButton }}><IconX size={18} /></button>
        </div>
        <div style={{ overflow: 'auto', flex: 1, padding: 16, ...mStyles?.body }}>{children}</div>
      </div>
    </div>
  );
};

// Drawer — bottom sheet implementation (no Mantine dependency)
const Drawer = ({ children, opened, onClose, title }) => {
  const [visible, setVisible] = React.useState(false);
  const [animating, setAnimating] = React.useState(false);
  React.useEffect(() => {
    if (opened) { setVisible(true); requestAnimationFrame(() => requestAnimationFrame(() => setAnimating(true))); }
    else { setAnimating(false); const t = setTimeout(() => setVisible(false), 300); return () => clearTimeout(t); }
  }, [opened]);
  if (!visible) return null;
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 500 }}>
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(2px)', opacity: animating ? 1 : 0, transition: 'opacity 0.3s ease' }} />
      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, maxHeight: '72vh', background: 'var(--color-bg, #fff)', borderRadius: '20px 20px 0 0', display: 'flex', flexDirection: 'column', boxShadow: '0 -8px 40px rgba(0,0,0,0.2)', transform: animating ? 'translateY(0)' : 'translateY(100%)', transition: 'transform 0.32s cubic-bezier(0.32,0.72,0,1)' }}>
        <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 10, paddingBottom: 4, flexShrink: 0 }}>
          <div style={{ width: 40, height: 4, borderRadius: 9999, background: 'var(--color-border, #e4e4e7)' }} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 16px 12px', borderBottom: '1px solid var(--color-border, #e4e4e7)', flexShrink: 0 }}>
          <span style={{ fontWeight: 700, fontSize: 15, color: 'var(--color-text, #18181b)' }}>{title}</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-text-muted, #000)' }}>
            <IconX size={18} />
          </button>
        </div>
        <div style={{ overflowY: 'auto', flex: 1, padding: '14px 16px 20px', WebkitOverflowScrolling: 'touch' }}>
          {children}
        </div>
      </div>
    </div>
  );
};

const Notification = ({ children, title, color, onClose }) => (
  <div style={{ padding: '10px 14px', background: color === 'orange' ? '#fff7ed' : '#f0f9ff', border: `1px solid ${color === 'orange' ? '#fed7aa' : '#bae6fd'}`, borderRadius: 8, display: 'flex', gap: 8 }}>
    {title && <strong>{title}: </strong>}{children}
    {onClose && <button onClick={onClose} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer' }}><IconX size={14} /></button>}
  </div>
);

// Photo-tool icons (served from /public/images/photo-tools/ — the tool → file map is shared with Settings, see constants/photoToolIcons.js)
const sunglassIcon        = PHOTO_TOOL_ICONS.removeGlasses;
const faceIcon            = PHOTO_TOOL_ICONS.glowMakeup;
const smoothSkinIcon      = PHOTO_TOOL_ICONS.smoothSkin;
const fixOilySkinIcon     = PHOTO_TOOL_ICONS.fixOilySkin;
const keepNamePlateIcon   = PHOTO_TOOL_ICONS.keepNamePlate;
const hairCutIcon         = PHOTO_TOOL_ICONS.removeStrayHair;
const beardCutIcon        = PHOTO_TOOL_ICONS.removeBeard;
const eyeIcon             = PHOTO_TOOL_ICONS.openEyes;
const closeMouthIcon      = PHOTO_TOOL_ICONS.closeMouth;
const capIcon             = PHOTO_TOOL_ICONS.removeTupi;
const headIcon            = PHOTO_TOOL_ICONS.straightenHead;
const addBeardIcon        = PHOTO_TOOL_ICONS.addBeard;
const applyHairIcon       = PHOTO_TOOL_ICONS.applyHair;
const keepBeardIcon       = PHOTO_TOOL_ICONS.keepBeard;
const keepMustacheIcon    = PHOTO_TOOL_ICONS.keepMustache;
const keepFacialMarksIcon = PHOTO_TOOL_ICONS.keepFacialMarks;
const keepNosePinIcon     = PHOTO_TOOL_ICONS.keepNosePin;
const keepSunGlassesIcon  = PHOTO_TOOL_ICONS.keepSunGlasses;
const showEarIcon         = PHOTO_TOOL_ICONS.showEar;
const halfBodyIcon        = PHOTO_TOOL_ICONS.halfBody;
const customWriteIcon     = PHOTO_TOOL_ICONS.showCustomInstruction;
const schoolDressIcon     = PHOTO_TOOL_ICONS.keepSchoolDressDetails;
const addTupiIcon         = PHOTO_TOOL_ICONS.addTupi;
const brightenIcon        = PHOTO_TOOL_ICONS.brightenImage;
const studioLightIcon     = PHOTO_TOOL_ICONS.studioLighting;
const lookAtCameraIcon    = PHOTO_TOOL_ICONS.lookAtCamera;
const lipstickIcon        = PHOTO_TOOL_ICONS.lipstick;

// Result action-tile icons (crop / download / custom-size download / copy)
const RENAME_MAX = 120; // the result's file name (Image Info → rename)

// Lipstick tool — preset shades. `prompt` is the single colour word sent to the
// model (one word keeps the tool token-cheap); `hex` is only the swatch colour.
const LIPSTICK_SHADES = [
    { id: 'red',    prompt: 'red',    hex: '#C21807', labelKey: 'imageGeneration.lipstickRed' },
    { id: 'maroon', prompt: 'maroon', hex: '#7B1E22', labelKey: 'imageGeneration.lipstickMaroon' },
    { id: 'pink',   prompt: 'pink',   hex: '#E75480', labelKey: 'imageGeneration.lipstickPink' },
    { id: 'rose',   prompt: 'rose',   hex: '#C96B7B', labelKey: 'imageGeneration.lipstickRose' },
    { id: 'nude',   prompt: 'nude',   hex: '#C98276', labelKey: 'imageGeneration.lipstickNude' },
    { id: 'coral',  prompt: 'coral',  hex: '#F26D5B', labelKey: 'imageGeneration.lipstickCoral' },
];


// ── Tailwind UI helpers ──────────────────────────────────────────────────────
const TBtn = ({ children, onClick, disabled, loading, variant = 'primary', size = 'md', className = '', ...props }) => {
  const base = 'inline-flex items-center gap-1.5 font-medium rounded-md transition-colors disabled:opacity-50';
  const sizes = { xs: 'px-2 py-1 text-xs', sm: 'px-3 py-1.5 text-sm', md: 'px-4 py-2 text-sm', lg: 'px-5 py-2.5 text-base' };
  const variants = {
    primary:  'bg-orange-500 hover:bg-orange-600 text-white',
    secondary:'border border-zinc-300 dark:border-zinc-600 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800',
    subtle:   'text-zinc-600 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-700',
    red:      'bg-red-600 hover:bg-red-700 text-white',
    green:    'bg-green-600 hover:bg-green-700 text-white',
    light:    'bg-orange-100 hover:bg-orange-200 dark:bg-orange-900/30 dark:hover:bg-orange-800/30 text-orange-700 dark:text-orange-400',
  };
  return (
    <button onClick={onClick} disabled={disabled || loading} className={`${base} ${sizes[size] || sizes.md} ${variants[variant] || variants.primary} ${className}`} {...props}>
      {loading && <span className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />}
      {children}
    </button>
  );
};
const IBtn = ({ children, onClick, disabled, title, color = 'default', className = '' }) => {
  const colors = { default: 'text-zinc-600 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-700', red: 'text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20', orange: 'text-orange-500 hover:bg-orange-50', green: 'text-green-600 hover:bg-green-50' };
  const btn = <button onClick={onClick} disabled={disabled} className={`p-1.5 rounded transition-colors ${colors[color] || colors.default} ${className}`}>{children}</button>;
  if (!title) return btn;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{btn}</TooltipTrigger>
      <TooltipContent side="top">{title}</TooltipContent>
    </Tooltip>
  );
};

function ZoomControls({ zoom, onIn, onOut, onReset }) {
    return (
        <div
            className="flex items-center gap-1"
            style={{ position: 'absolute', bottom: 14, left: '50%', transform: 'translateX(-50%)', background: 'rgba(0,0,0,0.65)', borderRadius: 24, padding: '5px 10px', backdropFilter: 'blur(6px)', zIndex: 2, userSelect: 'none' }}
            onPointerDown={(e) => e.stopPropagation()}
        >
            <button className="p-1 text-white hover:text-orange-300 disabled:opacity-40 transition-colors" onClick={onOut} disabled={zoom <= 0.5}>
                <IconMinus size={14} />
            </button>
            <span style={{ minWidth: 38, textAlign: 'center', fontSize: 12, fontWeight: 700, color: 'white' }}>
                {Math.round(zoom * 100)}%
            </span>
            <button className="p-1 text-white hover:text-orange-300 disabled:opacity-40 transition-colors" onClick={onIn} disabled={zoom >= 4}>
                <IconPlus size={14} />
            </button>
            <button className="p-1 text-orange-400 hover:text-orange-300 disabled:opacity-40 transition-colors" onClick={onReset} disabled={zoom === 1}>
                <IconRefresh size={14} />
            </button>
        </div>
    );
}

// Static reverse map: hex values (sent to API / stored in DB) → predefined bg ID
// Needed because the API currently sends the hex colour of predefined swatches, not the string ID.
const BG_HEX_TO_ID = {
    'var(--mantine-color-body)': 'white',
    '#ffffff': 'white',
    '#2196f3': 'blue',
    '#03a9f4': 'sky_blue',
    '#9e9e9e': 'grey',
    '#3068e1': 'dark_blue',
    '#00897b': 'teal',
    '#fff8dc': 'off_white',
};

// The bg IDs this page can actually represent (its swatch list, minus 'custom').
// 'transparent' is a real option now (gpt-image-2 transparent output → PNG), so it
// must survive resolveBgFromDb instead of falling back to white.
const KNOWN_BG_IDS = ['white', 'blue', 'sky_blue', 'grey', 'dark_blue', 'teal', 'off_white', 'transparent'];

// Convert a stored background token to the post-gen "applied colour" representation:
// null = keep transparent (checkerboard), otherwise the hex colour to composite.
const resolveBgToApplied = (token) =>
    (!token || token === 'transparent') ? null : (String(token).startsWith('#') ? token : null);

// Named bg id → hex. Resolve the LEFT picker's current selection to a solid colour to
// composite (or null = keep transparent). This is the single source for the displayed
// background of a transparent result.
const BG_ID_TO_HEX = { white: '#ffffff', blue: '#2196F3', sky_blue: '#03A9F4', grey: '#9E9E9E', dark_blue: '#3068E1', teal: '#00897B', off_white: '#FFF8DC' };
const pickerColorFrom = (bg, customColor) => {
    if (!bg || bg === 'transparent') return null;
    if (bg === 'custom') return (customColor && customColor.startsWith('#')) ? customColor : null;
    return BG_ID_TO_HEX[bg] || (String(bg).startsWith('#') ? bg : null);
};

// Checkerboard fill for the "Transparent" swatch / preview so users can tell an
// alpha result apart from a plain white one.
const CHECKERBOARD_STYLE = {
    backgroundColor: '#ffffff',
    backgroundImage:
        'linear-gradient(45deg,#cbd5e1 25%,transparent 25%),linear-gradient(-45deg,#cbd5e1 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#cbd5e1 75%),linear-gradient(-45deg,transparent 75%,#cbd5e1 75%)',
    backgroundSize: '12px 12px',
    backgroundPosition: '0 0,0 6px,6px -6px,-6px 0',
};

/** Resolve a background value from DB to a bg state ID.
 *  Returns the predefined ID (e.g. 'white') or null if it's a truly custom hex. */
function resolveBgFromDb(value) {
    if (!value) return null;
    if (!value.startsWith('#')) {
        // Only real bg IDs pass through. Values this page can't represent —
        // 'transparent' from manual-editor records, 'gradient:'/'preset:' tokens —
        // fall back to white; a poisoned bg state used to be sent VERBATIM as the
        // next generation's background and stored ('transparent' in the DB).
        return KNOWN_BG_IDS.includes(value) ? value : 'white';
    }
    return BG_HEX_TO_ID[value.toLowerCase()] ?? null;     // map hex → ID, or null if unknown hex
}

const GENERATING_STEPS = [
    'ফটো এডিট করা হচ্ছে...',
    'ছবির সাইজ ঠিক করা হচ্ছে...',
    'পোশাক স্টাইল যোগ করা হচ্ছে...',
    'ব্যাকগ্রাউন্ড পরিবর্তন করা হচ্ছে...',
    'ছবি সুন্দর করা হচ্ছে...',
    'আপনার ছবি প্রায় তৈরি...',
];
const STEP_DELAYS = [3000, 6000, 10000, 14000, 18000]; // ms from generation start for steps 1-5

// ── Reload-surviving generations ──────────────────────────────────────────────
// A generation's HTTP request dies when the tab reloads, but the backend keeps
// processing and saves the finished image to history. We persist a lightweight
// marker (+ tiny preview) per in-flight job so that after a reload we can restore
// its processing card in the recent slider and reconcile it against history.
const PENDING_JOBS_KEY = 'sohozkaj_pending_gen_jobs';
const PENDING_JOB_TTL_MS = 200000;              // past this, an unresolved restored job is shown as failed
const PENDING_JOB_MAX_AGE_MS = 15 * 60 * 1000;  // drop truly-stale markers silently
const {
    read: readPendingJobs, add: addPendingJob, update: updatePendingJob, remove: removePendingJob,
} = createPendingJobStore(PENDING_JOBS_KEY);

// Physical dimensions per photo size — drive the upload-box shape, the crop-stage
// default ratio/output size, and the uploaded-preview shape. dual_image is excluded
// (its two-slot flow keeps its own fixed boxes). Label text lives in i18n as
// imageGeneration.sizeDim_<value>.
const SIZE_DIMS = {
    bd_passport:  { w: 45,   h: 55   },
    bd_epassport: { w: 25,   h: 30   },
    bd_visa:      { w: 35,   h: 45   },
    bd_birth:     { w: 35,   h: 45   },
    visa_2x2:     { w: 50.8, h: 50.8 },
    visa_35x45:   { w: 35, h: 45 }, visa_40x60: { w: 40, h: 60 }, visa_50x70: { w: 50, h: 70 },
    visa_33x48:   { w: 33, h: 48 }, visa_40x50: { w: 40, h: 50 }, visa_35x50: { w: 35, h: 50 },
    visa_40x40:   { w: 40, h: 40 }, visa_50x50: { w: 50, h: 50 }, visa_36x47: { w: 36, h: 47 },
    visa_38x48:   { w: 38, h: 48 }, visa_50x60: { w: 50, h: 60 },
    // Country e-passport sizes (non-BD users). Reuse the visa dimensions so the
    // aspect-ratio, hint label and generation all work; EPASS_TO_BACKEND maps them
    // to the backend-known visa key when sending.
    epass_35x45:  { w: 35, h: 45 }, epass_2x2: { w: 50.8, h: 50.8 },
    epass_50x70:  { w: 50, h: 70 }, epass_33x48: { w: 33, h: 48 },
};

// Non-BD e-passport photo size, keyed by ISO-2 country code. Built from well-known
// standards — most countries use 35×45 mm; these are the notable exceptions. Extend
// as needed (values must be keys in SIZE_DIMS / EPASS_TO_BACKEND).
const COUNTRY_EPASSPORT = {
    US: 'epass_2x2',   // United States — 2×2 inch
    CA: 'epass_50x70', // Canada — 50×70 mm
    CN: 'epass_33x48', // China — 33×48 mm
};
const DEFAULT_EPASS = 'epass_35x45'; // 35×45 mm — the global norm
// Frontend epass_* key → backend-understood visa_* key (same dimensions).
const EPASS_TO_BACKEND = {
    epass_35x45: 'visa_35x45', epass_2x2: 'visa_2x2',
    epass_50x70: 'visa_50x70', epass_33x48: 'visa_33x48',
};

// ── Legacy guide-label parsing ──────────────────────────────────────────────
// Records created before `toolSelections` existed stored only the joined ENGLISH
// tool labels (plus any custom text) in the extraGuide column. Map those fixed
// names back to tool keys so old records reopen with their tools re-selected —
// DB rows are never modified, everything happens at load time.
const LEGACY_GUIDE_LABEL_TO_TOOL = {
    'Remove Glasses': 'removeGlasses',
    'Remove glass/sunglasses': 'removeGlasses',   // actual string older records stored
    'Remove Tupi/Cap': 'removeTupi',
    'Add Tupi': 'addTupi',
    'Face Glow': 'glowMakeup',
    'Smooth Skin': 'smoothSkin',
    'Remove Beard': 'removeBeard',
    'Straighten Head': 'straightenHead',
    'Open Eyes': 'openEyes',
    'Close Mouth': 'closeMouth',
    'Remove Stray Hair': 'removeStrayHair',
    'Add Beard & Mustache': 'addBeard',
    'Add Hair': 'applyHair',
    'Keep Beard': 'keepBeard',
    'Keep Mustache': 'keepMustache',
    'Keep Facial Marks': 'keepFacialMarks',
    'Keep Nose Pin': 'keepNosePin',
    'Keep Sun Glasses': 'keepSunGlasses',
    'Show Ears': 'showEar',
    'Half Body': 'halfBody',
    'Brighten Image': 'brightenImage',
    'Studio Lighting': 'studioLighting',
    'Look at Camera': 'lookAtCamera',
    'Keep Name Plate': 'keepNamePlate',
    'School Dress Unchanged': 'keepSchoolDressDetails',
    'Keep School Dress Details': 'keepSchoolDressDetails',   // actual string older records stored
};
// Case-insensitive index — the DB holds both 'Remove Tupi/Cap' and 'Remove tupi/cap',
// so match on a lowercased key rather than the exact string.
const LEGACY_GUIDE_LOOKUP = Object.fromEntries(
    Object.entries(LEGACY_GUIDE_LABEL_TO_TOOL).map(([label, key]) => [label.toLowerCase(), key])
);

// Split a history/DB record into { tools, customText, plateText } for restore.
// New records carry machine-readable `toolSelections`; legacy records are parsed
// from the English label string — recognised names become selections, and only
// the leftover text is the user's real custom instruction.
function parseRecordTools(record) {
    const tools = {};
    let customText = record?.extraEditingGuide || '';
    let plateText = '';
    if (record?.toolSelections && typeof record.toolSelections === 'object') {
        Object.entries(record.toolSelections).forEach(([k, v]) => { if (v === true) tools[k] = true; });
    } else if (customText) {
        const leftover = [];
        customText.split(',').map(s => s.trim()).filter(Boolean).forEach(part => {
            const badge = /^Badge Text:\s*(.+)$/.exec(part);
            if (badge) { tools.keepNamePlate = true; plateText = badge[1]; return; }
            const key = LEGACY_GUIDE_LOOKUP[part.toLowerCase()];
            if (key) tools[key] = true;
            else leftover.push(part);
        });
        customText = leftover.join(', ');
    }
    if (customText) tools.showExtraGuide = true;
    return { tools, customText, plateText };
}

// A phone bottom tab's icon: a line icon in the tab's own colour.
function NavIcon({ Icon, className, active = false }) {
    return <Icon size={25} strokeWidth={1.8} className={`${className} transition-transform duration-200 ${active ? 'scale-110' : ''}`} />;
}

export default function GenerateImage() {
    const { t, i18n } = useTranslation();
    const isBn = i18n.language === 'bn';
    // Localize Western digits to Bengali when the app is in Bangla (e.g. "21/21" → "২১/২১").
    const fmtNum = (n) => (isBn ? toBnDigits(n) : String(n));
    // Human label for a generated image's quality tier (normal|hd|ultra).
    const qualityTierLabel = (q) => t(`imageGeneration.quality.${q === 'hd' ? 'hd' : q === 'ultra' ? 'ultra' : 'regular'}`);
    const { user, updateProfile, updateSettings } = useAuthStore();
    const { openModal: openIncomeExpenseModal } = useIncomeExpenseStore();
    const isMobile = useMediaQuery('(max-width: 768px)');
    // Country-aware editor: a user whose profile country is set and NOT Bangladesh
    // gets that country's e-passport size, no জন্ম (birth-reg) option, and Sharee
    // dresses hidden. Empty/BD country → the default Bangladesh experience.
    const userCC = String(user?.country || '').toUpperCase();
    const isBdUser = !userCC || userCC === 'BD';
    const epassKey = COUNTRY_EPASSPORT[userCC] || DEFAULT_EPASS;
    const isDesktop = useMediaQuery('(min-width: 1025px)');
    const isTabletView = useMediaQuery('(max-width: 1023px) and (min-width: 769px)');
    const fitsScreen = isMobile || isTabletView; // these layouts fill the screen below the header, with no page scroll

    // Lock page scroll on mobile — layout is fully fixed, no content to scroll
    useEffect(() => {
        if (!fitsScreen) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = prev; };
    }, [fitsScreen]);

    // The mobile editor shell is position:fixed, so it must be inset by the exact
    // heights of the sticky header and the fixed bottom nav. Those heights are
    // dynamic — the contest banner (when active) makes the header taller and can be
    // dismissed — so hardcoded offsets clip the image (head hidden under the header).
    // Measure the real bars and keep the insets in sync via ResizeObserver.
    const [mobileInsets, setMobileInsets] = useState({ top: 50, bottom: 56 });
    useEffect(() => {
        if (!fitsScreen) return;
        const header = document.querySelector('.main-nav-wrapper');
        const bottomNav = document.querySelector('nav.fixed.bottom-0');
        const measure = () => {
            const top = header ? Math.round(header.getBoundingClientRect().bottom) : 50;
            // the nav leaves the page while the crop screen is open: a height of 0 then must not stick
            const navH = bottomNav ? Math.round(bottomNav.getBoundingClientRect().height) : 0;
            setMobileInsets((prev) => ({ top, bottom: navH || prev.bottom }));
        };
        measure();
        const ro = new ResizeObserver(measure);
        if (header) ro.observe(header);
        if (bottomNav) ro.observe(bottomNav);
        window.addEventListener('resize', measure);
        return () => { ro.disconnect(); window.removeEventListener('resize', measure); };
    }, [fitsScreen]);
    const showQrInUploadArea = useMediaQuery('(max-width: 1023px)');
    const { selectedOfficeSpace: rawSelectedOfficeSpace } = useOfficeSpaceStore();

    // Admins don't belong to any office space. If a stale selection is sitting in
    // localStorage from an earlier role, ignore it so the backend doesn't reject
    // the admin's own history / stats / generation calls with a 403.
    const isAdmin = user?.role === 'admin';
    const selectedOfficeSpace = isAdmin ? null : rawSelectedOfficeSpace;

    // True when the logged-in user is an operator (not the owner) of the selected office space
    const isOperator = !!(selectedOfficeSpace && selectedOfficeSpace.ownerId !== user?.id);

    const [file, setFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [generatedImage, setGeneratedImage] = useState(null);
    const [generatedImageId, setGeneratedImageId] = useState(null);
    // Background the CURRENT result was saved with (raw DB value: hex or 'transparent').
    // The LEFT `bg` picker is the single background control; post-generation, changing it
    // recolours the transparent master instantly and persists here.
    const [resultBg, setResultBg] = useState(null);
    const [isGenerating, setIsGenerating] = useState(false);
    const [tabletBoothOpen, setTabletBoothOpen] = useState(false); // the tablet's booth popover (the sidebar inbox is hidden there)
    // Quality-tier info modal: null | 'hd' | 'ultra' (shown before an HD/Ultra generation
    // unless the user previously chose "don't show again").
    const [qualityInfoTier, setQualityInfoTier] = useState(null);
    // true only after the generated image URL has been fully preloaded in the browser.
    // Prevents the frame collapsing to 0-height while the new <img> is fetching.
    const [generatedImageReady, setGeneratedImageReady] = useState(false);
    const [blurRevealing, setBlurRevealing] = useState(false);
    // Horizontal-swipe tracking for the mobile before/after stacked cards.
    const [generatingStep, setGeneratingStep] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const [progress, setProgress] = useState(0);

    const [usageStats, setUsageStats] = useState(null);
    // Per-tier effective credit cost (Normal/HD/Ultra), shown beside the quality buttons.
    const [imageTierCosts, setImageTierCosts] = useState(null);
    useEffect(() => {
        usageService.getCreditCosts()
            .then((r) => setImageTierCosts((r?.data || r)?.imageTiers || null))
            .catch(() => { /* costs are best-effort — buttons still work without them */ });
    }, []);

    // Image history and modal
    const [imageHistory, setImageHistory] = useState([]);
    const [selectedHistoryImage, setSelectedHistoryImage] = useState(null);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [showImageModal, setShowImageModal] = useState(false);
    // The result's eye button opens the viewer on the before/after compare; a history photo opens it as a single image.
    const [imageModalCompare, setImageModalCompare] = useState(false);
    const [isLoadingEdit, setIsLoadingEdit] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deletingImageId, setDeletingImageId] = useState(null);
    const [historyPage, setHistoryPage] = useState(1);
    const [hasMoreHistory, setHasMoreHistory] = useState(true);
    const [isLoadingHistory, setIsLoadingHistory] = useState(false);

    // Background (hidden) generations. When the user hides an in-flight generation
    // the network request keeps running; each entry renders a processing card in the
    // recent slider. Shape: { token, previewUrl, status: 'processing' | 'error' }.
    const [bgJobs, setBgJobs] = useState([]);
    const genTokenRef = useRef(0);          // monotonic id assigned to every generation
    const foregroundTokenRef = useRef(null); // token of the job currently shown in the main preview
    const hiddenTokensRef = useRef(new Set()); // tokens that have been pushed to the background
    const progressIntervalRef = useRef(null); // the single foreground progress-simulation interval
    const jobUidByToken = useRef({});          // genToken -> localStorage marker uid (for reload survival)
    const restoredJobsLoadedRef = useRef(false); // guard: restore persisted jobs only once per mount

    // Left sidebar states — persisted to localStorage
    const PREFS_KEY = 'sohozkaj_generate_prefs';
    const savedPrefs = (() => { try { return JSON.parse(localStorage.getItem(PREFS_KEY)) || {}; } catch { return {}; } })();
    const [size, setSize] = useState(savedPrefs.size ?? 'bd_passport');
    const [visaPopoverOpen, setVisaPopoverOpen] = useState(false);
    const [visaCountryLabel, setVisaCountryLabel] = useState(savedPrefs.visaCountryLabel ?? '');
    const VALID_DRESS_TYPES  = ['shirt', 't-shirt', 'suite', 'suite-tieless', 'sharee', 'sharee-design', 'hizab', 'panjbi', 'tops', 'round-collar-tshirt', 'kameez-hijab', 'shari-hijab', 'orna-kameez', 'jacket', 'abaya', 'thobe', 'thobe-ghutra', 'nehru-jacket', 'hijab-formal'];
    // Every colour of the palette is a valid saved colour (it used to list 12 of the 20, so a saved maroon / teal / gold… dress reset to "no change" on reload).
    const VALID_DRESS_COLORS = ALL_DRESS_COLORS.map(c => c.id);
    const validateDress = (saved) => {
        if (!saved || saved === 'original') return 'original';
        const idx = saved.indexOf('_');
        if (idx === -1) return 'original';
        return (VALID_DRESS_COLORS.includes(saved.slice(0, idx)) && VALID_DRESS_TYPES.includes(saved.slice(idx + 1))) ? saved : 'original';
    };
    const [dress, setDress] = useState(validateDress(savedPrefs.dress));
    const [dress1, setDress1] = useState(validateDress(savedPrefs.dress1)); // dual left
    const [dress2, setDress2] = useState(validateDress(savedPrefs.dress2)); // dual right
    // Dual (couple) only: which side's shoulder overlaps in front where the two meet.
    const [shoulderTop, setShoulderTop] = useState(savedPrefs.shoulderTop === 'right' ? 'right' : 'left');
    // Saved prefs may hold a bg this page can't represent (e.g. 'transparent'
    // poisoned by an old reopen bug) — sanitise to a real swatch or 'custom'.
    const [bg, setBg] = useState(() => {
        const saved = savedPrefs.bg ?? 'white';
        return KNOWN_BG_IDS.includes(saved) || saved === 'custom' ? saved : 'white';
    });
    const [customColor, setCustomColor] = useState(savedPrefs.customColor ?? '#000000');
    const [extraEditingGuide, setExtraEditingGuide] = useState(savedPrefs.extraEditingGuide ?? '');
    const [guideFiltered, setGuideFiltered] = useState(false);
    const guideFilterTimerRef = useRef(null);
    const dp = {
        // New-user defaults: top row of the guide grid (glow/smooth/brighten/
        // studio/straighten) + the custom-instruction tool. Existing users
        // override this via user.defaultPrompts (spread below).
        removeGlasses: false,
        glowMakeup: true,
        smoothSkin: true,
        fixOilySkin: true,
        showCustomInstruction: true,
        removeStrayHair: false,
        removeBeard: false,
        openEyes: false,
        closeMouth: true,
        removeTupi: false,
        straightenHead: true,
        keepSchoolDressDetails: false,
        addBeard: false,
        applyHair: false,
        keepBeard: false,
        keepMustache: false,
        keepFacialMarks: true,
        keepNosePin: true,
        keepSunGlasses: false,
        showEar: false,
        halfBody: true,
        addTupi: false,
        brightenImage: true,
        studioLighting: true,
        lookAtCamera: false,
        keepNamePlate: false,
        lipstick: true,
        ...(user?.defaultPrompts || {})
    };
    // Full-sentence tooltip text, keyed by each tile's short label. The grid tile
    // keeps the compact generic name; hovering shows the complete instruction
    // (e.g. "চশমা বাদ" tile → "চশমা বাদ দিন" tooltip).
    const toolFullLabel = {
        [t('imageGeneration.removeGlass')]:             t('imageGeneration.removeGlassFull'),
        [t('imageGeneration.keepSunGlasses')]:          t('imageGeneration.keepSunGlassesFull'),
        [t('imageGeneration.removeBeard')]:             t('imageGeneration.removeBeardFull'),
        [t('imageGeneration.addBeard')]:                t('imageGeneration.addBeardFull'),
        [t('imageGeneration.keepBeard')]:               t('imageGeneration.keepBeardFull'),
        [t('imageGeneration.keepMustache')]:            t('imageGeneration.keepMustacheFull'),
        [t('imageGeneration.keepFacialMarks')]:         t('imageGeneration.keepFacialMarksFull'),
        [t('imageGeneration.keepNosePin')]:             t('imageGeneration.keepNosePinFull'),
        [t('imageGeneration.removeStrayHair')]:         t('imageGeneration.removeStrayHairFull'),
        [t('imageGeneration.applyHair')]:               t('imageGeneration.applyHairFull'),
        [t('imageGeneration.glowMakeup')]:              t('imageGeneration.glowMakeupFull'),
        [t('imageGeneration.smoothSkin')]:              t('imageGeneration.smoothSkinFull'),
        [t('imageGeneration.fixOilySkin')]:             t('imageGeneration.fixOilySkinFull'),
        [t('imageGeneration.openEyes')]:                t('imageGeneration.openEyesFull'),
        [t('imageGeneration.closeMouth')]:              t('imageGeneration.closeMouthFull'),
        [t('imageGeneration.removeTupi')]:              t('imageGeneration.removeTupiFull'),
        [t('imageGeneration.addTupi')]:                 t('imageGeneration.addTupiFull'),
        [t('imageGeneration.straightenHead')]:          t('imageGeneration.straightenHeadFull'),
        [t('imageGeneration.keepSchoolDressDetails')]:  t('imageGeneration.keepSchoolDressDetailsFull'),
        [t('imageGeneration.showEar')]:                 t('imageGeneration.showEarFull'),
        [t('imageGeneration.halfBody')]: t('imageGeneration.halfBodyFull'),
        [t('imageGeneration.brightenImage')]:           t('imageGeneration.brightenImageFull'),
        [t('imageGeneration.studioLighting')]:          t('imageGeneration.studioLightingFull'),
        [t('imageGeneration.lookAtCamera')]:            t('imageGeneration.lookAtCameraFull'),
        [t('imageGeneration.keepNamePlate')]:           t('imageGeneration.keepNamePlateFull'),
        [t('imageGeneration.lipstick')]:                t('imageGeneration.lipstickFull'),
        [t('imageGeneration.enableCustomInstruction')]: t('imageGeneration.enableCustomInstructionFull'),
    };
    const [removeGlasses, setRemoveGlasses] = useState(savedPrefs.removeGlasses ?? false);
    const [removeTupi, setRemoveTupi] = useState(savedPrefs.removeTupi ?? false);
    const [glowMakeup, setGlowMakeup] = useState(savedPrefs.glowMakeup ?? false);
    const [smoothSkin, setSmoothSkin] = useState(savedPrefs.smoothSkin ?? false);
    const [fixOilySkin, setFixOilySkin] = useState(savedPrefs.fixOilySkin ?? false);
    const [removeBeard, setRemoveBeard] = useState(savedPrefs.removeBeard ?? false);
    const [straightenHead, setStraightenHead] = useState(savedPrefs.straightenHead ?? false);
    const [openEyes, setOpenEyes] = useState(savedPrefs.openEyes ?? false);
    const [closeMouth, setCloseMouth] = useState(savedPrefs.closeMouth ?? false);
    const [removeStrayHair, setRemoveStrayHair] = useState(savedPrefs.removeStrayHair ?? false);
    const [keepSchoolDressDetails, setKeepSchoolDressDetails] = useState(savedPrefs.keepSchoolDressDetails ?? false);
    const [addBeard, setAddBeard]         = useState(savedPrefs.addBeard ?? false);
    const [applyHair, setApplyHair]       = useState(savedPrefs.applyHair ?? false);
    const [addTupi, setAddTupi]           = useState(savedPrefs.addTupi ?? false);
    const [keepBeard, setKeepBeard]       = useState(savedPrefs.keepBeard ?? false);
    const [keepMustache, setKeepMustache]     = useState(savedPrefs.keepMustache ?? false);
    const [keepFacialMarks, setKeepFacialMarks] = useState(savedPrefs.keepFacialMarks ?? false);
    const [keepNosePin, setKeepNosePin] = useState(savedPrefs.keepNosePin ?? false);
    const [keepSunGlasses, setKeepSunGlasses] = useState(savedPrefs.keepSunGlasses ?? false);
    const [showEar, setShowEar]               = useState(savedPrefs.showEar ?? false);
    const [halfBody, setHalfBody] = useState(savedPrefs.halfBody ?? false);
    const [brightenImage, setBrightenImage]   = useState(savedPrefs.brightenImage ?? false);
    const [studioLighting, setStudioLighting] = useState(savedPrefs.studioLighting ?? false);
    const [lookAtCamera, setLookAtCamera]     = useState(savedPrefs.lookAtCamera ?? false);
    const [keepNamePlate, setKeepNamePlate]   = useState(savedPrefs.keepNamePlate ?? false);
    const [namePlateText, setNamePlateText]   = useState(savedPrefs.namePlateText ?? '');
    const [lipstick, setLipstick]             = useState(savedPrefs.lipstick ?? false);
    const [lipstickShade, setLipstickShade]   = useState(savedPrefs.lipstickShade ?? 'red'); // preset id or 'custom'
    const [lipstickCustomColor, setLipstickCustomColor] = useState(savedPrefs.lipstickCustomColor ?? '#c21807');
    const [lipGloss, setLipGloss] = useState(savedPrefs.lipGloss ?? savedPrefs.lipsClosed ?? false); // lipstick popup: glossy lip finish (old prefs key: lipsClosed)
    const [tupiColor, setTupiColor]           = useState(savedPrefs.tupiColor ?? 'white');
    const [lipstickColorPopover, setLipstickColorPopover] = useState(false); // desktop floating shade picker
    const [tupiColorPopover, setTupiColorPopover]         = useState(false); // desktop floating tupi-colour picker
    const [levelPopoverFor, setLevelPopoverFor]           = useState(null);  // which leveled tool's intensity popover is open (glowMakeup|smoothSkin|brightenImage|studioLighting|null)
    const [toolLevels, setToolLevels]                     = useState(savedPrefs.toolLevels ?? {}); // per-tool intensity: subtle|medium|strong (default medium)
    const [showExtraGuide, setShowExtraGuide] = useState(savedPrefs.showExtraGuide ?? false);
    const [dressColorPopover, setDressColorPopover] = useState(null);
    const [suitShirtColor,    setSuitShirtColor]    = useState(savedPrefs.suitShirtColor    || 'white');
    const [suitTieColor,      setSuitTieColor]      = useState(savedPrefs.suitTieColor      || 'navy');
    const [suitShirtColor1,   setSuitShirtColor1]   = useState(savedPrefs.suitShirtColor1   || 'white');
    const [suitTieColor1,     setSuitTieColor1]     = useState(savedPrefs.suitTieColor1     || 'navy');
    const [suitShirtColor2,   setSuitShirtColor2]   = useState(savedPrefs.suitShirtColor2   || 'white');
    const [suitTieColor2,     setSuitTieColor2]     = useState(savedPrefs.suitTieColor2     || 'navy');
    const [shareeBlouseColor,  setShareeBlouseColor]  = useState(savedPrefs.shareeBlouseColor  || 'red');
    const [shareeBlouseColor1, setShareeBlouseColor1] = useState(savedPrefs.shareeBlouseColor1 || 'red');
    const [shareeBlouseColor2, setShareeBlouseColor2] = useState(savedPrefs.shareeBlouseColor2 || 'red');
    const [hijabColor,  setHijabColor]  = useState(savedPrefs.hijabColor  || 'black');
    const [hijabColor1, setHijabColor1] = useState(savedPrefs.hijabColor1 || 'black');
    const [hijabColor2, setHijabColor2] = useState(savedPrefs.hijabColor2 || 'black');
    const [ornaColor,  setOrnaColor]  = useState(savedPrefs.ornaColor  || 'red');
    const [ornaColor1, setOrnaColor1] = useState(savedPrefs.ornaColor1 || 'red');
    const [ornaColor2, setOrnaColor2] = useState(savedPrefs.ornaColor2 || 'red');
    // Thobe + Ghutra: the thobe takes the main colour, the ghutra (head cloth) its own — per person in dual mode.
    const [ghutraColor,  setGhutraColor]  = useState(savedPrefs.ghutraColor  || 'white');
    const [ghutraColor1, setGhutraColor1] = useState(savedPrefs.ghutraColor1 || 'white');
    const [ghutraColor2, setGhutraColor2] = useState(savedPrefs.ghutraColor2 || 'white');
    // Hijab + Formal wear: the hijab takes the main colour, the formal outfit (suit) its own — per person in dual mode.
    const [formalColor,  setFormalColor]  = useState(savedPrefs.formalColor  || 'navy');
    const [formalColor1, setFormalColor1] = useState(savedPrefs.formalColor1 || 'navy');
    const [formalColor2, setFormalColor2] = useState(savedPrefs.formalColor2 || 'navy');
    const [jacketHatColor,  setJacketHatColor]  = useState(savedPrefs.jacketHatColor  || 'black');
    const [jacketHatColor1, setJacketHatColor1] = useState(savedPrefs.jacketHatColor1 || 'black');
    const [jacketHatColor2, setJacketHatColor2] = useState(savedPrefs.jacketHatColor2 || 'black');
    const [shareeHeadCover,  setShareeHeadCover]  = useState(savedPrefs.shareeHeadCover  ?? false);
    const [shareeHeadCover1, setShareeHeadCover1] = useState(savedPrefs.shareeHeadCover1 ?? false);
    const [shareeHeadCover2, setShareeHeadCover2] = useState(savedPrefs.shareeHeadCover2 ?? false);
    const [kameezHeadCover,  setKameezHeadCover]  = useState(savedPrefs.kameezHeadCover  ?? false);
    const [kameezHeadCover1, setKameezHeadCover1] = useState(savedPrefs.kameezHeadCover1 ?? false);
    const [kameezHeadCover2, setKameezHeadCover2] = useState(savedPrefs.kameezHeadCover2 ?? false);
    const [shirtCheck,  setShirtCheck]  = useState(savedPrefs.shirtCheck  ?? false);
    const [shirtCheck1, setShirtCheck1] = useState(savedPrefs.shirtCheck1 ?? false);
    const [shirtCheck2, setShirtCheck2] = useState(savedPrefs.shirtCheck2 ?? false);
    // Stripe pattern for the Panjabi (the Panjabi's counterpart of the check shirt) — per person in dual mode.
    const [panjabiStripe,  setPanjabiStripe]  = useState(savedPrefs.panjabiStripe  ?? false);
    const [panjabiStripe1, setPanjabiStripe1] = useState(savedPrefs.panjabiStripe1 ?? false);
    const [panjabiStripe2, setPanjabiStripe2] = useState(savedPrefs.panjabiStripe2 ?? false);
    // Whether the plain shirt should be worn with a tie (tie colour reuses suitTieColor).
    const [shirtTie,  setShirtTie]  = useState(savedPrefs.shirtTie  ?? false);
    const [shirtTie1, setShirtTie1] = useState(savedPrefs.shirtTie1 ?? false);
    const [shirtTie2, setShirtTie2] = useState(savedPrefs.shirtTie2 ?? false);
    const [suitColorTab, setSuitColorTab] = useState('suit');
    const [dpPopoverOpen, setDpPopoverOpen] = useState(false);
    const [dressManagePopoverOpen, setDressManagePopoverOpen] = useState(false);
    const [loadingDressStyle, setLoadingDressStyle] = useState(null);
    const [loadingTool, setLoadingTool] = useState(null);


    useEffect(() => {
        // Don't overwrite user prefs while programmatically loading settings from history/DB.
        // Reset the flag here (inside the effect) — never via setTimeout, because React 18 concurrent
        // mode can defer renders past a setTimeout(0), which would clear the flag too early.
        if (skipLocalStorageSave.current) {
            skipLocalStorageSave.current = false; // clear so the NEXT user-driven change saves normally
            return;
        }
        try {
            localStorage.setItem(PREFS_KEY, JSON.stringify({
                size, dress, dress1, dress2, shoulderTop, bg, customColor,
                suitShirtColor, suitTieColor,
                suitShirtColor1, suitTieColor1,
                suitShirtColor2, suitTieColor2,
                shareeBlouseColor, shareeBlouseColor1, shareeBlouseColor2,
                hijabColor, hijabColor1, hijabColor2,
                ornaColor, ornaColor1, ornaColor2,
                ghutraColor, ghutraColor1, ghutraColor2, formalColor, formalColor1, formalColor2,
                jacketHatColor, jacketHatColor1, jacketHatColor2,
                shirtTie, shirtTie1, shirtTie2,
                lipstickShade, lipstickCustomColor, lipGloss, tupiColor, toolLevels,
                // Dress sub-options (ghomta / orna head-cover / check pattern), per person.
                shareeHeadCover, shareeHeadCover1, shareeHeadCover2,
                kameezHeadCover, kameezHeadCover1, kameezHeadCover2,
                shirtCheck, shirtCheck1, shirtCheck2,
                panjabiStripe, panjabiStripe1, panjabiStripe2,
                // Extra-instruction tool toggles + their text — so the form remembers them.
                removeGlasses, glowMakeup, smoothSkin, fixOilySkin, removeBeard, addBeard, keepBeard,
                keepMustache, keepFacialMarks, keepNosePin, removeStrayHair, applyHair, openEyes, closeMouth, removeTupi,
                addTupi, straightenHead, keepSchoolDressDetails, showEar, halfBody, keepSunGlasses,
                brightenImage, studioLighting, lookAtCamera, keepNamePlate, lipstick, showExtraGuide,
                namePlateText, extraEditingGuide,
            }));
        } catch {}
    }, [size, dress, dress1, dress2, shoulderTop, bg, customColor, suitShirtColor, suitTieColor, suitShirtColor1, suitTieColor1, suitShirtColor2, suitTieColor2, shareeBlouseColor, shareeBlouseColor1, shareeBlouseColor2, hijabColor, hijabColor1, hijabColor2, ornaColor, ornaColor1, ornaColor2, ghutraColor, ghutraColor1, ghutraColor2, formalColor, formalColor1, formalColor2, jacketHatColor, jacketHatColor1, jacketHatColor2, shirtTie, shirtTie1, shirtTie2, lipstickShade, lipstickCustomColor, lipGloss, tupiColor, toolLevels, shareeHeadCover, shareeHeadCover1, shareeHeadCover2, kameezHeadCover, kameezHeadCover1, kameezHeadCover2, shirtCheck, shirtCheck1, shirtCheck2, panjabiStripe, panjabiStripe1, panjabiStripe2, removeGlasses, glowMakeup, smoothSkin, fixOilySkin, removeBeard, addBeard, keepBeard, keepMustache, keepFacialMarks, keepNosePin, removeStrayHair, applyHair, openEyes, closeMouth, removeTupi, addTupi, straightenHead, keepSchoolDressDetails, showEar, halfBody, keepSunGlasses, brightenImage, studioLighting, lookAtCamera, keepNamePlate, lipstick, showExtraGuide, namePlateText, extraEditingGuide]);

    useEffect(() => {
        setGeneratedImageReady(false);
        setBlurRevealing(false);
        if (!generatedImage) return;
        const img = new window.Image();
        img.onload  = () => { setGeneratedImageReady(true); setBlurRevealing(true); };
        img.onerror = () => setGeneratedImageReady(true);
        img.src = generatedImage;
    }, [generatedImage]);

    useEffect(() => {
        if (!blurRevealing) return;
        const t = setTimeout(() => setBlurRevealing(false), 800);
        return () => clearTimeout(t);
    }, [blurRevealing]);

    useEffect(() => {
        if (!isGenerating) { setGeneratingStep(0); return; }
        setGeneratingStep(0);
        const timers = STEP_DELAYS.map((delay, i) =>
            setTimeout(() => setGeneratingStep(i + 1), delay)
        );
        return () => timers.forEach(clearTimeout);
    }, [isGenerating]);

    // Track if settings changed for regenerate
    const [settingsChanged, setSettingsChanged] = useState(false);

    // Regenerate-tracking key for the DUAL per-person garments/sub-options + effect LEVELS —
    // none of these are in the flat initialSettings comparison below, so a dual dress change
    // (or a per-person colour/ghomta/check, or an intensity level) wasn't enabling the button.
    // Bundle them into one serialized key: change any → key changes → settingsChanged flips.
    // dress1/dress2 are passed in so history-restore can seed them from the record's dressCode.
    const buildDualLevelKey = (d1, d2) => JSON.stringify([
        d1, d2,
        suitShirtColor1, suitTieColor1, suitShirtColor2, suitTieColor2,
        shareeBlouseColor1, shareeBlouseColor2, hijabColor1, hijabColor2,
        ornaColor1, ornaColor2, ghutraColor1, ghutraColor2, formalColor1, formalColor2, jacketHatColor1, jacketHatColor2,
        shirtTie1, shirtTie2, shirtCheck1, shirtCheck2, panjabiStripe1, panjabiStripe2,
        shareeHeadCover1, shareeHeadCover2, kameezHeadCover1, kameezHeadCover2,
        toolLevels,
        // Which side's shoulder is on top — changing it must re-enable regenerate.
        shoulderTop,
    ]);
    const dualLevelKey = buildDualLevelKey(dress1, dress2);
    const [initialSettings, setInitialSettings] = useState(null);

    // When true the localStorage useEffect skips saving — set during programmatic loads from history/DB
    const skipLocalStorageSave = useRef(false);

    const fileInputRef = useRef(null);
    const cameraInputRef = useRef(null);
    const [showUploadSheet, setShowUploadSheet] = useState(false);
    const colorPickerRef = useRef(null);
    const compareContainerRef = useRef(null);
    // Zoom modal state — separate for each panel
    const [zoomOrig, setZoomOrig] = useState(1);
    const [panOrig, setPanOrig] = useState({ x: 0, y: 0 });
    const [zoomGen, setZoomGen] = useState(1);
    const [panGen, setPanGen] = useState({ x: 0, y: 0 });
    const dragRef = useRef(null); // { panel, startX, startY, startPanX, startPanY }

    const zoomLevel = zoomGen; // keep alias for reset in handleOpenZoom

    const makeZoomIn  = (set) => () => set(z => Math.min(+(z + 0.25).toFixed(2), 4));
    const makeZoomOut = (set) => () => set(z => Math.max(+(z - 0.25).toFixed(2), 0.5));

    // Compare view: zoom is SYNCED across both panels — zooming either side zooms BOTH
    // together (pan stays independent per side). Reset clears both zoom + both pan.
    const zoomBothIn    = () => { makeZoomIn(setZoomOrig)(); makeZoomIn(setZoomGen)(); };
    const zoomBothOut   = () => { makeZoomOut(setZoomOrig)(); makeZoomOut(setZoomGen)(); };
    const zoomBothReset = () => { setZoomOrig(1); setZoomGen(1); setPanOrig({ x: 0, y: 0 }); setPanGen({ x: 0, y: 0 }); };

    const handleZoomPanStart = (panel, e) => {
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const clientY = e.touches ? e.touches[0].clientY : e.clientY;
        const pan = panel === 'orig' ? panOrig : panGen;
        dragRef.current = { panel, startX: clientX, startY: clientY, startPanX: pan.x, startPanY: pan.y };
        e.currentTarget.setPointerCapture?.(e.pointerId);
    };

    const handleZoomPanMove = (panel, e, zoom) => {
        if (!dragRef.current || dragRef.current.panel !== panel) return;
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const clientY = e.touches ? e.touches[0].clientY : e.clientY;
        const dx = (clientX - dragRef.current.startX) / zoom;
        const dy = (clientY - dragRef.current.startY) / zoom;
        const setPan = panel === 'orig' ? setPanOrig : setPanGen;
        setPan({ x: dragRef.current.startPanX + dx, y: dragRef.current.startPanY + dy });
    };

    const handleZoomPanEnd = () => { dragRef.current = null; };

    // Image metadata state
    const [imageMetadata, setImageMetadata] = useState(null);

    // Rename state for the generated image
    const [customImageName, setCustomImageName] = useState(null);
    const [renameEditing, setRenameEditing] = useState(false);
    const [renameInputVal, setRenameInputVal] = useState('');
    const [renameSaving, setRenameSaving] = useState(false);

    // Feedback state: Set of image IDs that already have feedback
    const [feedbackGiven, setFeedbackGiven] = useState(new Set());
    const [feedbackLoading, setFeedbackLoading] = useState(null); // 'positive' | 'negative' | null
    const [showFeedbackModal, setShowFeedbackModal] = useState(false); // dislike → reason/message modal
    const [pendingFeedbackId, setPendingFeedbackId] = useState(null);
    const [copyingResult, setCopyingResult] = useState(false); // result hover-overlay copy button spinner
    const [downloading, setDownloading] = useState(false);
    const [customDownloadLoading, setCustomDownloadLoading] = useState(false); // custom size & quality: the edited image is being prepared
    const downloadBusy = downloading || customDownloadLoading; // the Download button / tile spins for either
    const [printing, setPrinting] = useState(false);

    // History carousel (Embla)
    const [emblaApi, setEmblaApi] = useState(null);
    // Maps a dual (landscape) history image id → its aspect ratio (w/h), so each
    // slide can be sized to the image's exact shape with no letterbox blank space.
    const [dualHistoryAspects, setDualHistoryAspects] = useState({});
    const HISTORY_SLIDE_H = isTabletView ? 120 : 160; // matches .historyItem height in the SCSS
    const HISTORY_SLIDE_W = isTabletView ? 90 : 120;
    const pendingNextNavigateRef = useRef(false);

    // The result's edits (the Studio Editor's drawer: tools, filters, rotate / flip — utils/photoEdits.js). Never saved: they are baked
    // into the download / custom size / copy / print by getEditedImageUrl. `editorOpen` = the drawer over the result photo.
    const [edits, setEdits] = useState(NEUTRAL_EDITS);
    const [editorOpen, setEditorOpen] = useState(false);

    // No-credit modal state
    const [noCreditModalOpen, setNoCreditModalOpen] = useState(false);

    // Eraser modal state
    const [eraserModalOpen, setEraserModalOpen] = useState(false);
    const [erasedImageUrl, setErasedImageUrl] = useState(null);

    // Zoom modal state
    const [showZoomModal, setShowZoomModal] = useState(false);
    const [zoomImgNaturalSize, setZoomImgNaturalSize] = useState({ w: 3, h: 4 });

    // Print modal state
    const [showPrintModal, setShowPrintModal] = useState(false);
    const [printImageUrl, setPrintImageUrl] = useState(null);
    const [printImageId, setPrintImageId] = useState(null);

    // Custom download modal state
    const [showCustomDownload, setShowCustomDownload] = useState(false);
    const [customInline, setCustomInline] = useState(false); // phone: custom size opened from the result's bar, shown in its place
    const [resultActionsOpen, setResultActionsOpen] = useState(true); // phone: the action bar under the result (the সেটিং tab toggles it)
    const [customDownloadImageUrl, setCustomDownloadImageUrl] = useState(null);

    // Crop-GENERATED-image modal state (distinct from the upload CropStage flow above).
    // resultCropSrc is a blob object-URL (fetched via the same-origin download API so the
    // crop canvas isn't CORS-tainted); resultCropMime keeps the source format so Apply
    // re-saves in the same format at full resolution.
    const [showResultCrop, setShowResultCrop] = useState(false);
    const [resultCropSrc, setResultCropSrc] = useState(null);
    const [resultCropMime, setResultCropMime] = useState('image/jpeg');
    const [resultCropLoading, setResultCropLoading] = useState(false);

    // Dual / merge state
    const [dualUploadMode, setDualUploadMode] = useState(savedPrefs.size === 'dual_image');
    const [mergeLeft, setMergeLeft] = useState(null);   // { file, url }
    const [mergeRight, setMergeRight] = useState(null); // { file, url }
    const [merging, setMerging] = useState(false);
    const [isMergedImage, setIsMergedImage] = useState(false);
    const inlineMergeLeftRef = useRef(null);
    const inlineMergeRightRef = useRef(null);
    const cameraMergeLeftRef = useRef(null);
    const cameraMergeRightRef = useRef(null);
    const [dualUploadSide, setDualUploadSide] = useState(null); // 'left' | 'right' | null
    // Single-person dual: when the user closes one box, the photo already contains
    // both people so only one image is uploaded (no join). null = both boxes shown,
    // 'left'/'right' = only that box is kept.
    const [dualSoloSide, setDualSoloSide] = useState(null);
    const leftZoneRef = useRef(null);
    const rightZoneRef = useRef(null);
    const dualDragRef = useRef(null);

    // Crop/Rotate modal state
    const [showCropModal, setShowCropModal] = useState(false);
    const [cropImageSrc, setCropImageSrc] = useState(null);
    const [pendingCropFile, setPendingCropFile] = useState(null);
    const cropStageRef = useRef(null); // imperative handle to CropStage.apply()
    // Where the cropped result goes: null = single `file`, 'left'/'right' = a dual merge slot.
    const cropTargetSideRef = useRef(null);
    // Crop aspect + output size. Portrait 512×640 for single mode and the two-person
    // boxes; landscape 836×512 (the 418/256 zone) for the "one photo of two people" box.
    const [cropAspect, setCropAspect] = useState({ ratio: 512 / 640, outW: 512, outH: 640 });
    const cropAspectRef = useRef({ outW: 512, outH: 640 }); // mirror for the skip callback

    // Mobile bottom-nav: only one drawer open at a time
    const [activeDrawer, setActiveDrawer] = useState(null); // 'photo' | 'style' | 'extra' | null
    const [styleDrTab, setStyleDrTab]     = useState('dress'); // 'dress' | 'bg'
    const mobilePhotoDrawer = activeDrawer === 'photo';
    const mobileDressDrawer = activeDrawer === 'style' && styleDrTab === 'dress';
    const mobileBgDrawer    = activeDrawer === 'style' && styleDrTab === 'bg';
    const mobileExtraDrawer = activeDrawer === 'extra';
    const setMobilePhotoDrawer = (v) => setActiveDrawer(v ? 'photo' : null);
    const setMobileDressDrawer = (v) => { setActiveDrawer(v ? 'style' : null); if (v) setStyleDrTab('dress'); };
    const setMobileBgDrawer    = (v) => { setActiveDrawer(v ? 'style' : null); if (v) setStyleDrTab('bg'); };
    const setMobileExtraDrawer = (v) => setActiveDrawer(v ? 'extra' : null);

    // QR upload modal state
    const [showQrModal, setShowQrModal] = useState(false);
    const [qrDataUrl, setQrDataUrl] = useState(null);
    const [qrToken, setQrToken] = useState(null);
    const [qrMobileUrl, setQrMobileUrl] = useState(null);
    const [qrTimeLeft, setQrTimeLeft] = useState(null);
    const [qrStatus, setQrStatus] = useState('idle'); // idle | loading | ready | received | expired
    const qrPollRef = useRef(null);
    const qrTimerRef = useRef(null);
    // Permanent-QR customer photo inbox (per office space). Bumping pcRefreshKey
    // forces the panel to re-fetch; loadedQueueRef tracks which queue photo is
    // currently loaded so a SUCCESSFUL generation removes it from the inbox (the
    // removal is hooked into handleGenerate's success, not a generic effect, so
    // merely viewing a history item never deletes a queued photo).
    const [pcRefreshKey, setPcRefreshKey] = useState(0);
    const loadedQueueRef = useRef({ id: null, file: null });
    // Booth photo currently travelling through the crop modal — commitCropResult
    // links the CROPPED file back to this upload id for remove-on-generate.
    const pendingQueueIdRef = useRef(null);
    // Booth photos the CUSTOMER marked private, and the file that came from one: whatever is made from them is deleted once it has been
    // downloaded / printed (utils/privateResults.js). The file is remembered past the booth photo's removal so a REGENERATE is private too.
    const privateQueueIdsRef = useRef(new Set());
    const privateFileRef = useRef(null);
    // Booth photos sitting in the dual boxes (upload id per side) — consumed on a
    // successful dual generation, mirroring the single-mode loadedQueueRef flow.
    const dualQueueIdsRef = useRef({ left: null, right: null });
    // Floating Generate: a fixed gradient-backed clone takes over at the same
    // x-position the MOMENT the real button starts to clip out of the viewport
    // (≥95% visible counts as "in view"), so the handoff is seamless — no dead
    // zone where the real button is half-cut with no sticky one yet.
    const [genBtnEl, setGenBtnEl] = useState(null);
    const [genBtnInView, setGenBtnInView] = useState(true);
    const [genFloatX, setGenFloatX] = useState(null);
    useEffect(() => {
        if (!genBtnEl || typeof IntersectionObserver === 'undefined') { setGenBtnInView(true); return; }
        const measure = () => { const r = genBtnEl.getBoundingClientRect(); setGenFloatX({ left: r.left, width: r.width }); };
        measure();
        window.addEventListener('resize', measure);
        const io = new IntersectionObserver(([en]) => setGenBtnInView(en.intersectionRatio >= 0.95), { threshold: [0.95] });
        io.observe(genBtnEl);
        return () => { window.removeEventListener('resize', measure); io.disconnect(); };
    }, [genBtnEl]);
    // Whether the Photo Booth inbox is ON — decides WHICH QR card shows. To avoid a
    // flash on reload (the one-time QR appearing then swapping to the booth), seed
    // from a cached value and only reveal the one-time QR once the state is RESOLVED.
    const [pcEnabled, setPcEnabled] = useState(() => { try { return localStorage.getItem('sk_photobooth_enabled') === '1'; } catch { return false; } });
    const [pcResolved, setPcResolved] = useState(false);
    // Live booth queue count reported up by the (always-mounted) desktop panel — feeds
    // the mobile floating pill's N/M badge without a second poller.
    const [pcCount, setPcCount] = useState({ count: 0, max: 15 });
    const handlePcCount = useCallback((count, max) => setPcCount({ count, max }), []);
    useEffect(() => {
        let alive = true;
        const oid = selectedOfficeSpace?.id;
        if (!oid) {
            // Admin / no office → no booth, show the one-time QR. A non-admin whose
            // office is still loading stays UNRESOLVED so neither card flashes.
            if (isAdmin) { setPcEnabled(false); setPcResolved(true); }
            return;
        }
        photoCollectorService.get(oid)
            .then(res => {
                if (!alive) return;
                const en = res?.data?.collector?.enabled === true;
                setPcEnabled(en);
                setPcResolved(true);
                try { localStorage.setItem('sk_photobooth_enabled', en ? '1' : '0'); } catch { /* ignore */ }
            })
            .catch(() => { if (alive) setPcResolved(true); });
        return () => { alive = false; };
    }, [selectedOfficeSpace?.id, isAdmin, pcRefreshKey]);
    const [customDownloadImageId, setCustomDownloadImageId] = useState(null);

    // URL search params for edit mode
    const [searchParams, setSearchParams] = useSearchParams();
    const [isLoadingFromUrl, setIsLoadingFromUrl] = useState(false);

    // Load usage stats to check limit status
    // Reload when office space changes to get owner's limit status
    useEffect(() => {
        const loadUsageStats = async () => {
            try {
                const stats = await usageService.getUserUsageStats(selectedOfficeSpace?.id);
                setUsageStats(stats);
                // console.log({ stats });
            } catch (error) {
                console.error('Failed to load usage stats:', error);
            }
        };
        loadUsageStats();
    }, [selectedOfficeSpace]);

    // Load image history on mount and when office space changes. Admins have
    // no office space — load their personal history once on mount.
    useEffect(() => {
        if (selectedOfficeSpace || isAdmin) {
            loadHistory();
        }
    }, [selectedOfficeSpace, isAdmin]);

    // Restore reload-surviving generations: any job that was still in-flight when
    // the tab was reloaded gets re-shown as a processing card in the recent slider.
    useEffect(() => {
        if (!(selectedOfficeSpace || isAdmin)) return;
        if (restoredJobsLoadedRef.current) return;
        restoredJobsLoadedRef.current = true;

        const scopeId = selectedOfficeSpace?.id ?? null;
        const now = Date.now();
        const all = readPendingJobs();
        // Drop truly-stale markers outright.
        all.filter(j => (now - j.startedAt) >= PENDING_JOB_MAX_AGE_MS).forEach(j => removePendingJob(j.uid));
        const mine = all.filter(j => (j.officeSpaceId ?? null) === scopeId && (now - j.startedAt) < PENDING_JOB_MAX_AGE_MS);
        if (mine.length === 0) return;

        setBgJobs(prev => [
            ...mine.map(j => ({
                token: j.uid,            // string uid — never collides with numeric live tokens
                previewUrl: j.thumb || null,
                status: 'processing',
                restored: true,
                uid: j.uid,
                startedAt: j.startedAt,
                baselineMaxId: j.baselineMaxId || 0,
            })),
            ...prev,
        ]);
    }, [selectedOfficeSpace, isAdmin]);

    // While restored jobs are pending: poll history so they can be reconciled, and
    // time out ones that never appear (backend likely failed & refunded).
    useEffect(() => {
        const hasRestored = bgJobs.some(j => j.restored && j.status === 'processing');
        if (!hasRestored) return;
        const iv = setInterval(() => {
            const now = Date.now();
            setBgJobs(prev => prev.map(j => {
                if (j.restored && j.status === 'processing' && (now - j.startedAt) > PENDING_JOB_TTL_MS) {
                    removePendingJob(j.uid);
                    return { ...j, status: 'error' };
                }
                return j;
            }));
            loadHistory(); // refreshes imageHistory → triggers the reconcile effect below
        }, 4000);
        return () => clearInterval(iv);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bgJobs]);

    // Reconcile restored jobs against freshly-loaded history: match each pending
    // job (oldest first) to the earliest image created after it started.
    useEffect(() => {
        const restored = bgJobs.filter(j => j.restored && j.status === 'processing');
        if (restored.length === 0 || imageHistory.length === 0) return;

        const jobsOldestFirst = [...restored].sort((a, b) => a.startedAt - b.startedAt);
        const claimed = new Set();
        const resolved = [];
        for (const job of jobsOldestFirst) {
            const match = imageHistory
                .filter(img => img.id > (job.baselineMaxId || 0) && !claimed.has(img.id))
                .sort((a, b) => a.id - b.id)[0];
            if (match) { claimed.add(match.id); resolved.push(job.uid); }
        }
        if (resolved.length === 0) return;

        setBgJobs(prev => prev.filter(j => !resolved.includes(j.uid)));
        resolved.forEach(uid => removePendingJob(uid));
        playSuccessChime();
        notifications.show({
            title: t('imageGeneration.notifications.success'),
            message: t('imageGeneration.notifications.backgroundImageReady'),
            color: 'green',
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [imageHistory]);

    // Handle editId query param to load image for editing from URL
    const editIdLoadedRef = useRef(false);
    useEffect(() => {
        const editId = searchParams.get('editId');
        if (!editId) return;
        if (editIdLoadedRef.current) return;
        editIdLoadedRef.current = true;

        const loadImageFromUrl = async () => {
            setIsLoadingFromUrl(true);
            try {
                // Fetch image metadata
                const response = await imageGenerationService.getImageById(editId);
                if (!response?.success || !response?.data) {
                    throw new Error('Image not found');
                }

                const imageData = response.data;

                // Fetch the original uploaded image blob for preview
                const originalBlob = await imageGenerationService.downloadImage(editId, 'original');
                const uploadedFile = new File([originalBlob], 'uploaded-image.jpg', { type: originalBlob.type || 'image/jpeg' });
                const previewBlobUrl = URL.createObjectURL(originalBlob);

                // Set the file and preview (original image)
                setFile(uploadedFile);
                setPreviewUrl(previewBlobUrl);
                // Set the AI generated image
                setGeneratedImage(imageData.aiImage);
                setResultBg(imageData.background || null);

                // Set the image ID so rename / download / tracking work.
                // Image ids are now opaque source-tagged refs (e.g. "g390") — keep as-is.
                setGeneratedImageId(editId);

                // Populate Image Info panel
                const metaImg = new window.Image();
                metaImg.onload = function () {
                    setImageMetadata({
                        filename: imageData.fileName || 'generated-image.jpg',
                        dimensions: `${this.naturalWidth} × ${this.naturalHeight}px`,
                        photoSize: photoSizes.find(s => s.value === imageData.photoSize)?.label || imageData.photoSize || '',
                        dressCode: formatDressLabel(imageData.dressCode) || imageData.dressCode || '',
                        background: imageData.background === 'transparent'
                            ? t('imageGeneration.bgTransparent')
                            : backgrounds.find(b => b.id === imageData.background || b.color === imageData.background)?.label || imageData.background || '',
                        quality: imageData.quality || imageData.metadata?.quality || 'normal',
                    });
                    // Restore custom name if the image was previously renamed
                    setCustomImageName(imageData.metadata?.fileName || null);
                    setRenameEditing(false);
                };
                metaImg.src = imageData.aiImage;

                // Restore settings from the image — skip localStorage save during this block.
                // The flag is cleared inside the useEffect once it fires, not via setTimeout.
                skipLocalStorageSave.current = true;

                if (imageData.photoSize) setSize(imageData.photoSize);
                if (imageData.dressCode) {
                    if (imageData.dressCode.includes('|')) {
                        const [d1, d2] = imageData.dressCode.split('|');
                        setDress1(validateDress(d1));
                        setDress2(validateDress(d2));
                    } else {
                        setDress(imageData.dressCode);
                    }
                }
                if (imageData.background) {
                    const resolvedId = resolveBgFromDb(imageData.background);
                    if (resolvedId) {
                        setBg(resolvedId);
                    } else {
                        setBg('custom');
                        setCustomColor(imageData.background);
                    }
                }
                // Re-select the guide tools used for this generation (legacy records
                // parse the stored English label names — see parseRecordTools).
                const restored = parseRecordTools(imageData);
                applyRestoredTools(restored);

                // Handle dual vs single mode without touching localStorage
                if (imageData.photoSize === 'dual_image') {
                    setIsMergedImage(true);
                    setDualUploadMode(false);
                } else {
                    setIsMergedImage(false);
                    setDualUploadMode(false);
                }

                // Set initial settings for regenerate tracking
                setInitialSettings(restoredInitialSettings(imageData, restored));
                setSettingsChanged(false);

                // Clear the editId from URL to prevent re-loading on refresh
                setSearchParams({}, { replace: true });

                notifications.show({
                    title: t('imageGeneration.notifications.imageLoaded'),
                    message: t('imageGeneration.notifications.imageLoadedMessage'),
                    color: 'blue',
                });
            } catch (error) {
                console.error('Error loading image from URL:', error);
                notifications.show({
                    title: t('imageGeneration.notifications.loadFailed'),
                    message: t('imageGeneration.notifications.loadFailedMessage'),
                    color: 'red',
                });
                // Clear invalid editId from URL
                setSearchParams({}, { replace: true });
            } finally {
                setIsLoadingFromUrl(false);
            }
        };

        loadImageFromUrl();
    }, [searchParams, t, setSearchParams]);

    // Embla infinite scroll — load next page when near end or when fully scrolled
    useEffect(() => {
        if (!emblaApi) return;

        const tryLoadMore = () => {
            if (!hasMoreHistory || isLoadingHistory) return;
            if (emblaApi.scrollProgress() >= 0.75 || !emblaApi.canScrollNext()) {
                loadHistory(historyPage + 1, true);
            }
        };

        emblaApi.on('scroll', tryLoadMore);
        emblaApi.on('settle', tryLoadMore);
        return () => {
            emblaApi.off('scroll', tryLoadMore);
            emblaApi.off('settle', tryLoadMore);
        };
    }, [emblaApi, hasMoreHistory, isLoadingHistory, historyPage]);


    // Track settings changes
    useEffect(() => {
        if (initialSettings && generatedImage) {
            // `bg` counts as a settings change ONLY for opaque (baked-bg) results —
            // their colour is fixed inside the image, so changing it needs a paid
            // re-generation. Transparent masters recolour instantly (composited +
            // saved) and must NOT trip the regenerate button. Compare via the APPLIED
            // colour: the restore path stores a raw hex while the live state holds a
            // swatch id, and pickerColorFrom normalises both.
            const resultOpaque = !(resultBg === 'transparent' || /\.(png|webp)(\?|#|$)/i.test(generatedImage || ''));
            const bgChanged = resultOpaque &&
                pickerColorFrom(bg, customColor) !== pickerColorFrom(initialSettings.bg, initialSettings.customColor ?? customColor);
            const changed =
                size !== initialSettings.size ||
                dress !== initialSettings.dress ||
                dualLevelKey !== initialSettings.dualLevelKey ||
                bgChanged ||
                removeGlasses !== initialSettings.removeGlasses ||
                removeTupi !== initialSettings.removeTupi ||
                glowMakeup !== initialSettings.glowMakeup ||
                smoothSkin !== initialSettings.smoothSkin ||
                fixOilySkin !== initialSettings.fixOilySkin ||
                removeBeard !== initialSettings.removeBeard ||
                straightenHead !== initialSettings.straightenHead ||
                openEyes !== initialSettings.openEyes ||
                closeMouth !== initialSettings.closeMouth ||
                removeStrayHair !== initialSettings.removeStrayHair ||
                keepSchoolDressDetails !== initialSettings.keepSchoolDressDetails ||
                addBeard !== initialSettings.addBeard ||
                applyHair !== initialSettings.applyHair ||
                keepBeard !== initialSettings.keepBeard ||
                keepMustache !== initialSettings.keepMustache ||
                keepFacialMarks !== initialSettings.keepFacialMarks ||
                keepNosePin !== initialSettings.keepNosePin ||
                keepSunGlasses !== initialSettings.keepSunGlasses ||
                showEar !== initialSettings.showEar ||
                halfBody !== initialSettings.halfBody ||
                addTupi !== initialSettings.addTupi ||
                brightenImage !== initialSettings.brightenImage ||
                studioLighting !== initialSettings.studioLighting ||
                lookAtCamera !== initialSettings.lookAtCamera ||
                keepNamePlate !== initialSettings.keepNamePlate ||
                namePlateText !== initialSettings.namePlateText ||
                lipstick !== initialSettings.lipstick ||
                (lipstick && lipstickShade !== initialSettings.lipstickShade) ||
                (lipstick && lipstickShade === 'custom' && lipstickCustomColor !== initialSettings.lipstickCustomColor) ||
                (lipstick && lipGloss !== (initialSettings.lipGloss === true)) ||
                (addTupi && tupiColor !== initialSettings.tupiColor) ||
                shareeHeadCover !== initialSettings.shareeHeadCover ||
                kameezHeadCover !== initialSettings.kameezHeadCover ||
                shirtCheck !== initialSettings.shirtCheck ||
                panjabiStripe !== initialSettings.panjabiStripe ||
                ghutraColor !== initialSettings.ghutraColor ||
                formalColor !== initialSettings.formalColor ||
                showExtraGuide !== initialSettings.showExtraGuide ||
                extraEditingGuide !== initialSettings.extraEditingGuide;
            setSettingsChanged(changed);
        }
    }, [size, dress, bg, customColor, resultBg, removeGlasses, removeTupi, glowMakeup, smoothSkin, fixOilySkin, removeBeard, straightenHead, openEyes, closeMouth, removeStrayHair, keepSchoolDressDetails, addBeard, applyHair, keepBeard, keepMustache, keepFacialMarks, keepNosePin, keepSunGlasses, showEar, halfBody, addTupi, brightenImage, studioLighting, lookAtCamera, keepNamePlate, namePlateText, lipstick, lipstickShade, lipstickCustomColor, lipGloss, tupiColor, shareeHeadCover, kameezHeadCover, shirtCheck, panjabiStripe, ghutraColor, formalColor, showExtraGuide, extraEditingGuide, dualLevelKey, initialSettings, generatedImage]);

    const loadHistory = async (page = 1, append = false) => {
        if (isLoadingHistory || (!hasMoreHistory && append)) return;

        // Regular users need an office space selected to see shared history.
        // Admins don't belong to any office space — fetch their personal history instead.
        if (!isAdmin && !selectedOfficeSpace) {
            setImageHistory([]);
            return;
        }

        try {
            setIsLoadingHistory(true);
            const response = await imageGenerationService.getHistory({
                page,
                limit: 20,
                source: 'regular',
                ...(selectedOfficeSpace?.id ? { officeSpaceId: selectedOfficeSpace.id } : {}),
            });
            if (response.data && response.data.history) {
                const newHistory = response.data.history;

                if (append) {
                    setImageHistory(prev => {
                        const existingIds = new Set(prev.map(img => img.id));
                        const unique = newHistory.filter(img => !existingIds.has(img.id));
                        return [...prev, ...unique];
                    });
                } else {
                    setImageHistory(newHistory);
                }

                // Pre-populate feedbackGiven from history metadata
                const withFeedback = newHistory.filter(img => img.feedback).map(img => img.id);
                setFeedbackGiven(prev => new Set([...prev, ...withFeedback]));

                // Check if there are more pages using the pagination metadata
                const pagination = response.data.pagination;
                setHasMoreHistory(pagination ? pagination.page < pagination.totalPages : newHistory.length === 20);
                setHistoryPage(page);
            }
        } catch (error) {
            console.error('Failed to load history:', error);
        } finally {
            setIsLoadingHistory(false);
        }
    };

    const openPrintModal = (imageUrl, imageId) => {
        setPrintImageUrl(imageUrl);
        setPrintImageId(imageId);
        setShowPrintModal(true);
        // Close the image preview modal so it doesn't float above the print page.
        // Previously this only happened as a side effect of the income/expense
        // modal stealing focus, so it broke when the auto-popup was disabled.
        setShowImageModal(false);

        // Auto-open income/expense modal when print is triggered — clean module name +
        // price come from the unified income service list (moduleAutoFill).
        if (user?.settings?.autoAppearIncomeExpense === true) {
            const fill = moduleAutoFill(user?.settings, 'image_generation');
            openIncomeExpenseModal({
                name: fill.name,
                type: 'income',
                amount: fill.price,
                category: 'image_generation',
                referenceId: imageId,
            });
        }
    };

    const handleFileSelect = (event) => {
        const selectedFile = event.target.files[0];
        processFile(selectedFile);
    };

    const commitFile = async (selectedFile) => {
        // Optimize every committed image to ≤ 500KB before storing it. Files
        // already under the cap (e.g. the 512×640 crop output) pass through
        // untouched; oversized originals from the fallback paths get downscaled
        // and JPEG-compressed here so we never keep an unoptimized upload.
        const optimizedFile = await optimizeImageFile(selectedFile);
        setFile(optimizedFile);
        setIsMergedImage(false);
        const reader = new FileReader();
        reader.onload = (e) => { setPreviewUrl(e.target.result); };
        reader.onerror = () => {
            notifications.show({
                title: t('imageGeneration.notifications.fileReadError'),
                message: t('imageGeneration.notifications.fileReadErrorMessage'),
                color: 'red',
            });
        };
        reader.readAsDataURL(optimizedFile);
        setGeneratedImage(null);
    };

    // Route a finalized (cropped or fallback) file to its destination: the single
    // `file` state, or a dual merge slot when the upload came from a dual zone.
    // Reads cropTargetSideRef so it stays correct inside the crop-modal callbacks.
    const commitCropResult = (fileObj) => {
        const side = cropTargetSideRef.current;
        cropTargetSideRef.current = null;
        if (side === 'left' || side === 'right') {
            const url = URL.createObjectURL(fileObj);
            const slot = { file: fileObj, url, offsetX: 0, offsetY: 0, zoom: 1 };
            if (side === 'left') setMergeLeft(slot); else setMergeRight(slot);
            // Booth photo → remember its upload id for this side; a normal upload
            // into the side clears any stale booth link.
            dualQueueIdsRef.current[side] = pendingQueueIdRef.current || null;
            pendingQueueIdRef.current = null;
            return;
        }
        // Booth photo → link the committed (cropped) file for remove-on-generate;
        // any other upload breaks a stale link (the booth photo left the editor).
        loadedQueueRef.current = pendingQueueIdRef.current
            ? { id: pendingQueueIdRef.current, file: fileObj }
            : { id: null, file: null };
        pendingQueueIdRef.current = null;
        commitFile(fileObj);
    };

    // Preload an image handed off from another tool (e.g. the Sohoz Job photo
    // crop "Make professional" button) via localStorage — so it doesn't need
    // to be uploaded again here. NOTE: no cancel guard — under React StrictMode
    // the effect runs twice; reading + removing the key makes it one-shot, and
    // the async commit must NOT be cancelled by the first cleanup.
    useEffect(() => {
        let raw;
        try { raw = localStorage.getItem('genimage_preload'); } catch { raw = null; }
        if (!raw) return;
        try { localStorage.removeItem('genimage_preload'); } catch { /* ignore */ }
        fetch(raw)
            .then((r) => r.blob())
            .then((blob) => {
                const file = new File([blob], 'sohozkaj-photo.jpg', { type: blob.type || 'image/jpeg' });
                commitFile(file);
            })
            .catch(() => { /* ignore bad preload */ });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Compress a canvas to JPEG ≤ maxBytes, stepping quality down from 0.92 → 0.72.
    // Kept high (was 0.85 → 0.50) so images routed through here — dual merges and the
    // oversized-original fallback — don't get visibly softened.
    const canvasToCompressedBlob = (canvas, maxBytes = 900 * 1024) => new Promise((resolve) => {
        let quality = 0.92;
        const attempt = () => canvas.toBlob((blob) => {
            if (blob.size <= maxBytes || quality <= 0.72) { resolve(blob); }
            else { quality = Math.round((quality - 0.06) * 100) / 100; attempt(); }
        }, 'image/jpeg', quality);
        attempt();
    });

    // Hard cap for any stored upload. The crop path already produces small
    // files, but fallback paths (HEIC-convert failure, undecodable image,
    // cross-tool preload) commit the untouched original — this keeps those ≤1MB.
    // Raised from 500KB so the sharper native-resolution crop (up to 1600px long edge)
    // passes through UNTOUCHED instead of being re-compressed/softened here.
    const MAX_UPLOAD_BYTES = 1024 * 1024;

    // Downscale + JPEG-compress an image file so it fits under MAX_UPLOAD_BYTES.
    // Files already under the cap are returned as-is. If the image can't be
    // decoded (e.g. a HEIC that failed conversion), the original is returned
    // unchanged so the upload still goes through.
    const optimizeImageFile = (inputFile) => new Promise((resolve) => {
        if (!inputFile || inputFile.size <= MAX_UPLOAD_BYTES) { resolve(inputFile); return; }
        const url = URL.createObjectURL(inputFile);
        const img = new window.Image();
        img.onload = () => {
            URL.revokeObjectURL(url);
            // Cap the longest edge at 1600px to shrink oversized camera photos
            const maxEdge = 1600;
            let width = img.naturalWidth;
            let height = img.naturalHeight;
            if (Math.max(width, height) > maxEdge) {
                const scale = maxEdge / Math.max(width, height);
                width = Math.round(width * scale);
                height = Math.round(height * scale);
            }
            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            canvas.getContext('2d').drawImage(img, 0, 0, width, height);
            canvasToCompressedBlob(canvas, MAX_UPLOAD_BYTES).then((blob) => {
                if (!blob || blob.size >= inputFile.size) { resolve(inputFile); return; }
                const name = (inputFile.name || 'photo').replace(/\.[^.]+$/, '') + '.jpg';
                resolve(new File([blob], name, { type: 'image/jpeg' }));
            });
        };
        img.onerror = () => { URL.revokeObjectURL(url); resolve(inputFile); };
        img.src = url;
    });

    // CropStage produced the cropped file (portrait aspect, up to ~1024×1280 for detail)
    // → commit it (to `file` or a dual slot).
    const handleCropApply = useCallback((croppedFile) => {
        setShowCropModal(false);
        setCropImageSrc(null);
        setPendingCropFile(null);
        commitCropResult(croppedFile);
    }, []);

    // CropStage failed to load/draw → drop the crop and upload the original file.
    const handleCropError = useCallback(() => {
        notifications.show({ message: t('imageGeneration.notifications.imageLoadFailed') || 'Image preview unavailable — uploading directly', color: 'orange' });
        setShowCropModal(false);
        setCropImageSrc(null);
        const f = pendingCropFile;
        setPendingCropFile(null);
        if (f) commitCropResult(f);
    }, [pendingCropFile, t]);

    // Modal closed via the X / backdrop → apply the current selection.
    const closeCropModal = useCallback(() => {
        cropStageRef.current?.apply();
    }, []);

    const skipCropAndProcess = useCallback(() => {
        setShowCropModal(false);
        setCropImageSrc(null);
        const f = pendingCropFile;
        setPendingCropFile(null);
        // Resize to the active output size (portrait or landscape) to keep AI token count controlled
        const { outW, outH } = cropAspectRef.current;
        const img = new window.Image();
        const url = URL.createObjectURL(f);
        img.onload = () => {
            URL.revokeObjectURL(url);
            const canvas = document.createElement('canvas');
            canvas.width = outW;
            canvas.height = outH;
            canvas.getContext('2d').drawImage(img, 0, 0, outW, outH);
            canvasToCompressedBlob(canvas).then((blob) => {
                commitCropResult(new File([blob], f.name, { type: 'image/jpeg' }));
            });
        };
        img.onerror = () => { URL.revokeObjectURL(url); commitCropResult(f); };
        img.src = url;
    }, [pendingCropFile]);

    const processFile = async (selectedFile, targetSide = null, queueUploadId = null) => {
        // Remember where the eventual crop result should land (null = single `file`,
        // 'left'/'right' = dual merge slot). Cleared on any early bail-out so a later
        // single-mode upload can't inherit a stale target. queueUploadId marks a
        // Photo-Booth photo so its crop commit stays linked for remove-on-generate.
        cropTargetSideRef.current = targetSide;
        pendingQueueIdRef.current = queueUploadId;
        // The "one photo of two people" solo box is a landscape zone (418/256); crop
        // to landscape there. Single mode follows the selected photo size's physical
        // ratio (square for 2×2 visa, 45:55 for passport, …) at a fixed 640px height;
        // unknown sizes (e.g. dual_image slots) keep the legacy 512×640 portrait.
        const sizeDim = SIZE_DIMS[size];
        const aspectCfg = (targetSide && dualSoloSide)
            ? { ratio: 418 / 256, outW: 836, outH: 512 }
            : sizeDim
                ? { ratio: sizeDim.w / sizeDim.h, outW: Math.round((sizeDim.w / sizeDim.h) * 320) * 2, outH: 640 }
                : { ratio: 512 / 640, outW: 512, outH: 640 };
        cropAspectRef.current = { outW: aspectCfg.outW, outH: aspectCfg.outH };
        setCropAspect(aspectCfg);
        if (!selectedFile) {
            cropTargetSideRef.current = null;
            return;
        }

        // Validate file type
        const isHeic = selectedFile.name.toLowerCase().endsWith('.heic') || selectedFile.name.toLowerCase().endsWith('.heif') || selectedFile.type === 'image/heic' || selectedFile.type === 'image/heif';
        // Google Photos on Android often returns files with empty type or application/octet-stream
        // — allow those through and let the image decode test below catch truly invalid files
        const hasValidMime = selectedFile.type.startsWith('image/') || isHeic;
        const hasEmptyOrUnknownMime = !selectedFile.type || selectedFile.type === 'application/octet-stream';
        if (!hasValidMime && !hasEmptyOrUnknownMime) {
            notifications.show({
                title: t('imageGeneration.notifications.invalidFileType'),
                message: t('imageGeneration.notifications.invalidFileMessage'),
                color: 'red',
            });
            cropTargetSideRef.current = null;
            return;
        }

        // Validate file size (100MB limit)
        const maxSize = 100 * 1024 * 1024; // 100MB in bytes
        if (selectedFile.size > maxSize) {
            notifications.show({
                title: t('imageGeneration.notifications.fileTooLarge'),
                message: t('imageGeneration.notifications.fileTooLargeMessage'),
                color: 'red',
            });
            cropTargetSideRef.current = null;
            return;
        }

        setGeneratedImage(null);

        // For HEIC/HEIF: convert to JPEG blob first — browsers can't display HEIC in <img>
        let fileForCrop = selectedFile;
        if (isHeic) {
            try {
                const heic2any = (await import('heic2any')).default;
                const convertedBlob = await heic2any({
                    blob: selectedFile,
                    toType: 'image/jpeg',
                    quality: 0.92,
                });
                const blob = Array.isArray(convertedBlob) ? convertedBlob[0] : convertedBlob;
                fileForCrop = new File([blob], selectedFile.name.replace(/\.(heic|heif)$/i, '.jpg'), { type: 'image/jpeg' });
            } catch (err) {
                console.error('HEIC conversion failed:', err);
                // Conversion failed — commit original without crop
                commitCropResult(selectedFile);
                return;
            }
        }

        // Check the image can actually be decoded before opening the crop modal
        const testUrl = URL.createObjectURL(fileForCrop);
        const testImg = new Image();
        await new Promise((resolve) => {
            testImg.onload = () => { URL.revokeObjectURL(testUrl); resolve(true); };
            testImg.onerror = () => { URL.revokeObjectURL(testUrl); resolve(false); };
            testImg.src = testUrl;
        }).then((ok) => {
            if (!ok) {
                // Can't decode — skip crop and commit as-is
                notifications.show({ message: t('imageGeneration.notifications.imageLoadFailed') || 'Could not read image — trying to upload directly', color: 'orange' });
                commitCropResult(fileForCrop);
                return;
            }

            // Open crop/rotate modal
            const reader = new FileReader();
            reader.onload = (e) => {
                setCropImageSrc(e.target.result);
                setPendingCropFile(fileForCrop);
                setShowCropModal(true);
            };
            reader.readAsDataURL(fileForCrop);
        });
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        const droppedFile = e.dataTransfer.files[0];
        if (droppedFile) {
            processFile(droppedFile);
        }
    };

    // Paste-to-upload: let users copy an image anywhere and Ctrl/Cmd+V it
    // straight into the upload area. The listener only acts when the clipboard
    // actually holds an image file, so pasting text into inputs is unaffected.
    // The single-upload path reuses processFile's full validation (type / size /
    // decodability); the dual zones mirror the drop handler's checks.
    useEffect(() => {
        const onPaste = (e) => {
            const items = e.clipboardData?.items;
            if (!items) return;

            let imageFile = null;
            for (const item of items) {
                if (item.kind === 'file' && item.type.startsWith('image/')) {
                    imageFile = item.getAsFile();
                    break;
                }
            }
            if (!imageFile) return; // no image on the clipboard → allow default paste

            // Single upload area active → run through the shared validation path
            if (!previewUrl && !isGenerating && !dualUploadMode) {
                e.preventDefault();
                processFile(imageFile);
                return;
            }

            // Dual upload mode → fill the first empty zone
            if (dualUploadMode) {
                let target = null;
                if (dualSoloSide) {
                    if (!(dualSoloSide === 'left' ? mergeLeft : mergeRight)) target = dualSoloSide;
                } else if (!mergeLeft) {
                    target = 'left';
                } else if (!mergeRight) {
                    target = 'right';
                }
                if (!target) return; // both zones full → ignore

                e.preventDefault();
                // Same crop/rotate modal as single mode; result lands in the target slot.
                processFile(imageFile, target);
            }
        };
        window.addEventListener('paste', onPaste);
        return () => window.removeEventListener('paste', onPaste);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [previewUrl, isGenerating, dualUploadMode, dualSoloSide, mergeLeft, mergeRight]);

    const handleRemoveImage = () => {
        skipLocalStorageSave.current = false; // ensure next change is saved (safety net for edge cases)
        setFile(null);
        setPreviewUrl(null);
        setGeneratedImage(null);
        setResultBg(null);
        setGeneratedImageReady(false);
        setIsMergedImage(false);
        if (size === 'dual_image') {
            // Restore dual upload mode so the UI matches the selected photo size.
            // mergeLeft/mergeRight were already cleared during generation so the
            // zones will be empty and ready for re-upload.
            setDualUploadMode(true);
            setDualSoloSide(null);
        } else {
            setDualUploadMode(false);
            setMergeLeft(null);
            setMergeRight(null);
            setDualSoloSide(null);
        }
    };

    // HD/Ultra generate buttons are gated behind the per-user photo-edit settings.
    const hdEnabled = user?.settings?.qualityHdEnabled === true;
    const ultraEnabled = AI_PHOTO_ULTRA_HD && user?.settings?.qualityUltraEnabled === true;

    // Route a quality-tier button press: Normal generates immediately; HD/Ultra show the
    // one-time info modal first (unless the user chose "don't show again"), then generate.
    const onTierGenerate = (tier) => {
        if (tier === 'normal') { handleGenerate('normal'); return; }
        const hidden = tier === 'hd' ? user?.settings?.hideHdInfo : user?.settings?.hideUltraInfo;
        if (hidden) { handleGenerate(tier); return; }
        setQualityInfoTier(tier);
    };

    // One shared row of tier buttons, reused by the first-time Generate + Regenerate
    // spots (main + floating clones). Normal keeps the original orange style and carries
    // the sticky-observer ref; HD is premium (violet→indigo), Ultra HD is most premium
    // (amber→yellow, ringed). HD/Ultra only render when enabled in the user's settings.
    const renderQualityButtons = ({ innerRef = null, disabled = false, label, icon = null, floating = false, regen = false }) => {
        const base = `relative overflow-hidden inline-flex items-center justify-center gap-1.5 font-bold px-4 py-3.5 rounded-lg transition-all disabled:opacity-50 w-full${floating ? ' shadow-lg' : ''}`;
        const tabIdx = floating && genBtnInView ? -1 : 0;
        // "আবার জেনারেট" (again-generate) prefix when regenerating, else "জেনারেট".
        const genPrefix = regen ? (t('imageGeneration.quality.regeneratePrefix') || 'Regenerate') : (t('imageGeneration.quality.generatePrefix') || 'Generate');
        // Show the credit badge ONLY when the tier picker is active (HD/Ultra enabled);
        // with just the Regular button there's nothing to compare, so no floating credit.
        const showCredit = hdEnabled || ultraEnabled;
        // Each button: "Generate · <tier>" with the credit as a FLOATING badge (top-right).
        // Regular = solid orange; HD = violet; Ultra HD = premium red with a left→right glass sweep on hover.
        const tiers = [
            { key: 'normal', name: t('imageGeneration.quality.regular'), cost: imageTierCosts?.normal, icon, innerRef, flat: true, cls: 'text-white shadow-md bg-primary hover:brightness-105' },
            hdEnabled && { key: 'hd', name: t('imageGeneration.quality.hd'), cost: imageTierCosts?.hd, icon: <IconSparkles size={18} />, cls: 'text-white shadow bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500' },
            ultraEnabled && { key: 'ultra', name: t('imageGeneration.quality.ultra'), cost: imageTierCosts?.ultra, icon: <IconCrown size={18} />, shine: true, cls: 'text-white shadow-md bg-gradient-to-r from-red-600 to-rose-700 hover:from-red-500 hover:to-rose-600' },
        ].filter(Boolean);
        return (
            <div className="flex w-full flex-col gap-2">
                {tiers.map((ti) => (
                    <div key={ti.key} className="relative w-full">
                        <button
                            ref={ti.innerRef || null}
                            className={`group ${base} ${ti.cls}`}
                            onClick={() => onTierGenerate(ti.key)}
                            tabIndex={tabIdx}
                            disabled={disabled}
                        >
                            {/* Glossy glass sheen — a bright top highlight fading down, plus a
                                soft inner ring. Skipped for `flat` tiers (Regular = solid colour). */}
                            {!ti.flat && (
                                <>
                                    <span className="pointer-events-none absolute inset-0 rounded-lg bg-gradient-to-b from-white/35 via-white/5 to-transparent" />
                                    <span className="pointer-events-none absolute inset-0 rounded-lg ring-1 ring-inset ring-white/20" />
                                </>
                            )}
                            {/* Glass shine sweep (left → right) on hover — Ultra HD only. */}
                            {ti.shine && (
                                <span className="pointer-events-none absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 ease-out bg-gradient-to-r from-transparent via-white/40 to-transparent" />
                            )}
                            {ti.icon}
                            {/* Size on the SPAN — a global `span{font-size:12px}` in global.scss overrides it otherwise.
                                With the tier picker OFF (only Regular), show the plain action label — no "· রেগুলার". */}
                            <span className="relative text-[15px] leading-tight">{showCredit ? `${genPrefix} · ${ti.name}` : label}</span>
                        </button>
                        {showCredit && ti.cost != null && (
                            <span className="absolute -top-2 -right-2 z-10 inline-flex items-center gap-1 text-[12px] font-bold px-2 py-0.5 rounded-full bg-white text-zinc-800 shadow border border-zinc-200">
                                {fmtNum(ti.cost)} <IconCoins size={13} className="text-primary" />
                            </span>
                        )}
                    </div>
                ))}
            </div>
        );
    };

    const handleGenerate = async (qualityArg = 'normal') => {
        // Quality tier (Normal/HD/Ultra HD) — guard against an event object being passed
        // when a button's onClick is wired directly to this handler.
        const quality = (qualityArg === 'hd' || qualityArg === 'ultra') ? qualityArg : 'normal';
        // Users must have an office space selected; admins generate against their
        // own credits so the check is skipped for them.
        if (!isAdmin && !selectedOfficeSpace) {
            notifications.show({
                title: t('common.error'),
                message: t('officeSpace.noWorkspaceSelected'),
                color: 'red',
            });
            return;
        }

        // Dual upload mode: silently merge the two images before generating
        let fileToUse = file;
        let isMergedForApi = isMergedImage;
        let previewForJob = previewUrl; // image used for the background-processing card
        if (dualUploadMode && dualSoloSide && !file) {
            // Single-person dual: the uploaded photo already contains both people.
            // Crop it (respecting the box's pan/zoom) into the landscape dual frame so
            // the AI generates a landscape (2-person) result instead of a portrait one.
            const solo = dualSoloSide === 'left' ? mergeLeft : mergeRight;
            const zoneEl = dualSoloSide === 'left' ? leftZoneRef.current : rightZoneRef.current;
            if (solo) {
                try {
                    const { croppedFile, croppedUrl } = await doSoloCrop(solo, zoneEl);
                    fileToUse = croppedFile;
                    isMergedForApi = true;
                    previewForJob = croppedUrl;
                    setFile(croppedFile);
                    setPreviewUrl(croppedUrl);
                    setIsMergedImage(true);
                    setDualUploadMode(false);
                    setMergeLeft(null);
                    setMergeRight(null);
                } catch {
                    notifications.show({ title: t('imageGeneration.mergeError'), color: 'red' });
                    return;
                }
            }
        } else if (dualUploadMode && mergeLeft && mergeRight && !file) {
            try {
                const { mergedFile, mergedUrl } = await doMerge(mergeLeft, mergeRight, leftZoneRef.current, rightZoneRef.current);
                fileToUse = mergedFile;
                isMergedForApi = true;
                previewForJob = mergedUrl;
                setFile(mergedFile);
                setPreviewUrl(mergedUrl);
                setIsMergedImage(true);
                setDualUploadMode(false);
                setMergeLeft(null);
                setMergeRight(null);
            } catch {
                notifications.show({ title: t('imageGeneration.mergeError'), color: 'red' });
                return;
            }
        }

        // Phone, no photo yet: the generate button opens the same upload choice as the upload box
        if (isMobile && !fileToUse && !generatedImage) {
            if (dualUploadMode) setDualUploadSide(dualSoloSide || (mergeLeft ? 'right' : 'left'));
            else setShowUploadSheet(true);
            return;
        }

        if (!fileToUse || !size || !dress || !bg) {
            notifications.show({
                title: t('imageGeneration.notifications.missingInfo'),
                message: t('imageGeneration.notifications.missingInfoMessage'),
                color: 'red',
            });
            return;
        }

        // Block if no credits.
        // Operators use the owner's credit (from usageStats, loaded with officeSpaceId).
        // Owners use their own credit from the auth store.
        // usageStats.credit is the balance that actually gets deducted (owner's) and
        // is refreshed after every generation, so it reflects a negative balance
        // immediately; fall back to the auth-store credit before stats load.
        const effectiveCredit = usageStats?.credit ?? user?.credit ?? 0;
        // Advance allowance: paying owners may generate past zero, down to the
        // advance floor (-advanceCredits). The backend is authoritative; this just
        // avoids a wasted request once the allowance is exhausted. With advanceCredits
        // = 0 (non-paying) this is the classic "not enough credit" block.
        const advanceCredits = usageStats?.advanceCredits ?? 0;
        const imageCost = usageStats?.advanceCost ?? 1;
        if (effectiveCredit - imageCost < -advanceCredits) {
            setNoCreditModalOpen(true);
            return;
        }

        // Unique id for this generation so its async result can be routed correctly
        // even if the user hides it (backgrounds it) and starts another one.
        const genToken = ++genTokenRef.current;
        foregroundTokenRef.current = genToken;
        const jobPreviewUrl = previewForJob; // snapshot for the background-processing card

        // Persist a marker so this job survives a page reload: the request dies on
        // reload but the backend keeps processing and saves the image to history.
        // Cleared in `finally` once the request settles client-side.
        const jobUid = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        jobUidByToken.current[genToken] = jobUid;
        const baselineMaxId = imageHistory.reduce((m, img) => Math.max(m, img.id || 0), 0);
        addPendingJob({
            uid: jobUid,
            thumb: null,
            startedAt: Date.now(),
            officeSpaceId: selectedOfficeSpace?.id ?? null,
            baselineMaxId,
        });
        makeThumbDataUrl(jobPreviewUrl).then(thumb => { if (thumb) updatePendingJob(jobUid, { thumb }); });

        setIsGenerating(true);
        setGeneratedImage(null);
        setGeneratedImageReady(false);
        setBlurRevealing(false);
        setProgress(0);

        // Simulate progress during generation with smooth linear animation. Kept in a
        // ref so hideCurrentGeneration() can stop it when the job goes to the background.
        if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
        const progressInterval = setInterval(() => {
            setProgress((prev) => {
                if (prev >= 99) {
                    clearInterval(progressInterval);
                    return 99;
                }
                // Smooth linear increment
                return Math.min(prev + 0.5, 99);
            });
        }, 50);
        progressIntervalRef.current = progressInterval;


        const formData = new FormData();
        formData.append('image', fileToUse);
        // Country e-passport uses a frontend epass_* key; the backend only knows the
        // visa_* keys (same dimensions), so translate before sending.
        formData.append('size', EPASS_TO_BACKEND[size] || size);
        // Both modes send the garment TYPE + colour as real fields — the backend composes
        // the short prompt `/type /color:x …` from them. Single sends one value per field;
        // dual sends two, pipe-delimited (left|right). `dressCode` keeps the raw combined
        // selection (e.g. "maroon_sharee") for DB storage/history.
        //
        // Per-person short-prompt dress fields → { type, color, extra }. Shared by single
        // and each dual side so both stay in lockstep. `extra` carries the secondary
        // part-colours (/tieColor: /shirtColor: /blouseColor: /hijabColor: /ornaColor:,
        // gated on showSuitSubColors; shirt tie independent) plus the independent
        // pattern / head-cover modifiers (/check on shirt & suit, /ghomta on saree,
        // /ornaHead on orna-kameez).
        const dressFieldsFor = (dressValue, { tie, tieColor, shirtColor, blouseColor, hijab, orna, ghutra, formal, check, stripe, shareeHead, ornaHead }) => {
            const { color, type } = parseDressValue(dressValue);
            const extra = [];
            if (type === 'shirt') {
                if (tie && tieColor) extra.push(`/tieColor:${tieColor}`);
            } else if (showSuitSubColors) {
                if (type === 'suite' || type === 'suite-tieless') {
                    if (shirtColor) extra.push(`/shirtColor:${shirtColor}`);
                    if (type === 'suite' && tieColor) extra.push(`/tieColor:${tieColor}`);
                } else if ((type === 'sharee' || type === 'sharee-design') && blouseColor) {
                    extra.push(`/blouseColor:${blouseColor}`);
                } else if ((type === 'kameez-hijab' || type === 'shari-hijab') && hijab) {
                    extra.push(`/hijabColor:${hijab}`);
                } else if (type === 'orna-kameez' && orna) {
                    extra.push(`/ornaColor:${orna}`);
                }
            }
            // Checked/plaid pattern (shirt & suit) and head-cover styling (saree ghomta,
            // orna worn over the head) — independent toggles, not gated on showSuitSubColors.
            // `/checkeredPattern` (not the weaker `/check`) — the model was rendering `/check`
            // as a plain colour; the explicit pattern word forces the plaid. Tunable.
            if (check && (type === 'shirt' || type === 'suite' || type === 'suite-tieless')) extra.push('/checkeredPattern');
            // Striped Panjabi — the Panjabi's pattern option (like the check shirt).
            if (stripe && type === 'panjbi') extra.push('/stripeCheckPanjabi');
            // Thobe + Ghutra: the thobe's colour is the main colour (the backend sends it as /thobeWithGhutraColor); the ghutra has its own.
            // Not gated by the "alternative colour options" setting — the second colour (and its tab) is part of this style.
            if (type === 'thobe-ghutra' && ghutra) extra.push(`/ghutraColor:${ghutra}`);
            // Hijab + Formal wear: the hijab is the main colour, the formal outfit has its own (/formalWearColor).
            if (type === 'hijab-formal' && formal) extra.push(`/formalWearColor:${formal}`);
            if (shareeHead && (type === 'sharee' || type === 'sharee-design')) extra.push('/ghomta');
            if (ornaHead && type === 'orna-kameez') extra.push('/ornaHead');
            return { type: type && type !== 'original' ? type : 'original', color: color || '', extra: extra.join(' ') };
        };

        if (isMergedImage || dualUploadMode) {
            const L = dressFieldsFor(dress1, { tie: shirtTie1, tieColor: suitTieColor1, shirtColor: suitShirtColor1, blouseColor: shareeBlouseColor1, hijab: hijabColor1, orna: ornaColor1, ghutra: ghutraColor1, formal: formalColor1, check: shirtCheck1, stripe: panjabiStripe1, shareeHead: shareeHeadCover1, ornaHead: kameezHeadCover1 });
            const R = dressFieldsFor(dress2, { tie: shirtTie2, tieColor: suitTieColor2, shirtColor: suitShirtColor2, blouseColor: shareeBlouseColor2, hijab: hijabColor2, orna: ornaColor2, ghutra: ghutraColor2, formal: formalColor2, check: shirtCheck2, stripe: panjabiStripe2, shareeHead: shareeHeadCover2, ornaHead: kameezHeadCover2 });
            // Pipe-delimited left|right — the backend splits and composes /left:(…) /right:(…).
            formData.append('dress', `${L.type}|${R.type}`);
            formData.append('dressColor', `${L.color}|${R.color}`);
            formData.append('dressColorExtra', `${L.extra}|${R.extra}`);
            formData.append('dressCode', `${dress1 || 'original'}|${dress2 || 'original'}`);
        } else {
            const S = dressFieldsFor(dress, { tie: shirtTie, tieColor: suitTieColor, shirtColor: suitShirtColor, blouseColor: shareeBlouseColor, hijab: hijabColor, orna: ornaColor, ghutra: ghutraColor, formal: formalColor, check: shirtCheck, stripe: panjabiStripe, shareeHead: shareeHeadCover, ornaHead: kameezHeadCover });
            formData.append('dress', S.type);
            if (S.color) formData.append('dressColor', S.color);
            if (S.extra) formData.append('dressColorExtra', S.extra);
            formData.append('dressCode', dress);
        }
        const selectedBg = backgrounds.find(b => b.id === bg);
        // Guard: an unknown bg state or a non-hex custom colour must never leak into
        // the prompt/DB (that's how 'transparent' records were born) — fall back to white.
        const isHexColor = (v) => /^#[0-9a-fA-F]{3,8}$/.test(v || '');
        const backgroundValue = bg === 'custom'
            ? (isHexColor(customColor) ? customColor : '#ffffff')
            : (selectedBg?.color || '#ffffff');
        formData.append('bg', backgroundValue);
        // Transparent output must come back as PNG — JPEG can't hold an alpha channel,
        // so it would re-bake a (black/white) background. The backend uses this to call
        // gpt-image-2 with background:"transparent" + output_format:"png".
        if (bg === 'transparent') formData.append('outputFormat', 'png');

        // Extra-instruction tools are now SHORT TOKENS composed in the backend
        // (AiPhotoEditService) from toolSelections (which tools are on, sent below) plus
        // toolParams (their values). The frontend forwards only the parameterised VALUES
        // as hex/text; no more long prose. Colours are hex per the token spec
        // (lipstick:hex, addTopi:hex); the nameplate name is capped at 20 chars.
        const hexOf = (colorId, fallback) => ALL_DRESS_COLORS.find(c => c.id === colorId)?.hex || fallback;
        const toolParams = {};
        if (addTupi) toolParams.addTopi = hexOf(tupiColor || 'white', '#ffffff');
        if (lipstick) {
            toolParams.lipstick = lipstickShade === 'custom'
                ? (lipstickCustomColor || '#c21807')
                : (LIPSTICK_SHADES.find(s => s.id === lipstickShade)?.hex || '#c21807');
        }
        if (keepNamePlate && namePlateText.trim()) toolParams.nameplate = namePlateText.trim().slice(0, 20);
        // Intensity level (subtle/medium/strong, default medium) per active effect tool →
        // backend composes `/faceBeauty:medium` etc.
        const levels = {};
        if (glowMakeup)     levels.glowMakeup     = toolLevels.glowMakeup     || 'medium';
        if (smoothSkin)     levels.smoothSkin     = toolLevels.smoothSkin     || 'medium';
        if (fixOilySkin)    levels.fixOilySkin    = toolLevels.fixOilySkin    || 'medium';
        if (brightenImage)  levels.brightenImage  = toolLevels.brightenImage  || 'medium';
        if (studioLighting) levels.studioLighting = toolLevels.studioLighting || 'medium';
        if (halfBody)       levels.halfBody       = toolLevels.halfBody       || 'strict';
        if (Object.keys(levels).length > 0) toolParams.levels = levels;
        // Dual (couple) only: which side's shoulder overlaps in front at the seam →
        // backend composes `/shoulderTop:left|right`. Irrelevant for single portraits.
        if (isMergedImage || dualUploadMode) toolParams.shoulderTop = shoulderTop;
        if (Object.keys(toolParams).length > 0) {
            formData.append('toolParams', JSON.stringify(toolParams));
        }
        // Store user's custom text separately so it can be restored without mixing tool labels
        if (showExtraGuide && extraEditingGuide) {
            formData.append('userCustomInstruction', extraEditingGuide.trim());
        }
        // Save only the tools the user actively selected (true) — no need to store inactive ones
        const activeToolSelections = Object.fromEntries(
            Object.entries({
                removeGlasses,
                glowMakeup,
                smoothSkin,
                fixOilySkin,
                removeBeard,
                addBeard,
                keepBeard,
                keepMustache,
                keepFacialMarks,
                keepNosePin,
                removeStrayHair,
                applyHair,
                openEyes,
                closeMouth,
                removeTupi,
                addTupi,
                straightenHead,
                keepSchoolDressDetails,
                showEar,
                halfBody,
                keepSunGlasses,
                brightenImage,
                studioLighting,
                lookAtCamera,
                keepNamePlate,
                lipstick,
                lipGloss,
                showExtraGuide,
            }).filter(([, v]) => v === true)
        );
        if (Object.keys(activeToolSelections).length > 0) {
            formData.append('toolSelections', JSON.stringify(activeToolSelections));
        }
        if (isMergedForApi) {
            formData.append('isMergedImage', 'true');
        }
        // Include office space ID for owner limit tracking
        if (selectedOfficeSpace?.id) {
            formData.append('officeSpaceId', selectedOfficeSpace.id);
        }
        // Quality tier (HD/Ultra HD → bigger input + output; Normal is the default).
        if (quality !== 'normal') {
            formData.append('quality', quality);
        }

        // Send the display name so the backend stores it as the image's file name
        const generationDisplayName = (() => {
            const shopName = (selectedOfficeSpace?.businessProfile?.shopName || selectedOfficeSpace?.name || user?.shopName || '').trim() || 'Sohozkaj';
            const settings = user?.settings?.photoNameSettings;
            // Increment the trailing serial off the LAST saved image name for today's
            // prefix+date (scanning the loaded history), not a backend usage counter —
            // the counter is unreliable here and left every new photo stuck at "- 1".
            const serial = nextPhotoSerial({
                shopName,
                settings,
                existingNames: imageHistory.map((img) => img.fileName),
            });
            // Prefix/date come from the user's naming settings (সেটিংস → ছবির নাম).
            return buildPhotoDisplayName({ shopName, settings, serial });
        })();
        formData.append('displayName', generationDisplayName);

        try {
            const response = await imageGenerationService.generateImage(formData);

            // Was the input a PRIVATE booth photo (this one or an earlier generation from the same file)? Then this result is private too.
            let madeFromPrivate = false;
            if (response?.data) {
                if (loadedQueueRef.current.id && file === loadedQueueRef.current.file && privateQueueIdsRef.current.has(loadedQueueRef.current.id)) {
                    privateFileRef.current = file;
                }
                madeFromPrivate = !!file && file === privateFileRef.current;
            }

            // If the input was a photo picked from the QR inbox, a successful
            // generation consumes it — remove it from the queue and refresh the panel.
            if (response?.data && loadedQueueRef.current.id && file === loadedQueueRef.current.file) {
                const usedId = loadedQueueRef.current.id;
                loadedQueueRef.current = { id: null, file: null };
                photoCollectorService.removeUpload(usedId).catch(() => {});
                setPcRefreshKey((k) => k + 1);
            }
            // Dual mode: booth photos loaded into the used boxes are consumed too.
            if (response?.data && dualUploadMode) {
                const usedSides = dualSoloSide ? [dualSoloSide] : ['left', 'right'];
                const usedIds = usedSides.map(s => dualQueueIdsRef.current[s]).filter(Boolean);
                if (usedIds.some((id) => privateQueueIdsRef.current.has(id))) {
                    madeFromPrivate = true;
                    privateFileRef.current = fileToUse; // a regenerate from the same merged picture is private too
                }
                dualQueueIdsRef.current = { left: null, right: null };
                if (usedIds.length) {
                    Promise.allSettled(usedIds.map(id => photoCollectorService.removeUpload(id)))
                        .then(() => setPcRefreshKey((k) => k + 1));
                }
            }

            if (madeFromPrivate && response?.data?.id != null) registerPrivateResult(response.data.id);

            // Is this result still the one shown in the main preview? If the user
            // hid it, it finishes silently in the recent slider instead.
            const isForeground = foregroundTokenRef.current === genToken && !hiddenTokensRef.current.has(genToken);

            if (response.data && !isForeground) {
                // ── Hidden job finished in the background ──
                hiddenTokensRef.current.delete(genToken);
                playSuccessChime();
                usageService.getUserUsageStats(selectedOfficeSpace?.id)
                    .then(stats => setUsageStats(stats))
                    .catch(err => console.error('Failed to refresh usage stats:', err));
                // Reload history so the finished image appears, then drop its
                // processing card (the real thumbnail takes its place at the front).
                loadHistory().finally(() => {
                    setBgJobs(prev => prev.filter(j => j.token !== genToken));
                });
                notifications.show({
                    title: t('imageGeneration.notifications.success'),
                    message: t('imageGeneration.notifications.backgroundImageReady'),
                    color: 'green',
                });
            } else if (response.data) {
                setGeneratedImage(response.data.aiImage);
                {
                    const genBg = response.data.background ?? (bg === 'transparent' ? 'transparent' : null);
                    setResultBg(genBg);
                }
                setGeneratedImageId(response.data.id);
                setErasedImageUrl(null);
                setProgress(100);
                setIsGenerating(false); // Unlock UI immediately — don't wait for background tasks
                playSuccessChime();

                // Background: refresh usage stats
                usageService.getUserUsageStats(selectedOfficeSpace?.id)
                    .then(stats => setUsageStats(stats))
                    .catch(err => console.error('Failed to refresh usage stats:', err));

                const displayFilename = `${generationDisplayName}.jpg`;

                // Extract metadata for the info box
                const img = new window.Image();
                img.onload = function () {
                    const dressLabel = (isMergedImage || dualUploadMode)
                        ? [formatDressLabel(dress1), formatDressLabel(dress2)].join(' | ')
                        : formatDressLabel(dress);
                    setImageMetadata({
                        filename: displayFilename,
                        dimensions: `${this.naturalWidth} × ${this.naturalHeight}px`,
                        photoSize: photoSizes.find(s => s.value === size)?.label || size,
                        dressCode: dressLabel,
                        background: backgrounds.find(b => b.id === bg)?.label || bg,
                        quality, // the tier this image was generated with (normal|hd|ultra)
                    });
                    setCustomImageName(null);
                    setRenameEditing(false);
                };
                img.src = response.data.aiImage;

                // Save initial settings for regenerate tracking
                setInitialSettings({
                    size, dress, bg, customColor,
                    removeGlasses, removeTupi, glowMakeup, smoothSkin, fixOilySkin, removeBeard, straightenHead,
                    openEyes, closeMouth, removeStrayHair, keepSchoolDressDetails, addBeard, applyHair,
                    keepBeard, keepMustache, keepFacialMarks, keepNosePin, keepSunGlasses, showEar, halfBody, addTupi,
                    brightenImage, studioLighting, lookAtCamera, keepNamePlate, namePlateText,
                    lipstick, lipstickShade, lipstickCustomColor, lipGloss, tupiColor,
                    shareeHeadCover, kameezHeadCover, shirtCheck, panjabiStripe, ghutraColor, formalColor,
                    showExtraGuide, extraEditingGuide, dualLevelKey,
                });
                setSettingsChanged(false);

                // Background: reload history
                loadHistory();

                notifications.show({
                    title: t('imageGeneration.notifications.success'),
                    message: t('imageGeneration.notifications.imageGeneratedSuccess'),
                    color: 'green',
                });
            } else if (!isForeground) {
                // Hidden job returned no data — flag its card as failed.
                hiddenTokensRef.current.delete(genToken);
                setBgJobs(prev => prev.map(j => j.token === genToken ? { ...j, status: 'error' } : j));
                notifications.show({
                    title: t('imageGeneration.notifications.error'),
                    message: t('imageGeneration.notifications.backgroundImageFailed'),
                    color: 'red',
                });
            } else {
                notifications.show({
                    title: t('imageGeneration.notifications.error'),
                    message: t('imageGeneration.notifications.imageGenerationFailed'),
                    color: 'red',
                });
            }
        } catch (error) {
            console.error('Full Error Object:', error);
            console.error('Error Response:', error.response);
            console.error('Error Message:', error.message);

            // A hidden (backgrounded) job that errors shouldn't hijack the main UI —
            // mark its slider card as failed and surface a toast, then bail.
            const isForeground = foregroundTokenRef.current === genToken && !hiddenTokensRef.current.has(genToken);
            if (!isForeground) {
                hiddenTokensRef.current.delete(genToken);
                setBgJobs(prev => prev.map(j => j.token === genToken ? { ...j, status: 'error' } : j));
                notifications.show({
                    title: t('imageGeneration.notifications.error'),
                    message: t('imageGeneration.notifications.backgroundImageFailed'),
                    color: 'red',
                });
                return;
            }

            // Backend rejects with code: 'INSUFFICIENT_CREDITS' when the owner's
            // balance is below the image cost. Surface the LimitReachedModal
            // (same UI as the 0-credit pre-check) instead of a red toast.
            if (error?.code === 'INSUFFICIENT_CREDITS') {
                setNoCreditModalOpen(true);
                return;
            }

            let errorMessage = t('imageGeneration.notifications.imageGenerationError');

            // NOTE: axios interceptor strips error.response and puts server message into error.message
            const msg = error.message || '';

            const isNoHumanError =
                msg.includes('does not contain a human') ||
                msg.includes('একক মানুষ');

            const isNotProperDualError =
                msg.includes('not_proper_dual') ||
                msg.includes('one person appears to be behind') ||
                msg.includes('Both persons must be side by side');

            const isTransientAIError =
                msg.includes('"code":500') ||
                msg.includes('Internal error encountered') ||
                msg.includes('"status":"INTERNAL"') ||
                msg.includes('"code":503') ||
                msg.includes('UNAVAILABLE') ||
                msg.includes('ছবি তৈরিতে সমস্যা হয়েছে');

            if (isNoHumanError) {
                errorMessage = 'ছবিতে কোনো মানুষ পাওয়া যায়নি! অনুগ্রহ করে একজন বা দুইজন মানুষের স্পষ্ট ছবি আপলোড করুন।';
            } else if (isNotProperDualError) {
                errorMessage = 'দুইজনের ছবি গ্রহণযোগ্য হয়নি! উভয় ব্যক্তিকে পাশাপাশি এবং সমান ফোকাসে থাকতে হবে। একজন অন্যজনের সামনে বা পেছনে থাকলে ডুয়াল ফটো তৈরি করা সম্ভব নয়।';
            } else if (isTransientAIError) {
                errorMessage = 'ছবি তৈরিতে সমস্যা হয়েছে। অনুগ্রহ করে আবার চেষ্টা করুন।';
            } else if (msg.includes('too large') || msg.includes('413')) {
                errorMessage = t('imageGeneration.notifications.fileTooLargeError');
            } else if (msg.includes('session') || msg.includes('unauthorized') || msg.includes('401')) {
                errorMessage = t('imageGeneration.notifications.loginAgainError');
            } else if (msg === 'Network Error' || msg.includes('network') || msg.includes('নেটওয়ার্ক')) {
                errorMessage = t('imageGeneration.notifications.networkError');
            }

            notifications.show({
                title: t('imageGeneration.notifications.error'),
                message: errorMessage,
                color: 'red',
            });
        } finally {
            // Request settled client-side → this job no longer needs reload survival.
            const uid = jobUidByToken.current[genToken];
            if (uid) { removePendingJob(uid); delete jobUidByToken.current[genToken]; }

            // Only reset the foreground UI if THIS job still owns it. A backgrounded
            // job finishing must not stop the spinner of whatever is now in front.
            if (foregroundTokenRef.current === genToken) {
                setIsGenerating(false);
                setProgress(0); // Reset progress
                if (progressIntervalRef.current) { clearInterval(progressIntervalRef.current); progressIntervalRef.current = null; }
                foregroundTokenRef.current = null;
            }
        }
    };

    // Push the in-flight foreground generation into the background. The network
    // request keeps running; its processing card moves to the recent slider and the
    // editor resets so the user can start a brand-new image right away.
    const hideCurrentGeneration = () => {
        const token = foregroundTokenRef.current;
        if (token == null || !isGenerating) return;
        if (progressIntervalRef.current) { clearInterval(progressIntervalRef.current); progressIntervalRef.current = null; }
        hiddenTokensRef.current.add(token);
        setBgJobs(prev => [{ token, previewUrl, status: 'processing' }, ...prev]);
        foregroundTokenRef.current = null;

        // Reset the editor to a fresh upload state (mirrors handleRemoveImage).
        skipLocalStorageSave.current = false;
        setIsGenerating(false);
        setProgress(0);
        setGeneratingStep(0);
        setGeneratedImage(null);
        setGeneratedImageId(null);
        setGeneratedImageReady(false);
        setBlurRevealing(false);
        setFile(null);
        setPreviewUrl(null);
        setIsMergedImage(false);
        if (size === 'dual_image') {
            setDualUploadMode(true);
            setDualSoloSide(null);
        } else {
            setDualUploadMode(false);
            setMergeLeft(null);
            setMergeRight(null);
            setDualSoloSide(null);
        }
    };

    // Clicking a still-processing card in the recent slider re-attaches that job to
    // the main preview. If another job is currently in the foreground, it swaps out
    // to the background first.
    const bringBgJobToForeground = (job) => {
        // Restored (reload-survived) jobs have no live request to re-attach to — they
        // only resolve via history reconciliation, so they aren't clickable.
        if (!job || job.status !== 'processing' || job.restored) return;
        if (foregroundTokenRef.current != null && isGenerating) {
            hideCurrentGeneration();
        }
        hiddenTokensRef.current.delete(job.token);
        foregroundTokenRef.current = job.token;
        setBgJobs(prev => prev.filter(j => j.token !== job.token));
        setPreviewUrl(job.previewUrl);
        setFile(null);
        setGeneratedImage(null);
        setGeneratedImageReady(false);
        setBlurRevealing(false);
        setGeneratingStep(0);
        setProgress(30);
        setIsGenerating(true);
        // Resume the simulated progress animation for the foreground.
        if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
        const progressInterval = setInterval(() => {
            setProgress((prev) => {
                if (prev >= 99) { clearInterval(progressInterval); return 99; }
                return Math.min(prev + 0.5, 99);
            });
        }, 50);
        progressIntervalRef.current = progressInterval;
    };

    // Output MIME chosen by getEditedImageUrl — PNG for transparent / eraser results so
    // the alpha channel survives the canvas re-encode, JPEG otherwise. handleDownload
    // reads this for the file extension.
    const exportMimeRef = useRef('image/jpeg');

    // Detect a transparent-subject source (PNG or alpha WebP master) by its byte
    // signature — more reliable than blob.type, which R2/proxy responses sometimes
    // report as octet-stream. Our transparent AI masters are always alpha WebP.
    const isTransparentSourceBlob = async (blob) => {
        try {
            const sig = new Uint8Array(await blob.slice(0, 16).arrayBuffer());
            // PNG signature
            if (sig[0] === 0x89 && sig[1] === 0x50 && sig[2] === 0x4e && sig[3] === 0x47) return true;
            // WebP: 'RIFF'....'WEBP'
            if (sig[0] === 0x52 && sig[1] === 0x49 && sig[2] === 0x46 && sig[3] === 0x46 &&
                sig[8] === 0x57 && sig[9] === 0x45 && sig[10] === 0x42 && sig[11] === 0x50) return true;
            return false;
        } catch { return false; }
    };

    // Helper function to create an edited image with filters applied.
    // Fetches the AI image through the backend download proxy to avoid R2 CORS issues.
    const getEditedImageUrl = async (overrideUrl = null, overrideId = null) => {
        // If user has done manual eraser edits, use that as base; otherwise use AI image via proxy
        const useId  = erasedImageUrl ? null : (overrideId || generatedImageId);
        const useUrl = overrideUrl || erasedImageUrl || generatedImage;
        if (!useId && !useUrl) return null;

        try {
            // Fetch via backend proxy — avoids R2 CORS restriction on canvas taint
            const blob = useId
                ? await imageGenerationService.downloadImage(useId, 'ai')
                : await fetch(useUrl).then(r => r.blob());

            // Preserve transparency: transparent AI masters (WebP) and eraser edits carry
            // an alpha channel. A colour applied behind them makes the image opaque again
            // → composite + export JPEG. Kept transparent → export PNG (max compatibility,
            // and it strips the WebP so downloads open anywhere). Opaque → JPEG.
            const transparentSource = blob.type === 'image/png' || blob.type === 'image/webp' || await isTransparentSourceBlob(blob);
            const bgColor = pickerColorFrom(bg, customColor); // current LEFT-picker colour, or null
            const composite = transparentSource && !!bgColor;
            const finalMime = composite ? 'image/jpeg' : (transparentSource ? 'image/png' : 'image/jpeg');
            exportMimeRef.current = finalMime;

            const blobUrl = window.URL.createObjectURL(blob);

            const img = new window.Image();
            await new Promise((resolve, reject) => {
                img.onload = resolve;
                img.onerror = reject;
                img.src = blobUrl;
            });

            // The drawer's edits (utils/photoEdits.js — the same pipeline as the live preview), rotation included.
            const subject = isEdited(edits) ? renderEdits(img, edits) : img;
            window.URL.revokeObjectURL(blobUrl);
            const canvas = document.createElement('canvas');
            canvas.width = subject === img ? img.naturalWidth : subject.width;
            canvas.height = subject === img ? img.naturalHeight : subject.height;
            const ctx = canvas.getContext('2d');

            // Post-gen background colour: paint it behind the transparent subject first
            // (unedited so the colour stays exact), then the edited subject on top.
            if (composite) {
                ctx.fillStyle = bgColor;
                ctx.fillRect(0, 0, canvas.width, canvas.height);
            }
            ctx.drawImage(subject, 0, 0, canvas.width, canvas.height);

            // Convert to blob URL — PNG keeps alpha for transparent results; JPEG stays
            // quality-capped for the usual opaque case.
            return new Promise((resolve) => {
                canvas.toBlob((out) => {
                    resolve(window.URL.createObjectURL(out));
                }, finalMime, finalMime === 'image/jpeg' ? 0.95 : undefined);
            });
        } catch (error) {
            console.error('Error creating edited image:', error);
            return null;
        }
    };

    // ── Filename helpers ──────────────────────────────────────────────────────
    const makeFileName = (prefix, ext) => {
        const uuid = (crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2, 10));
        return `${prefix}-${uuid}+${Date.now()}.${ext}`;
    };

    const makeDisplayName = (ext = 'jpg') => {
        // Prefer the name the current image was actually saved with (already carries the
        // correct prefix/date/serial); otherwise rebuild from the naming settings,
        // deriving the serial from existing image names rather than the unreliable
        // daily usage counter (which left every download stuck at "- 1").
        const saved = (imageMetadata?.filename || '').replace(/\.[^/.]+$/, '').trim();
        if (saved) return `${saved}.${ext}`;
        const shopName = (selectedOfficeSpace?.businessProfile?.shopName || selectedOfficeSpace?.name || user?.shopName || '').trim() || 'Sohozkaj';
        const settings = user?.settings?.photoNameSettings;
        const serial = nextPhotoSerial({ shopName, settings, existingNames: imageHistory.map((img) => img.fileName) });
        return `${buildPhotoDisplayName({ shopName, settings, serial })}.${ext}`;
    };

    const handleRenameImage = async () => {
        const nextName = sanitizeText(renameInputVal, { maxLen: RENAME_MAX }).trim();
        if (!generatedImageId || !nextName) {
            setRenameEditing(false);
            return;
        }
        // If name unchanged, just close without saving
        const currentName = customImageName || imageMetadata?.filename?.replace(/\.[^/.]+$/, '') || '';
        if (nextName === currentName) {
            setRenameEditing(false);
            return;
        }
        setRenameSaving(true);
        try {
            await imageGenerationService.updateImageName(generatedImageId, nextName);
            setCustomImageName(nextName);
            setRenameEditing(false);
            notifications.show({ title: t('imageGeneration.renamed'), message: t('imageGeneration.imageNameUpdated'), color: 'green', autoClose: 2000 });
        } catch {
            notifications.show({ title: t('common.error'), message: t('imageGeneration.renameFailed'), color: 'red', autoClose: 3000 });
        } finally {
            setRenameSaving(false);
        }
    };

    const handleDownload = async () => {
        if (!generatedImage) return;
        setDownloading(true);
        try {
            const editedUrl = await getEditedImageUrl();
            if (!editedUrl) {
                throw new Error('Failed to create edited image');
            }

            const link = document.createElement('a');
            link.href = editedUrl;

            // getEditedImageUrl set the MIME (PNG for transparent/eraser results) — match
            // the extension so the download keeps its alpha and isn't mislabelled.
            const ext = exportMimeRef.current === 'image/png' ? 'png' : 'jpg';
            const filename = customImageName
                ? `${customImageName}.${ext}`
                : makeDisplayName(ext);

            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(editedUrl);

            // Track download
            if (generatedImageId) {
                imageGenerationService.trackDownload(generatedImageId).catch(err => console.error('Failed to track download:', err));
            }

            // Auto-open income/expense modal after download
            if (user?.settings?.autoAppearIncomeExpense === true) {
                const fill = moduleAutoFill(user?.settings, 'image_generation');
                openIncomeExpenseModal({
                    name: fill.name,
                    type: 'income',
                    amount: fill.price,
                    category: 'image_generation',
                    referenceId: generatedImageId,
                });
            }
        } catch (error) {
            console.error('Error downloading image:', error);
            notifications.show({
                title: t('imageGeneration.notifications.downloadFailed'),
                message: t('imageGeneration.notifications.downloadFailedMessage'),
                color: 'red',
            });
        } finally {
            setDownloading(false);
        }
    };

    // Custom size & quality: the edited image is built first (the stored photo is downloaded and the adjustments are applied,
    // which takes a moment), then the size window opens. `customDownloadLoading` keeps the button spinning meanwhile and
    // ignores a second click.
    const openCustomDownload = async () => {
        if (customDownloadLoading) return;
        setCustomDownloadLoading(true);
        try {
            const editedUrl = await getEditedImageUrl();
            setCustomDownloadImageUrl(editedUrl || generatedImage);
            setCustomDownloadImageId(generatedImageId);
            setCustomInline(isMobile);
            setShowCustomDownload(true);
        } finally {
            setCustomDownloadLoading(false);
        }
    };
    const closeCustomDownload = () => { setShowCustomDownload(false); setCustomInline(false); };
    // Phone: the size / style / instruction options and the recent photos open in a dock under the photo: their sideways rows, and one dock at a time
    const dockOpen = ['photo', 'style', 'extra', 'recent'].includes(activeDrawer);
    const dockSizeRef = useDragWheelScroll();
    const dockVisaRef = useDragWheelScroll();
    const dockDressRef = useDragWheelScroll();
    const dockSwatchRef = useDragWheelScroll();
    const dockBgRef = useDragWheelScroll();
    const dockToolRef = useDragWheelScroll();
    const dockRecentRef = useDragWheelScroll();
    useEffect(() => {
        if (!dockOpen) return;
        setEditorOpen(false);
        if (customInline) closeCustomDownload();
    }, [activeDrawer]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => { if (isGenerating && dockOpen) setActiveDrawer(null); }, [isGenerating]); // eslint-disable-line react-hooks/exhaustive-deps
    // a new result starts with the action bar showing, and never inside the previous photo's custom size
    useEffect(() => {
        setResultActionsOpen(true);
        if (customInline) closeCustomDownload();
    }, [generatedImageId]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Photo Booth: load a queued customer photo into the editor ─────────────
    // Booth photos arrive UNCROPPED (the mobile page no longer crops), so route
    // through processFile → the crop modal opens and the owner crops manually.
    // The crop commit re-links the cropped file to the upload id, so a successful
    // generation still removes it from the booth (see the effect near the top).
    const loadQueuePhoto = async (uploadId, item) => {
        if (item?.isPrivate) privateQueueIdsRef.current.add(uploadId);
        try {
            const blob = await photoCollectorService.imageBlob(uploadId, false);
            const f = new File([blob], 'booth-photo.jpg', { type: blob.type || 'image/jpeg' });
            // Dual mode: fill the first EMPTY box (left first, then right) via the
            // same per-side crop flow; the next pick lands in the other box. When
            // both are already filled, the right box is replaced.
            const side = dualUploadMode
                ? (dualSoloSide || (!mergeLeft ? 'left' : 'right'))
                : null;
            await processFile(f, side, uploadId);
        } catch {
            notifications.show({ title: t('photoCollectorPanel.loadFailedTitle'), message: t('photoCollectorPanel.loadFailed'), color: 'red' });
        }
    };

    // ── QR Upload handlers ────────────────────────────────────────────────────
    const stopQrSession = () => {
        clearInterval(qrPollRef.current);
        clearInterval(qrTimerRef.current);
        qrPollRef.current = null;
        qrTimerRef.current = null;
    };

    const openQrModal = async () => {
        // Guard against concurrent calls (double-click)
        if (qrStatus === 'loading') return;
        stopQrSession();

        // Cancel previous session in DB so it doesn't linger as 'pending'
        if (qrToken) {
            qrSessionService.cancelSession(qrToken).catch(() => {});
        }

        setQrDataUrl(null);
        setQrToken(null);
        setQrMobileUrl(null);
        setQrTimeLeft(300);
        setQrStatus('loading');
        setShowQrModal(true);

        try {
            const sessionMode = dualUploadMode ? 'dual' : 'single';
            const res = await qrSessionService.createSession(sessionMode);
            const { token, mobileUrl } = res.data;
            setQrToken(token);
            setQrMobileUrl(mobileUrl);

            // Generate QR image
            const dataUrl = await QRCode.toDataURL(mobileUrl, { width: 220, margin: 2 });
            setQrDataUrl(dataUrl);
            setQrStatus('ready');

            // Countdown timer (5 min)
            const expiresAt = Date.now() + 5 * 60 * 1000;
            qrTimerRef.current = setInterval(() => {
                const left = Math.max(0, Math.floor((expiresAt - Date.now()) / 1000));
                setQrTimeLeft(left);
                if (left === 0) {
                    setQrStatus('expired');
                    stopQrSession();
                }
            }, 1000);

            // Helper: base64 string → File + object URL (sync, no network needed)
            const b64ToFile = (base64, mime, name) => {
                const byteString = atob(base64);
                const ab = new ArrayBuffer(byteString.length);
                const ia = new Uint8Array(ab);
                for (let i = 0; i < byteString.length; i++) ia[i] = byteString.charCodeAt(i);
                const blob = new Blob([ab], { type: mime });
                const file = new File([blob], name, { type: mime });
                return { file, url: URL.createObjectURL(blob) };
            };

            // Poll for image every 2.5 seconds
            qrPollRef.current = setInterval(async () => {
                try {
                    const statusRes = await qrSessionService.getStatus(token);
                    const data = statusRes.data;

                    if (data.status === 'received') {
                        stopQrSession();
                        setQrStatus('received');
                        setShowQrModal(false);

                        if (data.mode === 'dual') {
                            // Dual mode: set mergeLeft + mergeRight, keep dual upload mode active
                            const leftMime  = data.leftMimeType  || 'image/jpeg';
                            const rightMime = data.rightMimeType || 'image/jpeg';
                            const left  = b64ToFile(data.leftImageData,  leftMime,  'left.jpg');
                            const right = b64ToFile(data.rightImageData, rightMime, 'right.jpg');
                            setMergeLeft({ file: left.file, url: left.url, offsetX: 0, offsetY: 0, zoom: 1 });
                            setMergeRight({ file: right.file, url: right.url, offsetX: 0, offsetY: 0, zoom: 1 });
                            setDualUploadMode(true);
                            setIsMergedImage(false);
                            setGeneratedImage(null);
                        } else {
                            // Single mode: resize to 836×1024 then load into editor
                            const mime = data.imageMimeType || 'image/jpeg';
                            const { file: mobileFile, url: previewBlobUrl } = b64ToFile(data.imageData, mime, 'mobile-upload.jpg');
                            const mImg = new window.Image();
                            mImg.onload = () => {
                                URL.revokeObjectURL(previewBlobUrl);
                                const c = document.createElement('canvas');
                                c.width = 512; c.height = 640;
                                c.getContext('2d').drawImage(mImg, 0, 0, 512, 640);
                                canvasToCompressedBlob(c).then((blob) => {
                                    setFile(new File([blob], 'mobile-upload.jpg', { type: 'image/jpeg' }));
                                    setPreviewUrl(URL.createObjectURL(blob));
                                    setGeneratedImage(null);
                                });
                            };
                            mImg.onerror = () => { setFile(mobileFile); setPreviewUrl(previewBlobUrl); setGeneratedImage(null); };
                            mImg.src = previewBlobUrl;
                        }

                        notifications.show({
                            title: t('imageGeneration.qrReceivedTitle'),
                            message: t('imageGeneration.qrReceivedMessage'),
                            color: 'green',
                        });
                    } else if (data.status === 'expired') {
                        if (Date.now() >= expiresAt) {
                            stopQrSession();
                            setQrStatus('expired');
                        }
                    }
                } catch {
                    // ignore poll errors silently
                }
            }, 2500);

        } catch (err) {
            setQrStatus('idle');
            setShowQrModal(false);
            notifications.show({
                title: 'Error',
                message: 'Could not create QR session. Please try again.',
                color: 'red',
            });
        }
    };

    const closeQrModal = () => {
        // Cancel the backend session so mobile gets a clear error instead of silent failure
        if (qrToken) {
            qrSessionService.cancelSession(qrToken).catch(() => {});
        }
        stopQrSession();
        setShowQrModal(false);
        setQrStatus('idle');
    };

    const formatQrTime = (seconds) => {
        const m = Math.floor(seconds / 60).toString().padStart(2, '0');
        const s = (seconds % 60).toString().padStart(2, '0');
        return `${m}:${s}`;
    };
    // ─────────────────────────────────────────────────────────────────────────

    // ── Merge images handler ──────────────────────────────────────────────────
    // Two images picked/dropped at once → fill BOTH dual boxes directly (each
    // slot has its own pan/zoom, so the crop modal is skipped for the pair).
    // The clicked/dropped side gets the first image, the other side the second.
    const fillDualSlots = (imgs, side) => {
        const mk = (f) => ({ file: f, url: URL.createObjectURL(f), offsetX: 0, offsetY: 0, zoom: 1 });
        (side === 'left' ? setMergeLeft : setMergeRight)(mk(imgs[0]));
        (side === 'left' ? setMergeRight : setMergeLeft)(mk(imgs[1]));
        dualQueueIdsRef.current = { left: null, right: null }; // direct picks, not booth photos
    };

    const handleMergePickSide = (side, e) => {
        const files = Array.from(e.target.files || []); // snapshot BEFORE clearing the input
        e.target.value = '';
        const imgs = files.filter(f => f.type.startsWith('image/') || !f.type);
        if (!imgs.length) return;
        if (imgs.length >= 2 && !dualSoloSide) { fillDualSlots(imgs, side); return; }
        // Same crop/rotate modal as single mode; the result lands in this side's slot.
        processFile(imgs[0], side);
    };

    const handleZoomSide = (side, delta) => {
        const setter = side === 'left' ? setMergeLeft : setMergeRight;
        setter(prev => prev ? { ...prev, zoom: Math.max(1, Math.min(4, (prev.zoom ?? 1) + delta)) } : prev);
    };

    // Pan (drag) image within its upload zone to choose visible crop
    const handleZonePanStart = (e, side) => {
        e.preventDefault();
        e.currentTarget.setPointerCapture(e.pointerId);
        const cur = side === 'left' ? mergeLeft : mergeRight;
        if (!cur) return;
        dualDragRef.current = { side, startX: e.clientX, startY: e.clientY, startOX: cur.offsetX ?? 0, startOY: cur.offsetY ?? 0 };
    };

    const handleZonePanMove = (e, side) => {
        if (!dualDragRef.current || dualDragRef.current.side !== side) return;
        const d = dualDragRef.current;
        const dx = e.clientX - d.startX;
        const dy = e.clientY - d.startY;
        // No clamping — image moves freely; final crop is applied on generate
        const newOX = d.startOX + dx;
        const newOY = d.startOY + dy;
        if (side === 'left') setMergeLeft(prev => ({ ...prev, offsetX: newOX, offsetY: newOY }));
        else setMergeRight(prev => ({ ...prev, offsetX: newOX, offsetY: newOY }));
    };

    const handleZonePanEnd = () => { dualDragRef.current = null; };


    // Shared canvas merge — respects per-zone pan + zoom for crop
    const doMerge = (left, right, leftZoneEl, rightZoneEl) => new Promise((resolve, reject) => {
        const loadImg = (url) => new Promise((res, rej) => {
            const img = new window.Image();
            img.onload = () => res(img);
            img.onerror = rej;
            img.src = url;
        });

        // Render one side WYSIWYG — replicate the preview's CSS exactly:
        //   objectFit: contain  +  transform: translate(offsetX, offsetY) scale(zoom)
        //   transformOrigin: center, zone background letterboxed with black.
        // We draw the image into its real destination rect (preserving aspect ratio)
        // instead of stretching a source crop to fill the half — this prevents the
        // image from being squished/"flattened" when zoom is left at 1 and the photo's
        // aspect ratio differs from the zone (contain letterboxing case).
        Promise.all([loadImg(left.url), loadImg(right.url)]).then(([imgL, imgR]) => {
            // Height-normalise + abut: scale BOTH images to the SAME height and place them
            // side by side with no gap and NO crop — both people stay fully visible at the
            // same scale, joined seamlessly. (Cover-fill was cropping tall/narrow sources, so
            // the right person got cut off and the two looked mismatched / unreal. Contain
            // left a visible seam. Height-normalise avoids both: no bars AND no crop; each
            // half's width just follows its own aspect ratio. leftZoneEl/rightZoneEl and the
            // per-zone pan/zoom are no longer used for the merge.)
            const H = 1024;
            const wL = Math.max(1, Math.round(imgL.naturalWidth  * H / imgL.naturalHeight));
            const wR = Math.max(1, Math.round(imgR.naturalWidth  * H / imgR.naturalHeight));
            const canvas = document.createElement('canvas');
            canvas.width  = wL + wR;
            canvas.height = H;
            const ctx = canvas.getContext('2d');
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(imgL, 0,  0, wL, H);
            ctx.drawImage(imgR, wL, 0, wR, H);

            canvasToCompressedBlob(canvas, MAX_UPLOAD_BYTES).then((blob) => {
                resolve({
                    mergedFile: new File([blob], makeFileName('sohozkaj-dual-image', 'jpg'), { type: 'image/jpeg' }),
                    mergedUrl: URL.createObjectURL(blob),
                });
            });
        }).catch(reject);
    });

    // Single-person dual: the photo already contains both people, so we render the
    // user's pan/zoom crop (WYSIWYG from the solo box) into the same landscape frame
    // the two-box merge produces (1672×1024). This makes the result landscape and
    // respects how the image was positioned inside the upload box.
    const doSoloCrop = (solo, zoneEl) => new Promise((resolve, reject) => {
        const img = new window.Image();
        img.onload = () => {
            const zW = zoneEl?.offsetWidth  || 600;
            const zH = zoneEl?.offsetHeight || 368;
            const OUT_W = 1672;
            const OUT_H = 1024;
            const canvas = document.createElement('canvas');
            canvas.width  = OUT_W;
            canvas.height = OUT_H;
            const ctx = canvas.getContext('2d');
            // Replicate the box preview: objectFit contain, centered, then pan + zoom.
            const containScale = Math.min(zW / img.naturalWidth, zH / img.naturalHeight);
            const zoom = solo.zoom ?? 1;
            const drawnW = img.naturalWidth  * containScale * zoom;
            const drawnH = img.naturalHeight * containScale * zoom;
            const drawnLeft = (zW - drawnW) / 2 + (solo.offsetX ?? 0);
            const drawnTop  = (zH - drawnH) / 2 + (solo.offsetY ?? 0);
            const sx = OUT_W / zW;
            const sy = OUT_H / zH;
            ctx.fillStyle = '#000';
            ctx.fillRect(0, 0, OUT_W, OUT_H);
            ctx.drawImage(img, drawnLeft * sx, drawnTop * sy, drawnW * sx, drawnH * sy);
            canvasToCompressedBlob(canvas, MAX_UPLOAD_BYTES).then((blob) => {
                resolve({
                    croppedFile: new File([blob], makeFileName('sohozkaj-dual-image', 'jpg'), { type: 'image/jpeg' }),
                    croppedUrl: URL.createObjectURL(blob),
                });
            });
        };
        img.onerror = reject;
        img.src = solo.url;
    });
    // ─────────────────────────────────────────────────────────────────────────

    const handleReset = () => {
        skipLocalStorageSave.current = false; // ensure localStorage is cleared on reset
        // Keep the couple/dual photo-map mode across a reset — reset should clear the
        // selections + uploads, NOT kick the user out of dual back to single.
        const wasDual = size === 'dual_image' || dualUploadMode || isMergedImage;
        // Reset all selections to default values
        setSize(wasDual ? 'dual_image' : 'bd_passport');
        setDress('original');
        setDress1('original');
        setDress2('original');
        setBg('white');
        setCustomColor('#000000');
        setExtraEditingGuide('');
        // Clear every extra-guide tool selection (keep this list complete — a missing
        // setter leaves that tool stuck "on" after a reset).
        setRemoveGlasses(false);
        setKeepSunGlasses(false);
        setGlowMakeup(false);
        setSmoothSkin(false);
        setFixOilySkin(false);
        setBrightenImage(false);
        setStudioLighting(false);
        setHalfBody(false);
        // Clear per-tool intensity levels so a re-enabled tool starts at its default.
        setToolLevels({});
        setStraightenHead(false);
        setLookAtCamera(false);
        setOpenEyes(false);
        setCloseMouth(false);
        setShowEar(false);
        setRemoveStrayHair(false);
        setApplyHair(false);
        setRemoveBeard(false);
        setAddBeard(false);
        setKeepBeard(false);
        setKeepMustache(false);
        setKeepFacialMarks(false);
        setKeepNosePin(false);
        setRemoveTupi(false);
        setAddTupi(false);
        setKeepSchoolDressDetails(false);
        setKeepNamePlate(false);
        setNamePlateText('');
        setLipstick(false);
        setLipGloss(false); // the lipstick popup's gloss finish (was lipsClosed)
        setShowExtraGuide(false);
        // Collapse the floating colour pickers so they don't linger open after reset.
        setLipstickColorPopover(false);
        setTupiColorPopover(false);
        // Reset dual image state — but stay in dual mode when we were in it, just with
        // fresh empty upload slots (wasDual keeps the two-slot workflow instead of single).
        setDualUploadMode(wasDual);
        setMergeLeft(null);
        setMergeRight(null);
        setDualSoloSide(null);
        setIsMergedImage(false);
        setShoulderTop('left');
        try { localStorage.removeItem(PREFS_KEY); } catch {}

        notifications.show({
            title: t('imageGeneration.notifications.settingsReset'),
            message: t('imageGeneration.notifications.settingsResetMessage'),
            color: 'blue',
        });
    };

    // Download a history record exactly as its preview shows it: transparent masters
    // keep their alpha as .png, colour/preset backgrounds get composited back in
    // (→ .jpg), opaque legacy masters download as-is. Extension follows the real
    // blob type so a transparent image is never mislabelled .jpg.
    const downloadHistoryRecord = async (image) => {
        // Use backend proxy to avoid CORS issues
        const blob = await imageGenerationService.downloadImage(image.id);
        const finalBlob = hasTransparentSubject(image)
            ? await compositeBackground(blob, image.background)
            : blob;
        const ext = (finalBlob.type || '').includes('png') ? 'png'
            : (finalBlob.type || '').includes('webp') ? 'webp' : 'jpg';
        const url = window.URL.createObjectURL(finalBlob);
        const link = document.createElement('a');
        link.href = url;
        // Name the file after THIS record's saved name, not the current preview's.
        const recordName = (image.fileName || '').replace(/\.[^/.]+$/, '').trim();
        link.setAttribute('download', recordName ? `${recordName}.${ext}` : makeDisplayName(ext));
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        imageGenerationService.trackDownload(image.id).catch(err => console.error('Failed to track download:', err));
    };

    const handleHistoryDownload = async (e, image) => {
        e.stopPropagation();
        try {
            await downloadHistoryRecord(image);
        } catch (error) {
            console.error('Error downloading image:', error);
            notifications.show({
                title: t('imageGeneration.notifications.downloadFailed'),
                message: t('imageGeneration.notifications.downloadFailedMessage'),
                color: 'red',
            });
        }
    };

    const scrollHistoryLeft = () => emblaApi?.scrollPrev();
    const scrollHistoryRight = () => emblaApi?.scrollNext();

    const handleModalDownload = async () => {
        if (!selectedHistoryImage) return;
        try {
            await downloadHistoryRecord(selectedHistoryImage);
        } catch (error) {
            console.error('Error downloading image:', error);
            notifications.show({
                title: t('imageGeneration.notifications.downloadFailed'),
                message: t('imageGeneration.notifications.downloadFailedMessage'),
                color: 'red',
            });
        }
    };

    // Share the ACTUAL image, never its R2 URL: native share sheet with the
    // file attached where available, else copy the image to the clipboard (and
    // open WhatsApp Web / Gmail for those targets) so the user pastes it in.
    const handleShareImage = async (type) => {
        if (!selectedHistoryImage) return;

        try {
            const getBlob = () => imageGenerationService.downloadImage(selectedHistoryImage.id);
            if (type === 'copy') {
                await copyImageToClipboard(getBlob());
                markPrivateResultDelivered(selectedHistoryImage.id);
                notifications.show({
                    title: t('imageGeneration.imageCopied'),
                    message: t('imageGeneration.imageCopiedPasteHint'),
                    color: 'green',
                    autoClose: 7000,
                });
                return;
            }
            const result = await shareImageToTarget(getBlob, type, {
                filename: `${selectedHistoryImage.fileName || 'sohozkaj-image'}.jpg`,
                title: t('imageGeneration.notifications.shareTitle'),
            });
            markPrivateResultDelivered(selectedHistoryImage.id);
            if (result === 'copied') {
                notifications.show({
                    title: t('imageGeneration.imageCopied'),
                    message: t('imageGeneration.imageCopiedPasteHint'),
                    color: 'green',
                    autoClose: 7000,
                });
            }
        } catch (error) {
            if (error?.name === 'AbortError') return; // user closed the native share sheet
            console.error('Error sharing image:', error);
            notifications.show({
                title: t('imageGeneration.notifications.shareFailed'),
                message: error.message || t('imageGeneration.notifications.shareFailedMessage'),
                color: 'red',
            });
        }
    };

    // Apply a parseRecordTools() bundle — every tool is set explicitly true/false so
    // a previous session's selections never leak into a reopened record.
    const applyRestoredTools = ({ tools, customText, plateText }) => {
        setRemoveGlasses(tools.removeGlasses === true);
        setGlowMakeup(tools.glowMakeup === true);
        setSmoothSkin(tools.smoothSkin === true);
        setFixOilySkin(tools.fixOilySkin === true);
        setRemoveBeard(tools.removeBeard === true);
        setAddBeard(tools.addBeard === true);
        setKeepBeard(tools.keepBeard === true);
        setKeepMustache(tools.keepMustache === true);
        setKeepFacialMarks(tools.keepFacialMarks === true);
        setKeepNosePin(tools.keepNosePin === true);
        setRemoveStrayHair(tools.removeStrayHair === true);
        setApplyHair(tools.applyHair === true);
        setOpenEyes(tools.openEyes === true);
        setCloseMouth(tools.closeMouth === true);
        setRemoveTupi(tools.removeTupi === true);
        setAddTupi(tools.addTupi === true);
        setStraightenHead(tools.straightenHead === true);
        setKeepSchoolDressDetails(tools.keepSchoolDressDetails === true);
        setShowEar(tools.showEar === true);
        setHalfBody(tools.halfBody === true);
        setKeepSunGlasses(tools.keepSunGlasses === true);
        setBrightenImage(tools.brightenImage === true);
        setStudioLighting(tools.studioLighting === true);
        setLookAtCamera(tools.lookAtCamera === true);
        setKeepNamePlate(tools.keepNamePlate === true);
        setNamePlateText(plateText || '');
        setLipstick(tools.lipstick === true);
        setLipGloss(tools.lipGloss === true);
        setShowExtraGuide(tools.showExtraGuide === true);
        setExtraEditingGuide(customText || '');
    };

    // Initial-settings snapshot built from the RESTORED values (not current state,
    // which hasn't re-rendered yet) so the regenerate-tracking comparison is exact.
    const restoredInitialSettings = (record, { tools, customText, plateText }) => ({
        size: record.photoSize,
        dress: record.dressCode,
        bg: record.background,
        // Raw hex records resolve through pickerColorFrom directly; keep the hex as
        // the custom colour so a 'custom' live state compares equal after restore.
        customColor: String(record.background || '').startsWith('#') ? record.background : undefined,
        removeGlasses: tools.removeGlasses === true,
        removeTupi: tools.removeTupi === true,
        glowMakeup: tools.glowMakeup === true,
        smoothSkin: tools.smoothSkin === true,
        fixOilySkin: tools.fixOilySkin === true,
        removeBeard: tools.removeBeard === true,
        straightenHead: tools.straightenHead === true,
        openEyes: tools.openEyes === true,
        closeMouth: tools.closeMouth === true,
        removeStrayHair: tools.removeStrayHair === true,
        keepSchoolDressDetails: tools.keepSchoolDressDetails === true,
        addBeard: tools.addBeard === true,
        applyHair: tools.applyHair === true,
        keepBeard: tools.keepBeard === true,
        keepMustache: tools.keepMustache === true,
        keepFacialMarks: tools.keepFacialMarks === true,
        keepNosePin: tools.keepNosePin === true,
        keepSunGlasses: tools.keepSunGlasses === true,
        showEar: tools.showEar === true,
        halfBody: tools.halfBody === true,
        addTupi: tools.addTupi === true,
        brightenImage: tools.brightenImage === true,
        studioLighting: tools.studioLighting === true,
        lookAtCamera: tools.lookAtCamera === true,
        keepNamePlate: tools.keepNamePlate === true,
        namePlateText: plateText || '',
        lipstick: tools.lipstick === true,
        lipstickShade,
        lipstickCustomColor,
        lipGloss: tools.lipGloss === true,
        tupiColor,
        shareeHeadCover, kameezHeadCover, shirtCheck, panjabiStripe, ghutraColor, formalColor,
        showExtraGuide: tools.showExtraGuide === true,
        extraEditingGuide: customText || '',
        dualLevelKey: buildDualLevelKey(
            (record.dressCode || '').includes('|') ? validateDress(record.dressCode.split('|')[0]) : 'original',
            (record.dressCode || '').includes('|') ? validateDress(record.dressCode.split('|')[1]) : 'original',
        ),
    });

    const handleEditImage = async (image) => {
        const img = image || selectedHistoryImage;
        if (!img) return;

        setIsLoadingEdit(true);
        try {
            // Fetch the original uploaded image via backend proxy to avoid CORS issues
            const originalBlob = await imageGenerationService.downloadImage(img.id, 'original');

            // Create a File object from the blob
            const uploadedFile = new File([originalBlob], 'uploaded-image.jpg', { type: originalBlob.type || 'image/jpeg' });

            // Create blob URL for preview
            const previewBlobUrl = URL.createObjectURL(originalBlob);

            // Set the file and preview (original image)
            setFile(uploadedFile);
            setPreviewUrl(previewBlobUrl);
            // Set the AI generated image
            setGeneratedImage(img.aiImage);
            setResultBg(img.background || null);

            // Set the image ID so rename / download / tracking work
            setGeneratedImageId(img.id);

            // Populate Image Info panel
            const metaImg = new window.Image();
            metaImg.onload = function () {
                setImageMetadata({
                    filename: img.fileName || 'generated-image.jpg',
                    dimensions: `${this.naturalWidth} × ${this.naturalHeight}px`,
                    photoSize: photoSizes.find(s => s.value === img.photoSize)?.label || img.photoSize || '',
                    dressCode: formatDressLabel(img.dressCode) || img.dressCode || '',
                    background: img.background === 'transparent'
                        ? t('imageGeneration.bgTransparent')
                        : backgrounds.find(b => b.id === img.background || b.color === img.background)?.label || img.background || '',
                    quality: img.quality || img.metadata?.quality || 'normal',
                });
                // Restore custom name if the image was previously renamed
                setCustomImageName(img.fileName || null);
                setRenameEditing(false);
            };
            metaImg.src = img.aiImage;

            // Restore settings from the history image — skip localStorage save during this block.
            // The flag is cleared inside the useEffect once it fires, not via setTimeout.
            skipLocalStorageSave.current = true;

            if (img.photoSize) setSize(img.photoSize);
            if (img.dressCode) {
                if (img.dressCode.includes('|')) {
                    const [d1, d2] = img.dressCode.split('|');
                    setDress1(validateDress(d1));
                    setDress2(validateDress(d2));
                } else {
                    setDress(img.dressCode);
                }
            }
            if (img.background) {
                const resolvedId = resolveBgFromDb(img.background);
                if (resolvedId) {
                    setBg(resolvedId);
                } else {
                    setBg('custom');
                    setCustomColor(img.background);
                }
            }
            // Re-select the guide tools used for this generation (legacy records
            // parse the stored English label names — see parseRecordTools).
            const restored = parseRecordTools(img);
            applyRestoredTools(restored);

            // Handle dual vs single mode without touching localStorage
            if (img.photoSize === 'dual_image') {
                setIsMergedImage(true);
                setDualUploadMode(false);
            } else {
                setIsMergedImage(false);
                setDualUploadMode(false);
            }

            // Set initial settings for regenerate tracking
            setInitialSettings(restoredInitialSettings(img, restored));
            setSettingsChanged(false);

            // Close modal
            setShowImageModal(false);

            // Scroll to top
            window.scrollTo({ top: 0, behavior: 'smooth' });

            notifications.show({
                title: t('imageGeneration.notifications.imageLoaded'),
                message: t('imageGeneration.notifications.imageLoadedMessage'),
                color: 'blue',
            });
        } catch (error) {
            console.error('Error loading image for edit:', error);
            notifications.show({
                title: t('imageGeneration.notifications.loadFailed'),
                message: t('imageGeneration.notifications.loadFailedMessage'),
                color: 'red',
            });
        } finally {
            setIsLoadingEdit(false);
        }
    };

    const handleDeleteImage = async () => {
        if (!selectedHistoryImage) return;

        setDeletingImageId(selectedHistoryImage.id);
        try {
            await imageGenerationService.deleteImage(selectedHistoryImage.id);

            // Remove deleted image from local history immediately
            setImageHistory(prevHistory =>
                prevHistory.filter(img => img.id !== selectedHistoryImage.id)
            );

            // Close modals
            setShowDeleteConfirm(false);
            setShowImageModal(false);

            notifications.show({
                title: t('imageGeneration.notifications.imageDeleted'),
                message: t('imageGeneration.notifications.imageDeletedMessage'),
                color: 'green',
            });
        } catch (error) {
            console.error('Error deleting image:', error);
            notifications.show({
                title: t('imageGeneration.notifications.deleteFailed'),
                message: error.response?.data?.message || t('imageGeneration.notifications.deleteFailedMessage'),
                color: 'red',
            });
        } finally {
            setDeletingImageId(null);
        }
    };

    const handleFeedback = async (imageId, feedback, reason = null, note = null) => {
        // Dislike opens the feedback modal first to collect a reason/message; we only
        // submit once we have it. A like needs neither and submits straight away.
        if (feedback === 'negative' && !reason) {
            setPendingFeedbackId(imageId);
            setShowFeedbackModal(true);
            return;
        }
        setFeedbackLoading(feedback);
        try {
            await imageGenerationService.submitFeedback(imageId, feedback, reason, note);
            setFeedbackGiven(prev => new Set([...prev, imageId]));
            setShowFeedbackModal(false);
            notifications.show({
                message: 'আপনার মতামতের জন্য ধন্যবাদ!',
                color: 'green',
                autoClose: 3000,
            });
        } catch (error) {
            console.error('Failed to submit feedback:', error);
        } finally {
            setFeedbackLoading(null);
        }
    };

    // Copy the ACTUAL displayed image to the clipboard (never its R2 URL) — WITH the
    // current background composited in, so a transparent master is copied on its colour.
    // getEditedImageUrl handles eraser edits + bg compositing + format (JPEG when a colour
    // is applied, PNG when kept transparent).
    const handleCopyResultImage = async () => {
        if (copyingResult) return;
        setCopyingResult(true);
        try {
            const url = await getEditedImageUrl();
            if (!url) throw new Error('Failed to prepare image for copy');
            const blob = await fetch(url).then((r) => r.blob());
            window.URL.revokeObjectURL(url);
            // Count the copy as a download NOW that the image is prepared — BEFORE the
            // clipboard write, whose first call can silently no-op on its async
            // user-gesture window in some browsers (that's why the first copy wasn't
            // being counted). Firing here guarantees every copy is counted once.
            if (generatedImageId) {
                imageGenerationService.trackDownload(generatedImageId).catch((err) => console.error('Failed to track copy as download:', err));
            }
            await copyImageToClipboard(blob);
            notifications.show({
                title: t('imageGeneration.imageCopied'),
                message: t('imageGeneration.imageCopiedPasteHint'),
                color: 'green',
                autoClose: 7000,
            });
        } catch (error) {
            console.error('Failed to copy image:', error);
            notifications.show({ message: t('imageGeneration.qrCopyFailed'), color: 'red' });
        } finally {
            setCopyingResult(false);
        }
    };

    // ── Crop the generated image (manual, no AI, no credit) ─────────────────────────
    // Fetch the STORED AI image bytes via the same-origin download API (so the crop
    // canvas isn't CORS-tainted by the R2 host), open the crop modal with a blob URL.
    const openResultCrop = async () => {
        if (!generatedImageId || resultCropLoading) return;
        setResultCropLoading(true);
        try {
            // The api interceptor unwraps response.data, so downloadImage resolves to the Blob itself.
            const blob = await imageGenerationService.downloadImage(generatedImageId, 'ai');
            const url = window.URL.createObjectURL(blob);
            setResultCropSrc(url);
            setResultCropMime(blob.type || 'image/jpeg');
            setShowResultCrop(true);
        } catch (err) {
            console.error('Failed to open crop editor:', err);
            notifications.show({ message: t('imageGeneration.cropFailed'), color: 'red' });
        } finally {
            setResultCropLoading(false);
        }
    };

    const closeResultCrop = () => {
        setShowResultCrop(false);
        if (resultCropSrc) window.URL.revokeObjectURL(resultCropSrc);
        setResultCropSrc(null);
    };

    // Apply the crop: upload the cropped blob to overwrite the SAME R2 object, then
    // cache-bust the displayed URL and refresh the in-panel dimensions.
    const handleResultCropApply = async (blob, dims) => {
        if (!generatedImageId || !blob) return;
        try {
            const ext = (resultCropMime || '').includes('png') ? 'png' : (resultCropMime || '').includes('webp') ? 'webp' : 'jpg';
            const formData = new FormData();
            formData.append('image', blob, `crop.${ext}`);
            await imageGenerationService.replaceAiImage(generatedImageId, formData);

            // Same R2 key was overwritten → bust the browser cache so the new crop shows.
            const bust = (u) => {
                if (!u || typeof u !== 'string' || u.startsWith('blob:') || u.startsWith('data:')) return u;
                const base = u.split('#')[0].replace(/([?&])v=\d+(&|$)/, '$1').replace(/[?&]$/, '');
                return `${base}${base.includes('?') ? '&' : '?'}v=${Date.now()}`;
            };
            // Bust the history row of this image too (matched by URL — history ids are
            // encoded, generatedImageId is raw) so its thumbnail, the preview modal and the
            // print slider show the crop. Same busted URL keeps handleOpenZoom's match.
            const nextUrl = bust(generatedImage);
            const stripV = (u) => (typeof u === 'string' ? u.split(/[?#]/)[0] : u);
            setImageHistory((prev) => prev.map((im) => (stripV(im.aiImage) === stripV(generatedImage) ? { ...im, aiImage: nextUrl } : im)));
            setGeneratedImage(nextUrl);
            if (erasedImageUrl) setErasedImageUrl((prev) => bust(prev));
            if (dims?.width && dims?.height) {
                setImageMetadata((m) => (m ? { ...m, dimensions: `${dims.width} × ${dims.height}px` } : m));
            }
            notifications.show({ message: t('imageGeneration.cropSaved'), color: 'green' });
            closeResultCrop();
        } catch (err) {
            console.error('Failed to save cropped image:', err);
            notifications.show({ message: t('imageGeneration.cropFailed'), color: 'red' });
            throw err; // keep the modal open so the user can retry
        }
    };

    // Persist the chosen background of a TRANSPARENT-subject result (no AI re-gen, no
    // credit). The R2 file stays a transparent cut-out; only the stored `background`
    // token changes, so it shows with this colour everywhere (history, preview, slider).
    const persistBg = async (colorHexOrNull) => {
        if (!generatedImageId) return;
        const value = colorHexOrNull || 'transparent';
        try {
            await imageGenerationService.updateBackground(generatedImageId, value);
            setResultBg(value);
            // Keep the history strip in sync so its thumbnail recolours immediately.
            setImageHistory((prev) => prev.map((im) => im.id === generatedImageId ? { ...im, background: value } : im));
        } catch (error) {
            console.error('Failed to update background:', error);
            notifications.show({ message: t('common.error'), color: 'red' });
        }
    };

    // LEFT background picker click. Pre-generation it just sets the colour used for the
    // first generation; post-generation it recolours the transparent master INSTANTLY
    // (preview reads `bg` live) and saves the new colour so it shows everywhere.
    // Selecting a colour only PREVIEWS it (the preview reads `bg` live) — it is NOT saved
    // until the user clicks the "Apply background" button. So closing without applying
    // never changes the stored colour.
    // Bg changes flow through the settings-change effect: for opaque (baked-bg)
    // results a colour change counts as a settings change (shows "আবার তৈরি");
    // transparent masters keep the free instant-recolour flow.
    const handleBgSelect = (bgId) => { setBg(bgId); };
    const handleCustomBgChange = (hex) => { setCustomColor(hex); setBg('custom'); };

    const handleHistoryImageClick = (image) => {
        const index = imageHistory.findIndex(img => img.id === image.id);
        setCurrentImageIndex(index);
        setSelectedHistoryImage(image);
        setImageModalCompare(false);
        setShowImageModal(true);
    };

    const handleOpenZoom = () => {
        // Open the preview modal with the JUST-generated image. Match it in history by its
        // image URL — the generate response returns a RAW id while history rows carry an
        // ENCODED id, so matching on id never worked and it silently fell back to
        // imageHistory[0]. That [0] is the newest image only AFTER loadHistory() finishes;
        // clicked before that, [0] is the PREVIOUS image — the intermittent "wrong image
        // opens" bug. When the new image isn't in history yet, synthesise a record from the
        // current generated state instead of falling back to [0].
        const index = generatedImage ? imageHistory.findIndex(img => img.aiImage === generatedImage) : -1;
        let image = index >= 0 ? imageHistory[index] : null;
        if (!image && generatedImage) {
            image = {
                id: generatedImageId,
                aiImage: generatedImage,
                uploadedImage: previewUrl || undefined,
                background: resultBg ?? bg,
                fileName: imageMetadata?.filename,
            };
        }
        if (image) {
            setCurrentImageIndex(index >= 0 ? index : 0);
            setSelectedHistoryImage(image);
        }
        setImageModalCompare(true);
        setShowImageModal(true);
    };

    // e-Passport tile: BD keeps the fixed BD e-passport; non-BD shows that country's
    // size (the dimension key IS item.value, so the grid's `size === item.value`
    // selection and setSize just work — generation maps it back via EPASS_TO_BACKEND).
    const epassSizeLabel = t(`imageGeneration.sizeDim_${epassKey}`);
    const epassCountryName = countryLabels[userCC] || userCC;
    const epassEntry = isBdUser
        ? { value: 'bd_epassport', label: t('imageGeneration.bdEPassport'), abbr: t('imageGeneration.abbrEPassport'), icon: IconNfc, tooltip: t('imageGeneration.bdEPassport') }
        : { value: epassKey, label: `${epassCountryName} e-Passport`, abbr: epassSizeLabel, icon: IconNfc, tooltip: `${epassCountryName} e-Passport · ${epassSizeLabel}` };
    const photoSizes = [
        { value: 'bd_passport',  label: t('imageGeneration.bdPassport'),  abbr: t('imageGeneration.abbrPassport'),   icon: IconUserSquare,  tooltip: t('imageGeneration.bdPassport')  },
        { value: 'dual_image',   label: t('imageGeneration.dualImage'),   abbr: t('imageGeneration.abbrDual'),       icon: IconFriends,     tooltip: t('imageGeneration.dualImage')   },
        epassEntry,
        { value: 'bd_visa',      label: t('imageGeneration.bdVisa'),      abbr: t('imageGeneration.abbrVisa'),       icon: IconPlaneTilt,   tooltip: t('imageGeneration.bdVisa')      },
        // জন্ম (birth registration) is a Bangladesh-only document — hide it for non-BD.
        ...(isBdUser ? [{ value: 'bd_birth', label: t('imageGeneration.bdBirth'), abbr: t('imageGeneration.abbrBirth'), icon: IconCertificate, tooltip: t('imageGeneration.bdBirth') }] : []),
    ];

    // Selected-size physical dims — null for dual/unknown sizes (legacy shapes apply).
    // Drives the upload-box ratio, its "(45×55 mm)" hint, and the uploaded-preview shape.
    const sizeDims = SIZE_DIMS[size] || null;
    const sizeRatioCss = sizeDims ? `${sizeDims.w} / ${sizeDims.h}` : null;
    const sizeDimLabel = sizeDims ? t(`imageGeneration.sizeDim_${size}`) : null;

    const enabledDressColors = user?.settings?.enabledDressColors;
    const dressColors = ALL_DRESS_COLORS
        .filter(c => !enabledDressColors?.length || enabledDressColors.includes(c.id))
        .map(c => ({ id: c.id, label: t(c.i18nKey) || c.colorLabel, hex: c.hex }));

    const clothingStyles = [
        { value: 'original',       label: t('imageGeneration.originalClothing'), image: null,                                                  defaultColor: null    },
        { value: 'shirt',          label: t('imageGeneration.shirt'),             image: '/images/clothing-styles/shirt.svg',                   defaultColor: 'white' },
        { value: 't-shirt',        label: t('imageGeneration.tShirt'),            image: '/images/clothing-styles/t-shirt.svg',                 defaultColor: 'blue'  },
        { value: 'suite',               label: t('imageGeneration.suite'),              image: '/images/clothing-styles/suite.svg',                        defaultColor: 'black' },
        { value: 'suite-tieless',       label: t('imageGeneration.suiteTieless'),       image: '/images/clothing-styles/suite-tieless.svg',                defaultColor: 'black' },
        { value: 'sharee',              label: t('imageGeneration.sharee'),             image: '/images/clothing-styles/sharee.svg',                       defaultColor: 'red'   },
        { value: 'hizab',               label: t('imageGeneration.hizab'),              image: '/images/clothing-styles/hizab.svg',                        defaultColor: 'red'   },
        { value: 'panjbi',              label: t('imageGeneration.panjbi'),             image: '/images/clothing-styles/panjbi.svg',                       defaultColor: 'white' },
        { value: 'tops',                label: t('imageGeneration.tops'),               image: '/images/clothing-styles/tops.svg',                         defaultColor: 'blue'  },
        { value: 'round-collar-tshirt', label: t('imageGeneration.roundCollarTshirt'), image: '/images/clothing-styles/round-collar-tshirt.svg',           defaultColor: 'blue'  },
        { value: 'kameez-hijab',        label: t('imageGeneration.kameezHijab'),        image: '/images/clothing-styles/kameez-hijab.svg',                   defaultColor: 'blue'  },
        { value: 'shari-hijab',         label: t('imageGeneration.shariHijab'),          image: '/images/clothing-styles/shari-hijab.svg',                    defaultColor: 'red'   },
        { value: 'orna-kameez',         label: t('imageGeneration.ornaKameez'),          image: '/images/clothing-styles/orna-kameez.svg',                    defaultColor: 'blue'  },
        { value: 'sharee-design',       label: t('imageGeneration.designSharee'),        image: '/images/clothing-styles/sharee-design.svg',                  defaultColor: 'red'   },
        { value: 'jacket',              label: t('imageGeneration.jacket'),              image: '/images/clothing-styles/jacket.svg',                          defaultColor: 'black' },
        // Gulf / formal additions — each has its OWN colour token in the backend (/abayaColor: /thobeColor: /nehruJacketColor: /hijabFormalWearColor:).
        { value: 'abaya',              label: t('imageGeneration.abaya'),               image: '/images/clothing-styles/abaya.svg',                           defaultColor: 'navy'     },
        { value: 'thobe',              label: t('imageGeneration.thobe'),               image: '/images/clothing-styles/thobe.svg',                           defaultColor: 'white'    },
        { value: 'thobe-ghutra',       label: t('imageGeneration.thobeGhutra'),         image: '/images/clothing-styles/thobe-ghutra.svg',                    defaultColor: 'white'    },
        { value: 'nehru-jacket',       label: t('imageGeneration.nehruJacket'),         image: '/images/clothing-styles/nehru-jacket.svg',                    defaultColor: 'charcoal' },
        { value: 'hijab-formal',       label: t('imageGeneration.hijabFormalWear'),     image: '/images/clothing-styles/hijab-formal.svg',                    defaultColor: 'maroon'   },
    ];

    const parseDressValue = (val) => {
        if (!val || val === 'original') return { type: 'original', color: null };
        const idx = val.indexOf('_');
        if (idx === -1) return { type: val, color: null };
        return { color: val.slice(0, idx), type: val.slice(idx + 1) };
    };

    // Fully-localised label for a stored dress value ('black_suite' → 'কালো স্যুট');
    // dual values ('a|b') are joined. Colours resolve through their i18n keys so the
    // whole label is in the UI language — old records benefit too, since formatting
    // always happens at render time from the raw stored value.
    const formatDressLabel = (val) => {
        if (!val) return '';
        const str = String(val);
        if (str.includes('|')) return str.split('|').map(formatDressLabel).join(' | ');
        if (str === 'original') return t('imageGeneration.originalClothing');
        const { color, type } = parseDressValue(str);
        const typeLabel = clothingStyles.find(d => d.value === type)?.label || type;
        if (!color) return typeLabel;
        const colorDef = ALL_DRESS_COLORS.find(c => c.id === color);
        return `${colorDef ? t(colorDef.i18nKey) : color} ${typeLabel}`;
    };

    const showSuitSubColors = user?.settings?.showSuitColorOptions === true;
    const showDressTabs = (val) => (showSuitSubColors && (val === 'suite' || val === 'suite-tieless' || val === 'sharee' || val === 'sharee-design')) || val === 'kameez-hijab' || val === 'shari-hijab' || val === 'orna-kameez' || val === 'thobe-ghutra' || val === 'hijab-formal';
    const enabledDressStyles = user?.settings?.enabledDressStyles;
    // Every style stays available to everyone; the country only decides which ones start switched ON (utils/dressStyles.js).
    const COUNTRY_HIDDEN_DRESS = [];
    const countryDressStyles = COUNTRY_HIDDEN_DRESS.length
        ? clothingStyles.filter(s => !COUNTRY_HIDDEN_DRESS.includes(s.value))
        : clothingStyles;
    const visibleDressStyles = countryDressStyles.slice(1).filter(s => isDressStyleEnabled(enabledDressStyles, s.value, user?.country));

    // Reset any active dress to 'original' if its style has been hidden — either by the
    // user's enabledDressStyles setting, or because it's hidden for this country.
    useEffect(() => {
        const getType = (val) => {
            if (!val || val === 'original') return null;
            const idx = val.indexOf('_');
            return idx === -1 ? val : val.slice(idx + 1);
        };
        const isHidden = (val) => {
            const type = getType(val);
            if (!type) return false;
            if (COUNTRY_HIDDEN_DRESS.includes(type)) return true;
            return !isDressStyleEnabled(enabledDressStyles, type, user?.country);
        };
        if (isHidden(dress))  setDress('original');
        if (isHidden(dress1)) setDress1('original');
        if (isHidden(dress2)) setDress2('original');
    }, [enabledDressStyles, isBdUser]); // eslint-disable-line react-hooks/exhaustive-deps

    // Ctrl/Cmd+U opens the image picker while the upload area is visible.
    useEffect(() => {
        const onKeyDown = (e) => {
            if ((e.ctrlKey || e.metaKey) && (e.key === 'u' || e.key === 'U')) {
                const active = document.activeElement;
                if (active && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA' || active.isContentEditable)) return;
                if (previewUrl || isGenerating || dualUploadMode) return;
                e.preventDefault();
                if (isMobile) setShowUploadSheet(true);
                else fileInputRef.current?.click();
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [previewUrl, isGenerating, dualUploadMode, isMobile]);

    // Whether generation can start — mirrors the generate/regenerate button state.
    const overGuideLimit = showExtraGuide && (extraEditingGuide.length > GUIDE_MAX_CHARS || extraEditingGuide.trim().split(/\s+/).filter(Boolean).length > GUIDE_MAX_WORDS);
    const hasUploadedImage = !!(file || previewUrl || (dualUploadMode && (mergeLeft || mergeRight)));
    const canGenerate = !isGenerating && !overGuideLimit && hasUploadedImage && (generatedImage ? settingsChanged : (!!size && !!dress && !!bg));

    // Ctrl/Cmd+G starts generation when ready.
    useEffect(() => {
        const onKeyDown = (e) => {
            if ((e.ctrlKey || e.metaKey) && (e.key === 'g' || e.key === 'G')) {
                e.preventDefault();
                if (canGenerate) handleGenerate();
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [canGenerate]); // eslint-disable-line react-hooks/exhaustive-deps

    // Parse combined dress value (e.g. "red_shirt" → { color: "red", type: "shirt" })
    const parsedDress  = parseDressValue(dress);
    const parsedDress1 = parseDressValue(dress1);
    const parsedDress2 = parseDressValue(dress2);

    // ── Gender / head-cover-aware tool gating (single-person mode only) ────────────
    // Some editing tools don't apply to the chosen dress, so we disable (and clear) them:
    //   • any FEMALE dress → hide male grooming tools (beard / mustache / tupi)
    //   • head fully covered (hijab types, or sharee ghomta / orna head-cover toggled on)
    //     → additionally hide the hair / ear tools
    // Dual-upload mode mixes two people of possibly different genders, so gating is skipped there.
    const FEMALE_DRESS_TYPES_UI = ['sharee', 'sharee-design', 'hizab', 'tops', 'kameez-hijab', 'shari-hijab', 'orna-kameez', 'abaya', 'hijab-formal'];
    const HIJAB_DRESS_TYPES_UI  = ['hizab', 'kameez-hijab', 'shari-hijab', 'hijab-formal', 'thobe-ghutra']; // garments that cover the head (ghutra included)
    const singlePersonMode = !(isMergedImage || dualUploadMode);
    const isFemaleDress = singlePersonMode && FEMALE_DRESS_TYPES_UI.includes(parsedDress.type);
    // A non-female dress that isn't "original" is treated as male (panjabi, shirt, suit,
    // jacket…) — mirror of isFemaleDress. Used to hide the female-only lipstick tool.
    const isMaleDress = singlePersonMode && parsedDress.type !== 'original' && !FEMALE_DRESS_TYPES_UI.includes(parsedDress.type);
    const isHeadCovered = singlePersonMode && (
        HIJAB_DRESS_TYPES_UI.includes(parsedDress.type) ||
        (['sharee', 'sharee-design'].includes(parsedDress.type) && shareeHeadCover) ||
        (parsedDress.type === 'orna-kameez' && kameezHeadCover)
    );
    // "Keep school dress" and picking a cloth are mutually exclusive: if any dress is
    // chosen, disable the tool (the reverse — tool on → dress picker disabled — is the overlay below).
    const anyDressSelected = parsedDress.type !== 'original' || parsedDress1.type !== 'original' || parsedDress2.type !== 'original';
    const disabledTools = new Set([
        ...(isFemaleDress ? ['removeBeard', 'addBeard', 'keepBeard', 'keepMustache', 'addTupi', 'removeTupi'] : []),
        ...(isMaleDress ? ['lipstick'] : []),
        // The ghutra already covers the head — a tupi (cap) on top of it makes no sense.
        ...(singlePersonMode && parsedDress.type === 'thobe-ghutra' ? ['addTupi', 'removeTupi'] : []),
        ...(isHeadCovered ? ['showEar', 'removeStrayHair', 'applyHair'] : []),
        ...(anyDressSelected ? ['keepSchoolDressDetails'] : []),
    ]);

    // When the dress makes an active tool irrelevant, clear it so it can't leak into the prompt.
    useEffect(() => {
        const clears = [
            ['removeBeard', removeBeard, setRemoveBeard], ['addBeard', addBeard, setAddBeard],
            ['keepBeard', keepBeard, setKeepBeard], ['keepMustache', keepMustache, setKeepMustache],
            ['addTupi', addTupi, setAddTupi], ['removeTupi', removeTupi, setRemoveTupi],
            ['showEar', showEar, setShowEar], ['removeStrayHair', removeStrayHair, setRemoveStrayHair],
            ['applyHair', applyHair, setApplyHair],
            ['keepSchoolDressDetails', keepSchoolDressDetails, setKeepSchoolDressDetails],
            ['lipstick', lipstick, setLipstick],
        ];
        clears.forEach(([key, val, setter]) => { if (disabledTools.has(key) && val) setter(false); });
        // Also collapse the floating colour pickers when their tool is dress-disabled.
        if (disabledTools.has('lipstick')) setLipstickColorPopover(false);
        if (disabledTools.has('addTupi')) setTupiColorPopover(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dress, dress1, dress2, shareeHeadCover, kameezHeadCover, isMergedImage, dualUploadMode]);

    // ── Tool selection limits ─────────────────────────────────────────────────────
    // Count active editing tools (custom-instruction excluded — it's a separate free-text mode).
    const activeToolCount = [
        glowMakeup, smoothSkin, fixOilySkin, brightenImage, studioLighting, straightenHead,
        removeStrayHair, showEar, halfBody, openEyes, closeMouth, keepSchoolDressDetails, keepNamePlate,
        removeGlasses, keepSunGlasses, removeBeard, addBeard, keepBeard, keepMustache,
        keepFacialMarks, keepNosePin, applyHair, removeTupi, addTupi, lookAtCamera, lipstick,
    ].filter(Boolean).length;
    // Blocking rules for each tile:
    //   • custom-instruction → ALWAYS available; it can be combined with the other tools.
    //   • every other tool   → max 5 active at once; still-inactive tiles grey out once the cap is hit.
    const MAX_ACTIVE_TOOLS = 5;
    const isToolCapBlocked = (item) =>
        item.label === t('imageGeneration.enableCustomInstruction')
            ? false // custom instruction stays selectable even when other tools are active
            : activeToolCount >= MAX_ACTIVE_TOOLS && !item.active;

    // Intensity level picker (subtle / medium / strong, default medium) for the 4 effect
    // tools. Selecting the tool opens the popover; the chosen level rides per-tool in
    // toolParams.levels → backend composes `/token:level`.
    const LEVEL_OPTIONS = [
        { id: 'subtle', label: t('imageGeneration.levelSubtle') },
        { id: 'medium', label: t('imageGeneration.levelMedium') },
        { id: 'strong', label: t('imageGeneration.levelStrong') },
    ];
    // Half-body framing amount. Ids are backend tokens → `/halfBody:<id>`:
    //   upper    → ~40% head-to-chest   (হালকা)
    //   strict   → ~50% head-to-waist   (মাঝারি, default)
    //   extended → ~60% head-to-hips    (বেশি)
    const HALF_BODY_LEVEL_OPTIONS = [
        { id: 'upper',    label: t('imageGeneration.levelSubtle') },
        { id: 'strict',   label: t('imageGeneration.levelMedium') },
        { id: 'extended', label: t('imageGeneration.levelStrong') },
    ];
    // Each leveled tool's default + option list, so the intensity picker and the
    // floating number badge on the active tool agree on the 1-based level number
    // (হালকা = 1, মাঝারি = 2, বেশি = 3).
    const LEVEL_DEFAULTS = { glowMakeup: 'medium', smoothSkin: 'medium', fixOilySkin: 'medium', brightenImage: 'medium', studioLighting: 'medium', halfBody: 'strict' };
    const levelOptionsFor = (key) => (key === 'halfBody' ? HALF_BODY_LEVEL_OPTIONS : LEVEL_OPTIONS);
    const levelNumberFor = (key) => {
        const opts = levelOptionsFor(key);
        const i = opts.findIndex((o) => o.id === (toolLevels[key] || LEVEL_DEFAULTS[key] || 'medium'));
        return i < 0 ? 1 : i + 1;
    };
    const makeLevelPicker = (key, options = LEVEL_OPTIONS, defaultLevel = 'medium') => ({
        open: levelPopoverFor === key,
        setOpen: (o) => setLevelPopoverFor(o ? key : null),
        title: t('imageGeneration.intensityHint'),
        options,
        selected: toolLevels[key] || defaultLevel,
        onSelect: (lvl) => setToolLevels(p => ({ ...p, [key]: lvl })),
    });

    // Custom-instruction may be combined with the other tools, so it is NOT cleared when
    // a tool becomes active (previously selecting any tool hid the box and wiped its text).

    // Reset button visibility — include ANY active extra-guide tool (activeToolCount
    // covers them all) and custom-instruction mode, so "reset" shows whenever there's
    // something to clear, not just size/dress/bg changes.
    const hasSettingsChanged = size !== 'bd_passport' || dress !== 'original' || dress1 !== 'original' || dress2 !== 'original' || bg !== 'white' || extraEditingGuide !== '' || activeToolCount > 0 || showExtraGuide;
    const hasAdjustmentsChanged = isEdited(edits);
    // The edit drawer's live preview: the result's bytes (the eraser's edit when there is one, else the stored image through the
    // backend — the R2 host sends no CORS headers), fetched when the drawer first opens; re-rendered with the edits on every change.
    const [editSource, setEditSource] = useState(null);
    const editSourceKeyRef = useRef(null);
    const editSourceKey = erasedImageUrl || generatedImageId || null;
    useEffect(() => { setEdits(NEUTRAL_EDITS); setEditorOpen(false); }, [generatedImageId]);
    useEffect(() => { setEditSource(null); editSourceKeyRef.current = null; }, [editSourceKey]);
    const loadEditSource = async () => {
        if (!editSourceKey || editSourceKeyRef.current === editSourceKey) return;
        editSourceKeyRef.current = editSourceKey;
        try {
            const blob = erasedImageUrl ? await fetch(erasedImageUrl).then((r) => r.blob()) : await imageGenerationService.downloadImage(generatedImageId, 'ai');
            setEditSource(blob);
        } catch {
            editSourceKeyRef.current = null;
        }
    };
    const toggleEditor = () => setEditorOpen((o) => { if (!o) loadEditSource(); if (!o) setResultView('result'); return !o; });
    // Desktop / tablet: one photo at a time — the result, the uploaded original, or the before-after slider (the Studio Editor's switch).
    const [resultView, setResultView] = useState('result');
    useEffect(() => { setResultView('result'); }, [generatedImageId]);
    const editedPreviewUrl = usePhotoEditPreview(editSource, edits);
    const filterThumbs = useFilterThumbs(editSource);
    const resultDisplayUrl = editedPreviewUrl || erasedImageUrl || generatedImage; // the result as it will leave (download / print)

    // The current result has a transparent subject (AI .webp/.png cut-out). Only then do
    // we composite the picker colour behind it (else show the raw opaque image).
    const resultIsTransparent = resultBg === 'transparent' || /\.(png|webp)(\?|#|$)/i.test(generatedImage || '');
    // The colour the LEFT picker currently represents (null = keep transparent). This is
    // what the preview composites and what a download bakes in.
    const currentBgColor = pickerColorFrom(bg, customColor);
    // The colour saved on the current result; the "Apply background" button shows only
    // when the picked colour differs from it (and no cloth/tool change forces a regen).
    const bgChangedVsSaved = resultIsTransparent && currentBgColor !== resolveBgToApplied(resultBg);

    // CSS backdrop for a history thumbnail so transparent cut-outs show their stored
    // colour (or checkerboard) instead of the dark strip behind them.
    const thumbBgStyle = (image) => {
        if (!hasTransparentSubject(image) || !image.background) return {};
        if (image.background === 'transparent') return CHECKERBOARD_STYLE;
        const hex = resolveBgToApplied(image.background);
        return hex ? { background: hex } : {};
    };

    // The transparent swatch only exists for users who opted into transparent output
    // (settings.transparentImageOutput, photo-edit settings tab). Default mode bakes
    // the colour into the image, so "transparent" would silently produce white.
    const transparentOutputEnabled = user?.settings?.transparentImageOutput === true;
    const backgrounds = [
        { id: 'white', label: t('imageGeneration.white'), color: '#ffffff' },
        { id: 'blue', label: t('imageGeneration.lightBlue'), color: '#2196F3' },
        { id: 'sky_blue', label: t('imageGeneration.skyBlue'), color: '#03A9F4' },
        { id: 'grey', label: t('imageGeneration.lightGrey'), color: '#9E9E9E' },
        { id: 'dark_blue', label: t('imageGeneration.darkBlue'), color: '#3068E1' },
        { id: 'teal', label: t('imageGeneration.teal'), color: '#00897B' },
        { id: 'off_white', label: t('imageGeneration.offWhite'), color: '#FFF8DC' },
        ...(transparentOutputEnabled ? [{ id: 'transparent', label: t('imageGeneration.bgTransparent'), color: 'transparent' }] : []),
        { id: 'custom', label: t('imageGeneration.customColor'), color: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' },
    ];

    // The result's details (file name — double-click to rename — size, quality, dress, background), shown in the info popover.
    const imageInfoBody = imageMetadata ? (
        <div className="flex flex-col gap-1.5 text-sm">
            <div className="flex items-center justify-between flex-nowrap">
                <span className="shrink-0 text-zinc-500 dark:text-zinc-400">{t('imageGeneration.fileName')}:</span>
                {renameEditing ? (
                    <div style={{display:"flex",alignItems:"center",gap:4,flexWrap:"nowrap",flex:1,minWidth:0,marginLeft:8}}>
                        <input
                            type="text"
                            style={{padding:"5px 8px",borderRadius:4,border:"1px solid #d1d5db",fontSize:13,background:"inherit",color:"inherit", width: "100%", minWidth: 180}}
                            value={renameInputVal}
                            maxLength={RENAME_MAX}
                            onChange={(e) => setRenameInputVal(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleRenameImage(); if (e.key === 'Escape') setRenameEditing(false); }}
                            onBlur={handleRenameImage}
                            autoFocus
                        />
                    </div>
                ) : (
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <span className={styles.infoValue} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, wordBreak: 'break-all', textAlign: 'right', maxWidth: '65%', cursor: 'pointer', borderBottom: '1px dashed currentColor' }} onDoubleClick={() => { const fn = imageMetadata.filename; setRenameInputVal(customImageName || fn.substring(0, fn.lastIndexOf('.')) || fn); setRenameEditing(true); }}>
                                <span style={{ wordBreak: 'break-all' }}>{customImageName || imageMetadata.filename}</span>
                                <IconEdit size={13} style={{ flexShrink: 0, opacity: 0.6 }} onClick={(e) => { e.stopPropagation(); const fn = imageMetadata.filename; setRenameInputVal(customImageName || fn.substring(0, fn.lastIndexOf('.')) || fn); setRenameEditing(true); }} />
                            </span>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="z-[2000]">{t('imageGeneration.doubleClickToRename')}</TooltipContent>
                    </Tooltip>
                )}
            </div>
            <div className="flex items-center justify-between"><span className="text-zinc-500 dark:text-zinc-400">{t('imageGeneration.dimensions')}:</span><span className={styles.infoValue}>{imageMetadata.dimensions}</span></div>
            <div className="flex items-center justify-between"><span className="text-zinc-500 dark:text-zinc-400">{t('imageGeneration.photoSize')}:</span><span className={styles.infoValue}>{imageMetadata.photoSize}</span></div>
            {imageMetadata.quality && <div className="flex items-center justify-between"><span className="text-zinc-500 dark:text-zinc-400">{t('imageGeneration.quality.rowLabel')}:</span><span className={styles.infoValue}>{qualityTierLabel(imageMetadata.quality)}</span></div>}
            <div className="flex items-center justify-between"><span className="text-zinc-500 dark:text-zinc-400">{t('imageGeneration.dressCode')}:</span><span className={styles.infoValue}>{imageMetadata.dressCode}</span></div>
            <div className="flex items-center justify-between"><span className="text-zinc-500 dark:text-zinc-400">{t('imageGeneration.background')}:</span><span className={styles.infoValue}>{imageMetadata.background}</span></div>
        </div>
    ) : null;
    const printEdited = async () => {
        const u = await getEditedImageUrl();
        openPrintModal(u || generatedImage, generatedImageId);
    };
    const compactActions = useMediaQuery('(max-width: 1279px)');
    const anyToolOn = removeGlasses || removeTupi || glowMakeup || smoothSkin || fixOilySkin || removeBeard || straightenHead || openEyes || closeMouth || removeStrayHair || keepSchoolDressDetails || addBeard || applyHair || addTupi || keepBeard || keepMustache || keepFacialMarks || keepNosePin || keepSunGlasses || showEar || halfBody || brightenImage || studioLighting || lookAtCamera || (showExtraGuide && !!extraEditingGuide);
    const resultMode = !!generatedImage && !isGenerating; // a finished result is on screen → the right column holds its actions

    // Phone: choosing ডুয়াল under ছবির মাপ. A photo already in moves across with it (exitDualMode brings it back).
    const switchToDual = () => {
        if (dualUploadMode) return;
        if (file) {
            setMergeLeft({ file, url: previewUrl, offsetX: 0, offsetY: 0, zoom: 1 });
            setFile(null);
            setPreviewUrl(null);
        }
        setDualSoloSide(null);
        setDualUploadMode(true);
        setSize('dual_image');
    };
    const exitDualMode = () => {
        setDualUploadMode(false);
        setIsMergedImage(false);
        setDualSoloSide(null);
        if (size === 'dual_image') setSize('bd_passport');
        // Carry whichever single image exists into single mode
        const carry = mergeLeft || mergeRight;
        if (carry) commitFile(carry.file);
        setMergeLeft(null);
        setMergeRight(null);
    };

    // Recent photos: below the page on desktop, docked under the stage on a tablet.
    const recentPhotos = (imageHistory.length > 0 || bgJobs.length > 0) && (
        <div className={`${styles.historySection} ${isTabletView ? '' : 'mt-8'}`}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12,paddingTop:8}}>
                <span className={styles.sectionTitle}>{t('imageGeneration.recentGenerations')}</span>
                <div style={{display:"flex",alignItems:"center",gap:6}}>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <button style={{display:"inline-flex",alignItems:"center",justifyContent:"center",width:28,height:28,borderRadius:"6px",border:"none",cursor:"pointer",flexShrink:0,background:"#6b7280",color:"white"}} onClick={scrollHistoryLeft} disabled={isGenerating}>
                                ‹
                            </button>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="z-[2000]">{t('imageGeneration.tooltipPrev')}</TooltipContent>
                    </Tooltip>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <button style={{display:"inline-flex",alignItems:"center",justifyContent:"center",width:28,height:28,borderRadius:"6px",border:"none",cursor:"pointer",flexShrink:0,background:"#6b7280",color:"white"}} onClick={scrollHistoryRight} disabled={isGenerating}>
                                ›
                            </button>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="z-[2000]">{t('imageGeneration.tooltipNext')}</TooltipContent>
                    </Tooltip>
                    <Link to="/documents?tab=images" style={{display:"inline-flex",alignItems:"center",justifyContent:"center",gap:6,height:28,padding:"0 12px",borderRadius:"6px",flexShrink:0,background:"var(--brand-orange)",color:"white",fontSize:12,fontWeight:600,whiteSpace:"nowrap",textDecoration:"none"}}>
                        <IconExternalLink size={14} />
                        {t('imageGeneration.viewAllImages')}
                    </Link>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Link to="/settings?tab=photoeditsettings" style={{display:"inline-flex",alignItems:"center",justifyContent:"center",width:28,height:28,borderRadius:"6px",flexShrink:0,background:"#6b7280",color:"white",textDecoration:"none"}}>
                                <IconSettings size={15} />
                            </Link>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="z-[2000]">{t('imageGeneration.photoEditSettings')}</TooltipContent>
                    </Tooltip>
                </div>
            </div>

            <Carousel
                getEmblaApi={setEmblaApi}
                dragFree
                slideGap={12}
                align="start"
                withControls={false}
                loop={false}
                styles={{ viewport: { cursor: 'grab', overflow: 'hidden', paddingTop: 4, marginTop: -4, paddingBottom: 8, marginBottom: -8 } }}
            >
                {/* Background (hidden) generations — processing indicators pinned to the front */}
                {bgJobs.map((job) => (
                    <Carousel.Slide key={`bg-${job.token}`} style={{ flex: `0 0 ${HISTORY_SLIDE_W}px` }}>
                        <div
                            className={styles.bgJobCard}
                            style={{ width: HISTORY_SLIDE_W, cursor: (job.status === 'processing' && !job.restored) ? 'pointer' : 'default' }}
                            onClick={() => bringBgJobToForeground(job)}
                            title={job.status === 'error'
                                ? t('imageGeneration.notifications.backgroundImageFailed')
                                : (job.restored ? t('imageGeneration.processingRestored') : t('imageGeneration.processingInBackground'))}
                        >
                            {job.previewUrl
                                ? <img src={job.previewUrl} alt="" />
                                : <div style={{ width: '100%', height: '100%', background: '#1f2430' }} />}
                            {job.status === 'processing' && (
                                <>
                                    <div className={styles.scannerWrap}>
                                        <div className={styles.scannerTrail}></div>
                                        <div className={styles.scannerLine}></div>
                                    </div>
                                    <div className={styles.bgJobLabel}>
                                        <span className="inline-block w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin flex-shrink-0" />
                                        {t('imageGeneration.processing')}
                                    </div>
                                </>
                            )}
                            {job.status === 'error' && (
                                <div className={styles.bgJobError}>
                                    <button
                                        onClick={(e) => { e.stopPropagation(); setBgJobs(prev => prev.filter(j => j.token !== job.token)); }}
                                        style={{ position:'absolute', top:4, right:4, width:20, height:20, borderRadius:'50%', border:'none', background:'rgba(0,0,0,0.55)', color:'#fff', cursor:'pointer', lineHeight:1, fontSize:13 }}
                                    >×</button>
                                    <span>{t('imageGeneration.failed')}</span>
                                </div>
                            )}
                        </div>
                    </Carousel.Slide>
                ))}
                {imageHistory.map((image) => {
                    const dualAspect = dualHistoryAspects[image.id];
                    // Landscape duals: width = height × aspect so the slot matches the
                    // image shape exactly (no blank bars). Singles keep the default width.
                    const slideW = dualAspect ? Math.round(HISTORY_SLIDE_H * dualAspect) : HISTORY_SLIDE_W;
                    return (
                    <Carousel.Slide
                        key={image.id}
                        style={{ flex: `0 0 ${slideW}px` }}
                    >
                        <div
                            className={styles.historyItem}
                            onClick={() => !isGenerating && handleHistoryImageClick(image)}
                            style={{
                                cursor: isGenerating ? 'not-allowed' : 'pointer',
                                width: slideW,
                                opacity: isGenerating ? 0.5 : 1,
                                ...thumbBgStyle(image),
                            }}
                        >
                            <div className={styles.historySkeletonOverlay} />
                            <img
                                src={image.aiImage}
                                alt="Generated"
                                className={styles.historyThumbnail}
                                onLoad={(e) => {
                                    const { naturalWidth, naturalHeight } = e.target;
                                    const ratio = naturalWidth / naturalHeight;
                                    // Landscape (wider than tall) = dual joint image.
                                    if (ratio > 1.1) {
                                        setDualHistoryAspects(prev => prev[image.id] === ratio ? prev : { ...prev, [image.id]: ratio });
                                    }
                                    e.target.style.opacity = '1';
                                    const skeleton = e.target.previousSibling;
                                    if (skeleton) skeleton.style.opacity = '0';
                                }}
                                style={{
                                    opacity: 0,
                                    transition: 'opacity 0.3s ease',
                                }}
                            />
                            <Menu shadow="md" width={200}>
                                <Menu.Target>
                                    <button
                                        className={styles.historyDownloadBtn}
                                        style={{display:"inline-flex",alignItems:"center",justifyContent:"center",width:28,height:28,borderRadius:"6px",border:"none",cursor:"pointer",flexShrink:0,background:"#16a34a",color:"white"}}
                                        onClick={(e) => e.stopPropagation()}
                                        disabled={isGenerating}
                                    >
                                        <IconDownload size={16} />
                                    </button>
                                </Menu.Target>

                                <Menu.Dropdown>
                                    <Menu.Label>{t('imageGeneration.downloadOptions')}</Menu.Label>
                                    <Menu.Item
                                        leftSection={<IconDownload size={14} />}
                                        onClick={(e) => handleHistoryDownload(e, image)}
                                    >
                                        {t('imageGeneration.downloadOriginal')}
                                    </Menu.Item>
                                    <Menu.Item
                                        leftSection={<IconPlus size={14} />}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setCustomDownloadImageUrl(image.aiImage);
                                            setCustomDownloadImageId(image.id);
                                            setShowCustomDownload(true);
                                        }}
                                    >
                                        {t('imageGeneration.customSizeQuality')}
                                    </Menu.Item>
                                </Menu.Dropdown>
                            </Menu>
                        </div>
                    </Carousel.Slide>
                    );
                })}
                {/* Loading sentinel — visible when more pages exist */}
                {hasMoreHistory && (
                    <Carousel.Slide style={{ flex: '0 0 80px' }}>
                        <div style={{ width: 80, height: HISTORY_SLIDE_H, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 8, background: 'rgba(0,0,0,0.06)' }}>
                            {isLoadingHistory ? <span className="inline-block w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin flex-shrink-0" /> : <span>more</span>}
                        </div>
                    </Carousel.Slide>
                )}
            </Carousel>
        </div>
    );

    const effectiveCreditForBanner = usageStats?.credit ?? user?.credit ?? 0;

    return (
        <>
            {user?.role === 'OWNER' && (
                <LowCreditBanner credit={effectiveCreditForBanner} />
            )}
            <div
                className={`font-hind ${styles.container} ${styles.containerWide} ${isMobile ? styles.containerMobile : ''} ${isTabletView ? styles.containerTablet : ''} ${resultMode && !isMobile ? styles.containerResult : ''}`}
                style={isMobile ? { top: mobileInsets.top, bottom: mobileInsets.bottom } : isTabletView ? { top: mobileInsets.top } : undefined}
            >
                {/* Left Sidebar */}
                <div className={styles.leftSidebar} style={{ position: 'relative' }}>
                    <div
                        className={`flex flex-col ${isMobile ? 'gap-2' : 'gap-6'} ${isTabletView ? styles.tabletPanel : ''}`}
                    >
                        {/* Photo Size */}
                        <div>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                                <div className={styles.sectionTitle}>{t('imageGeneration.photoSize')}</div>
                                {hasSettingsChanged && (
                                    <IBtn title={t('imageGeneration.resetChanges')} color="orange" disabled={isGenerating} onClick={handleReset}>
                                        <IconRepeat size={16} />
                                    </IBtn>
                                )}
                            </div>
                            <div className={styles.sizeGrid}>
                                {photoSizes.map((item) => {
                                    const isVisa = item.value === 'bd_visa';
                                    const isSelected = isVisa
                                        ? (size === 'bd_visa' || size.startsWith('visa_'))
                                        : size === item.value;

                                    const card = (
                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, minWidth: 0, overflow: 'hidden', width: '100%' }}>
                                            <div
                                                className={`${styles.sizeCard} ${isSelected ? styles.selected : ''} ${isGenerating ? styles.disabled : ''}`}
                                                onClick={() => {
                                                    if (isGenerating) return;
                                                    if (isVisa) {
                                                        setVisaPopoverOpen(o => !o);
                                                    } else {
                                                        setSize(item.value);
                                                        setVisaCountryLabel('');
                                                        if (item.value === 'dual_image') {
                                                            setDualUploadMode(true);
                                                            setDualSoloSide(null);
                                                        } else {
                                                            setDualUploadMode(false);
                                                            setMergeLeft(null);
                                                            setMergeRight(null);
                                                            setDualSoloSide(null);
                                                            setIsMergedImage(false);
                                                        }
                                                    }
                                                }}
                                            >
                                                <div style={{ width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                                    <item.icon size={26} strokeWidth={1.5} />
                                                </div>
                                                {isSelected && (
                                                    <div className={styles.dressCheckmark}>
                                                        <IconCheck size={10} color='white' strokeWidth={3} />
                                                    </div>
                                                )}
                                            </div>
                                            <span className={`${styles.sizeCardLabel} ${isSelected ? styles.sizeCardLabelActive : ''}`}>
                                                {isVisa && visaCountryLabel ? visaCountryLabel : item.abbr}
                                            </span>
                                        </div>
                                    );

                                    if (isVisa) {
                                        return (
                                            <Tooltip key={item.value}>
                                                <TooltipTrigger asChild>
                                                <span style={{ display: 'block', width: '100%' }}>
                                            <Popover
                                                opened={visaPopoverOpen}
                                                onChange={setVisaPopoverOpen}
                                                position="right"
                                                withArrow
                                                shadow="xl"
                                                disabled={isMobile || isGenerating}
                                                zIndex={1000}
                                                targetStyle={{ width: '100%' }}
                                            >
                                                <Popover.Target>
                                                    
                                                        {card}
                                                    
                                                </Popover.Target>
                                                <Popover.Dropdown p="sm" style={{ background: 'var(--color-body, #ffffff)', border: '2px solid #fe9a00', borderRadius: '12px', boxShadow: '0 8px 32px rgba(0,0,0,0.28)' }}>
                                                    <span>{isBn ? 'ভিসা ফটো মাপ' : 'Visa Photo Size'}</span>
                                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, auto)', gap: 6 }}>
                                                        {VISA_GROUPS.map((group) => {
                                                            const isGroupSelected = size === group.key;
                                                            return (
                                                                    <div
                                                                        key={group.key}
                                                                        onClick={() => {
                                                                            setSize(group.key);
                                                                            setVisaCountryLabel(isBn ? group.sizeBn : group.size);
                                                                            setDualUploadMode(false);
                                                                            setMergeLeft(null);
                                                                            setMergeRight(null);
                                                                            setIsMergedImage(false);
                                                                            setVisaPopoverOpen(false);
                                                                        }}
                                                                        style={{
                                                                            padding: '4px 9px',
                                                                            borderRadius: 6,
                                                                            cursor: 'pointer',
                                                                            fontSize: 11,
                                                                            fontWeight: isGroupSelected ? 700 : 500,
                                                                            color: isGroupSelected ? 'white' : 'var(--color-text, #18181b)',
                                                                            background: isGroupSelected ? '#fe9a00' : 'var(--color-surface, #f3f4f6)',
                                                                            boxShadow: isGroupSelected ? '0 0 0 2px #fe9a00' : '0 0 0 1px #e5e7eb',
                                                                            transition: 'all 120ms',
                                                                            whiteSpace: 'nowrap',
                                                                        }}
                                                                    >
                                                                        {isBn ? group.sizeBn : group.size}
                                                                    </div>
                                                                
                                                            );
                                                        })}
                                                    </div>
                                                </Popover.Dropdown>
                                            </Popover>
                                                </span>
                                                </TooltipTrigger>
                                                <TooltipContent side="bottom" className="z-[2000]">{item.tooltip}</TooltipContent>
                                            </Tooltip>
                                        );
                                    }

                                    return (
                                        <Tooltip key={item.value}>
                                            <TooltipTrigger asChild>
                                                <div style={{ width: '100%' }}>{card}</div>
                                            </TooltipTrigger>
                                            <TooltipContent side="bottom" className="z-[2000]">{item.tooltip}</TooltipContent>
                                        </Tooltip>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Background */}
                        <div>
                            <div className={styles.sectionTitle} style={{ marginBottom: 8 }}>
                                {t('imageGeneration.background')}
                                {/* Green dot = this account is in transparent-output mode (free recolour).
                                    Inline (not flex): flex would turn the title's ::before orange bar
                                    into a flex item and push a gap between the bar and the text. */}
                                {transparentOutputEnabled && (
                                    <span
                                        title={t('imageGeneration.transparentModeOn')}
                                        style={{ width: 8, height: 8, borderRadius: '50%', background: '#22c55e', display: 'inline-block', marginLeft: 6, verticalAlign: 'middle' }}
                                    />
                                )}
                            </div>
                            <div className={styles.colorGrid}>
                                {backgrounds.map((item) => (
                                    <Tooltip key={item.id}>
                                        <TooltipTrigger asChild>
                                            <div
                                                className={`${styles.bgColorCard} ${bg === item.id ? styles.selected : ''} ${isGenerating ? styles.disabled : ''}`}
                                                onClick={(e) => {
                                                    if (isGenerating) return;
                                                    if (item.id === 'custom') {
                                                        e.stopPropagation();
                                                        setBg(item.id);
                                                        colorPickerRef.current?.click();
                                                    } else {
                                                        handleBgSelect(item.id);
                                                    }
                                                }}
                                                style={{
                                                    ...(item.id === 'transparent'
                                                        ? CHECKERBOARD_STYLE
                                                        : { background: item.id === 'custom'
                                                            ? 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 60%, #2563eb 100%)'
                                                            : item.color }),
                                                    cursor: isGenerating ? 'not-allowed' : 'pointer',
                                                    opacity: isGenerating ? 0.5 : 1,
                                                    position: 'relative',
                                                }}
                                            >
                                                {item.id === 'custom' ? (
                                                    <>
                                                        <IconPalette size={22} color='rgba(255,255,255,0.95)' style={{ filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.4))' }} />
                                                        {/* small swatch dot showing the chosen color */}
                                                        <div style={{ position: 'absolute', bottom: 3, right: 3, width: 9, height: 9, borderRadius: '50%', background: customColor, border: '1.5px solid rgba(255,255,255,0.7)', boxShadow: '0 1px 3px rgba(0,0,0,0.4)' }} />
                                                        <input
                                                            ref={colorPickerRef}
                                                            type="color"
                                                            value={customColor}
                                                            onChange={(e) => handleCustomBgChange(e.target.value)}
                                                            disabled={isGenerating}
                                                            style={{ position: 'absolute', opacity: 0, pointerEvents: 'none', width: 0, height: 0 }}
                                                        />
                                                    </>
                                                ) : (
                                                    bg === item.id && (
                                                        <div className={styles.dressCheckmark}>
                                                            <IconCheck size={12} color='white' strokeWidth={3} />
                                                        </div>
                                                    )
                                                )}
                                            </div>
                                        </TooltipTrigger>
                                        <TooltipContent side="bottom" className="z-[2000]">{item.label}</TooltipContent>
                                    </Tooltip>
                                ))}
                                {/* Selected custom color — same-size swatch so the user sees the picked color */}
                                {bg === 'custom' && (
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <div
                                                className={`${styles.bgColorCard} ${styles.selected} ${isGenerating ? styles.disabled : ''}`}
                                                onClick={() => { if (!isGenerating) colorPickerRef.current?.click(); }}
                                                style={{ background: customColor, cursor: isGenerating ? 'not-allowed' : 'pointer', opacity: isGenerating ? 0.5 : 1 }}
                                            >
                                                <div className={styles.dressCheckmark}>
                                                    <IconCheck size={12} color='white' strokeWidth={3} />
                                                </div>
                                            </div>
                                        </TooltipTrigger>
                                        <TooltipContent side="bottom" className="z-[2000]">{t('imageGeneration.selectedColor')}: {customColor.toUpperCase()}</TooltipContent>
                                    </Tooltip>
                                )}
                            </div>
                        </div>

                        {/* Clothing Style */}
                        <div style={{ position: 'relative' }}>
                            {keepSchoolDressDetails && (
                                <div
                                    style={{
                                        position: 'absolute',
                                        inset: 0,
                                        background: 'rgba(250,250,250,0.5)',
                                        zIndex: 10,
                                        borderRadius: 8,
                                        cursor: 'not-allowed',
                                        backdropFilter: 'grayscale(0.9)',
                                        WebkitBackdropFilter: 'grayscale(0.9)',
                                    }}
                                />
                            )}

                            <div style={{ display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8, opacity: keepSchoolDressDetails ? 0.4 : 1 }}>
                                <span className={styles.sectionTitle} style={{ flex: 1 }}>{t('imageGeneration.clothingStyle')}</span>
                                <span style={{ fontSize: 10, color: '#9ca3af', fontWeight: 400, marginRight: 6 }}>{fmtNum(visibleDressStyles.length)}/{fmtNum(countryDressStyles.length - 1)}</span>
                                <Tooltip>
                                <TooltipTrigger asChild>
                                <span style={{ display: 'inline-flex' }}>
                                <ShadcnPopover open={dressManagePopoverOpen} onOpenChange={setDressManagePopoverOpen}>
                                    <PopoverTrigger asChild>
                                        <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                                        <button
                                            className="bg-orange-50 dark:bg-orange-900/20 text-orange-500 hover:bg-orange-100 dark:hover:bg-orange-900/30 transition-colors disabled:opacity-50"
                                            style={{display:"inline-flex",alignItems:"center",justifyContent:"center",width:28,height:28,borderRadius:"6px",border:"none",cursor:"pointer",flexShrink:0}}
                                            onClick={() => setDressManagePopoverOpen(o => !o)}
                                            disabled={keepSchoolDressDetails || isGenerating}
                                        >
                                            <IconSettings size={14} />
                                        </button>
                                        </span>
                                    </PopoverTrigger>
                                    <PopoverContent side="bottom" className="p-0 w-auto">
                                        <div style={{ padding: 12 }}>
                                        <span>{t('imageGeneration.chooseDressStyles')}</span>
                                        <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:4,marginBottom:0}}>
                                            {countryDressStyles.slice(1).map(item => {
                                                const isEnabled = isDressStyleEnabled(enabledDressStyles, item.value, user?.country);
                                                return (
                                                    <div style={{display:"flex",alignItems:"center",gap:4,justifyContent:"space-between",flexWrap:"nowrap",...{ border: '1px solid #f0f0f0', borderRadius: 6, padding: '4px 6px', cursor: loadingDressStyle !== null ? 'not-allowed' : 'pointer' }}} key={item.value}

                                                        onClick={async () => {
                                                            if (loadingDressStyle !== null) return;
                                                            setLoadingDressStyle(item.value);
                                                            const newEnabled = toggleDressStyle(enabledDressStyles, countryDressStyles.slice(1).map(s => s.value), item.value, user?.country);
                                                            try { await updateSettings({ enabledDressStyles: newEnabled || [] }); } catch {}
                                                            setLoadingDressStyle(null);
                                                        }}
                                                    >
                                                        <div style={{display:"flex",alignItems:"center",gap:4,flexWrap:"nowrap",...{ flex: 1, minWidth: 0 }}}>
                                                            {item.image && <DressPreviewSvg src={item.image} alt="" style={{ width: 16, height: 16, flexShrink: 0 }} />}
                                                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: 1.2 }}>{item.label}</span>
                                                        </div>
                                                        {loadingDressStyle === item.value
                                                            ? <span className="inline-block w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin flex-shrink-0" />
                                                            : <Checkbox checked={isEnabled} onCheckedChange={() => {}} onClick={e => e.stopPropagation()} style={{ flexShrink: 0, pointerEvents: 'none' }} />
                                                        }
                                                    </div>
                                                );
                                            })}
                                        </div>
                                        </div>
                                    </PopoverContent>
                                </ShadcnPopover>
                                </span>
                                </TooltipTrigger>
                                <TooltipContent side="bottom" className="z-[2000]">{t('imageGeneration.manageDressStyles')}</TooltipContent>
                                </Tooltip>
                            </div>

                            {(isMergedImage || dualUploadMode) ? (
                                /* ── Dual image: two independent dress pickers (Left / Right) ── */
                                <div>
                                    {/* Shoulder-overlap selector now lives in the main dual PREVIEW (right
                                        panel) — the two uploaded photos overlap there and the on-top one is
                                        the shoulder in front. No sidebar control. */}
                                    {[
                                        { label: t('imageGeneration.leftPerson'),  currentDress: dress1, setCurrentDress: setDress1, parsedD: parsedDress1, personKey: 'left',  shirtColor: suitShirtColor1, setShirtColor: setSuitShirtColor1, tieColor: suitTieColor1, setTieColor: setSuitTieColor1, blouseColor: shareeBlouseColor1, setBlouseColor: setShareeBlouseColor1, personHijabColor: hijabColor1, setPersonHijabColor: setHijabColor1, personOrnaColor: ornaColor1, setPersonOrnaColor: setOrnaColor1, personGhutraColor: ghutraColor1, setPersonGhutraColor: setGhutraColor1, personFormalColor: formalColor1, setPersonFormalColor: setFormalColor1, personJacketHatColor: jacketHatColor1, setPersonJacketHatColor: setJacketHatColor1, headCoverSharee: shareeHeadCover1, setHeadCoverSharee: setShareeHeadCover1, headCoverKameez: kameezHeadCover1, setHeadCoverKameez: setKameezHeadCover1, shirtCheck: shirtCheck1, setShirtCheck: setShirtCheck1, panjabiStripe: panjabiStripe1, setPanjabiStripe: setPanjabiStripe1, shirtTie: shirtTie1, setShirtTie: setShirtTie1 },
                                        { label: t('imageGeneration.rightPerson'), currentDress: dress2, setCurrentDress: setDress2, parsedD: parsedDress2, personKey: 'right', shirtColor: suitShirtColor2, setShirtColor: setSuitShirtColor2, tieColor: suitTieColor2, setTieColor: setSuitTieColor2, blouseColor: shareeBlouseColor2, setBlouseColor: setShareeBlouseColor2, personHijabColor: hijabColor2, setPersonHijabColor: setHijabColor2, personOrnaColor: ornaColor2, setPersonOrnaColor: setOrnaColor2, personGhutraColor: ghutraColor2, setPersonGhutraColor: setGhutraColor2, personFormalColor: formalColor2, setPersonFormalColor: setFormalColor2, personJacketHatColor: jacketHatColor2, setPersonJacketHatColor: setJacketHatColor2, headCoverSharee: shareeHeadCover2, setHeadCoverSharee: setShareeHeadCover2, headCoverKameez: kameezHeadCover2, setHeadCoverKameez: setKameezHeadCover2, shirtCheck: shirtCheck2, setShirtCheck: setShirtCheck2, panjabiStripe: panjabiStripe2, setPanjabiStripe: setPanjabiStripe2, shirtTie: shirtTie2, setShirtTie: setShirtTie2 },
                                    ].map(({ label, currentDress, setCurrentDress, parsedD, personKey, shirtColor, setShirtColor, tieColor, setTieColor, blouseColor, setBlouseColor, personHijabColor, setPersonHijabColor, personOrnaColor, setPersonOrnaColor, personGhutraColor, setPersonGhutraColor, personFormalColor, setPersonFormalColor, personJacketHatColor, setPersonJacketHatColor, headCoverSharee, setHeadCoverSharee, headCoverKameez, setHeadCoverKameez, shirtCheck, setShirtCheck, panjabiStripe, setPanjabiStripe, shirtTie, setShirtTie }) => (
                                        <div key={personKey} mb={10}>
                                            <span>{label}</span>
                                            <div className={styles.dualPersonDressGrid}>
                                                {/* Original / no-change */}
                                                <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <div
                                                        className={`${styles.dressCard} ${currentDress === 'original' ? styles.selected : ''} ${isGenerating ? styles.disabled : ''}`}
                                                        onClick={() => { if (!isGenerating) { setCurrentDress('original'); setDressColorPopover(null); } }}
                                                    >
                                                        <IconCircleOff size={28} color="gray" strokeWidth={3} />
                                                        {currentDress === 'original' && <div className={styles.dressCheckmark}><IconCheck size={12} color='white' strokeWidth={3} /></div>}
                                                    </div>
                                                </TooltipTrigger>
                                                <TooltipContent side="bottom" className="z-[2000]">{t('imageGeneration.originalClothing')}</TooltipContent>
                                                </Tooltip>


                                                {/* Dress type cards with color picker */}
                                                {visibleDressStyles.map((item) => {
                                                    const isThisType = parsedD.type === item.value;
                                                    const activeColor = isThisType ? dressColors.find(c => c.id === parsedD.color) : null;
                                                    const popoverKey = `${personKey}_${item.value}`;
                                                    return (
                                                        <Tooltip key={item.value} open={dressColorPopover === popoverKey ? false : undefined}>
                                                        <TooltipTrigger asChild>
                                                        <span style={{ display: 'block', width: '100%' }}>
                                                        <Popover
                                                            opened={dressColorPopover === popoverKey}
                                                            onChange={(o) => { setDressColorPopover(o ? popoverKey : null); if (o) setSuitColorTab('suit'); }}
                                                            position="bottom"
                                                            withArrow
                                                            shadow="xl"
                                                            width={(showDressTabs(item.value) || (item.value === 'shirt' && shirtTie)) ? 260 : 224}
                                                            disabled={isMobile || isGenerating}
                                                            zIndex={1000}
                                                        >
                                                            <Popover.Target>

                                                                    <div
                                                                        className={`${styles.dressCard} ${isThisType ? styles.selected : ''} ${isGenerating ? styles.disabled : ''}`}
                                                                        onClick={() => {
                                                                            if (!isGenerating) {
                                                                                if (!isThisType) setCurrentDress(`${item.defaultColor}_${item.value}`);
                                                                                setDressColorPopover(prev => prev === popoverKey ? null : popoverKey);
                                                                                setSuitColorTab('suit');
                                                                            }
                                                                        }}
                                                                    >
                                                                        <DressPreviewSvg src={item.value === 'shirt' && shirtTie ? '/images/clothing-styles/shirt-tie.svg' : item.image} color={isThisType && activeColor ? activeColor.hex : null} shirtColor={isThisType && (item.value === 'suite' || item.value === 'suite-tieless') ? dressColors.find(c => c.id === shirtColor)?.hex : null} tieColor={isThisType && (item.value === 'suite' || (item.value === 'shirt' && shirtTie)) ? dressColors.find(c => c.id === tieColor)?.hex : null} blouseColor={isThisType && (item.value === 'sharee' || item.value === 'sharee-design') ? dressColors.find(c => c.id === blouseColor)?.hex : isThisType && (item.value === 'kameez-hijab' || item.value === 'shari-hijab') ? dressColors.find(c => c.id === personHijabColor)?.hex : isThisType && item.value === 'orna-kameez' ? dressColors.find(c => c.id === personOrnaColor)?.hex : isThisType && item.value === 'hijab-formal' ? dressColors.find(c => c.id === personFormalColor)?.hex : isThisType && item.value === 'thobe-ghutra' ? dressColors.find(c => c.id === personGhutraColor)?.hex : isThisType && item.value === 'jacket' ? dressColors.find(c => c.id === personJacketHatColor)?.hex : null} alt={item.label} className={styles.dressCardImg} onError={(e) => { e.target.style.display = 'none'; }} />
                                                                        {isThisType && <div className={styles.dressCheckmark} style={activeColor ? { background: activeColor.hex, border: '2px solid rgba(255,255,255,0.8)' } : undefined}><IconCheck size={10} color={activeColor && ['white', 'yellow'].includes(parsedD.color) ? '#9ca3af' : 'white'} strokeWidth={3} /></div>}
                                                                    </div>

                                                            </Popover.Target>
                                                            <Popover.Dropdown p="sm" style={{ background: 'var(--color-body, #ffffff)', border: '2px solid #fe9a00', borderRadius: '12px', boxShadow: '0 8px 32px rgba(0,0,0,0.28)' }}>
                                                                {(showDressTabs(item.value) || (item.value === 'shirt' && shirtTie)) ? (
                                                                    <>
                                                                        <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:8}}>
                                                                            {[
                                                                                { value: 'suit', label: item.value === 'jacket' ? 'জ্যাকেট বডি' : item.value === 'shirt' ? 'শার্ট' : (item.value === 'sharee' || item.value === 'sharee-design' || item.value === 'shari-hijab') ? 'শাড়ি' : (item.value === 'kameez-hijab' || item.value === 'orna-kameez') ? 'কামিজ' : item.value === 'hijab-formal' ? t('imageGeneration.dressTabHijab') : item.value === 'thobe-ghutra' ? t('imageGeneration.dressTabThobe') : 'স্যুট', colorId: parsedD.color },
                                                                                ...(item.value === 'shirt' ? [] : [{ value: 'shirt', label: item.value === 'jacket' ? 'হাতা' : (item.value === 'sharee' || item.value === 'sharee-design') ? 'ব্লাউজ' : (item.value === 'kameez-hijab' || item.value === 'shari-hijab') ? 'হিজাব' : item.value === 'orna-kameez' ? 'ওড়না' : item.value === 'hijab-formal' ? t('imageGeneration.dressTabFormal') : item.value === 'thobe-ghutra' ? t('imageGeneration.dressTabGhutra') : 'শার্ট', colorId: (item.value === 'sharee' || item.value === 'sharee-design') ? blouseColor : (item.value === 'kameez-hijab' || item.value === 'shari-hijab') ? personHijabColor : item.value === 'orna-kameez' ? personOrnaColor : item.value === 'hijab-formal' ? personFormalColor : item.value === 'thobe-ghutra' ? personGhutraColor : item.value === 'jacket' ? personJacketHatColor : shirtColor }]),
                                                                                ...((item.value === 'suite' || item.value === 'shirt') ? [{ value: 'tie', label: 'টাই', colorId: tieColor }] : []),
                                                                            ].map(tab => {
                                                                                const hex = dressColors.find(c => c.id === tab.colorId)?.hex || '#aaa';
                                                                                const isActive = suitColorTab === tab.value;
                                                                                return (
                                                                                    <div key={tab.value} onClick={() => setSuitColorTab(tab.value)} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '4px 10px', borderRadius: 20, cursor: 'pointer', border: isActive ? '1.5px solid #fe9a00' : '1.5px solid #e5e7eb', borderBottom: isActive ? '3px solid #fe9a00' : '1.5px solid #e5e7eb', background: isActive ? 'rgba(254,154,0,0.22)' : 'var(--color-surface, #f3f4f6)', userSelect: 'none' }}>
                                                                                        <div style={{ width: 10, height: 10, borderRadius: '50%', background: hex, border: tab.colorId === 'white' ? '1.5px solid #e5e7eb' : '1.5px solid rgba(0,0,0,0.12)', flexShrink: 0 }} />
                                                                                        <span style={{ fontSize: 11, fontWeight: isActive ? 700 : 500, color: isActive ? '#fe9a00' : 'var(--color-text, #18181b)', lineHeight: 1 }}>{tab.label}</span>
                                                                                    </div>
                                                                                );
                                                                            })}
                                                                            {/* Head-cover toggle for sharee / orna-kameez (dual mode — per-person state) */}
                                                                            {(item.value === 'sharee' || item.value === 'sharee-design') && (
                                                                                <HeadCoverToggle
                                                                                    active={headCoverSharee}
                                                                                    onToggle={() => setHeadCoverSharee(v => !v)}
                                                                                    label="ঘোমটা"
                                                                                    tooltip="শাড়ির আঁচল দিয়ে মাথা ঢাকুন (ঘোমটা)"
                                                                                />
                                                                            )}
                                                                            {item.value === 'orna-kameez' && (
                                                                                <HeadCoverToggle
                                                                                    active={headCoverKameez}
                                                                                    onToggle={() => setHeadCoverKameez(v => !v)}
                                                                                    label="মাথা ঢাকুন"
                                                                                    tooltip="ওড়না দিয়ে মাথা ঢাকুন"
                                                                                />
                                                                            )}
                                                                            {item.value === 'suite-tieless' && suitColorTab === 'suit' && (
                                                                                <HeadCoverToggle active={shirtCheck} onToggle={() => setShirtCheck(v => !v)} label={t('imageGeneration.checkSuit')} tooltip={t('imageGeneration.checkSuitTooltip')} />
                                                                            )}
                                                                            {item.value === 'shirt' && (
                                                                                <>
                                                                                    {suitColorTab === 'suit' && <HeadCoverToggle active={shirtCheck} onToggle={() => setShirtCheck(v => !v)} label={t('imageGeneration.checkShirt')} tooltip={t('imageGeneration.checkShirtTooltip')} />}
                                                                                    <HeadCoverToggle active={shirtTie} onToggle={() => setShirtTie(v => !v)} label={t('imageGeneration.tie')} tooltip={t('imageGeneration.tieTooltip')} />
                                                                                </>
                                                                            )}
                                                                        </div>
                                                                        <div className={styles.colorSwatchGrid}>
                                                                            {dressColors.map(color => {
                                                                                const isSelected = suitColorTab === 'suit'
                                                                                    ? (isThisType && parsedD.color === color.id)
                                                                                    : suitColorTab === 'shirt'
                                                                                    ? ((item.value === 'sharee' || item.value === 'sharee-design') ? blouseColor === color.id : (item.value === 'kameez-hijab' || item.value === 'shari-hijab') ? personHijabColor === color.id : item.value === 'orna-kameez' ? personOrnaColor === color.id : item.value === 'hijab-formal' ? personFormalColor === color.id : item.value === 'thobe-ghutra' ? personGhutraColor === color.id : item.value === 'jacket' ? personJacketHatColor === color.id : shirtColor === color.id)
                                                                                    : tieColor === color.id;
                                                                                const shirtPatterned = shirtCheck && (item.value === 'suite-tieless' || item.value === 'shirt') && suitColorTab === 'suit';
                                                                                return (

                                                                                        <div
                                                                                            key={color.id}
                                                                                            className={`${styles.colorSwatch} ${isSelected ? styles.colorSwatchSelected : ''}`}
                                                                                            style={{
                                                                                                ...(shirtPatterned ? checkSwatchStyle(color.hex) : { background: color.hex }),
                                                                                                border: color.id === 'white' ? '2px solid #e5e7eb' : undefined,
                                                                                            }}
                                                                                            onClick={() => {
                                                                                                if (suitColorTab === 'suit') { setCurrentDress(`${color.id}_${item.value}`); setDressColorPopover(null); }
                                                                                                else if (suitColorTab === 'shirt') {
                                                                                                    if (item.value === 'sharee' || item.value === 'sharee-design') setBlouseColor(color.id);
                                                                                                    else if (item.value === 'kameez-hijab' || item.value === 'shari-hijab') setPersonHijabColor(color.id);
                                                                                                    else if (item.value === 'orna-kameez') setPersonOrnaColor(color.id);
                                                                                                    else if (item.value === 'thobe-ghutra') setPersonGhutraColor(color.id);
                                                                                        else if (item.value === 'hijab-formal') setPersonFormalColor(color.id);
                                                                                                    else if (item.value === 'jacket') setPersonJacketHatColor(color.id);
                                                                                                    else setShirtColor(color.id);
                                                                                                }
                                                                                                else setTieColor(color.id);
                                                                                            }}
                                                                                        >
                                                                                            {isSelected && <SwatchCheck patterned={shirtPatterned} colorId={color.id} />}
                                                                                        </div>

                                                                                );
                                                                            })}
                                                                        </div>
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 10 }}>
                                                                            <span>{item.label} — {t('imageGeneration.pickColor')}</span>
                                                                            {item.value === 'shirt' && (
                                                                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                                                    <HeadCoverToggle active={shirtCheck} onToggle={() => setShirtCheck(v => !v)} label={t('imageGeneration.checkShirt')} tooltip={t('imageGeneration.checkShirtTooltip')} />
                                                                                    <HeadCoverToggle active={shirtTie} onToggle={() => setShirtTie(v => !v)} label={t('imageGeneration.tie')} tooltip={t('imageGeneration.tieTooltip')} />
                                                                                </div>
                                                                            )}
                                                                            {item.value === 'panjbi' && (
                                                                                <HeadCoverToggle active={panjabiStripe} onToggle={() => setPanjabiStripe(v => !v)} label={t('imageGeneration.stripePanjabi')} tooltip={t('imageGeneration.stripePanjabiTooltip')} />
                                                                            )}
                                                                        </div>
                                                                        <div className={styles.colorSwatchGrid}>
                                                                            {dressColors.map(color => (

                                                                                    <div
                                                                                        key={color.id}
                                                                                        className={`${styles.colorSwatch} ${isThisType && parsedD.color === color.id ? styles.colorSwatchSelected : ''}`}
                                                                                        style={{
                                                                                            ...(shirtCheck && item.value === 'shirt'
                                                                                                ? checkSwatchStyle(color.hex)
                                                                                                : (panjabiStripe && item.value === 'panjbi')
                                                                                                ? stripeSwatchStyle(color.hex)
                                                                                                : { background: color.hex }),
                                                                                            border: color.id === 'white' ? '2px solid #e5e7eb' : undefined,
                                                                                        }}
                                                                                        onClick={() => { setCurrentDress(`${color.id}_${item.value}`); setDressColorPopover(null); }}
                                                                                    >
                                                                                        {isThisType && parsedD.color === color.id && (
                                                                                            <SwatchCheck patterned={(shirtCheck && item.value === 'shirt') || (panjabiStripe && item.value === 'panjbi')} colorId={color.id} />
                                                                                        )}
                                                                                    </div>

                                                                            ))}
                                                                        </div>
                                                                    </>
                                                                )}
                                                            </Popover.Dropdown>
                                                        </Popover>
                                                        </span>
                                                        </TooltipTrigger>
                                                        <TooltipContent side="bottom" className="z-[2000]">{item.label}</TooltipContent>
                                                        </Tooltip>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                /* ── Single image: 5 dress types + color picker popover ── */
                                <div className={styles.dressGrid}>
                                    {/* Original / no-change card */}
                                    <Tooltip>
                                    <TooltipTrigger asChild>
                                        <div
                                            className={`${styles.dressCard} ${dress === 'original' ? styles.selected : ''} ${isGenerating ? styles.disabled : ''}`}
                                            onClick={() => { if (!isGenerating) { setDress('original'); setDressColorPopover(null); } }}
                                        >
                                            <IconCircleOff size={32} color="gray" strokeWidth={3} />
                                            {dress === 'original' && (
                                                <div className={styles.dressCheckmark}>
                                                    <IconCheck size={12} color='white' strokeWidth={3} />
                                                </div>
                                            )}
                                        </div>
                                    </TooltipTrigger>
                                    <TooltipContent side="bottom" className="z-[2000]">{t('imageGeneration.originalClothing')}</TooltipContent>
                                    </Tooltip>


                                    {/* Dress type cards — each opens a color picker popover */}
                                    {visibleDressStyles.map((item) => {
                                        const isThisType = parsedDress.type === item.value;
                                        const activeColor = isThisType ? dressColors.find(c => c.id === parsedDress.color) : null;
                                        return (
                                            <Tooltip key={item.value} open={dressColorPopover === item.value ? false : undefined}>
                                            <TooltipTrigger asChild>
                                            <span style={{ display: 'block', width: '100%' }}>
                                            <Popover
                                                opened={dressColorPopover === item.value}
                                                onChange={(o) => { setDressColorPopover(o ? item.value : null); if (o) setSuitColorTab('suit'); }}
                                                position="bottom"
                                                withArrow
                                                shadow="xl"
                                                width={(showDressTabs(item.value) || (item.value === 'shirt' && shirtTie)) ? 260 : 224}
                                                disabled={isMobile || isGenerating}
                                                zIndex={1000}
                                            >
                                                <Popover.Target>

                                                        <div
                                                            className={`${styles.dressCard} ${isThisType ? styles.selected : ''} ${isGenerating ? styles.disabled : ''}`}
                                                            onClick={() => {
                                                                if (!isGenerating) {
                                                                    if (!isThisType) setDress(`${item.defaultColor}_${item.value}`);
                                                                    setDressColorPopover(prev => prev === item.value ? null : item.value);
                                                                    setSuitColorTab('suit');
                                                                }
                                                            }}
                                                        >
                                                            <DressPreviewSvg src={item.value === 'shirt' && shirtTie ? '/images/clothing-styles/shirt-tie.svg' : item.image} color={isThisType && activeColor ? activeColor.hex : null} shirtColor={isThisType && (item.value === 'suite' || item.value === 'suite-tieless') ? dressColors.find(c => c.id === suitShirtColor)?.hex : null} tieColor={isThisType && (item.value === 'suite' || (item.value === 'shirt' && shirtTie)) ? dressColors.find(c => c.id === suitTieColor)?.hex : null} blouseColor={isThisType && (item.value === 'sharee' || item.value === 'sharee-design') ? dressColors.find(c => c.id === shareeBlouseColor)?.hex : isThisType && (item.value === 'kameez-hijab' || item.value === 'shari-hijab') ? dressColors.find(c => c.id === hijabColor)?.hex : isThisType && item.value === 'orna-kameez' ? dressColors.find(c => c.id === ornaColor)?.hex : isThisType && item.value === 'hijab-formal' ? dressColors.find(c => c.id === formalColor)?.hex : isThisType && item.value === 'thobe-ghutra' ? dressColors.find(c => c.id === ghutraColor)?.hex : isThisType && item.value === 'jacket' ? dressColors.find(c => c.id === jacketHatColor)?.hex : null} alt={item.label} className={styles.dressCardImg} onError={(e) => { e.target.style.display = 'none'; }} />
                                                            {isThisType && (
                                                                <div className={styles.dressCheckmark} style={activeColor ? { background: activeColor.hex, border: '2px solid rgba(255,255,255,0.8)' } : undefined}>
                                                                    <IconCheck size={10} color={activeColor && ['white', 'yellow'].includes(parsedDress.color) ? '#9ca3af' : 'white'} strokeWidth={3} />
                                                                </div>
                                                            )}
                                                        </div>

                                                </Popover.Target>
                                                <Popover.Dropdown p="sm" style={{ background: 'var(--color-body, #ffffff)', border: '2px solid #fe9a00', borderRadius: '12px', boxShadow: '0 8px 32px rgba(0,0,0,0.28)' }}>
                                                    {(showDressTabs(item.value) || (item.value === 'shirt' && shirtTie)) ? (
                                                        <>
                                                            <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:8}}>
                                                                {[
                                                                    { value: 'suit', label: item.value === 'jacket' ? 'জ্যাকেট বডি' : item.value === 'shirt' ? 'শার্ট' : (item.value === 'sharee' || item.value === 'sharee-design' || item.value === 'shari-hijab') ? 'শাড়ি' : (item.value === 'kameez-hijab' || item.value === 'orna-kameez') ? 'কামিজ' : item.value === 'hijab-formal' ? t('imageGeneration.dressTabHijab') : item.value === 'thobe-ghutra' ? t('imageGeneration.dressTabThobe') : 'স্যুট', colorId: parsedDress.color },
                                                                    ...(item.value === 'shirt' ? [] : [{ value: 'shirt', label: item.value === 'jacket' ? 'হাতা' : (item.value === 'sharee' || item.value === 'sharee-design') ? 'ব্লাউজ' : (item.value === 'kameez-hijab' || item.value === 'shari-hijab') ? 'হিজাব' : item.value === 'orna-kameez' ? 'ওড়না' : item.value === 'hijab-formal' ? t('imageGeneration.dressTabFormal') : item.value === 'thobe-ghutra' ? t('imageGeneration.dressTabGhutra') : 'শার্ট', colorId: (item.value === 'sharee' || item.value === 'sharee-design') ? shareeBlouseColor : (item.value === 'kameez-hijab' || item.value === 'shari-hijab') ? hijabColor : item.value === 'orna-kameez' ? ornaColor : item.value === 'hijab-formal' ? formalColor : item.value === 'thobe-ghutra' ? ghutraColor : item.value === 'jacket' ? jacketHatColor : suitShirtColor }]),
                                                                    ...((item.value === 'suite' || item.value === 'shirt') ? [{ value: 'tie', label: 'টাই', colorId: suitTieColor }] : []),
                                                                ].map(tab => {
                                                                    const hex = dressColors.find(c => c.id === tab.colorId)?.hex || '#aaa';
                                                                    const isActive = suitColorTab === tab.value;
                                                                    return (
                                                                        <div key={tab.value} onClick={() => setSuitColorTab(tab.value)} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '4px 10px', borderRadius: 20, cursor: 'pointer', border: isActive ? '1.5px solid #fe9a00' : '1.5px solid #e5e7eb', borderBottom: isActive ? '3px solid #fe9a00' : '1.5px solid #e5e7eb', background: isActive ? 'rgba(254,154,0,0.22)' : 'var(--color-surface, #f3f4f6)', userSelect: 'none' }}>
                                                                            <div style={{ width: 10, height: 10, borderRadius: '50%', background: hex, border: tab.colorId === 'white' ? '1.5px solid #e5e7eb' : '1.5px solid rgba(0,0,0,0.12)', flexShrink: 0 }} />
                                                                            <span style={{ fontSize: 11, fontWeight: isActive ? 700 : 500, color: isActive ? '#fe9a00' : 'var(--color-text, #18181b)', lineHeight: 1 }}>{tab.label}</span>
                                                                        </div>
                                                                    );
                                                                })}
                                                                {/* Head-cover toggle for sharee / orna-kameez */}
                                                                {(item.value === 'sharee' || item.value === 'sharee-design') && (
                                                                    <HeadCoverToggle
                                                                        active={shareeHeadCover}
                                                                        onToggle={() => setShareeHeadCover(v => !v)}
                                                                        label="ঘোমটা"
                                                                        tooltip="শাড়ির আঁচল দিয়ে মাথা ঢাকুন (ঘোমটা)"
                                                                    />
                                                                )}
                                                                {item.value === 'orna-kameez' && (
                                                                    <HeadCoverToggle
                                                                        active={kameezHeadCover}
                                                                        onToggle={() => setKameezHeadCover(v => !v)}
                                                                        label="মাথা ঢাকুন"
                                                                        tooltip="ওড়না দিয়ে মাথা ঢাকুন"
                                                                    />
                                                                )}
                                                                {item.value === 'suite-tieless' && suitColorTab === 'suit' && (
                                                                    <HeadCoverToggle active={shirtCheck} onToggle={() => setShirtCheck(v => !v)} label={t('imageGeneration.checkSuit')} tooltip={t('imageGeneration.checkSuitTooltip')} />
                                                                )}
                                                                {item.value === 'shirt' && (
                                                                    <>
                                                                        {suitColorTab === 'suit' && <HeadCoverToggle active={shirtCheck} onToggle={() => setShirtCheck(v => !v)} label={t('imageGeneration.checkShirt')} tooltip={t('imageGeneration.checkShirtTooltip')} />}
                                                                        <HeadCoverToggle active={shirtTie} onToggle={() => setShirtTie(v => !v)} label={t('imageGeneration.tie')} tooltip={t('imageGeneration.tieTooltip')} />
                                                                    </>
                                                                )}
                                                            </div>
                                                            <div className={styles.colorSwatchGrid}>
                                                                {dressColors.map(color => {
                                                                    const isSelected = suitColorTab === 'suit'
                                                                        ? (isThisType && parsedDress.color === color.id)
                                                                        : suitColorTab === 'shirt'
                                                                        ? ((item.value === 'sharee' || item.value === 'sharee-design') ? shareeBlouseColor === color.id : (item.value === 'kameez-hijab' || item.value === 'shari-hijab') ? hijabColor === color.id : item.value === 'orna-kameez' ? ornaColor === color.id : item.value === 'hijab-formal' ? formalColor === color.id : item.value === 'thobe-ghutra' ? ghutraColor === color.id : item.value === 'jacket' ? jacketHatColor === color.id : suitShirtColor === color.id)
                                                                        : suitTieColor === color.id;
                                                                    const shirtPatterned = shirtCheck && (item.value === 'suite-tieless' || item.value === 'shirt') && suitColorTab === 'suit';
                                                                    return (

                                                                            <div
                                                                                key={color.id}
                                                                                className={`${styles.colorSwatch} ${isSelected ? styles.colorSwatchSelected : ''}`}
                                                                                style={{
                                                                                    ...(shirtPatterned ? checkSwatchStyle(color.hex) : { background: color.hex }),
                                                                                    border: color.id === 'white' ? '2px solid #e5e7eb' : undefined,
                                                                                }}
                                                                                onClick={() => {
                                                                                    if (suitColorTab === 'suit') { setDress(`${color.id}_${item.value}`); setDressColorPopover(null); }
                                                                                    else if (suitColorTab === 'shirt') {
                                                                                        if (item.value === 'sharee' || item.value === 'sharee-design') setShareeBlouseColor(color.id);
                                                                                        else if (item.value === 'kameez-hijab' || item.value === 'shari-hijab') setHijabColor(color.id);
                                                                                        else if (item.value === 'orna-kameez') setOrnaColor(color.id);
                                                                                        else if (item.value === 'thobe-ghutra') setGhutraColor(color.id);
                                                                                        else if (item.value === 'hijab-formal') setFormalColor(color.id);
                                                                                        else if (item.value === 'jacket') setJacketHatColor(color.id);
                                                                                        else setSuitShirtColor(color.id);
                                                                                    }
                                                                                    else setSuitTieColor(color.id);
                                                                                }}
                                                                            >
                                                                                {isSelected && <SwatchCheck patterned={shirtPatterned} colorId={color.id} />}
                                                                            </div>

                                                                    );
                                                                })}
                                                            </div>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 10 }}>
                                                                <span>{item.label} — {t('imageGeneration.pickColor')}</span>
                                                                {item.value === 'shirt' && (
                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                                        <HeadCoverToggle active={shirtCheck} onToggle={() => setShirtCheck(v => !v)} label={t('imageGeneration.checkShirt')} tooltip={t('imageGeneration.checkShirtTooltip')} />
                                                                        <HeadCoverToggle active={shirtTie} onToggle={() => setShirtTie(v => !v)} label={t('imageGeneration.tie')} tooltip={t('imageGeneration.tieTooltip')} />
                                                                    </div>
                                                                )}
                                                                {item.value === 'panjbi' && (
                                                                    <HeadCoverToggle active={panjabiStripe} onToggle={() => setPanjabiStripe(v => !v)} label={t('imageGeneration.stripePanjabi')} tooltip={t('imageGeneration.stripePanjabiTooltip')} />
                                                                )}
                                                            </div>
                                                            <div className={styles.colorSwatchGrid}>
                                                                {dressColors.map(color => (

                                                                        <div
                                                                            key={color.id}
                                                                            className={`${styles.colorSwatch} ${isThisType && parsedDress.color === color.id ? styles.colorSwatchSelected : ''}`}
                                                                            style={{
                                                                                ...(shirtCheck && item.value === 'shirt'
                                                                                    ? checkSwatchStyle(color.hex)
                                                                                    : (panjabiStripe && item.value === 'panjbi')
                                                                                    ? stripeSwatchStyle(color.hex)
                                                                                    : { background: color.hex }),
                                                                                border: color.id === 'white' ? '2px solid #e5e7eb' : undefined,
                                                                            }}
                                                                            onClick={() => { setDress(`${color.id}_${item.value}`); setDressColorPopover(null); }}
                                                                        >
                                                                            {isThisType && parsedDress.color === color.id && (
                                                                                <SwatchCheck patterned={(shirtCheck && item.value === 'shirt') || (panjabiStripe && item.value === 'panjbi')} colorId={color.id} />
                                                                            )}
                                                                        </div>

                                                                ))}
                                                            </div>
                                                        </>
                                                    )}
                                                </Popover.Dropdown>
                                            </Popover>
                                            </span>
                                            </TooltipTrigger>
                                            <TooltipContent side="bottom" className="z-[2000]">{item.label}</TooltipContent>
                                            </Tooltip>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {/* Extra Editing Guide */}
                        <div>
                            {(() => {
                                const allToolDefs = [
                                    { key: 'glowMakeup',            icon: faceIcon,           label: t('imageGeneration.glowMakeup'),             iconComponent: null },
                                    { key: 'smoothSkin',            icon: smoothSkinIcon,     label: t('imageGeneration.smoothSkin'),             iconComponent: null },
                                    { key: 'fixOilySkin',           icon: fixOilySkinIcon,    label: t('imageGeneration.fixOilySkin'),            iconComponent: null },
                                    { key: 'brightenImage',         icon: brightenIcon,       label: t('imageGeneration.brightenImage'),          iconComponent: null },
                                    { key: 'studioLighting',        icon: studioLightIcon,    label: t('imageGeneration.studioLighting'),         iconComponent: null },
                                    { key: 'straightenHead',        icon: headIcon,           label: t('imageGeneration.straightenHead'),         iconComponent: null },
                                    { key: 'removeStrayHair',       icon: hairCutIcon,        label: t('imageGeneration.removeStrayHair'),        iconComponent: null },
                                    { key: 'halfBody', icon: halfBodyIcon, label: t('imageGeneration.halfBody'), iconComponent: null },
                                    { key: 'showEar',               icon: showEarIcon,        label: t('imageGeneration.showEar'),                iconComponent: null },
                                    { key: 'openEyes',              icon: eyeIcon,            label: t('imageGeneration.openEyes'),               iconComponent: null },
                                    { key: 'closeMouth',            icon: closeMouthIcon,     label: t('imageGeneration.closeMouth'),             iconComponent: null },
                                    { key: 'keepSchoolDressDetails',icon: schoolDressIcon,    label: t('imageGeneration.keepSchoolDressDetails'), iconComponent: null },
                                    { key: 'keepNamePlate',         icon: keepNamePlateIcon,  label: t('imageGeneration.keepNamePlate'),          iconComponent: null },
                                    { key: 'removeGlasses',         icon: sunglassIcon,       label: t('imageGeneration.removeGlass'),            iconComponent: null },
                                    { key: 'keepSunGlasses',        icon: keepSunGlassesIcon, label: t('imageGeneration.keepSunGlasses'),          iconComponent: null },
                                    { key: 'removeBeard',           icon: beardCutIcon,       label: t('imageGeneration.removeBeard'),            iconComponent: null },
                                    { key: 'addBeard',              icon: addBeardIcon,       label: t('imageGeneration.addBeard'),               iconComponent: null },
                                    { key: 'keepBeard',             icon: keepBeardIcon,      label: t('imageGeneration.keepBeard'),              iconComponent: null },
                                    { key: 'keepMustache',          icon: keepMustacheIcon,   label: t('imageGeneration.keepMustache'),           iconComponent: null },
                                    { key: 'keepFacialMarks',       icon: keepFacialMarksIcon, label: t('imageGeneration.keepFacialMarks'),       iconComponent: null },
                                    { key: 'keepNosePin',          icon: keepNosePinIcon,    label: t('imageGeneration.keepNosePin'),           iconComponent: null },
                                    { key: 'applyHair',             icon: applyHairIcon,      label: t('imageGeneration.applyHair'),              iconComponent: null },
                                    { key: 'removeTupi',            icon: capIcon,            label: t('imageGeneration.removeTupi'),             iconComponent: null },
                                    { key: 'addTupi',               icon: addTupiIcon,        label: t('imageGeneration.addTupi'),                iconComponent: null },
                                    { key: 'lookAtCamera',          icon: lookAtCameraIcon,   label: t('imageGeneration.lookAtCamera'),           iconComponent: null },
                                    { key: 'lipstick',              icon: lipstickIcon,       label: t('imageGeneration.lipstick'),               iconComponent: null },
                                    { key: 'showCustomInstruction', icon: customWriteIcon,    label: t('imageGeneration.enableCustomInstruction'),iconComponent: null },
                                ];
                                const extraToolKeys = allToolDefs.map(t => t.key).filter(k => k !== 'showCustomInstruction');
                                const totalTools = extraToolKeys.length;
                                const visibleTools = extraToolKeys.filter(k => dp[k]).length;
                                // Cap how many grid tools can be enabled at once. Once the cap is
                                // hit, still-unchecked tools grey out (already-on ones stay
                                // toggleable so the user can swap). Custom-instruction is exempt.
                                const MAX_ENABLED_TOOLS = 20;
                                const toolCapReached = visibleTools >= MAX_ENABLED_TOOLS;
                                return (
                                    <>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8, width: '100%' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1 }}>
                                            <span className={styles.sectionTitle} style={{ flex: 1 }}>{t('imageGeneration.extraEditingGuide')}</span>
                                            <span style={{ fontSize: 10, color: toolCapReached ? '#fe9a00' : '#9ca3af', fontWeight: toolCapReached ? 600 : 400 }} title={toolCapReached ? t('imageGeneration.toolLimitReached', { max: fmtNum(MAX_ENABLED_TOOLS) }) : undefined}>{fmtNum(visibleTools)}/{fmtNum(totalTools)}</span>
                                        </div>
                                        <Tooltip>
                                        <TooltipTrigger asChild>
                                        <span style={{ display: 'inline-flex' }}>
                                        <ShadcnPopover open={dpPopoverOpen} onOpenChange={setDpPopoverOpen}>
                                            <PopoverTrigger asChild>
                                                <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                                                <button
                                                    onClick={() => setDpPopoverOpen(o => !o)}
                                                    aria-label={t('imageGeneration.addMoreTools')}
                                                    className="bg-orange-50 dark:bg-orange-900/20 text-orange-500 hover:bg-orange-100 dark:hover:bg-orange-900/30 transition-colors"
                                                    style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: 6, border: 'none', cursor: 'pointer', flexShrink: 0 }}
                                                >
                                                    <IconSettings size={14} />
                                                </button>
                                                </span>
                                            </PopoverTrigger>
                                            <PopoverContent side="bottom" className="p-0 w-auto">
                                                <div style={{ padding: 12 }}>
                                                <span>{t('imageGeneration.manageTools')}</span>
                                                <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:4,marginBottom:0}}>
                                                    {allToolDefs.map(tool => {
                                                        // Block turning ON a new tool once the cap is hit; OFF (unselecting) always allowed.
                                                        const capBlocked = toolCapReached && !dp[tool.key] && tool.key !== 'showCustomInstruction';
                                                        return (
                                                        <div style={{display:"flex",alignItems:"center",gap:4,justifyContent:"space-between",flexWrap:"nowrap",...{ border: '1px solid #f0f0f0', borderRadius: 6, padding: '4px 6px', cursor: (loadingTool !== null || capBlocked) ? 'not-allowed' : 'pointer', opacity: capBlocked ? 0.4 : 1 }}} key={tool.key}
                                                            title={capBlocked ? t('imageGeneration.toolLimitReached', { max: fmtNum(MAX_ENABLED_TOOLS) }) : undefined}
                                                            onClick={async () => {
                                                                if (loadingTool !== null || capBlocked) return;
                                                                setLoadingTool(tool.key);
                                                                const updated = { ...(user?.defaultPrompts || {}), [tool.key]: !dp[tool.key] };
                                                                try { await updateProfile({ defaultPrompts: updated }); } catch {}
                                                                setLoadingTool(null);
                                                            }}
                                                        >
                                                            <div style={{display:"flex",alignItems:"center",gap:4,flexWrap:"nowrap",...{ flex: 1, minWidth: 0 }}}>
                                                                <div className="bg-zinc-100 dark:bg-zinc-700" style={{ width: 24, height: 24, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 6 }}>
                                                                    {tool.iconComponent
                                                                        ? tool.iconComponent
                                                                        : <img src={tool.icon} alt="" width={16} className={styles.toolIcon} />
                                                                    }
                                                                </div>
                                                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: 1.2 }}>{tool.label}</span>
                                                            </div>
                                                            {loadingTool === tool.key
                                                                ? <span className="inline-block w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin flex-shrink-0" />
                                                                : <Checkbox checked={!!dp[tool.key]} disabled={capBlocked} onCheckedChange={() => {}} onClick={e => e.stopPropagation()} style={{ flexShrink: 0, pointerEvents: 'none' }} />
                                                            }
                                                        </div>
                                                        );
                                                    })}
                                                </div>
                                                </div>
                                            </PopoverContent>
                                        </ShadcnPopover>
                                        </span>
                                        </TooltipTrigger>
                                        <TooltipContent side="bottom" className="z-[2000]">{t('imageGeneration.manageTools')}</TooltipContent>
                                        </Tooltip>
                                    </div>
                                    </>
                                );
                            })()}
                            <div className={styles.extraToolGrid}>
                                {[
                                    { show: dp.glowMakeup, active: glowMakeup, toggle: () => { const on = !glowMakeup; setGlowMakeup(on); if (on) setSmoothSkin(false); setLevelPopoverFor(on ? 'glowMakeup' : null); }, icon: faceIcon, label: t('imageGeneration.glowMakeup'), levelPicker: makeLevelPicker('glowMakeup') },
                                    { show: dp.smoothSkin, active: smoothSkin, toggle: () => { const on = !smoothSkin; setSmoothSkin(on); if (on) setGlowMakeup(false); setLevelPopoverFor(on ? 'smoothSkin' : null); }, icon: smoothSkinIcon, label: t('imageGeneration.smoothSkin'), levelPicker: makeLevelPicker('smoothSkin') },
                                    { show: dp.fixOilySkin, active: fixOilySkin, toggle: () => { const on = !fixOilySkin; setFixOilySkin(on); setLevelPopoverFor(on ? 'fixOilySkin' : null); }, icon: fixOilySkinIcon, label: t('imageGeneration.fixOilySkin'), levelPicker: makeLevelPicker('fixOilySkin') },
                                    { show: dp.brightenImage, active: brightenImage, toggle: () => { const on = !brightenImage; setBrightenImage(on); if (on) setStudioLighting(false); setLevelPopoverFor(on ? 'brightenImage' : null); }, icon: brightenIcon, label: t('imageGeneration.brightenImage'), levelPicker: makeLevelPicker('brightenImage') },
                                    { show: dp.studioLighting, active: studioLighting, toggle: () => { const on = !studioLighting; setStudioLighting(on); if (on) setBrightenImage(false); setLevelPopoverFor(on ? 'studioLighting' : null); }, icon: studioLightIcon, label: t('imageGeneration.studioLighting'), levelPicker: makeLevelPicker('studioLighting') },
                                    { show: dp.straightenHead,        active: straightenHead,        toggle: () => { if (!straightenHead) { setStraightenHead(true); setLookAtCamera(false); } else setStraightenHead(false); },                                                                                                            icon: headIcon,           label: t('imageGeneration.straightenHead') },
                                    { show: dp.removeStrayHair, disabled: disabledTools.has('removeStrayHair'),       active: removeStrayHair,       toggle: () => { if (!removeStrayHair) { setRemoveStrayHair(true); setApplyHair(false); } else setRemoveStrayHair(false); },                                 icon: hairCutIcon,        label: t('imageGeneration.removeStrayHair') },
                                    { show: dp.halfBody, disabled: disabledTools.has('halfBody'), active: halfBody, toggle: () => { const on = !halfBody; setHalfBody(on); setLevelPopoverFor(on ? 'halfBody' : null); }, icon: halfBodyIcon, label: t('imageGeneration.halfBody'), levelPicker: makeLevelPicker('halfBody', HALF_BODY_LEVEL_OPTIONS, 'strict') },
                                    { show: dp.showEar, disabled: disabledTools.has('showEar'),               active: showEar,               toggle: () => setShowEar(!showEar),                                                                                                                         icon: showEarIcon,        label: t('imageGeneration.showEar') },
                                    { show: dp.openEyes,              active: openEyes,              toggle: () => setOpenEyes(!openEyes),                                                                                                                        icon: eyeIcon,            label: t('imageGeneration.openEyes') },
                                    { show: dp.closeMouth,            active: closeMouth,            toggle: () => setCloseMouth(!closeMouth),                                                                                                                    icon: closeMouthIcon,     label: t('imageGeneration.closeMouth') },
                                    { show: dp.keepSchoolDressDetails, disabled: disabledTools.has('keepSchoolDressDetails'),active: keepSchoolDressDetails,toggle: () => setKeepSchoolDressDetails(!keepSchoolDressDetails),                                                                                           icon: schoolDressIcon,    label: t('imageGeneration.keepSchoolDressDetails') },
                                    { show: dp.keepNamePlate,         active: keepNamePlate,         toggle: () => { if (keepNamePlate) setNamePlateText(''); setKeepNamePlate(!keepNamePlate); },                                                                                                             icon: keepNamePlateIcon,  label: t('imageGeneration.keepNamePlate') },
                                    { show: dp.removeGlasses,         active: removeGlasses,         toggle: () => { if (!removeGlasses) { setRemoveGlasses(true); setKeepSunGlasses(false); } else setRemoveGlasses(false); },                                     icon: sunglassIcon,       label: t('imageGeneration.removeGlass') },
                                    { show: dp.keepSunGlasses,        active: keepSunGlasses,        toggle: () => { if (!keepSunGlasses) { setKeepSunGlasses(true); setRemoveGlasses(false); } else setKeepSunGlasses(false); },                                   icon: keepSunGlassesIcon, label: t('imageGeneration.keepSunGlasses') },
                                    { show: dp.removeBeard, disabled: disabledTools.has('removeBeard'),           active: removeBeard,           toggle: () => { if (!removeBeard) { setRemoveBeard(true); setAddBeard(false); setKeepBeard(false); } else setRemoveBeard(false); },                           icon: beardCutIcon,       label: t('imageGeneration.removeBeard') },
                                    { show: dp.addBeard, disabled: disabledTools.has('addBeard'),              active: addBeard,              toggle: () => { if (!addBeard) { setAddBeard(true); setRemoveBeard(false); setKeepBeard(false); } else setAddBeard(false); },                               icon: addBeardIcon,       label: t('imageGeneration.addBeard') },
                                    { show: dp.keepBeard, disabled: disabledTools.has('keepBeard'),             active: keepBeard,             toggle: () => { if (!keepBeard) { setKeepBeard(true); setRemoveBeard(false); setAddBeard(false); } else setKeepBeard(false); },                             icon: keepBeardIcon,      label: t('imageGeneration.keepBeard') },
                                    { show: dp.keepMustache, disabled: disabledTools.has('keepMustache'),          active: keepMustache,          toggle: () => setKeepMustache(!keepMustache),                                                                                                                icon: keepMustacheIcon,   label: t('imageGeneration.keepMustache') },
                                    { show: dp.keepFacialMarks,       active: keepFacialMarks,       toggle: () => setKeepFacialMarks(!keepFacialMarks),                                                                                                          icon: keepFacialMarksIcon, label: t('imageGeneration.keepFacialMarks') },
                                    { show: dp.keepNosePin,           active: keepNosePin,           toggle: () => setKeepNosePin(!keepNosePin),                                                                                                                  icon: keepNosePinIcon,    label: t('imageGeneration.keepNosePin') },
                                    { show: dp.applyHair, disabled: disabledTools.has('applyHair'),             active: applyHair,             toggle: () => { if (!applyHair) { setApplyHair(true); setRemoveStrayHair(false); } else setApplyHair(false); },                                             icon: applyHairIcon,      label: t('imageGeneration.applyHair') },
                                    { show: dp.removeTupi, disabled: disabledTools.has('removeTupi'),            active: removeTupi,            toggle: () => { if (!removeTupi) { setRemoveTupi(true); setAddTupi(false); } else setRemoveTupi(false); },                                                 icon: capIcon,            label: t('imageGeneration.removeTupi') },
                                    { show: dp.addTupi, disabled: disabledTools.has('addTupi'),               active: addTupi,               toggle: () => { const on = !addTupi; setAddTupi(on); if (on) setRemoveTupi(false); setTupiColorPopover(on); },                                          icon: addTupiIcon,        label: t('imageGeneration.addTupi'),
                                        colorPicker: { open: tupiColorPopover, setOpen: setTupiColorPopover, title: t('imageGeneration.tupiColorHint'), swatches: dressColors, selected: tupiColor, selectedHex: dressColors.find(c => c.id === tupiColor)?.hex, onSelect: setTupiColor } },
                                    { show: dp.lookAtCamera,          active: lookAtCamera,          toggle: () => { if (!lookAtCamera) { setLookAtCamera(true); setStraightenHead(false); } else setLookAtCamera(false); },                                                                                                               icon: lookAtCameraIcon,   label: t('imageGeneration.lookAtCamera') },
                                    { show: dp.lipstick, disabled: disabledTools.has('lipstick'),        active: lipstick,              toggle: () => { const on = !lipstick; setLipstick(on); setLipstickColorPopover(on); },                                                                        icon: lipstickIcon,       label: t('imageGeneration.lipstick'),
                                        colorPicker: { open: lipstickColorPopover, setOpen: setLipstickColorPopover, title: t('imageGeneration.lipstickHint'), swatches: LIPSTICK_SHADES.map(s => ({ id: s.id, hex: s.hex, label: t(s.labelKey) })), selected: lipstickShade, selectedHex: lipstickShade === 'custom' ? lipstickCustomColor : LIPSTICK_SHADES.find(s => s.id === lipstickShade)?.hex, onSelect: setLipstickShade, custom: { color: lipstickCustomColor, active: lipstickShade === 'custom', label: t('imageGeneration.lipstickCustom'), onChange: (hex) => { setLipstickCustomColor(hex); setLipstickShade('custom'); } }, checkbox: { checked: lipGloss, onChange: setLipGloss, label: t('imageGeneration.lipGloss') } } },
                                    { show: dp.showCustomInstruction, active: showExtraGuide,        toggle: () => setShowExtraGuide(!showExtraGuide),                                                                                                            icon: customWriteIcon,    label: t('imageGeneration.enableCustomInstruction') },
                                ].filter(item => item.show).map((item, i) => {
                                    const cp = item.colorPicker;
                                    const lp = item.levelPicker;
                                    const pk = cp || lp;
                                    // Leveled tools show their 1-based intensity number (হালকা 1 / মাঝারি 2 /
                                    // বেশি 3) as a floating badge on the active tile's top-right corner.
                                    const lpNum = lp ? (lp.options.findIndex((o) => o.id === lp.selected) + 1 || 1) : 0;
                                    const tile = (
                                            <div style={{display:"flex",flexDirection:"column",gap:3,alignItems:"center",...{ cursor: (isGenerating || (item.disabled || isToolCapBlocked(item))) ? 'not-allowed' : 'pointer', opacity: (item.disabled || isToolCapBlocked(item)) ? 0.35 : (isGenerating ? 0.6 : 1) }}}
                                                onClick={() => !isGenerating && !(item.disabled || isToolCapBlocked(item)) && item.toggle()}
                                            >
                                                <div
                                                    style={{
                                                        width: '100%',
                                                        aspectRatio: '1',
                                                        borderRadius: 12,
                                                        border: `2px solid ${item.active ? '#fe9a00' : 'var(--color-border, #e5e7eb)'}`,
                                                        boxShadow: item.active ? '0 0 0 2px #ffe4b0' : 'none',
                                                        backgroundColor: 'var(--color-surface, #f3f4f6)',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        position: 'relative',
                                                        transition: 'all 0.15s',
                                                        padding: 3,
                                                    }}
                                                >
                                                    {item.iconComponent
                                                        ? item.iconComponent
                                                        : <img src={item.icon} alt="Icon" width={40} className={styles.toolIcon} />
                                                    }
                                                    {item.active && !lp && (
                                                        <div style={{
                                                            position: 'absolute', bottom: 2, right: 2,
                                                            width: cp ? 15 : 13, height: cp ? 15 : 13, borderRadius: '50%',
                                                            backgroundColor: cp?.selectedHex || '#fe9a00',
                                                            // The chosen colour: a white ring lifts it off the icon, and a darker hairline outside the ring keeps a light
                                                            // colour (white, beige, yellow…) readable on the light tile.
                                                            border: cp ? '1.5px solid #fff' : undefined,
                                                            boxShadow: cp ? '0 0 0 1px rgba(0,0,0,0.45)' : undefined,
                                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                        }}>
                                                            {(!cp || !['white','yellow','beige','gold'].includes(cp.selected)) && <IconCheck size={8} color='white' strokeWidth={3.5} />}
                                                        </div>
                                                    )}
                                                    {item.active && lp && (
                                                        <div style={{
                                                            // Level number badge — bottom-right corner (same spot as the
                                                            // active-check circle), a TRUE circle (equal w/h, no padding).
                                                            position: 'absolute', bottom: 2, right: 2,
                                                            width: 15, height: 15, borderRadius: '50%',
                                                            backgroundColor: '#fe9a00', color: '#fff',
                                                            fontSize: 9.5, fontWeight: 800, lineHeight: 1,
                                                            border: '1.5px solid #fff', boxShadow: '0 0 0 1px rgba(0,0,0,0.15)',
                                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                        }}>
                                                            {fmtNum(lpNum)}
                                                        </div>
                                                    )}
                                                </div>
                                                <span style={{ color: item.active ? '#fe9a00' : 'var(--mantine-color-text, #000)', lineHeight: 1.15, fontSize: 10, width: '100%', textAlign: 'center' }}>{item.label}</span>
                                            </div>
                                    );
                                    return (
                                    <Tooltip key={i} open={pk?.open ? false : undefined}>
                                        <TooltipTrigger asChild>
                                            {pk ? (
                                                <span style={{ display: 'block', width: '100%' }}>
                                                    <Popover opened={pk.open} onChange={(o) => { if (!o) pk.setOpen(false); }} position="bottom" sideOffset={lp ? -2 : 4} disabled={isGenerating || item.disabled || isToolCapBlocked(item)} targetStyle={{ width: '100%' }}>
                                                        <Popover.Target>{tile}</Popover.Target>
                                                        <Popover.Dropdown p="sm" style={{ background: 'var(--color-body, #ffffff)', border: '2px solid #fe9a00', borderRadius: '12px', boxShadow: '0 8px 32px rgba(0,0,0,0.28)' }}>
                                                            <div style={{ minWidth: lp ? 150 : 196 }}>
                                                                {/* Title row — hidden for the intensity (level) picker so it stays compact
                                                                    and close to the tool; kept for colour pickers (title + optional checkbox). */}
                                                                {!lp && (
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '0 0 8px' }}>
                                                                    <p style={{ fontSize: 12, fontWeight: 700, color: '#fe9a00', margin: 0, lineHeight: 1 }}>{pk.title}</p>
                                                                    {cp?.checkbox && (
                                                                        <CheckChip active={cp.checkbox.checked} disabled={isGenerating}
                                                                            onToggle={() => !isGenerating && cp.checkbox.onChange(!cp.checkbox.checked)}
                                                                            label={cp.checkbox.label} style={{ marginLeft: 'auto' }} />
                                                                    )}
                                                                </div>
                                                                )}
                                                                {lp ? (
                                                                <div style={{ display: 'flex', gap: 5 }}>
                                                                    {lp.options.map((opt, oi) => {
                                                                        const sel = lp.selected === opt.id;
                                                                        return (
                                                                            <button key={opt.id} type="button" onClick={() => { if (!isGenerating) lp.onSelect(opt.id); }}
                                                                                style={{ flex: 1, display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 4, padding: '5px 4px', borderRadius: 7, fontSize: 11, fontWeight: 600, cursor: isGenerating ? 'not-allowed' : 'pointer', border: sel ? '1.5px solid #fe9a00' : '1.5px solid #e5e7eb', background: sel ? '#fff7ec' : 'var(--color-surface, #f3f4f6)', color: sel ? '#fe9a00' : 'var(--mantine-color-text, #374151)' }}>
                                                                                <span style={{ width: 14, height: 14, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9.5, fontWeight: 800, lineHeight: 1, background: sel ? '#fe9a00' : '#e5e7eb', color: sel ? '#fff' : '#6b7280', flexShrink: 0 }}>{fmtNum(oi + 1)}</span>
                                                                                {opt.label}
                                                                            </button>
                                                                        );
                                                                    })}
                                                                </div>
                                                                ) : (
                                                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6,1fr)', gap: 8 }}>
                                                                    {cp.swatches.map(sw => {
                                                                        const sel = cp.selected === sw.id;
                                                                        return (
                                                                            <div key={sw.id} title={sw.label} onClick={() => { if (!isGenerating) cp.onSelect(sw.id); }}
                                                                                style={{ width: '100%', aspectRatio: '1', borderRadius: '50%', background: sw.hex, cursor: isGenerating ? 'not-allowed' : 'pointer', border: sw.id === 'white' ? '1.5px solid #e5e7eb' : '1.5px solid rgba(0,0,0,0.12)', boxShadow: sel ? '0 0 0 2.5px #fff, 0 0 0 4px #fe9a00' : 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                                {sel && <IconCheck size={13} color={['white','yellow','beige','gold'].includes(sw.id) ? '#9ca3af' : '#fff'} strokeWidth={3} />}
                                                                            </div>
                                                                        );
                                                                    })}
                                                                    {cp.custom && (
                                                                        <label title={cp.custom.label} style={{ position: 'relative', width: '100%', aspectRatio: '1', borderRadius: '50%', background: cp.custom.color, cursor: isGenerating ? 'not-allowed' : 'pointer', border: '1.5px solid rgba(0,0,0,0.12)', boxShadow: cp.custom.active ? '0 0 0 2.5px #fff, 0 0 0 4px #fe9a00' : 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                                                                            <IconPalette size={13} color="#fff" style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.5))' }} />
                                                                            <input type="color" value={cp.custom.color} disabled={isGenerating}
                                                                                onChange={(e) => cp.custom.onChange(e.target.value)}
                                                                                style={{ position: 'absolute', inset: 0, opacity: 0, width: '100%', height: '100%', padding: 0, border: 'none', cursor: isGenerating ? 'not-allowed' : 'pointer' }} />
                                                                        </label>
                                                                    )}
                                                                </div>
                                                                )}
                                                            </div>
                                                        </Popover.Dropdown>
                                                    </Popover>
                                                </span>
                                            ) : tile}
                                        </TooltipTrigger>
                                        <TooltipContent side="top" className="z-[2000]">{toolFullLabel[item.label] || item.label}</TooltipContent>
                                    </Tooltip>
                                    );
                                })}
                            </div>

                            {keepNamePlate && (
                                <div style={{marginTop:8}}>
                                    <input
                                        type="text"
                                        value={namePlateText}
                                        onChange={(e) => setNamePlateText(e.currentTarget.value.slice(0, 20))}
                                        placeholder={t('imageGeneration.namePlatePlaceholder')}
                                        disabled={isGenerating}
                                        style={{width:"100%",padding:"8px 12px",borderRadius:6,border:"1px solid #d1d5db",fontSize:13,background:"inherit",color:"inherit"}}
                                    />
                                    <span style={{fontSize:11,color:"#9ca3af"}}>{t('imageGeneration.namePlateHint')}</span>
                                </div>
                            )}

                            {showExtraGuide && (
                                <div style={{marginTop:8}}>
                                    <textarea
                                        value={extraEditingGuide}
                                        onChange={(e) => {
                                            const raw = e.currentTarget.value;
                                            const clean = sanitizeExtraGuide(raw);
                                            if (clean !== raw) {
                                                clearTimeout(guideFilterTimerRef.current);
                                                setGuideFiltered(true);
                                                guideFilterTimerRef.current = setTimeout(() => setGuideFiltered(false), 2500);
                                            }
                                            setExtraEditingGuide(clean);
                                        }}
                                        placeholder={t('imageGeneration.extraEditingGuidePlaceholder')} maxLength={GUIDE_MAX_CHARS}
                                        rows={3}
                                        disabled={isGenerating}
                                        className={(extraEditingGuide.length > GUIDE_MAX_CHARS || extraEditingGuide.trim().split(/\s+/).filter(Boolean).length > GUIDE_MAX_WORDS) ? `${styles.textareaInput} ${styles.textareaInputError}` : styles.textareaInput}
                                        style={{width:"100%",padding:"8px 12px",borderRadius:6,border:"1px solid #d1d5db",fontSize:13,resize:"none",background:"inherit",color:"inherit"}}
                                    />
                                    {(() => {
                                        const charOver = extraEditingGuide.length > GUIDE_MAX_CHARS;
                                        const wordCount = extraEditingGuide.trim().split(/\s+/).filter(Boolean).length;
                                        const wordOver = wordCount > GUIDE_MAX_WORDS;
                                        if (charOver || wordOver) {
                                            return (
                                                <span>
                                                    {charOver
                                                        ? t('imageGeneration.guideMaxChars', { max: fmtNum(GUIDE_MAX_CHARS), count: fmtNum(extraEditingGuide.length) })
                                                        : t('imageGeneration.guideMaxWords', { max: fmtNum(GUIDE_MAX_WORDS), count: fmtNum(wordCount) })}
                                                </span>
                                            );
                                        }
                                        if (guideFiltered) {
                                            return (
                                                <span>
                                                    {t('imageGeneration.guideLimits', { chars: fmtNum(GUIDE_MAX_CHARS), words: fmtNum(GUIDE_MAX_WORDS) })}
                                                </span>
                                            );
                                        }
                                        return (
                                            <span>
                                                {t('imageGeneration.extraEditingGuideHint')}
                                            </span>
                                        );
                                    })()}
                                </div>
                            )}
                        </div>

                    </div>

                        {/* Generate / Regenerate — on a tablet floating at the bottom of the sidebar column, so the photo gets the height */}
                        <div className={isTabletView ? styles.tabletGenerate : 'mt-6'}>
                            <div style={{display:"flex",flexDirection:"column",gap:8}}>
                                {/* Cloth / extra-tool / size changed → regenerate (AI, costs a credit).
                                    Also shown DISABLED when nothing changed (so the button stays
                                    visible, greyed out, instead of disappearing). Hidden only when a
                                    bg-only change is pending — the Apply-background button below covers that. */}
                                {generatedImage && (settingsChanged || !bgChangedVsSaved) && (
                                    <>
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            {renderQualityButtons({
                                                innerRef: setGenBtnEl,
                                                disabled: !settingsChanged || isGenerating || (showExtraGuide && (extraEditingGuide.length > 150 || extraEditingGuide.trim().split(/\s+/).filter(Boolean).length > GUIDE_MAX_WORDS)),
                                                label: t('imageGeneration.regenerate'),
                                                icon: <IconRefresh size={18} />,
                                                regen: true,
                                            })}
                                        </TooltipTrigger>
                                        <TooltipContent side="top" className="z-[2000]">{`${t('imageGeneration.regenerate')} (Ctrl+G)`}</TooltipContent>
                                    </Tooltip>
                                    {/* Fixed clone while the real Regenerate button is scrolled out of view —
                                        same sticky behaviour as the Generate button. */}
                                    {genFloatX && !isTabletView && (
                                        <div style={{
                                            position: 'fixed', bottom: 0, left: genFloatX.left, width: genFloatX.width, zIndex: 70,
                                            padding: '64px 0 8px',
                                            background: 'linear-gradient(to top, var(--color-body, #ffffff) 52%, transparent 100%)',
                                            opacity: genBtnInView ? 0 : 1,
                                            transform: genBtnInView ? 'translateY(12px)' : 'translateY(0)',
                                            transition: 'opacity 300ms cubic-bezier(0.22,1,0.36,1), transform 300ms cubic-bezier(0.22,1,0.36,1)',
                                            pointerEvents: genBtnInView ? 'none' : 'auto',
                                        }}>
                                            {renderQualityButtons({
                                                disabled: !settingsChanged || isGenerating || (showExtraGuide && (extraEditingGuide.length > 150 || extraEditingGuide.trim().split(/\s+/).filter(Boolean).length > GUIDE_MAX_WORDS)),
                                                label: t('imageGeneration.regenerate'),
                                                icon: <IconRefresh size={18} />,
                                                regen: true,
                                                floating: true,
                                            })}
                                        </div>
                                    )}
                                    </>
                                )}

                                {/* ONLY the background changed → apply it (composite + save, no AI, no
                                    credit). Not shown when a cloth/tool change requires a full regen.
                                    Styled + sticky exactly like the regenerate button (the two are
                                    mutually exclusive, so they never mount together and can share the
                                    setGenBtnEl ref / float state). Logic unchanged: persistBg, no AI. */}
                                {generatedImage && !settingsChanged && bgChangedVsSaved && (
                                    <>
                                    <button
                                        ref={setGenBtnEl}
                                        className="inline-flex items-center gap-2 font-bold px-5 py-3 text-base rounded-lg transition-colors disabled:opacity-50 w-full justify-center"
                                        style={{background:"#e68a00",color:"white"}}
                                        onClick={() => persistBg(currentBgColor)}
                                        disabled={isGenerating}
                                    >
                                        <IconCheck size={18} strokeWidth={2.5} />
                                        {t('imageGeneration.applyBackground')}
                                    </button>
                                    {/* Fixed clone while the real button is scrolled out of view — same sticky behaviour as regenerate. */}
                                    {genFloatX && !isTabletView && (
                                        <div style={{
                                            position: 'fixed', bottom: 0, left: genFloatX.left, width: genFloatX.width, zIndex: 70,
                                            padding: '64px 0 8px',
                                            background: 'linear-gradient(to top, var(--color-body, #ffffff) 52%, transparent 100%)',
                                            opacity: genBtnInView ? 0 : 1,
                                            transform: genBtnInView ? 'translateY(12px)' : 'translateY(0)',
                                            transition: 'opacity 300ms cubic-bezier(0.22,1,0.36,1), transform 300ms cubic-bezier(0.22,1,0.36,1)',
                                            pointerEvents: genBtnInView ? 'none' : 'auto',
                                        }}>
                                            <button
                                                className="inline-flex items-center gap-2 font-bold px-5 py-3 text-base rounded-lg transition-colors disabled:opacity-50 w-full justify-center shadow-lg"
                                                style={{background:"#e68a00",color:"white"}}
                                                onClick={() => persistBg(currentBgColor)}
                                                tabIndex={genBtnInView ? -1 : 0}
                                                disabled={isGenerating}
                                            >
                                                <IconCheck size={18} strokeWidth={2.5} />
                                                {t('imageGeneration.applyBackground')}
                                            </button>
                                        </div>
                                    )}
                                    </>
                                )}

                                {!generatedImage && (
                                    <>
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            {renderQualityButtons({
                                                innerRef: setGenBtnEl,
                                                disabled: !hasUploadedImage || !size || !dress || !bg || isGenerating || (showExtraGuide && (extraEditingGuide.length > 150 || extraEditingGuide.trim().split(/\s+/).filter(Boolean).length > GUIDE_MAX_WORDS)),
                                                label: t('imageGeneration.generatePhoto'),
                                            })}
                                        </TooltipTrigger>
                                        <TooltipContent side="top" className="z-[2000]">{`${t('imageGeneration.generatePhoto')} (Ctrl+G)`}</TooltipContent>
                                    </Tooltip>
                                    {/* Fixed clone while the real button is scrolled out of view.
                                        Kept mounted so it fades/slides in and out smoothly; sits flush
                                        with the viewport bottom on a gradient that is solid at the
                                        bottom edge and dissolves upward. */}
                                    {genFloatX && !isTabletView && (
                                        <div style={{
                                            position: 'fixed', bottom: 0, left: genFloatX.left, width: genFloatX.width, zIndex: 70,
                                            padding: '64px 0 8px',
                                            background: 'linear-gradient(to top, var(--color-body, #ffffff) 52%, transparent 100%)',
                                            opacity: genBtnInView ? 0 : 1,
                                            transform: genBtnInView ? 'translateY(12px)' : 'translateY(0)',
                                            transition: 'opacity 300ms cubic-bezier(0.22,1,0.36,1), transform 300ms cubic-bezier(0.22,1,0.36,1)',
                                            pointerEvents: genBtnInView ? 'none' : 'auto',
                                        }}>
                                            {renderQualityButtons({
                                                disabled: !hasUploadedImage || !size || !dress || !bg || isGenerating || (showExtraGuide && (extraEditingGuide.length > 150 || extraEditingGuide.trim().split(/\s+/).filter(Boolean).length > GUIDE_MAX_WORDS)),
                                                label: t('imageGeneration.generatePhoto'),
                                                floating: true,
                                            })}
                                        </div>
                                    )}
                                    </>
                                )}

                            </div>
                        </div>
                </div>

                {/* Phone: the Photo Booth, top right — opens the customer inbox as a Sheet. Shown only when the booth is ON and no result is open
                    (mirrors the desktop right-sidebar gating). The live N/M count is fed by the always-mounted desktop panel via handlePcCount. */}
                {isMobile && !generatedImage && !isGenerating && selectedOfficeSpace?.id && pcEnabled && (
                    <div className="order-1 flex shrink-0 justify-end px-3 pt-3">
                        <button
                            type="button"
                            onClick={() => setActiveDrawer('booth')}
                            className="inline-flex items-center gap-2 rounded-full border border-zinc-200 bg-white/95 py-2 pl-2.5 pr-3 shadow-[0_6px_20px_-4px_rgba(0,0,0,0.25)] backdrop-blur-xl dark:border-zinc-700 dark:bg-zinc-800/95"
                        >
                            <span className="relative flex h-5 w-5 flex-none items-center justify-center rounded-full bg-primary/10 text-primary">
                                <IconQrcode size={13} />
                                <span className="absolute -top-0.5 -right-0.5 flex h-1.5 w-1.5">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                                    <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500" />
                                </span>
                            </span>
                            <span className="text-[12px] font-bold text-zinc-800 dark:text-zinc-100 leading-none">{t('photoCollectorPanel.title')}</span>
                            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-primary/10 text-primary tabular-nums leading-none flex-none">{fmtNum(pcCount.count)}/{fmtNum(pcCount.max)}</span>
                        </button>
                    </div>
                )}

                {/* Center Preview */}
                <div
                    className={styles.centerPreview}
                    onClick={() => {
                        if (!previewUrl && !isGenerating && !dualUploadMode) {
                            fileInputRef.current?.click();
                        }
                    }}
                    onDragOver={!previewUrl && !isGenerating && !dualUploadMode ? handleDragOver : undefined}
                    onDragLeave={!previewUrl && !isGenerating && !dualUploadMode ? handleDragLeave : undefined}
                    onDrop={!previewUrl && !isGenerating && !dualUploadMode ? handleDrop : undefined}
                    style={{ cursor: !previewUrl && !isGenerating && !dualUploadMode ? 'pointer' : 'default' }}
                >
                    {/* Skeleton while loading image from editId URL */}
                    {isLoadingFromUrl && !previewUrl && (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%', flex: 1, height: '100%', padding: '24px 16px', gap: 16 }}>
                            {/* Image placeholder skeleton */}
                            <div style={{ position: 'relative', width: 327, maxWidth: '100%', aspectRatio: '327/400', borderRadius: 10, overflow: 'hidden', background: 'rgba(255,255,255,0.07)' }}>
                                <div style={{
                                    position: 'absolute', inset: 0,
                                    background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.10) 50%, transparent 100%)',
                                    backgroundSize: '200% 100%',
                                    animation: 'skeletonShimmer 1.4s ease-in-out infinite',
                                }} />
                                {/* Face circle hint */}
                                <div style={{ position: 'absolute', top: '18%', left: '50%', transform: 'translateX(-50%)', width: 90, height: 90, borderRadius: '50%', background: 'rgba(255,255,255,0.07)' }} />
                                {/* Body block hint */}
                                <div style={{ position: 'absolute', bottom: '10%', left: '50%', transform: 'translateX(-50%)', width: 160, height: 120, borderRadius: 8, background: 'rgba(255,255,255,0.05)' }} />
                            </div>
                            {/* Text skeleton */}
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                                <div style={{ width: 160, height: 12, borderRadius: 6, background: 'rgba(255,255,255,0.10)', animation: 'skeletonShimmer 1.4s ease-in-out infinite' }} />
                                <div style={{ width: 100, height: 10, borderRadius: 6, background: 'rgba(255,255,255,0.07)', animation: 'skeletonShimmer 1.4s ease-in-out 0.2s infinite' }} />
                            </div>
                            <style>{`
                                @keyframes skeletonShimmer {
                                    0% { background-position: -200% 0; }
                                    100% { background-position: 200% 0; }
                                }
                            `}</style>
                        </div>
                    )}

                    {!previewUrl && !isGenerating && !dualUploadMode && !isLoadingFromUrl && (
                        <>
                            {isMobile ? (
                                /* ── Mobile: new full-screen upload design ── */
                                <div className="flex h-full min-h-full w-full flex-col items-center justify-center px-5 pb-14 pt-4">
                                    {/* Hidden file inputs */}
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept="image/*,.heic,.heif"
                                        onChange={handleFileSelect}
                                        style={{ display:'none' }}
                                    />
                                    <input
                                        ref={cameraInputRef}
                                        type="file"
                                        accept="image/*"
                                        capture="user"
                                        onChange={handleFileSelect}
                                        style={{ display:'none' }}
                                    />

                                    {/* Upload box — a photo-shaped card; the bottom padding keeps it clear of the সাম্প্রতিক ছবি button */}
                                    <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); setShowUploadSheet(true); }}
                                        className="flex aspect-[4/5] w-[min(420px,86vw)] max-h-full flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed border-white/15 bg-white/5 p-5 text-center"
                                    >
                                        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-white/10 text-white/60"><IconPhotoUp size={26} /></span>
                                        <span className="!text-[15px] font-bold leading-snug !text-white">{t('imageGeneration.mobileUploadBoxTitle')}</span>
                                        <span className="!text-[11.5px] leading-normal !text-white/50">{t('imageGeneration.mobileUploadBoxHint')}</span>
                                        <span className="mt-1 inline-flex items-center justify-center gap-1.5 rounded-xl bg-primary px-6 py-2.5 !text-sm font-bold !text-white"><IconUpload size={16} />{t('imageGeneration.uploadButton')}</span>
                                    </button>
                                </div>
                            ) : (
                                /* ── Desktop / Tablet layout ── */
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start', gap: 16, width: '100%', flex: 1, height: '100%', padding: '24px 16px' }}>
                                    {/* Tablet: the right sidebar (and its photo booth inbox) is hidden before a result, so the booth comes here —
                                        a button with its live count that opens the inbox; picking a photo loads it like an upload */}
                                    {isTabletView && selectedOfficeSpace?.id && pcEnabled && (
                                        <ShadcnPopover open={tabletBoothOpen} onOpenChange={setTabletBoothOpen}>
                                            <PopoverTrigger asChild>
                                                <button
                                                    type="button"
                                                    onClick={(e) => e.stopPropagation()}
                                                    className="absolute right-3.5 top-3.5 z-[3] inline-flex items-center gap-2 rounded-xl border border-white/25 bg-white/10 py-2 pl-2.5 pr-3 text-[13px] font-bold text-white shadow-[0_4px_14px_rgba(0,0,0,0.25)] backdrop-blur transition-colors hover:border-white/45 hover:bg-white/20 data-[state=open]:border-primary data-[state=open]:bg-primary/30"
                                                >
                                                    <span className="relative flex h-5 w-5 flex-none items-center justify-center rounded-full bg-white/15">
                                                        <IconQrcode size={13} />
                                                        <span className="absolute -right-0.5 -top-0.5 flex h-1.5 w-1.5">
                                                            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                                                            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
                                                        </span>
                                                    </span>
                                                    {t('photoCollectorPanel.title')}
                                                    <span className="rounded-full bg-white/15 px-1.5 py-0.5 text-[11px] font-bold tabular-nums leading-none">{fmtNum(pcCount.count)}/{fmtNum(pcCount.max)}</span>
                                                </button>
                                            </PopoverTrigger>
                                            <PopoverContent align="end" sideOffset={8} className="z-[1000] max-h-[70vh] w-[360px] overflow-y-auto p-3" onClick={(e) => e.stopPropagation()}>
                                                <PhotoCollectorPanel
                                                    officeSpaceId={selectedOfficeSpace.id}
                                                    shopName={selectedOfficeSpace?.businessProfile?.shopName || selectedOfficeSpace?.name}
                                                    shopLogo={selectedOfficeSpace?.businessProfile?.shopLogo}
                                                    shopPhone={selectedOfficeSpace?.businessProfile?.shopPhone}
                                                    shopAddress={selectedOfficeSpace?.businessProfile?.businessAddress}
                                                    onPick={async (id, item) => { await loadQueuePhoto(id, item); setTabletBoothOpen(false); }}
                                                    refreshKey={pcRefreshKey}
                                                />
                                            </PopoverContent>
                                        </ShadcnPopover>
                                    )}
                                    {/* QR scan — tablet only (when the booth is off), compact button pinned to the top-right of the preview area */}
                                    {isTabletView && !(selectedOfficeSpace?.id && pcEnabled) && (
                                        <button
                                            onClick={(e) => { e.stopPropagation(); openQrModal(); }}
                                            title={t('imageGeneration.qrUploadButton')}
                                            style={{ position: 'absolute', top: 14, right: 14, zIndex: 3, display: 'inline-flex', alignItems: 'center', gap: 7, padding: '8px 14px', borderRadius: 12, border: '1px solid rgba(255,255,255,0.22)', background: 'rgba(255,255,255,0.10)', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)', boxShadow: '0 4px 14px rgba(0,0,0,0.25)', transition: 'background 0.2s ease, border-color 0.2s ease' }}
                                            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.18)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.45)'; }}
                                            onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.10)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.22)'; }}
                                        >
                                            <IconQrcode size={18} />
                                            {t('imageGeneration.qrScanButton')}
                                        </button>
                                    )}
                                    <div className={`${styles.uploadArea} ${isDragging ? styles.dragging : ''}`} style={sizeRatioCss ? { aspectRatio: sizeRatioCss } : undefined} onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}>
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            accept="image/*,.heic,.heif"
                                            onChange={handleFileSelect}
                                            style={{ display: 'none' }}
                                        />
                                        <div className={styles.uploadIconWrap}><IconPhoto size={32} className={styles.uploadIcon} /></div>
                                        <p style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#fff', textAlign: 'center', lineHeight: 1.4 }}>{t('imageGeneration.uploadInstruction')}</p>
                                        <p style={{ margin: 0, fontSize: 13, color: 'rgba(255,255,255,0.5)', textAlign: 'center', lineHeight: 1.5 }}>{t('imageGeneration.uploadHint')}{sizeDimLabel ? ` (${sizeDimLabel})` : ''}</p>
                                        <div className={styles.uploadBtn}>
                                            <IconUpload size={15} />
                                            {t('imageGeneration.uploadButton')}
                                        </div>
                                        <p style={{ margin: '4px 0 0', display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'rgba(255,255,255,0.45)' }}>
                                            <kbd style={{ padding: '2px 6px', borderRadius: 5, border: '1px solid rgba(255,255,255,0.25)', background: 'rgba(255,255,255,0.08)', fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,0.75)' }}>Ctrl+U</kbd>
                                        </p>
                                    </div>
                                </div>
                            )}
                            {/* Desktop dual upload button — abs bottom */}
                            {!isMobile && (
                                <button
                                    className="inline-flex items-center gap-1.5 font-semibold px-2 py-1 text-xs rounded-md transition-colors disabled:opacity-50 lg:px-3 lg:py-2.5"
                                    style={{background:"#e68a0022",color:"#e68a00", position: 'absolute', bottom: 20, left: '50%', transform: 'translateX(-50%)', zIndex: 1, whiteSpace: 'nowrap'}}
                                    onClick={(e) => { e.stopPropagation(); setDualUploadMode(true); setSize('dual_image'); }}
                                >
                                    <IconUsers size={14} />
                                    {t('imageGeneration.dualUploadButton')}
                                </button>
                            )}
                        </>
                    )}

                    {!previewUrl && !isGenerating && dualUploadMode && (
                        <div className={styles.dualUploadInline} onClick={(e) => e.stopPropagation()}>
                            {/* Both photos uploaded → overlapping shoulder selector: the two portraits
                                overlap like a couple's shoulders and the ON-TOP photo is the shoulder in
                                front. Flip it with the seam switch OR by clicking the back photo (just
                                toggles `shoulderTop` — backend unchanged). Otherwise → upload zones. */}
                            {(!dualSoloSide && mergeLeft && mergeRight) ? (() => {
                                const frontSide = shoulderTop === 'right' ? 'right' : 'left';
                                const backSide  = frontSide === 'left' ? 'right' : 'left';
                                const dataOf  = (side) => (side === 'left' ? mergeLeft : mergeRight);
                                const clearOf = (side) => (side === 'left' ? setMergeLeft(null) : setMergeRight(null));
                                const shortOf = (side) => (side === 'left' ? t('imageGeneration.personLeftShort') : t('imageGeneration.personRightShort'));
                                const personCard = (side) => {
                                    const d = dataOf(side); const isFront = side === frontSide; const isLeft = side === 'left';
                                    return (
                                        <div key={side}
                                            onClick={isFront ? undefined : () => { if (!isGenerating) setShoulderTop(side); }}
                                            title={isFront ? undefined : t('imageGeneration.shoulderBringFront')}
                                            style={{
                                                // SAME level + SAME size for both — only ~5% of the image width
                                                // overlaps; the front photo simply sits ON TOP (z-index).
                                                position: 'relative', width: '51.5%', aspectRatio: '209/256', borderRadius: 16, overflow: 'hidden', background: '#000', flexShrink: 0,
                                                marginLeft: isLeft ? 0 : '-3%',
                                                zIndex: isFront ? 2 : 1,
                                                border: isFront ? '3px solid #fe9a00' : '2px solid rgba(255,255,255,0.20)',
                                                boxShadow: isFront ? '0 14px 34px rgba(0,0,0,0.55)' : '0 4px 12px rgba(0,0,0,0.35)',
                                                filter: isFront ? 'none' : 'brightness(0.8)',
                                                cursor: isFront ? 'default' : (isGenerating ? 'not-allowed' : 'pointer'),
                                                transition: 'transform .26s ease, box-shadow .26s ease, border-color .26s ease, filter .26s ease',
                                            }}>
                                            <img src={d.url} alt={side} draggable={false}
                                                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center', transform: `translate(${d.offsetX ?? 0}px, ${d.offsetY ?? 0}px) scale(${d.zoom ?? 1})`, transformOrigin: 'center center', pointerEvents: 'none', userSelect: 'none', display: 'block' }} />
                                            <span style={{ position: 'absolute', bottom: 8, [isLeft ? 'left' : 'right']: 8, padding: '2px 10px', borderRadius: 999, fontSize: 11, fontWeight: 800, color: '#fff', background: isFront ? '#fe9a00' : 'rgba(0,0,0,0.6)', zIndex: 2 }}>
                                                {shortOf(side)}{isFront ? ` · ${t('imageGeneration.shoulderFrontBadge')}` : ''}
                                            </span>
                                            <button
                                                style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: '50%', border: 'none', cursor: 'pointer', background: '#dc2626', color: '#fff', position: 'absolute', top: 6, [isLeft ? 'left' : 'right']: 6, zIndex: 3 }}
                                                onPointerDown={(e) => e.stopPropagation()}
                                                onClick={(e) => { e.stopPropagation(); clearOf(side); }}>
                                                <IconX size={13} />
                                            </button>
                                        </div>
                                    );
                                };
                                return (
                                    <div style={{ position: 'relative', width: '100%', margin: '0 auto' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', paddingTop: 8 }}>
                                            {personCard('left')}
                                            {personCard('right')}
                                        </div>
                                    </div>
                                );
                            })() : (
                            <div style={{ width: '100%', display: 'flex', gap: 14, justifyContent: dualSoloSide ? 'center' : 'stretch' }}>
                                {/* Left zone */}
                                {dualSoloSide !== 'right' && (
                                <div style={{ position: 'relative', flex: dualSoloSide ? '1 1 100%' : 1, maxWidth: 'none', minWidth: 0 }}>
                                    {/* Close-box button — collapses to the right side (only when both boxes are empty/shown) */}
                                    {!dualSoloSide && !mergeLeft && (
                                        <button
                                            title={t('imageGeneration.removeBox')}
                                            style={{display:"inline-flex",alignItems:"center",justifyContent:"center",width:24,height:24,borderRadius:"50%",border:"none",cursor:"pointer",background:"#dc2626",color:"white", position: 'absolute', top: -8, right: -8, zIndex: 3, boxShadow:'0 2px 6px rgba(0,0,0,0.3)'}}
                                            onClick={(e) => { e.stopPropagation(); setMergeLeft(null); setDualSoloSide('right'); }}
                                        >
                                            <IconX size={13} />
                                        </button>
                                    )}
                                    <div style={{ border: '2px dashed rgba(255,255,255,0.15)', borderRadius: 16, overflow: 'hidden', background: mergeLeft ? '#000' : 'rgba(255,255,255,0.04)' }}>
                                    <div
                                        ref={leftZoneRef}
                                        style={{
                                            width: '100%', aspectRatio: dualSoloSide ? '418/256' : '209/256',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            overflow: 'hidden', cursor: mergeLeft ? 'default' : 'pointer', position: 'relative', touchAction: 'none',
                                        }}
                                        onClick={!mergeLeft ? () => { if (isMobile) { setDualUploadSide('left'); } else { inlineMergeLeftRef.current?.click(); } } : undefined}
                                        onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                                        onDrop={(e) => {
                                            e.preventDefault(); e.stopPropagation();
                                            const imgs = Array.from(e.dataTransfer.files || []).filter(x => x.type.startsWith('image/'));
                                            if (!imgs.length) return;
                                            if (imgs.length >= 2 && !dualSoloSide) { fillDualSlots(imgs, 'left'); return; }
                                            processFile(imgs[0], 'left');
                                        }}
                                    >
                                        {mergeLeft ? (
                                            <>
                                                <img src={mergeLeft.url} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain', objectPosition: 'center', transform: `translate(${mergeLeft.offsetX ?? 0}px, ${mergeLeft.offsetY ?? 0}px) scale(${mergeLeft.zoom ?? 1})`, transformOrigin: 'center center', userSelect: 'none', pointerEvents: 'none', display: 'block' }} alt="left" />
                                                <button
                                                    style={{display:"inline-flex",alignItems:"center",justifyContent:"center",width:28,height:28,borderRadius:"50%",border:"none",cursor:"pointer",flexShrink:0,background:"#dc2626",color:"white", position: 'absolute', top: 6, right: 6, zIndex: 2}}
                                                    onPointerDown={(e) => e.stopPropagation()}
                                                    onClick={(e) => { e.stopPropagation(); setMergeLeft(null); }}
                                                >
                                                    <IconX size={12} />
                                                </button>
                                            </>
                                        ) : (
                                            <div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:8,padding:'14px 12px'}}>
                                                <div style={{width:46,height:46,borderRadius:'50%',background:'rgba(255,255,255,0.08)',display:'flex',alignItems:'center',justifyContent:'center'}}>
                                                    <IconPhoto size={22} color='rgba(255,255,255,0.6)' />
                                                </div>
                                                <p style={{margin:0,fontSize:12.5,fontWeight:700,color:'#fff',textAlign:'center',lineHeight:1.35}}>{dualSoloSide ? t('imageGeneration.dualSoloUpload') : t('imageGeneration.mergeLeftUpload')}</p>
                                                <p style={{margin:0,fontSize:10.5,color:'rgba(255,255,255,0.45)',textAlign:'center',lineHeight:1.4}}>{dualSoloSide ? t('imageGeneration.dualSoloHint') : t('imageGeneration.dualBoxHint')}</p>
                                                <div style={{marginTop:2,display:'inline-flex',alignItems:'center',gap:6,padding:'7px 16px',borderRadius:8,background:'#e68a00',color:'#fff',fontSize:12,fontWeight:700}}>
                                                    <IconUpload size={13} />
                                                    {t('imageGeneration.uploadButton')}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    </div>
                                    <input ref={inlineMergeLeftRef} type="file" accept="image/*" multiple style={{ display: 'none' }}
                                        onChange={(e) => handleMergePickSide('left', e)} />
                                    <input ref={cameraMergeLeftRef} type="file" accept="image/*" capture="user" style={{ display: 'none' }}
                                        onChange={(e) => handleMergePickSide('left', e)} />
                                </div>
                                )}

                                {/* Right zone */}
                                {dualSoloSide !== 'left' && (
                                <div style={{ position: 'relative', flex: dualSoloSide ? '1 1 100%' : 1, maxWidth: 'none', minWidth: 0 }}>
                                    {/* Close-box button — collapses to the left side (only when both boxes are empty/shown) */}
                                    {!dualSoloSide && !mergeRight && (
                                        <button
                                            title={t('imageGeneration.removeBox')}
                                            style={{display:"inline-flex",alignItems:"center",justifyContent:"center",width:24,height:24,borderRadius:"50%",border:"none",cursor:"pointer",background:"#dc2626",color:"white", position: 'absolute', top: -8, right: -8, zIndex: 3, boxShadow:'0 2px 6px rgba(0,0,0,0.3)'}}
                                            onClick={(e) => { e.stopPropagation(); setMergeRight(null); setDualSoloSide('left'); }}
                                        >
                                            <IconX size={13} />
                                        </button>
                                    )}
                                    <div style={{ border: '2px dashed rgba(255,255,255,0.15)', borderRadius: 16, overflow: 'hidden', background: mergeRight ? '#000' : 'rgba(255,255,255,0.04)' }}>
                                    <div
                                        ref={rightZoneRef}
                                        style={{
                                            width: '100%', aspectRatio: dualSoloSide ? '418/256' : '209/256',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            overflow: 'hidden', cursor: mergeRight ? 'default' : 'pointer', position: 'relative', touchAction: 'none',
                                        }}
                                        onClick={!mergeRight ? () => { if (isMobile) { setDualUploadSide('right'); } else { inlineMergeRightRef.current?.click(); } } : undefined}
                                        onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                                        onDrop={(e) => {
                                            e.preventDefault(); e.stopPropagation();
                                            const imgs = Array.from(e.dataTransfer.files || []).filter(x => x.type.startsWith('image/'));
                                            if (!imgs.length) return;
                                            if (imgs.length >= 2 && !dualSoloSide) { fillDualSlots(imgs, 'right'); return; }
                                            processFile(imgs[0], 'right');
                                        }}
                                    >
                                        {mergeRight ? (
                                            <>
                                                <img src={mergeRight.url} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain', objectPosition: 'center', transform: `translate(${mergeRight.offsetX ?? 0}px, ${mergeRight.offsetY ?? 0}px) scale(${mergeRight.zoom ?? 1})`, transformOrigin: 'center center', userSelect: 'none', pointerEvents: 'none', display: 'block' }} alt="right" />
                                                <button
                                                    style={{display:"inline-flex",alignItems:"center",justifyContent:"center",width:28,height:28,borderRadius:"50%",border:"none",cursor:"pointer",flexShrink:0,background:"#dc2626",color:"white", position: 'absolute', top: 6, right: 6, zIndex: 2}}
                                                    onPointerDown={(e) => e.stopPropagation()}
                                                    onClick={(e) => { e.stopPropagation(); setMergeRight(null); }}
                                                >
                                                    <IconX size={12} />
                                                </button>
                                            </>
                                        ) : (
                                            <div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:8,padding:'14px 12px'}}>
                                                <div style={{width:46,height:46,borderRadius:'50%',background:'rgba(255,255,255,0.08)',display:'flex',alignItems:'center',justifyContent:'center'}}>
                                                    <IconPhoto size={22} color='rgba(255,255,255,0.6)' />
                                                </div>
                                                <p style={{margin:0,fontSize:12.5,fontWeight:700,color:'#fff',textAlign:'center',lineHeight:1.35}}>{dualSoloSide ? t('imageGeneration.dualSoloUpload') : t('imageGeneration.mergeRightUpload')}</p>
                                                <p style={{margin:0,fontSize:10.5,color:'rgba(255,255,255,0.45)',textAlign:'center',lineHeight:1.4}}>{dualSoloSide ? t('imageGeneration.dualSoloHint') : t('imageGeneration.dualBoxHint')}</p>
                                                <div style={{marginTop:2,display:'inline-flex',alignItems:'center',gap:6,padding:'7px 16px',borderRadius:8,background:'#e68a00',color:'#fff',fontSize:12,fontWeight:700}}>
                                                    <IconUpload size={13} />
                                                    {t('imageGeneration.uploadButton')}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    </div>
                                    <input ref={inlineMergeRightRef} type="file" accept="image/*" multiple style={{ display: 'none' }}
                                        onChange={(e) => handleMergePickSide('right', e)} />
                                    <input ref={cameraMergeRightRef} type="file" accept="image/*" capture="user" style={{ display: 'none' }}
                                        onChange={(e) => handleMergePickSide('right', e)} />
                                </div>
                                )}
                            </div>
                            )}

                            {/* Action row: swap / add-second-person + exit dual mode */}
                            <div className="flex items-center justify-center gap-2 mt-3.5">
                                {dualSoloSide ? (
                                    <button
                                        className="inline-flex items-center justify-center gap-2 h-10 px-4 rounded-lg border border-zinc-700 bg-zinc-800 text-zinc-300 hover:bg-zinc-700 hover:text-white text-sm font-semibold transition-colors shrink-0"
                                        onClick={() => setDualSoloSide(null)}
                                    >
                                        <IconPlus size={16} />
                                        {t('imageGeneration.addSecondPerson')}
                                    </button>
                                ) : (
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <button
                                                className="inline-flex items-center justify-center w-10 h-10 rounded-lg border border-zinc-700 bg-zinc-800 text-zinc-300 hover:bg-zinc-700 hover:text-white transition-colors shrink-0 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-zinc-800 disabled:hover:text-zinc-300"
                                                disabled={!mergeLeft && !mergeRight}
                                                onClick={() => { const tmp = mergeLeft; setMergeLeft(mergeRight); setMergeRight(tmp); }}
                                            >
                                                <IconArrowsLeftRight size={20} />
                                            </button>
                                        </TooltipTrigger>
                                        <TooltipContent side="top" className="z-[2000]">{t('imageGeneration.swapImages', 'Swap')}</TooltipContent>
                                    </Tooltip>
                                )}
                                {/* Shoulder-front toggle — which photo's shoulder sits in front */}
                                {!dualSoloSide && mergeLeft && mergeRight && (
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <button
                                                className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-[#fe9a00] text-white hover:bg-[#e08a00] transition-colors shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                                                disabled={isGenerating}
                                                onClick={() => { if (!isGenerating) setShoulderTop(shoulderTop === 'left' ? 'right' : 'left'); }}
                                            >
                                                <IconStack2 size={20} />
                                            </button>
                                        </TooltipTrigger>
                                        <TooltipContent side="top" className="z-[2000]">{t('imageGeneration.shoulderTopSwap')}</TooltipContent>
                                    </Tooltip>
                                )}
                                {/* Exit dual mode — return to single-image upload (a phone has the tabs for this) */}
                                {!isMobile && <button
                                    className="inline-flex items-center justify-center gap-2 h-10 px-4 rounded-lg border border-red-500/40 bg-red-500/10 text-red-400 hover:bg-red-500 hover:text-white text-sm font-semibold transition-colors shrink-0"
                                    onClick={exitDualMode}
                                >
                                    <IconX size={16} />
                                    {t('imageGeneration.exitDualMode')}
                                </button>}
                            </div>

                            {dualSoloSide && (dualSoloSide === 'left' ? mergeLeft : mergeRight) && (
                                <span style={{ marginTop: 10, fontSize: 12, color: 'rgba(255,255,255,0.6)', textAlign: 'center' }}>
                                    {t('imageGeneration.dualSoloReadyHint')}
                                </span>
                            )}
                        </div>
                    )}

                    {/* Show animation while generating OR while the result image is still preloading.
                        This prevents the frame from collapsing to 0-height between generation
                        finishing and the browser having the image ready to display. */}
                    {(isGenerating || (generatedImage && !generatedImageReady)) && (
                        previewUrl ? (
                            /* ── Generating animation + blur-to-reveal ── */
                            <div className={`${styles.previewArea} ${isMobile ? styles.previewAreaResult : ''}`}>
                                {isMobile ? (
                                    /* ── Phone: the blurred photo, fitted like the result (a size container: both width and height) ── */
                                    <div className="h-full w-full p-3 [container-type:size]">
                                        <div className="flex h-full w-full items-center justify-center">
                                            <div className="relative inline-flex overflow-hidden rounded-2xl shadow-2xl">
                                                <img
                                                    src={previewUrl}
                                                    alt="Generating"
                                                    className="block h-auto max-h-[100cqh] w-auto max-w-[100cqw]"
                                                    style={{ filter: 'blur(22px) brightness(0.6) saturate(0.4)', transform: 'scale(1.05)' }}
                                                />
                                                <div className={styles.scannerWrap}>
                                                    <div className={styles.scannerTrail}></div>
                                                    <div className={styles.scannerLine}></div>
                                                </div>
                                                <div className={styles.generatingLabel}>
                                                    <span className="inline-block w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin flex-shrink-0" />
                                                    <span key={generatingStep} className={styles.stepText}>{GENERATING_STEPS[generatingStep]}</span>
                                                </div>
                                                {isGenerating && (
                                                    <button type="button" className={styles.hideJobBtn} onClick={hideCurrentGeneration} aria-label={t('imageGeneration.hideProcessing')}>
                                                        <IconX size={18} stroke={2.2} />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    /* ── Desktop: column layout ── */
                                    <div style={{display:"flex",flexDirection:"column",gap:12}} className={styles.beforeAfterContainer}>
                                        <div className={styles.genPlaceholderSlot}>
                                            <div className={styles.imageFrame} style={{ position: 'relative', padding: '4px' }}>
                                                <div className={styles.generatingImageWrap} style={{ position: 'relative', border: '10px solid #fff', borderRadius: '4px', overflow: 'hidden', display: 'inline-flex', ...(isMergedImage ? { maxWidth: '90%', margin: '0 auto', display: 'flex' } : {}) }}>
                                                    <img
                                                        src={previewUrl}
                                                        alt="Generating"
                                                        className={styles.comparisonImage}
                                                        style={{
                                                            filter: 'blur(24px) brightness(0.65) saturate(0.5)',
                                                            transform: 'scale(1.05)',
                                                            display: 'block',
                                                            aspectRatio: isMergedImage ? '2126/1299' : '1063/1299',
                                                            // Match the generated result exactly: it renders at .comparisonImage's
                                                            // max-height (600px) with the passport aspect, so drive the skeleton by
                                                            // the same height (width follows the aspect-ratio) instead of a fixed 327px.
                                                            width: isMergedImage ? '654px' : 'auto',
                                                            height: isMergedImage ? 'auto' : '600px',
                                                            maxWidth: '100%',
                                                            objectFit: 'cover',
                                                            ...(isMergedImage ? { margin: '0 auto' } : {}),
                                                        }}
                                                    />
                                                    {!blurRevealing && (
                                                        <div className={styles.scannerWrap}>
                                                            <div className={styles.scannerTrail}></div>
                                                            <div className={styles.scannerLine}></div>
                                                        </div>
                                                    )}
                                                    {blurRevealing && (
                                                        <div className={styles.resultRevealOverlay}>
                                                            <img src={generatedImage} alt="Generated" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                                                        </div>
                                                    )}
                                                    {/* Hide the job — pinned to the photo's own top-right corner */}
                                                    {isGenerating && !blurRevealing && (
                                                        <Tooltip>
                                                            <TooltipTrigger asChild>
                                                                <button className={styles.hideJobBtn} onClick={hideCurrentGeneration} aria-label={t('imageGeneration.hideProcessing')}>
                                                                    <IconX size={18} stroke={2.2} />
                                                                </button>
                                                            </TooltipTrigger>
                                                            <TooltipContent side="bottom" className="max-w-[260px] text-center z-[2000]">
                                                                {t('imageGeneration.hideProcessingHint')}
                                                            </TooltipContent>
                                                        </Tooltip>
                                                    )}
                                                </div>
                                                <div className={styles.generatingLabel} style={{ opacity: blurRevealing ? 0 : 1, transition: 'opacity 0.25s' }}>
                                                    <span className="inline-block w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin flex-shrink-0" />
                                                    <span key={generatingStep} className={styles.stepText}>{GENERATING_STEPS[generatingStep]}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ) : (
                            /* ── Fallback progress bar (no preview yet — silent cap flow) ── */
                            <div className={styles.loadingArea}>
                                <div style={{ width: '100%', maxWidth: '500px' }}>
                                    <span>
                                        {t('imageGeneration.generatingPhoto')}
                                    </span>
                                    <div style={{ position: 'relative', width: '100%' }}>
                                        <div style={{width:"100%",height:20,background:"#2a3150",borderRadius:8,overflow:"hidden"}}><div style={{width:`${progress}%`,height:"100%",background:"#f97316",transition:"width 0.3s ease",borderRadius:4}} /></div>
                                        <span style={{ position: 'absolute', top: '50%', right: '14px', transform: 'translateY(-50%)', fontWeight: 700, fontSize: '13px', color: 'white', textShadow: '0 2px 4px rgba(0,0,0,0.5)', zIndex: 1 }}>
                                            {Math.round(progress)}%
                                        </span>
                                    </div>
                                </div>
                            </div>
                        )
                    )}

                    {/* Result view — shown after reveal animation completes */}
                    {previewUrl && !isGenerating && (!generatedImage || generatedImageReady) && (
                        <div
                            className={`${styles.previewArea} ${generatedImage ? styles.previewAreaResult : ''} ${!generatedImage && isDragging ? styles.dragging : ''}`}
                            onDragOver={!generatedImage ? handleDragOver : undefined}
                            onDragLeave={!generatedImage ? handleDragLeave : undefined}
                            onDrop={!generatedImage ? handleDrop : undefined}
                        >

                            {/* Switch to dual mode — only available before generation */}
                            {!isMobile && !generatedImage && file && !dualUploadMode && (
                                <button
                                    style={{ position:'absolute', bottom:20, left:'50%', transform:'translateX(-50%)', display:'flex', alignItems:'center', justifyContent:'center', gap:8, padding:'6px 14px', borderRadius:14, border:'none', background:'linear-gradient(135deg,#065f46,#059669)', color:'#fff', fontSize:12, fontWeight:700, cursor:'pointer', boxShadow:'0 4px 14px rgba(5,150,105,0.4)', zIndex:1, whiteSpace:'nowrap' }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        // Move current image to left slot and enter dual mode
                                        setMergeLeft({ file, url: previewUrl, offsetX: 0, offsetY: 0, zoom: 1 });
                                        setFile(null);
                                        setPreviewUrl(null);
                                        setDualUploadMode(true);
                                        setSize('dual_image');
                                    }}
                                >
                                    <IconUsers size={14} />
                                    {t('imageGeneration.dualUploadButton')}
                                </button>
                            )}

                            {generatedImage ? (
                                isMobile ? (
                                    /* ── Phone: the view switch, the photo fitted to the space left, then the action bar above the tabs ── */
                                    customInline && showCustomDownload ? (
                                        <CustomDownloadModal inline opened onClose={closeCustomDownload} imageUrl={customDownloadImageUrl} imageId={customDownloadImageId} />
                                    ) : (
                                    <div className="flex h-full w-full flex-col">
                                        <div className="flex shrink-0 items-center gap-2 px-3 pt-3">
                                            {previewUrl && (
                                                <div role="group" className="inline-flex rounded-lg bg-white/10 p-0.5">
                                                    {['original', 'compare', 'result'].map((v) => (
                                                        <button
                                                            key={v}
                                                            type="button"
                                                            aria-pressed={resultView === v}
                                                            onClick={() => setResultView(v)}
                                                            className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${resultView === v ? 'bg-primary text-white shadow-sm' : 'text-white/70'}`}
                                                        >
                                                            {t(`studioEditor.${v}`)}
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                            <div className="ml-auto flex items-center gap-1.5">
                                                <button type="button" onClick={handleOpenZoom} aria-label={t('imageGeneration.previewTooltip')} className="flex h-9 w-9 items-center justify-center rounded-[10px] bg-primary text-white">
                                                    <IconEye size={18} />
                                                </button>
                                                <button type="button" onClick={handleRemoveImage} aria-label={t('common.close')} className="flex h-9 w-9 items-center justify-center rounded-[10px] bg-red-600 text-white">
                                                    <IconX size={18} />
                                                </button>
                                            </div>
                                        </div>

                                        {/* a size container: the photo fits both the width and the height left, whatever its shape */}
                                        <div className="min-h-0 flex-1 p-3 [container-type:size]">
                                            <div className="flex h-full w-full items-center justify-center">
                                                <div
                                                    key={generatedImageId || generatedImage}
                                                    className="relative inline-flex overflow-hidden rounded-2xl shadow-2xl animate-in fade-in-0 zoom-in-90 duration-500 ease-out"
                                                    style={resultIsTransparent ? (currentBgColor ? { background: currentBgColor } : CHECKERBOARD_STYLE) : undefined}
                                                >
                                                    <img
                                                        src={resultDisplayUrl}
                                                        alt="After"
                                                        onClick={handleOpenZoom}
                                                        className="block h-auto max-h-[100cqh] w-auto max-w-[100cqw]"
                                                        style={{ visibility: resultView === 'result' ? 'visible' : 'hidden' }}
                                                    />
                                                    {resultView === 'original' && previewUrl && (
                                                        <img src={previewUrl} alt="Before" className="absolute inset-0 h-full w-full bg-white object-contain" />
                                                    )}
                                                    {resultView === 'compare' && previewUrl && (
                                                        <div className="absolute inset-0 bg-white">
                                                            <CompareSlider fit="contain" before={previewUrl} after={resultDisplayUrl} beforeLabel={t('studioEditor.before')} afterLabel={t('studioEditor.after')} />
                                                        </div>
                                                    )}
                                                    {/* The edit drawer, on the photo's bottom edge as on desktop */}
                                                    <div style={resultView === 'result' ? undefined : { display: 'none' }}>
                                                        <PhotoEditDrawer
                                                            value={edits}
                                                            onChange={setEdits}
                                                            open={editorOpen}
                                                            onClose={() => setEditorOpen(false)}
                                                            thumbs={filterThumbs}
                                                            onCrop={() => { setEditorOpen(false); openResultCrop(); }}
                                                        />
                                                    </div>
                                                    {generatedImageId && resultView === 'result' && !editorOpen && !feedbackGiven.has(generatedImageId) && (
                                                        <div className="absolute inset-x-0 bottom-3 z-[3] flex items-center justify-center gap-2">
                                                            <button type="button" disabled={feedbackLoading !== null} onClick={() => handleFeedback(generatedImageId, 'positive')} className="inline-flex items-center gap-1.5 rounded-[10px] bg-green-600 px-3 py-1.5 text-xs font-semibold text-white shadow-[0_4px_12px_rgba(0,0,0,0.35)] disabled:opacity-50">
                                                                {feedbackLoading === 'positive' ? <IconLoader2 size={14} className="animate-spin" /> : <IconThumbUp size={14} />}
                                                                {t('imageGeneration.feedbackGood')}
                                                            </button>
                                                            <button type="button" disabled={feedbackLoading !== null} onClick={() => handleFeedback(generatedImageId, 'negative')} className="inline-flex items-center gap-1.5 rounded-[10px] bg-red-600 px-3 py-1.5 text-xs font-semibold text-white shadow-[0_4px_12px_rgba(0,0,0,0.35)] disabled:opacity-50">
                                                                {feedbackLoading === 'negative' ? <IconLoader2 size={14} className="animate-spin" /> : <IconThumbDown size={14} />}
                                                                {t('imageGeneration.feedbackBad')}
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        {resultActionsOpen && !dockOpen && <ResultActions
                                            layout="bar"
                                            onCrop={openResultCrop}
                                            cropLoading={resultCropLoading}
                                            cropDisabled={!generatedImageId}
                                            onCustom={openCustomDownload}
                                            customLoading={customDownloadLoading}
                                            onCopy={handleCopyResultImage}
                                            copying={copyingResult}
                                            editOpen={editorOpen}
                                            edited={hasAdjustmentsChanged}
                                            onToggleEdit={toggleEditor}
                                            infoContent={imageInfoBody}
                                            onDownload={handleDownload}
                                            downloading={downloading}
                                            onPrint={printEdited}
                                        />}
                                    </div>
                                    )
                                ) : (
                                    /* ── Desktop / tablet: the result, one view at a time (original / compare / result) ──
                                       `group` sits on the whole dark panel so the feedback buttons
                                       appear as soon as the mouse enters the result area, not just
                                       when hovering the generated image itself. */
                                    <div style={{display:"flex",flexDirection:"column",gap:6}} className={`${styles.beforeAfterContainer} group`}>
                                        {/* After Image */}
                                        <div className={styles.comparisonImageBox}>
                                            <div className={styles.imageFrame} style={{ position: 'relative', cursor: 'pointer', padding: '4px', display: 'flex', justifyContent: 'center' }} onClick={handleOpenZoom}>
                                                {/* a newly opened / generated result zooms in from the centre (keyed, so each result replays it) */}
                                                <div key={generatedImageId || generatedImage} className="animate-in fade-in-0 zoom-in-90 duration-500 ease-out" style={{ position: 'relative', border: '10px solid #fff', borderRadius: '4px', overflow: 'hidden', display: 'inline-flex', ...(resultIsTransparent ? (currentBgColor ? { background: currentBgColor } : CHECKERBOARD_STYLE) : {}), ...(isMergedImage ? { maxWidth: '90%' } : {}) }}>
                                                    {/* The result always sits here — it sizes the frame, so switching views never changes its size */}
                                                    <img
                                                        src={resultDisplayUrl}
                                                        alt="After"
                                                        className={styles.comparisonImage}
                                                        style={{ display: 'block', visibility: resultView === 'result' ? 'visible' : 'hidden', ...(!isMergedImage ? { width: 'auto', height: 'auto', maxWidth: 'calc(100cqw - 28px)', maxHeight: 'min(calc(100cqh - 28px), calc(100dvh - 9rem))' } : {}) }}
                                                    />
                                                    {resultView === 'original' && previewUrl && (
                                                        <img src={previewUrl} alt="Before" className="absolute inset-0 h-full w-full bg-white object-contain" />
                                                    )}
                                                    {resultView === 'compare' && previewUrl && (
                                                        // dragging the divider must not open the zoom view
                                                        <div className="absolute inset-0 bg-white" onClick={(e) => e.stopPropagation()}>
                                                            <CompareSlider fit="contain" before={previewUrl} after={resultDisplayUrl} beforeLabel={t('studioEditor.before')} afterLabel={t('studioEditor.after')} />
                                                        </div>
                                                    )}
                                                    {/* The edit drawer (the Studio Editor's) — on the result view; a click inside it must not open the zoom view */}
                                                    <div onClick={(e) => e.stopPropagation()} style={resultView === 'result' ? undefined : { display: 'none' }}>
                                                        <PhotoEditDrawer
                                                            value={edits}
                                                            onChange={setEdits}
                                                            open={editorOpen}
                                                            onClose={() => setEditorOpen(false)}
                                                            thumbs={filterThumbs}
                                                            onCrop={() => { setEditorOpen(false); openResultCrop(); }}
                                                        />
                                                    </div>
                                                    {/* Feedback + copy — floats just above the photo's bottom edge (inside its frame, so it stays on screen), revealed on hover only.
                                                        The copy button stays available even after feedback is given;
                                                        only the like/dislike pair is gated on no-feedback-yet. Only on the result view (not the original /
                                                        compare), and not while the edit drawer covers the photo's bottom edge. */}
                                                    {generatedImageId && resultView === 'result' && !editorOpen && (
                                                        <div
                                                            className="opacity-0 group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto transition-opacity duration-200"
                                                            style={{ position:'absolute', left:0, right:0, bottom:12, display:'flex', alignItems:'center', gap:8, justifyContent:'center', zIndex:3 }}
                                                            onClick={(e) => e.stopPropagation()}
                                                        >
                                                            {!feedbackGiven.has(generatedImageId) && (
                                                                <>
                                                                    <button className="inline-flex items-center gap-1.5 font-semibold px-3 py-1.5 text-xs rounded-[10px] transition-transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed" style={{background:"#16a34a",color:"#fff",border:'none',boxShadow:'0 4px 12px rgba(0,0,0,0.35)'}} disabled={feedbackLoading !== null} onClick={(e) => { e.stopPropagation(); handleFeedback(generatedImageId, 'positive'); }}>
                                                                        {feedbackLoading === 'positive' ? <svg className="animate-spin h-3.5 w-3.5" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/></svg> : <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M1 21h4V9H1v12zm22-11c0-1.1-.9-2-2-2h-6.31l.95-4.57.03-.32c0-.41-.17-.79-.44-1.06L14.17 1 7.59 7.59C7.22 7.95 7 8.45 7 9v10c0 1.1.9 2 2 2h9c.83 0 1.54-.5 1.84-1.22l3.02-7.05c.09-.23.14-.47.14-.73v-2z"/></svg>}
                                                                        {t('imageGeneration.feedbackGood')}
                                                                    </button>
                                                                    <button className="inline-flex items-center gap-1.5 font-semibold px-3 py-1.5 text-xs rounded-[10px] transition-transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed" style={{background:"#dc2626",color:"#fff",border:'none',boxShadow:'0 4px 12px rgba(0,0,0,0.35)'}} disabled={feedbackLoading !== null} onClick={(e) => { e.stopPropagation(); handleFeedback(generatedImageId, 'negative'); }}>
                                                                        {feedbackLoading === 'negative' ? <svg className="animate-spin h-3.5 w-3.5" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/></svg> : <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M15 3H6c-.83 0-1.54.5-1.84 1.22l-3.02 7.05c-.09.23-.14.47-.14.73v2c0 1.1.9 2 2 2h6.31l-.95 4.57-.03.32c0 .41.17.79.44 1.06L10.83 23l6.59-6.59c.36-.36.58-.86.58-1.41V5c0-1.1-.9-2-2-2zm4 0v12h4V3h-4z"/></svg>}
                                                                        {t('imageGeneration.feedbackBad')}
                                                                    </button>
                                                                </>
                                                            )}
                                                            {/* Copy the generated image to the clipboard */}
                                                            <button className="inline-flex items-center gap-1.5 font-semibold px-3 py-1.5 text-xs rounded-[10px] transition-transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed" style={{background:"#3f3f46",color:"#fff",border:'none',boxShadow:'0 4px 12px rgba(0,0,0,0.35)'}} disabled={copyingResult} onClick={(e) => { e.stopPropagation(); handleCopyResultImage(); }}>
                                                                {copyingResult ? <svg className="animate-spin h-3.5 w-3.5" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/></svg> : <IconCopy size={14} />}
                                                                {t('imageGeneration.copyImage')}
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                                {/* Original · Compare · Result — the panel's top-left corner (opposite the eye / ✕), as in the Studio Editor; it slides down
                                                    into view while the pointer is over the panel (or a button in it has focus) */}
                                                {previewUrl && (
                                                    <div
                                                        role="group"
                                                        onClick={(e) => e.stopPropagation()}
                                                        className="pointer-events-none absolute left-2 top-2.5 z-[2] inline-flex -translate-y-2 rounded-lg bg-white/90 p-0.5 opacity-0 shadow-md backdrop-blur transition-[transform,opacity] duration-300 ease-out group-hover:pointer-events-auto group-hover:translate-y-0 group-hover:opacity-100 group-focus-within:pointer-events-auto group-focus-within:translate-y-0 group-focus-within:opacity-100 dark:bg-zinc-900/90"
                                                    >
                                                        {['original', 'compare', 'result'].map((v) => (
                                                            <button
                                                                key={v}
                                                                type="button"
                                                                aria-pressed={resultView === v}
                                                                onClick={() => setResultView(v)}
                                                                className={`rounded-md px-3 py-1 text-xs font-semibold transition-colors ${resultView === v ? 'bg-primary text-white shadow-sm' : 'text-zinc-600 hover:text-zinc-900 dark:text-zinc-300'}`}
                                                            >
                                                                {t(`studioEditor.${v}`)}
                                                            </button>
                                                        ))}
                                                    </div>
                                                )}
                                                <div style={{ position:'absolute', top: 8, right: 8, display:'flex', gap: 6, zIndex:2 }}>
                                                    <Tooltip>
                                                        <TooltipTrigger asChild>
                                                            <button
                                                                style={{ display:'inline-flex', alignItems:'center', justifyContent:'center', width:36, height:36, borderRadius:10, border:'none', cursor:'pointer', background:'#fe9a00', color:'white', boxShadow:'0 4px 12px rgba(0,0,0,0.5)' }}
                                                                onClick={(e) => { e.stopPropagation(); handleOpenZoom(); }}
                                                            >
                                                                <IconEye size={18} />
                                                            </button>
                                                        </TooltipTrigger>
                                                        <TooltipContent side="top" className="z-[2000]">{t('imageGeneration.previewTooltip')}</TooltipContent>
                                                    </Tooltip>
                                                    <Tooltip>
                                                        <TooltipTrigger asChild>
                                                            <button
                                                                style={{ display:'inline-flex', alignItems:'center', justifyContent:'center', width:36, height:36, borderRadius:10, border:'none', cursor:'pointer', background:'#dc2626', color:'white', boxShadow:'0 4px 12px rgba(0,0,0,0.5)' }}
                                                                onClick={(e) => { e.stopPropagation(); handleRemoveImage(); }}
                                                            >
                                                                <IconX size={18} />
                                                            </button>
                                                        </TooltipTrigger>
                                                        <TooltipContent side="top" className="z-[2000]">{t('imageGeneration.tooltipClose')}</TooltipContent>
                                                    </Tooltip>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )
                            ) : (
                                // Just uploaded image — shown in the selected size's ratio (object-cover
                                // mirrors the backend's cover-crop, so this previews the final framing
                                // even if the size was changed after cropping). Desktop mirrors the
                                // generating skeleton EXACTLY (white frame + 600px height) so the image
                                // doesn't jump/resize the moment processing starts.
                                <div className={styles.imageFrame} style={{ position: 'relative', ...(!isMobile ? { padding: '4px', maxHeight: 'none' } : {}) }}>
                                    {!isMobile ? (
                                        <div className={styles.generatingImageWrap} style={{ border: '10px solid #fff', borderRadius: '4px', overflow: 'hidden', display: 'inline-flex' }}>
                                            <img
                                                src={previewUrl}
                                                alt="Preview"
                                                className={styles.comparisonImage}
                                                style={{ display: 'block', aspectRatio: sizeRatioCss || '1063/1299', width: 'auto', height: '600px', maxWidth: '100%', objectFit: 'cover' }}
                                            />
                                        </div>
                                    ) : (
                                        <img
                                            src={previewUrl}
                                            alt="Preview"
                                            className={styles.previewImage}
                                            style={sizeRatioCss ? { aspectRatio: sizeRatioCss, objectFit: 'cover', width: 'auto', maxWidth: '100%' } : undefined}
                                        />
                                    )}
                                    <button
                                        style={{ position:'absolute', top: 8, right: 8, display:'inline-flex', alignItems:'center', justifyContent:'center', width:36, height:36, borderRadius:10, border:'none', cursor:'pointer', background:'#dc2626', color:'white', boxShadow:'0 4px 12px rgba(0,0,0,0.5)', zIndex:2 }}
                                        onClick={(e) => { e.stopPropagation(); handleRemoveImage(); }}
                                    >
                                        <IconX size={18} />
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Phone: the bottom tabs' options, under the photo (which keeps showing, smaller) */}
                {isMobile && !showCropModal && !isGenerating && activeDrawer === 'photo' && (
                    <div data-mantine-color-scheme="dark" className={styles.mobileDock}>
                        <div className={styles.dockHead}>
                            <h2 className="min-w-0 flex-1 truncate text-sm font-bold text-white">{t('imageGeneration.photoSize')}</h2>
                            <button type="button" aria-label={t('common.close')} onClick={() => setActiveDrawer(null)} className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-white/80"><IconChevronDown size={16} /></button>
                        </div>
                                {/* Main photo types */}
                                <div>
                                    <div ref={dockSizeRef} className={`${styles.sizeGrid} ${styles.drawerGrid}`}>
                                        {photoSizes.map((item) => {
                                            const isVisa = item.value === 'bd_visa';
                                            const isSelected = isVisa ? (size === 'bd_visa' || size.startsWith('visa_')) : size === item.value;
                                            return (
                                                <div key={item.value} style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:4, minWidth:0, overflow:'hidden' }}>
                                                    <div className={`${styles.sizeCard} ${isSelected ? styles.selected : ''} ${isGenerating ? styles.disabled : ''}`}
                                                        onClick={() => {
                                                            if (isGenerating) return;
                                                            setVisaCountryLabel('');
                                                            if (item.value === 'dual_image') {
                                                                if (generatedImage) { setSize(item.value); setDualUploadMode(true); } else switchToDual();
                                                                return;
                                                            }
                                                            if (dualUploadMode) exitDualMode(); // the photo stays, as a single one
                                                            setSize(isVisa ? 'bd_visa' : item.value);
                                                        }}>
                                                        <div style={{ width:28, height:28, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                                                            <item.icon size={26} strokeWidth={1.5} />
                                                        </div>
                                                        {isSelected && <div className={styles.dressCheckmark}><IconCheck size={10} color='white' strokeWidth={3} /></div>}
                                                    </div>
                                                    <span className={`${styles.sizeCardLabel} ${isSelected ? styles.sizeCardLabelActive : ''}`}>
                                                        {isVisa && visaCountryLabel ? visaCountryLabel : item.abbr}
                                                    </span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>

                                {/* Visa sizes — one swipeable row, once ভিসা is chosen */}
                                {(size === 'bd_visa' || size.startsWith('visa_')) && (
                                <div>
                                    <div ref={dockVisaRef} className={styles.dockVisaRow}>
                                        {VISA_GROUPS.map((group) => {
                                            const isGroupSelected = size === group.key;
                                            return (
                                                <button key={group.key}
                                                    onClick={() => { if (isGenerating) return; setSize(group.key); setVisaCountryLabel(isBn ? group.sizeBn : group.size); setDualUploadMode(false); setMergeLeft(null); setMergeRight(null); setIsMergedImage(false); }}
                                                    className={`flex flex-col items-start gap-0.5 px-3 py-2.5 rounded-2xl border-2 transition-all text-left ${isGroupSelected ? 'border-orange-400 bg-orange-50 dark:bg-orange-900/20' : 'border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-700/50'}`}
                                                    disabled={isGenerating}>
                                                    <span className={`text-[12px] font-bold leading-tight ${isGroupSelected ? 'text-orange-500' : 'text-zinc-800 dark:text-zinc-100'}`}>{isBn ? group.sizeBn : group.size}</span>
                                                    <span className="text-[10px] text-zinc-400 dark:text-zinc-500 leading-tight line-clamp-1">{group.countries.slice(0,2).join(', ')}{group.countries.length > 2 ? '...' : ''}</span>
                                                    {isGroupSelected && <div className="absolute top-1.5 right-1.5 w-4 h-4 rounded-full bg-orange-400 flex items-center justify-center" style={{position:'absolute', top:6, right:6}}><IconCheck size={8} color='white' strokeWidth={3}/></div>}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                                )}
                    </div>
                )}

                {isMobile && !showCropModal && !isGenerating && activeDrawer === 'style' && (
                    <div data-mantine-color-scheme="dark" className={styles.mobileDock}>
                        <div className={styles.dockHead}>
                            <div className="flex min-w-0 flex-1 items-center gap-0.5">
                                {[{ key: 'dress', label: t('imageGeneration.clothingStyle') }, { key: 'bg', label: t('imageGeneration.background') }].map((x) => (
                                    <button key={x.key} type="button" onClick={() => setStyleDrTab(x.key)} aria-pressed={styleDrTab === x.key} className={`h-7 shrink-0 rounded-md px-2.5 text-[11px] font-semibold transition-colors ${styleDrTab === x.key ? 'bg-primary text-white shadow-md shadow-black/30' : 'text-white/75'}`}>{x.label}</button>
                                ))}
                            </div>
                            <button type="button" aria-label={t('common.close')} onClick={() => setActiveDrawer(null)} className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-white/80"><IconChevronDown size={16} /></button>
                        </div>
                                {/* Dress section — no Popover on mobile, inline color picker instead */}
                                {styleDrTab === 'dress' && (<div>
                                    <div ref={dockDressRef} className={`${styles.dressGrid} ${styles.drawerGrid}`}>
                                        <div className={`${styles.dressCard} ${dress === 'original' ? styles.selected : ''} ${isGenerating ? styles.disabled : ''}`}
                                            onClick={() => { if (!isGenerating) { setDress('original'); setDressColorPopover(null); } }}>
                                            <IconCircleOff size={32} color="gray" strokeWidth={3} />
                                            {dress === 'original' && <div className={styles.dressCheckmark}><IconCheck size={12} color='white' strokeWidth={3} /></div>}
                                        </div>
                                        {visibleDressStyles.map((item) => {
                                            const isThisType = parsedDress.type === item.value;
                                            const activeColor = isThisType ? dressColors.find(c => c.id === parsedDress.color) : null;
                                            return (
                                                <div key={item.value}
                                                    className={`${styles.dressCard} ${isThisType ? styles.selected : ''} ${isGenerating ? styles.disabled : ''}`}
                                                    onClick={() => { if (!isGenerating) { if (!isThisType) setDress(`${item.defaultColor}_${item.value}`); setDressColorPopover(prev => prev === item.value ? null : item.value); setSuitColorTab('suit'); } }}>
                                                    <DressPreviewSvg src={item.value === 'shirt' && shirtTie ? '/images/clothing-styles/shirt-tie.svg' : item.image} color={isThisType && activeColor ? activeColor.hex : null} shirtColor={isThisType && (item.value === 'suite' || item.value === 'suite-tieless') ? dressColors.find(c => c.id === suitShirtColor)?.hex : null} tieColor={isThisType && (item.value === 'suite' || (item.value === 'shirt' && shirtTie)) ? dressColors.find(c => c.id === suitTieColor)?.hex : null} blouseColor={isThisType && (item.value === 'sharee' || item.value === 'sharee-design') ? dressColors.find(c => c.id === shareeBlouseColor)?.hex : isThisType && (item.value === 'kameez-hijab' || item.value === 'shari-hijab') ? dressColors.find(c => c.id === hijabColor)?.hex : isThisType && item.value === 'orna-kameez' ? dressColors.find(c => c.id === ornaColor)?.hex : isThisType && item.value === 'hijab-formal' ? dressColors.find(c => c.id === formalColor)?.hex : isThisType && item.value === 'thobe-ghutra' ? dressColors.find(c => c.id === ghutraColor)?.hex : isThisType && item.value === 'jacket' ? dressColors.find(c => c.id === jacketHatColor)?.hex : null} alt={item.label} className={styles.dressCardImg} onError={(e) => { e.target.style.display = 'none'; }} />
                                                    {isThisType && (
                                                        <div className={styles.dressCheckmark} style={activeColor ? { background: activeColor.hex, border: '2px solid rgba(255,255,255,0.8)' } : undefined}>
                                                            <IconCheck size={10} color={activeColor && ['white','yellow'].includes(parsedDress.color) ? '#9ca3af' : 'white'} strokeWidth={3} />
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>

                                    {/* Inline color picker — stays open, compact row, with sub-tabs for complex dress types */}
                                    {dressColorPopover && (() => {
                                        const item = visibleDressStyles.find(s => s.value === dressColorPopover);
                                        if (!item) return null;
                                        const hasTabs = showDressTabs(item.value) || (item.value === 'shirt' && shirtTie);
                                        return (
                                            <div className="mt-2 pt-2 border-t border-zinc-100 dark:border-zinc-700">
                                                <div className="flex items-center justify-between mb-2">
                                                    <p className="text-[11px] font-bold text-orange-500 leading-none">{item.label}</p>
                                                    <div className="flex items-center gap-2">
                                                        {item.value === 'shirt' && (
                                                            <>
                                                                <HeadCoverToggle active={shirtCheck} onToggle={() => setShirtCheck(v => !v)} label={t('imageGeneration.checkShirt')} tooltip={t('imageGeneration.checkShirtTooltip')} />
                                                                <HeadCoverToggle active={shirtTie} onToggle={() => setShirtTie(v => !v)} label={t('imageGeneration.tie')} tooltip={t('imageGeneration.tieTooltip')} />
                                                            </>
                                                        )}
                                                        {item.value === 'panjbi' && (
                                                            <HeadCoverToggle active={panjabiStripe} onToggle={() => setPanjabiStripe(v => !v)} label={t('imageGeneration.stripePanjabi')} tooltip={t('imageGeneration.stripePanjabiTooltip')} />
                                                        )}
                                                        <button onClick={() => setDressColorPopover(null)} className="w-5 h-5 flex items-center justify-center rounded-full bg-zinc-100 dark:bg-zinc-700 text-zinc-400"><IconX size={10} /></button>
                                                    </div>
                                                </div>

                                                {hasTabs && (
                                                    <div style={{ display:'flex', alignItems:'center', gap:5, marginBottom:8, flexWrap:'wrap' }}>
                                                        {[
                                                            { value: 'suit', label: item.value === 'jacket' ? 'জ্যাকেট বডি' : item.value === 'shirt' ? 'শার্ট' : (item.value === 'sharee' || item.value === 'sharee-design' || item.value === 'shari-hijab') ? 'শাড়ি' : (item.value === 'kameez-hijab' || item.value === 'orna-kameez') ? 'কামিজ' : item.value === 'hijab-formal' ? t('imageGeneration.dressTabHijab') : item.value === 'thobe-ghutra' ? t('imageGeneration.dressTabThobe') : 'স্যুট', colorId: parsedDress.color },
                                                            ...(item.value === 'shirt' ? [] : [{ value: 'shirt', label: item.value === 'jacket' ? 'হাতা' : (item.value === 'sharee' || item.value === 'sharee-design') ? 'ব্লাউজ' : (item.value === 'kameez-hijab' || item.value === 'shari-hijab') ? 'হিজাব' : item.value === 'orna-kameez' ? 'ওড়না' : item.value === 'hijab-formal' ? t('imageGeneration.dressTabFormal') : item.value === 'thobe-ghutra' ? t('imageGeneration.dressTabGhutra') : 'শার্ট', colorId: (item.value === 'sharee' || item.value === 'sharee-design') ? shareeBlouseColor : (item.value === 'kameez-hijab' || item.value === 'shari-hijab') ? hijabColor : item.value === 'orna-kameez' ? ornaColor : item.value === 'hijab-formal' ? formalColor : item.value === 'thobe-ghutra' ? ghutraColor : item.value === 'jacket' ? jacketHatColor : suitShirtColor }]),
                                                            ...((item.value === 'suite' || item.value === 'shirt') ? [{ value: 'tie', label: 'টাই', colorId: suitTieColor }] : []),
                                                        ].map(tab => {
                                                            const hex = dressColors.find(c => c.id === tab.colorId)?.hex || '#aaa';
                                                            const isActive = suitColorTab === tab.value;
                                                            return (
                                                                <div key={tab.value} onClick={() => setSuitColorTab(tab.value)} style={{ display:'flex', alignItems:'center', gap:4, padding:'4px 9px', borderRadius:20, cursor:'pointer', border: isActive ? '1.5px solid #fe9a00' : '1.5px solid #e5e7eb', background: isActive ? 'rgba(254,154,0,0.15)' : '#f3f4f6', userSelect:'none' }}>
                                                                    <div style={{ width:9, height:9, borderRadius:'50%', background:hex, border: tab.colorId === 'white' ? '1.5px solid #e5e7eb' : '1.5px solid rgba(0,0,0,0.12)', flexShrink:0 }} />
                                                                    <span style={{ fontSize:11, fontWeight: isActive ? 700 : 500, color: isActive ? '#fe9a00' : '#d4d4d8', lineHeight:1 }}>{tab.label}</span>
                                                                </div>
                                                            );
                                                        })}
                                                        {(item.value === 'sharee' || item.value === 'sharee-design') && (
                                                            <HeadCoverToggle active={shareeHeadCover} onToggle={() => setShareeHeadCover(v => !v)} label="ঘোমটা" tooltip="শাড়ির আঁচল দিয়ে মাথা ঢাকুন" />
                                                        )}
                                                        {item.value === 'orna-kameez' && (
                                                            <HeadCoverToggle active={kameezHeadCover} onToggle={() => setKameezHeadCover(v => !v)} label="মাথা ঢাকুন" tooltip="ওড়না দিয়ে মাথা ঢাকুন" />
                                                        )}
                                                        {item.value === 'suite-tieless' && suitColorTab === 'suit' && (
                                                            <HeadCoverToggle active={shirtCheck} onToggle={() => setShirtCheck(v => !v)} label={t('imageGeneration.checkSuit')} tooltip={t('imageGeneration.checkSuitTooltip')} />
                                                        )}
                                                    </div>
                                                )}

                                                <div ref={dockSwatchRef} className={styles.drawerSwatchGrid}>
                                                    {dressColors.map((color) => {
                                                        let isSelected;
                                                        if (!hasTabs) {
                                                            isSelected = parsedDress.color === color.id && parsedDress.type === item.value;
                                                        } else if (suitColorTab === 'suit') {
                                                            isSelected = parsedDress.color === color.id && parsedDress.type === item.value;
                                                        } else if (suitColorTab === 'shirt') {
                                                            isSelected = (item.value === 'sharee' || item.value === 'sharee-design') ? shareeBlouseColor === color.id
                                                                : (item.value === 'kameez-hijab' || item.value === 'shari-hijab') ? hijabColor === color.id
                                                                : item.value === 'orna-kameez' ? ornaColor === color.id
                                                                : item.value === 'hijab-formal' ? formalColor === color.id : item.value === 'thobe-ghutra' ? ghutraColor === color.id
                                                                : item.value === 'jacket' ? jacketHatColor === color.id
                                                                : suitShirtColor === color.id;
                                                        } else {
                                                            isSelected = suitTieColor === color.id;
                                                        }
                                                        return (
                                                            <div key={color.id}
                                                                style={{ width:'100%', aspectRatio:'1', borderRadius:'50%', ...(shirtCheck && ((item.value === 'shirt' && (!hasTabs || suitColorTab === 'suit')) || (item.value === 'suite-tieless' && suitColorTab === 'suit')) ? checkSwatchStyle(color.hex) : (panjabiStripe && item.value === 'panjbi') ? stripeSwatchStyle(color.hex) : { background: color.hex }), cursor:'pointer', boxShadow: isSelected ? `0 0 0 2.5px #fff, 0 0 0 4px #fe9a00` : '0 0 0 1px rgba(0,0,0,0.12)', transition:'box-shadow 0.12s', flexShrink:0 }}
                                                                onClick={() => {
                                                                    if (!hasTabs || suitColorTab === 'suit') {
                                                                        setDress(`${color.id}_${item.value}`);
                                                                    } else if (suitColorTab === 'shirt') {
                                                                        if (item.value === 'sharee' || item.value === 'sharee-design') setShareeBlouseColor(color.id);
                                                                        else if (item.value === 'kameez-hijab' || item.value === 'shari-hijab') setHijabColor(color.id);
                                                                        else if (item.value === 'orna-kameez') setOrnaColor(color.id);
                                                                                        else if (item.value === 'thobe-ghutra') setGhutraColor(color.id);
                                                                                        else if (item.value === 'hijab-formal') setFormalColor(color.id);
                                                                        else if (item.value === 'jacket') setJacketHatColor(color.id);
                                                                        else setSuitShirtColor(color.id);
                                                                    } else {
                                                                        setSuitTieColor(color.id);
                                                                    }
                                                                }} />
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        );
                                    })()}
                                </div>)}

                                {/* Background section */}
                                {styleDrTab === 'bg' && (<div>
                                    <div ref={dockBgRef} className={`${styles.colorGrid} ${styles.drawerGrid}`}>
                                        {backgrounds.map((item) => (
                                            <div key={item.id}
                                                className={`${styles.bgColorCard} ${bg === item.id ? styles.selected : ''} ${isGenerating ? styles.disabled : ''}`}
                                                onClick={(e) => {
                                                    if (isGenerating) return;
                                                    if (item.id === 'custom') { e.stopPropagation(); setBg(item.id); colorPickerRef.current?.click(); }
                                                    else { handleBgSelect(item.id); }
                                                }}
                                                style={{ ...(item.id === 'transparent' ? CHECKERBOARD_STYLE : { background: item.id === 'custom' ? customColor : item.color }), cursor: isGenerating ? 'not-allowed' : 'pointer', opacity: isGenerating ? 0.5 : 1 }}>
                                                {item.id === 'custom' ? (
                                                    <>
                                                        <IconPalette size={28} color='white' style={{ filter:'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }} />
                                                        <input ref={colorPickerRef} type="color" value={customColor} onChange={(e) => handleCustomBgChange(e.target.value)} disabled={isGenerating} style={{ position:'absolute', opacity:0, pointerEvents:'none', width:0, height:0 }} />
                                                    </>
                                                ) : (
                                                    bg === item.id && <div className={styles.dressCheckmark}><IconCheck size={12} color='white' strokeWidth={3} /></div>
                                                )}
                                            </div>
                                        ))}
                                        {/* Selected custom color — same-size swatch so the user sees the picked color */}
                                        {bg === 'custom' && (
                                            <div
                                                className={`${styles.bgColorCard} ${styles.selected} ${isGenerating ? styles.disabled : ''}`}
                                                onClick={() => { if (!isGenerating) colorPickerRef.current?.click(); }}
                                                style={{ background: customColor, cursor: isGenerating ? 'not-allowed' : 'pointer', opacity: isGenerating ? 0.5 : 1 }}
                                            >
                                                <div className={styles.dressCheckmark}><IconCheck size={12} color='white' strokeWidth={3} /></div>
                                            </div>
                                        )}
                                    </div>
                                </div>)}
                    </div>
                )}

                {isMobile && !showCropModal && !isGenerating && activeDrawer === 'extra' && (
                    <div data-mantine-color-scheme="dark" className={styles.mobileDock}>
                        <div className={styles.dockHead}>
                            <h2 className="min-w-0 flex-1 truncate text-sm font-bold text-white">{t('imageGeneration.extraEditingGuide')}</h2>
                            <button type="button" aria-label={t('common.close')} onClick={() => setActiveDrawer(null)} className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-white/80"><IconChevronDown size={16} /></button>
                        </div>
                            <div className="flex flex-col">
                                {/* Tool grid — kept as order-2 so the active tools' option panels
                                    (wrapped below as order-1) render ABOVE it, right under the
                                    header, instead of far below the 5-row grid where they were
                                    off-screen on mobile. */}
                                <div className="order-2">
                                <div ref={dockToolRef} className={`${styles.extraToolGrid} ${styles.drawerGrid}`}>
                                    {[
                                        { show: dp.glowMakeup,            active: glowMakeup,            toggle: () => { if (!glowMakeup) { setGlowMakeup(true); setSmoothSkin(false); } else setGlowMakeup(false); },                                                                                                                    icon: faceIcon,           label: t('imageGeneration.glowMakeup'), levelKey: 'glowMakeup' },
                                        { show: dp.smoothSkin,            active: smoothSkin,            toggle: () => { if (!smoothSkin) { setSmoothSkin(true); setGlowMakeup(false); } else setSmoothSkin(false); },                                                                                                                    icon: smoothSkinIcon,     label: t('imageGeneration.smoothSkin'), levelKey: 'smoothSkin' },
                                        { show: dp.fixOilySkin,           active: fixOilySkin,           toggle: () => setFixOilySkin(!fixOilySkin),                                                                                                                                                                                    icon: fixOilySkinIcon,    label: t('imageGeneration.fixOilySkin'), levelKey: 'fixOilySkin' },
                                        { show: dp.brightenImage,         active: brightenImage,         toggle: () => { if (!brightenImage) { setBrightenImage(true); setStudioLighting(false); } else setBrightenImage(false); },                                                                                                             icon: brightenIcon,       label: t('imageGeneration.brightenImage'), levelKey: 'brightenImage' },
                                        { show: dp.studioLighting,        active: studioLighting,        toggle: () => { if (!studioLighting) { setStudioLighting(true); setBrightenImage(false); } else setStudioLighting(false); },                                                                                                           icon: studioLightIcon,    label: t('imageGeneration.studioLighting'), levelKey: 'studioLighting' },
                                        { show: dp.straightenHead,        active: straightenHead,        toggle: () => { if (!straightenHead) { setStraightenHead(true); setLookAtCamera(false); } else setStraightenHead(false); },                                                                                                            icon: headIcon,           label: t('imageGeneration.straightenHead') },
                                        { show: dp.removeStrayHair, disabled: disabledTools.has('removeStrayHair'),       active: removeStrayHair,       toggle: () => { if (!removeStrayHair) { setRemoveStrayHair(true); setApplyHair(false); } else setRemoveStrayHair(false); },                                 icon: hairCutIcon,        label: t('imageGeneration.removeStrayHair') },
                                        { show: dp.halfBody, disabled: disabledTools.has('halfBody'), active: halfBody, toggle: () => setHalfBody(!halfBody), icon: halfBodyIcon, label: t('imageGeneration.halfBody'), levelKey: 'halfBody' },
                                        { show: dp.showEar, disabled: disabledTools.has('showEar'),               active: showEar,               toggle: () => setShowEar(!showEar),                                                                                                                         icon: showEarIcon,        label: t('imageGeneration.showEar') },
                                        { show: dp.openEyes,              active: openEyes,              toggle: () => setOpenEyes(!openEyes),                                                                                                                        icon: eyeIcon,            label: t('imageGeneration.openEyes') },
                                        { show: dp.closeMouth,            active: closeMouth,            toggle: () => setCloseMouth(!closeMouth),                                                                                                                    icon: closeMouthIcon,     label: t('imageGeneration.closeMouth') },
                                        { show: dp.keepSchoolDressDetails, disabled: disabledTools.has('keepSchoolDressDetails'),active: keepSchoolDressDetails,toggle: () => setKeepSchoolDressDetails(!keepSchoolDressDetails),                                                                                           icon: schoolDressIcon,    label: t('imageGeneration.keepSchoolDressDetails') },
                                        { show: dp.keepNamePlate,         active: keepNamePlate,         toggle: () => { if (keepNamePlate) setNamePlateText(''); setKeepNamePlate(!keepNamePlate); },                                                                                                             icon: keepNamePlateIcon,  label: t('imageGeneration.keepNamePlate') },
                                        { show: dp.removeGlasses,         active: removeGlasses,         toggle: () => { if (!removeGlasses) { setRemoveGlasses(true); setKeepSunGlasses(false); } else setRemoveGlasses(false); },                                     icon: sunglassIcon,       label: t('imageGeneration.removeGlass') },
                                        { show: dp.keepSunGlasses,        active: keepSunGlasses,        toggle: () => { if (!keepSunGlasses) { setKeepSunGlasses(true); setRemoveGlasses(false); } else setKeepSunGlasses(false); },                                   icon: keepSunGlassesIcon, label: t('imageGeneration.keepSunGlasses') },
                                        { show: dp.removeBeard, disabled: disabledTools.has('removeBeard'),           active: removeBeard,           toggle: () => { if (!removeBeard) { setRemoveBeard(true); setAddBeard(false); setKeepBeard(false); } else setRemoveBeard(false); },                           icon: beardCutIcon,       label: t('imageGeneration.removeBeard') },
                                        { show: dp.addBeard, disabled: disabledTools.has('addBeard'),              active: addBeard,              toggle: () => { if (!addBeard) { setAddBeard(true); setRemoveBeard(false); setKeepBeard(false); } else setAddBeard(false); },                               icon: addBeardIcon,       label: t('imageGeneration.addBeard') },
                                        { show: dp.keepBeard, disabled: disabledTools.has('keepBeard'),             active: keepBeard,             toggle: () => { if (!keepBeard) { setKeepBeard(true); setRemoveBeard(false); setAddBeard(false); } else setKeepBeard(false); },                             icon: keepBeardIcon,      label: t('imageGeneration.keepBeard') },
                                        { show: dp.keepMustache, disabled: disabledTools.has('keepMustache'),          active: keepMustache,          toggle: () => setKeepMustache(!keepMustache),                                                                                                                icon: keepMustacheIcon,   label: t('imageGeneration.keepMustache') },
                                    { show: dp.keepFacialMarks,       active: keepFacialMarks,       toggle: () => setKeepFacialMarks(!keepFacialMarks),                                                                                                          icon: keepFacialMarksIcon, label: t('imageGeneration.keepFacialMarks') },
                                    { show: dp.keepNosePin,           active: keepNosePin,           toggle: () => setKeepNosePin(!keepNosePin),                                                                                                                  icon: keepNosePinIcon,    label: t('imageGeneration.keepNosePin') },
                                        { show: dp.applyHair, disabled: disabledTools.has('applyHair'),             active: applyHair,             toggle: () => { if (!applyHair) { setApplyHair(true); setRemoveStrayHair(false); } else setApplyHair(false); },                                             icon: applyHairIcon,      label: t('imageGeneration.applyHair') },
                                        { show: dp.removeTupi, disabled: disabledTools.has('removeTupi'),            active: removeTupi,            toggle: () => setRemoveTupi(!removeTupi),                                                                                                                    icon: capIcon,            label: t('imageGeneration.removeTupi') },
                                        { show: dp.addTupi, disabled: disabledTools.has('addTupi'),               active: addTupi,               toggle: () => setAddTupi(!addTupi),                                                                                                                          icon: addTupiIcon,        label: t('imageGeneration.addTupi') },
                                        { show: dp.lookAtCamera,          active: lookAtCamera,          toggle: () => { if (!lookAtCamera) { setLookAtCamera(true); setStraightenHead(false); } else setLookAtCamera(false); },                                                                                                               icon: lookAtCameraIcon,   label: t('imageGeneration.lookAtCamera') },
                                        { show: dp.lipstick, disabled: disabledTools.has('lipstick'),    active: lipstick,              toggle: () => setLipstick(!lipstick),                                                                                                                        icon: lipstickIcon,       label: t('imageGeneration.lipstick') },
                                        { show: dp.showCustomInstruction, active: showExtraGuide,        toggle: () => setShowExtraGuide(!showExtraGuide),                                                                                                            icon: customWriteIcon,    label: t('imageGeneration.enableCustomInstruction') },
                                    ].filter(item => item.show).map((item, i) => (
                                        <div style={{display:"flex",flexDirection:"column",gap:4,alignItems:"center",...{ cursor: (isGenerating || (item.disabled || isToolCapBlocked(item))) ? 'not-allowed' : 'pointer', opacity: (item.disabled || isToolCapBlocked(item)) ? 0.35 : (isGenerating ? 0.6 : 1) }}} key={i}
                                            title={toolFullLabel[item.label] || item.label}
                                            onClick={() => !isGenerating && !(item.disabled || isToolCapBlocked(item)) && item.toggle()}>
                                            <div style={{ width:'100%', aspectRatio:'1', borderRadius:12, border:`2px solid ${item.active ? '#fe9a00' : '#e5e7eb'}`, boxShadow: item.active ? '0 0 0 3px #ffe4b0' : '0 1px 3px rgba(0,0,0,0.08)', backgroundColor:'var(--color-body, #ffffff)', display:'flex', alignItems:'center', justifyContent:'center', position:'relative', transition:'all 0.15s', padding:0 }}>
                                                <img src={item.icon} alt="Icon" width={40} className={styles.toolIcon} style={{ opacity: item.active ? 1 : 0.65 }} />
                                                {item.active && !item.levelKey && (
                                                    <div style={{ position:'absolute', bottom:3, right:3, width:14, height:14, borderRadius:'50%', backgroundColor:'#fe9a00', display:'flex', alignItems:'center', justifyContent:'center' }}>
                                                        <IconCheck size={8} color='white' strokeWidth={3.5} />
                                                    </div>
                                                )}
                                                {item.active && item.levelKey && (
                                                    <div style={{ position:'absolute', bottom:3, right:3, width:15, height:15, borderRadius:'50%', backgroundColor:'#fe9a00', color:'#fff', fontSize:9.5, fontWeight:800, lineHeight:1, border:'1.5px solid var(--color-body, #fff)', boxShadow:'0 0 0 1px rgba(0,0,0,0.15)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                                                        {fmtNum(levelNumberFor(item.levelKey))}
                                                    </div>
                                                )}
                                            </div>
                                            <span style={{ color: item.active ? '#fe9a00' : 'var(--mantine-color-text, #000)', lineHeight:1.2, fontSize:10, width:'100%', textAlign:'center' }}>{item.label}</span>
                                        </div>
                                    ))}
                                </div>
                                </div>
                                {/* Active-tool option panels — rendered here in DOM but shown
                                    ABOVE the grid via order-1. empty:hidden avoids a phantom gap
                                    when no option-bearing tool is active. */}
                                <div className="order-1 empty:hidden mb-1">
                                {[
                                    { key: 'glowMakeup', on: glowMakeup, label: t('imageGeneration.glowMakeup') },
                                    { key: 'smoothSkin', on: smoothSkin, label: t('imageGeneration.smoothSkin') },
                                    { key: 'fixOilySkin', on: fixOilySkin, label: t('imageGeneration.fixOilySkin') },
                                    { key: 'brightenImage', on: brightenImage, label: t('imageGeneration.brightenImage') },
                                    { key: 'studioLighting', on: studioLighting, label: t('imageGeneration.studioLighting') },
                                    { key: 'halfBody', on: halfBody, label: t('imageGeneration.halfBody'), options: HALF_BODY_LEVEL_OPTIONS, def: 'strict' },
                                ].filter(x => x.on).map(x => (
                                    <div key={x.key} className="mb-1.5 flex items-center gap-2">
                                        <span className="min-w-0 flex-1 truncate !text-[11px] font-semibold !text-primary">{x.label}</span>
                                        <div className="flex shrink-0 gap-1">
                                            {(x.options || LEVEL_OPTIONS).map((opt) => {
                                                const sel = (toolLevels[x.key] || x.def || 'medium') === opt.id;
                                                return (
                                                    <button key={opt.id} type="button" aria-pressed={sel} onClick={() => setToolLevels(p => ({ ...p, [x.key]: opt.id }))} className={`h-7 rounded-full px-3 text-[11px] font-semibold transition-colors ${sel ? 'bg-primary text-white' : 'bg-white/10 text-white/80'}`}>
                                                        {opt.label}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ))}
                                {keepNamePlate && (
                                    <div style={{marginTop:8}}>
                                        <input
                                            type="text"
                                            value={namePlateText}
                                            onChange={(e) => setNamePlateText(e.currentTarget.value.slice(0, 20))}
                                            placeholder={t('imageGeneration.namePlatePlaceholder')}
                                            disabled={isGenerating}
                                            style={{width:"100%",padding:"8px 12px",borderRadius:6,border:"1px solid #d1d5db",fontSize:13,background:"inherit",color:"inherit"}}
                                        />
                                        <span style={{fontSize:11,color:"#9ca3af"}}>{t('imageGeneration.namePlateHint')}</span>
                                    </div>
                                )}
                                {lipstick && (
                                    <div style={{marginTop:8, padding:12, borderRadius:14, border:"2px solid #fe9a00", background:"var(--color-body, #ffffff)", boxShadow:"0 8px 24px rgba(0,0,0,0.12)"}}>
                                        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
                                            <p style={{fontSize:12,fontWeight:700,color:"#fe9a00",lineHeight:1,margin:0}}>{t('imageGeneration.lipstickHint')}</p>
                                            <CheckChip active={lipGloss} disabled={isGenerating}
                                                onToggle={() => !isGenerating && setLipGloss(v => !v)}
                                                label={t('imageGeneration.lipGloss')} style={{marginLeft:"auto"}} />
                                            <button type="button" onClick={() => setLipstick(false)} style={{width:22,height:22,display:"flex",alignItems:"center",justifyContent:"center",borderRadius:"50%",background:"#f3f4f6",border:"none",color:"#9ca3af",flexShrink:0}}><IconX size={12} /></button>
                                        </div>
                                        <div style={{display:"flex",gap:12,flexWrap:"wrap"}}>
                                            {LIPSTICK_SHADES.map(shade => {
                                                const sel = lipstickShade === shade.id;
                                                return (
                                                    <button key={shade.id} type="button" title={t(shade.labelKey)} aria-label={t(shade.labelKey)} disabled={isGenerating}
                                                        onClick={() => !isGenerating && setLipstickShade(shade.id)}
                                                        style={{width:34,height:34,borderRadius:"50%",backgroundColor:shade.hex,border:"none",cursor:isGenerating?"not-allowed":"pointer",boxShadow: sel ? "0 0 0 3px #fff, 0 0 0 5px #fe9a00" : "0 0 0 1px rgba(0,0,0,0.12)",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                                                        {sel && <IconCheck size={15} color="#fff" strokeWidth={3} />}
                                                    </button>
                                                );
                                            })}
                                            <label title={t('imageGeneration.lipstickCustom')} style={{position:"relative",width:34,height:34,borderRadius:"50%",background:lipstickCustomColor,border:"none",cursor:isGenerating?"not-allowed":"pointer",boxShadow: lipstickShade==='custom' ? "0 0 0 3px #fff, 0 0 0 5px #fe9a00" : "0 0 0 1px rgba(0,0,0,0.12)",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,overflow:"hidden"}}>
                                                <IconPalette size={15} color="#fff" style={{filter:"drop-shadow(0 1px 2px rgba(0,0,0,0.5))"}} />
                                                <input type="color" value={lipstickCustomColor} disabled={isGenerating}
                                                    onChange={(e) => { setLipstickCustomColor(e.target.value); setLipstickShade('custom'); }}
                                                    style={{position:"absolute",inset:0,opacity:0,width:"100%",height:"100%",padding:0,border:"none",cursor:isGenerating?"not-allowed":"pointer"}} />
                                            </label>
                                        </div>
                                    </div>
                                )}
                                {addTupi && (
                                    <div style={{marginTop:8, padding:12, borderRadius:14, border:"2px solid #fe9a00", background:"var(--color-body, #ffffff)", boxShadow:"0 8px 24px rgba(0,0,0,0.12)"}}>
                                        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
                                            <p style={{fontSize:12,fontWeight:700,color:"#fe9a00",lineHeight:1,margin:0}}>{t('imageGeneration.tupiColorHint')}</p>
                                            <button type="button" onClick={() => setAddTupi(false)} style={{width:22,height:22,display:"flex",alignItems:"center",justifyContent:"center",borderRadius:"50%",background:"#f3f4f6",border:"none",color:"#9ca3af",flexShrink:0}}><IconX size={12} /></button>
                                        </div>
                                        <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:8}}>
                                            {dressColors.map(color => {
                                                const sel = tupiColor === color.id;
                                                return (
                                                    <div key={color.id} title={color.label} onClick={() => !isGenerating && setTupiColor(color.id)}
                                                        style={{width:"100%",aspectRatio:"1",borderRadius:"50%",background:color.hex,cursor:isGenerating?"not-allowed":"pointer",border: color.id==="white" ? "1.5px solid #e5e7eb" : "1.5px solid rgba(0,0,0,0.12)",boxShadow: sel ? "0 0 0 2.5px #fff, 0 0 0 4px #fe9a00" : "none",display:"flex",alignItems:"center",justifyContent:"center"}}>
                                                        {sel && <IconCheck size={13} color={["white","yellow","beige","gold"].includes(color.id) ? "#9ca3af" : "#fff"} strokeWidth={3} />}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                                    {showExtraGuide && (
                                    <div style={{marginTop:8}}>
                                        <textarea
                                            value={extraEditingGuide}
                                            onChange={(e) => {
                                                const raw = e.currentTarget.value;
                                                const clean = sanitizeExtraGuide(raw);
                                                if (clean !== raw) { clearTimeout(guideFilterTimerRef.current); setGuideFiltered(true); guideFilterTimerRef.current = setTimeout(() => setGuideFiltered(false), 2500); }
                                                setExtraEditingGuide(clean);
                                            }}
                                            placeholder={t('imageGeneration.extraEditingGuidePlaceholder')} maxLength={GUIDE_MAX_CHARS}
                                            rows={3}
                                            disabled={isGenerating}
                                            className={(extraEditingGuide.length > GUIDE_MAX_CHARS || extraEditingGuide.trim().split(/\s+/).filter(Boolean).length > GUIDE_MAX_WORDS) ? `${styles.textareaInput} ${styles.textareaInputError}` : styles.textareaInput}
                                            style={{width:"100%",padding:"8px 12px",borderRadius:6,border:"1px solid #d1d5db",fontSize:13,resize:"none",background:"inherit",color:"inherit"}}
                                        />
                                        {(() => {
                                            const charOver = extraEditingGuide.length > GUIDE_MAX_CHARS;
                                            const wordCount = extraEditingGuide.trim().split(/\s+/).filter(Boolean).length;
                                            const wordOver = wordCount > GUIDE_MAX_WORDS;
                                            if (charOver || wordOver) {
                                                return <span>{charOver ? t('imageGeneration.guideMaxChars', { max: fmtNum(GUIDE_MAX_CHARS), count: fmtNum(extraEditingGuide.length) }) : t('imageGeneration.guideMaxWords', { max: fmtNum(GUIDE_MAX_WORDS), count: fmtNum(wordCount) })}</span>;
                                            }
                                            if (guideFiltered) { return <span>{t('imageGeneration.guideLimits', { chars: fmtNum(GUIDE_MAX_CHARS), words: fmtNum(GUIDE_MAX_WORDS) })}</span>; }
                                            return <span>{t('imageGeneration.extraEditingGuideHint')}</span>;
                                        })()}
                                    </div>
                                )}
                                </div>{/* end order-1 option panels */}
                            </div>
                    </div>
                )}

                {isMobile && !showCropModal && !isGenerating && activeDrawer === 'recent' && (
                    <div data-mantine-color-scheme="dark" className={styles.mobileDock}>
                        <div className={styles.dockHead}>
                            <h2 className="min-w-0 flex-1 truncate text-sm font-bold text-white">{t('imageGeneration.recentGenerations')}</h2>
                            <Link to="/documents?tab=images" className="inline-flex h-7 shrink-0 items-center gap-1 rounded-md bg-white/10 px-2 text-[11px] font-semibold text-white">
                                <IconExternalLink size={13} />
                                {t('imageGeneration.viewAllImages')}
                            </Link>
                            <button type="button" aria-label={t('common.close')} onClick={() => setActiveDrawer(null)} className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-white/80"><IconChevronDown size={16} /></button>
                        </div>
                        {/* One swipeable row: running background edits first, older photos load as it nears its end */}
                        <div
                            ref={dockRecentRef}
                            onScroll={(e) => {
                                const el = e.currentTarget;
                                if (hasMoreHistory && !isLoadingHistory && el.scrollLeft + el.clientWidth >= el.scrollWidth - 150) loadHistory(historyPage + 1, true);
                            }}
                            className="flex gap-1.5 overflow-x-auto [scrollbar-width:none]"
                        >
                            {bgJobs.map((job) => (
                                <div
                                    key={`bg-${job.token}`}
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => { if (job.status === 'processing' && !job.restored) { setActiveDrawer(null); bringBgJobToForeground(job); } }}
                                    className="relative h-[92px] w-[69px] shrink-0 overflow-hidden rounded-lg bg-zinc-800"
                                >
                                    {job.previewUrl && <img src={job.previewUrl} alt="" draggable="false" className="h-full w-full object-cover opacity-60" />}
                                    {job.status === 'processing' ? (
                                        <span className="absolute inset-0 flex items-center justify-center bg-black/35 text-white"><IconLoader2 size={18} className="animate-spin" /></span>
                                    ) : (
                                        <span className="absolute inset-0 flex items-center justify-center bg-red-600/60">
                                            <button type="button" aria-label={t('common.close')} onClick={(e) => { e.stopPropagation(); setBgJobs((prev) => prev.filter((j) => j.token !== job.token)); }} className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-black/55 text-white"><IconX size={12} /></button>
                                            <span className="!text-[10px] font-semibold !text-white">{t('imageGeneration.failed')}</span>
                                        </span>
                                    )}
                                </div>
                            ))}
                            {imageHistory.map((image) => (
                                <button
                                    key={image.id}
                                    type="button"
                                    onClick={() => handleHistoryImageClick(image)}
                                    className="h-[92px] w-[69px] shrink-0 overflow-hidden rounded-lg border border-zinc-700 bg-zinc-800"
                                    style={thumbBgStyle(image)}
                                >
                                    <img src={image.aiImage} alt="" loading="lazy" draggable="false" className="h-full w-full object-cover" />
                                </button>
                            ))}
                            {hasMoreHistory && (
                                <span className="flex h-[92px] w-10 shrink-0 items-center justify-center text-white/50">
                                    {isLoadingHistory && <IconLoader2 size={18} className="animate-spin" />}
                                </span>
                            )}
                        </div>
                    </div>
                )}

                {/* Right Sidebar — before a result the QR photo inbox; with a result the Studio-style action column (an icon strip with
                    tooltips below `xl`, labelled from there). On a tablet it only shows with a result. */}
                <div className={`${styles.rightSidebar} ${resultMode ? '!p-1.5 xl:!p-3' : ''}`} style={isTabletView && !resultMode ? { display: 'none' } : undefined}>
                    {/* One-time QR upload — hidden when the permanent customer inbox is ON
                        (only one QR card at a time, so users aren't confused). */}
                    {!showQrInUploadArea && pcResolved && !pcEnabled && !resultMode && (
                        <div className={styles.qrCard} style={{
                            borderRadius: 16,
                            background: 'linear-gradient(135deg, #1a1f3a 0%, #1a1f3a 60%, #1a1f3a 100%)',
                            padding: '18px 16px 16px',
                            boxShadow: '0 8px 24px rgba(25,113,194,0.35)',
                            position: 'relative',
                            overflow: 'hidden',
                        }}>
                            <div style={{ position: 'absolute', top: -20, right: -20, width: 80, height: 80, borderRadius: '50%', background: 'rgba(255,255,255,0.07)', pointerEvents: 'none' }} />
                            <div style={{ position: 'absolute', bottom: -12, left: -12, width: 56, height: 56, borderRadius: '50%', background: 'rgba(255,255,255,0.05)', pointerEvents: 'none' }} />
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                                <div style={{ width: 44, height: 44, borderRadius: 12, background: 'rgba(255,255,255,0.22)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, border: '1px solid rgba(255,255,255,0.2)' }}>
                                    <IconQrcode size={24} color="#fff" />
                                </div>
                                <div>
                                    <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#fff', lineHeight: 1.3 }}>{t('imageGeneration.qrUploadButton')}</p>
                                    <p style={{ margin: 0, fontSize: 11, color: 'rgba(255,255,255,0.7)', lineHeight: 1.4, marginTop: 2 }}>{t('imageGeneration.qrUploadLabel')}</p>
                                </div>
                            </div>
                            <button
                                disabled={isGenerating || !!file || !!generatedImage || !!(mergeLeft || mergeRight)}
                                onClick={openQrModal}
                                style={{ width: '100%', padding: '9px 16px', borderRadius: 10, border: '1.5px solid rgba(255,255,255,0.35)', background: 'rgba(255,255,255,0.22)', color: '#fff', fontWeight: 700, fontSize: 13, cursor: (isGenerating || !!file || !!generatedImage || !!(mergeLeft || mergeRight)) ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, transition: 'background 0.2s ease, border-color 0.2s ease', opacity: (isGenerating || !!file || !!generatedImage || !!(mergeLeft || mergeRight)) ? 0.45 : 1 }}
                                onMouseEnter={e => { if (!(isGenerating || !!file || !!generatedImage || !!(mergeLeft || mergeRight))) { e.currentTarget.style.background = 'rgba(255,255,255,0.25)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.55)'; } }}
                                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.15)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.35)'; }}
                            >
                                <IconNfc size={15} />
                                {t('imageGeneration.qrScanButton')}
                            </button>
                        </div>
                    )}

                    {/* Permanent QR customer photo inbox — shown only when it's ON
                        (replaces the one-time QR card above) and no generated result
                        is open (the edit panel takes over the sidebar then). */}
                    {selectedOfficeSpace?.id && pcEnabled && !generatedImage && (
                        <div>
                            <PhotoCollectorPanel
                                officeSpaceId={selectedOfficeSpace.id}
                                shopName={selectedOfficeSpace?.businessProfile?.shopName || selectedOfficeSpace?.name}
                                shopLogo={selectedOfficeSpace?.businessProfile?.shopLogo}
                                shopPhone={selectedOfficeSpace?.businessProfile?.shopPhone}
                                shopAddress={selectedOfficeSpace?.businessProfile?.businessAddress}
                                onPick={loadQueuePhoto}
                                refreshKey={pcRefreshKey}
                                onCount={handlePcCount}
                            />
                        </div>
                    )}

                    {resultMode && (
                        <ResultActions
                            compact={compactActions}
                            onCrop={openResultCrop}
                            cropLoading={resultCropLoading}
                            cropDisabled={!generatedImageId}
                            onCustom={openCustomDownload}
                            customLoading={customDownloadLoading}
                            onCopy={handleCopyResultImage}
                            copying={copyingResult}
                            editOpen={editorOpen}
                            edited={hasAdjustmentsChanged}
                            onToggleEdit={toggleEditor}
                            infoContent={imageInfoBody}
                            onDownload={handleDownload}
                            downloading={downloading}
                            onPrint={printEdited}
                        />
                    )}

                </div>
                {isTabletView && recentPhotos}
            </div>

            {!isTabletView && recentPhotos}

            {/* Bottom clearance so the fixed tablet action bar never hides the last content. */}

            {/* Image Preview Modal */}
            <ImagePreviewModal
                opened={showImageModal}
                onClose={() => setShowImageModal(false)}
                images={imageHistory}
                initialImage={selectedHistoryImage}
                initialViewMode={imageModalCompare ? 'side-by-side' : 'single'}
                hasMore={hasMoreHistory}
                isLoadingMore={isLoadingHistory}
                onLoadMore={() => loadHistory(historyPage + 1, true)}
                isLoadingEdit={isLoadingEdit}
                onEdit={handleEditImage}
                onImageDeleted={(deletedId) => {
                    setImageHistory(prev => prev.filter(img => img.id !== deletedId));
                    setShowImageModal(false);
                }}
                onPrint={(imageUrl, imageId) => openPrintModal(imageUrl, imageId)}
                onCustomDownload={(imageUrl, imageId) => {
                    setCustomDownloadImageUrl(imageUrl);
                    setCustomDownloadImageId(imageId);
                    setShowCustomDownload(true);
                }}
            />
            {/* QR Upload Modal */}
            <QrUploadModal
                open={showQrModal}
                onClose={closeQrModal}
                status={qrStatus}
                qrDataUrl={qrDataUrl}
                mobileUrl={qrMobileUrl}
                timeLeft={qrTimeLeft}
                onRefresh={openQrModal}
            />

            {/* Custom Download Modal */}
            {showCustomDownload && !customInline && (
                <CustomDownloadModal
                    opened={showCustomDownload}
                    onClose={closeCustomDownload}
                    imageUrl={customDownloadImageUrl}
                    imageId={customDownloadImageId}
                />
            )}

            {/* Crop generated image — SAME full-screen CropStage as the upload crop, with
                ratio presets; full-res output overwrites the stored R2 image in place. */}
            {showResultCrop && resultCropSrc && (() => {
                const m = /(\d+)\s*×\s*(\d+)/.exec(imageMetadata?.dimensions || '');
                const docAspect = m ? (parseInt(m[1], 10) / parseInt(m[2], 10)) : undefined;
                const resultRatios = [
                    ...(docAspect ? [{ id: 'doc', label: t('imageGeneration.cropRatioDoc'), value: docAspect }] : []),
                    { id: 'free', label: t('imageGeneration.cropRatioFree'), value: undefined },
                    { id: '1:1', label: '1:1', value: 1 },
                    { id: '3:4', label: '3:4', value: 3 / 4 },
                    { id: '4:5', label: '4:5', value: 4 / 5 },
                ];
                return (
                    <Modal
                        opened
                        onClose={closeResultCrop}
                        title={null}
                        centered
                        fullScreen
                        styles={{
                            header: { display: 'none' },
                            body: { padding: 0, display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 },
                            inner: { padding: 0 },
                            content: { display: 'flex', flexDirection: 'column', height: '100%', background: 'var(--color-body)' },
                        }}
                    >
                        <CropStage
                            imageSrc={resultCropSrc}
                            isMobile={isMobile}
                            compact={isTabletView}
                            t={t}
                            title={t('imageGeneration.cropImageBtn')}
                            primaryLabel={t('imageGeneration.cropApplySave')}
                            primaryLoadingLabel={t('imageGeneration.cropSaving')}
                            hint={t('imageGeneration.cropWarning')}
                            ratios={resultRatios}
                            defaultRatioId={docAspect ? 'doc' : 'free'}
                            outputMode="blobFull"
                            mimeOut={resultCropMime}
                            onApply={handleResultCropApply}
                            onClose={closeResultCrop}
                            onError={closeResultCrop}
                        />
                    </Modal>
                );
            })()}

            {/* Delete Confirmation Modal */}
            <Modal
                opened={showDeleteConfirm}
                onClose={() => setShowDeleteConfirm(false)}
                title={t('imageGeneration.deleteImage')}
                centered
                size="sm"
            >
                <span>
                    {t('imageGeneration.deleteConfirmation')}
                </span>
                <div style={{display:"flex",alignItems:"center",gap:8,justifyContent:"flex-end"}}>
                    <button
                        className="inline-flex items-center gap-1.5 font-semibold px-4 py-2 text-sm rounded-md transition-colors disabled:opacity-50"
                        style={{background:"#e68a0022",color:"#e68a00"}}
                        onClick={() => setShowDeleteConfirm(false)}
                    >
                        {t('common.cancel')}
                    </button>
                    <button
                        className="inline-flex items-center gap-1.5 font-semibold px-4 py-2 text-sm rounded-md transition-colors disabled:opacity-50"
                        style={{background:"#dc2626",color:"white"}}
                        onClick={handleDeleteImage}
                        disabled={deletingImageId === selectedHistoryImage?.id}
                    >
                        {(deletingImageId === selectedHistoryImage?.id) && <span className="inline-block w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />}
                        {t('common.delete')}
                    </button>
                </div>
            </Modal>

            {/* Zoom Modal removed — eye button now opens image preview modal */}
            {false && (() => {
                const _sidebarW = isMobile ? 0 : 260;
                const _headerH = isMobile ? 50 : 56;
                const _vpW = typeof window !== 'undefined' ? window.innerWidth : 1200;
                const _vpH = typeof window !== 'undefined' ? window.innerHeight : 900;
                const _imgAspect = zoomImgNaturalSize.h / Math.max(zoomImgNaturalSize.w, 1);
                const _maxPanelW = isMobile ? _vpW / 2 : Math.min((_vpW * 0.92 - _sidebarW) / 2, 500);
                const _maxPanelH = isMobile ? _vpH * 0.45 : Math.min(_vpH * 0.88 - _headerH, 800);
                const _panelHFromW = _maxPanelW * _imgAspect;
                const _panelH = Math.min(_panelHFromW, _maxPanelH);
                const _panelW = _panelH / _imgAspect;
                const _modalW = isMobile ? '100vw' : `${Math.round(_panelW * 2 + _sidebarW)}px`;
                const _modalH = isMobile ? '100vh' : `${Math.round(_panelH + _headerH)}px`;
                return (
            <Modal
                opened={showZoomModal}
                onClose={() => setShowZoomModal(false)}
                size={isMobile ? '100%' : 'auto'}
                title={isMobile ? undefined : t('imageGeneration.zoomView')}
                withCloseButton={!isMobile}
                styles={{
                    content: {
                        width: _modalW,
                        height: _modalH,
                        margin: isMobile ? 0 : undefined,
                        display: 'flex',
                        flexDirection: 'column',
                        borderRadius: isMobile ? 0 : '16px',
                        overflow: 'hidden',
                        backgroundColor: 'var(--color-body, #ffffff)',
                    },
                    body: {
                        padding: 0,
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: isMobile ? 'column' : 'row',
                        flex: 1,
                        minHeight: 0,
                    },
                    header: {
                        padding: '12px 20px',
                        flexShrink: 0,
                        borderBottom: '1px solid #e5e7eb',
                        backgroundColor: 'var(--color-body, #ffffff)',
                        color: '#000',
                    }
                }}
            >
                {/* Mobile custom header — back + title + share */}
                {isMobile && (
                    <div style={{ display:'flex', alignItems:'center', gap:10, padding:'12px 16px', borderBottom:'1px solid #e5e7eb', background:'var(--color-body,#fff)', flexShrink:0 }}>
                        <button onClick={() => setShowZoomModal(false)} style={{ display:'flex', alignItems:'center', justifyContent:'center', width:36, height:36, borderRadius:'50%', border:'none', background:'#f4f4f5', cursor:'pointer', flexShrink:0 }}>
                            <IconChevronLeft size={20} color="#18181b" />
                        </button>
                        <span style={{ flex:1, fontWeight:700, fontSize:15, color:'var(--color-text,#18181b)', textAlign:'center' }}>{t('imageGeneration.zoomView')}</span>
                        <button
                            onClick={async () => {
                                // Share the actual image, never its URL. Eraser edits live in a
                                // local blob URL (direct fetch); otherwise the backend proxy.
                                try {
                                    const getBlob = () => (erasedImageUrl
                                        ? fetch(erasedImageUrl).then((r) => r.blob())
                                        : imageGenerationService.downloadImage(generatedImageId, 'ai'));
                                    const result = await shareImageToTarget(getBlob, 'native', { title: 'SohozKaj' });
                                    markPrivateResultDelivered(generatedImageId);
                                    if (result === 'copied') {
                                        notifications.show({ title: t('imageGeneration.imageCopied'), message: t('imageGeneration.imageCopiedPasteHint'), color: 'green', autoClose: 7000 });
                                    }
                                } catch (err) {
                                    if (err?.name !== 'AbortError') {
                                        notifications.show({ title: t('common.error'), message: t('documents.shareFailed'), color: 'red' });
                                    }
                                }
                            }}
                            style={{ display:'flex', alignItems:'center', justifyContent:'center', width:36, height:36, borderRadius:'50%', border:'none', background:'#f4f4f5', cursor:'pointer', flexShrink:0 }}
                        >
                            <IconShare size={18} color="#18181b" />
                        </button>
                    </div>
                )}

                {/* Side-by-side: Original | AI Result */}
                <div style={{ flex: 1, display: 'flex', flexDirection: isMobile ? 'column' : 'row', overflow: 'hidden', backgroundColor: 'var(--color-surface, #f3f4f6)' }}>

                    {/* Left — Original uploaded image with zoom + drag */}
                    <div
                        style={{ flex: 1, position: 'relative', overflow: 'hidden', borderRight: isMobile ? 'none' : '1px solid #e5e7eb', borderBottom: isMobile ? '1px solid #e5e7eb' : 'none', cursor: zoomOrig > 1 ? 'grab' : 'default' }}
                        onPointerDown={(e) => { if (zoomOrig > 1) { e.currentTarget.setPointerCapture(e.pointerId); handleZoomPanStart('orig', e); } }}
                        onPointerMove={(e) => handleZoomPanMove('orig', e, zoomOrig)}
                        onPointerUp={handleZoomPanEnd}
                        onPointerCancel={handleZoomPanEnd}
                        onTouchStart={(e) => { if (zoomOrig > 1) handleZoomPanStart('orig', e); }}
                        onTouchMove={(e) => handleZoomPanMove('orig', e, zoomOrig)}
                        onTouchEnd={handleZoomPanEnd}
                    >
                        <img
                            src={previewUrl}
                            alt="Original"
                            draggable={false}
                            style={{
                                position: 'absolute', top: '50%', left: '50%',
                                width: '100%', height: '100%',
                                objectFit: 'contain',
                                transform: `translate(calc(-50% + ${panOrig.x}px), calc(-50% + ${panOrig.y}px)) scale(${zoomOrig})`,
                                transformOrigin: 'center center',
                                transition: dragRef.current?.panel === 'orig' ? 'none' : 'transform 0.15s ease',
                                userSelect: 'none', pointerEvents: 'none',
                            }}
                        />
                        <div style={{ position: 'absolute', top: 10, left: '50%', transform: 'translateX(-50%)', background: 'rgba(0,0,0,0.6)', borderRadius: 6, padding: '3px 10px', zIndex: 2, whiteSpace: 'nowrap' }}>
                            <span>{t('imageGeneration.beforeLabel') || 'Original'}</span>
                        </div>
                        <ZoomControls zoom={zoomOrig} onIn={zoomBothIn} onOut={zoomBothOut} onReset={zoomBothReset} />
                    </div>

                    {/* Right — Generated image with zoom + drag */}
                    <div
                        style={{ flex: 1, position: 'relative', overflow: 'hidden', cursor: zoomGen > 1 ? 'grab' : 'default', ...(resultIsTransparent ? (currentBgColor ? { background: currentBgColor } : CHECKERBOARD_STYLE) : {}) }}
                        onPointerDown={(e) => { if (zoomGen > 1) { e.currentTarget.setPointerCapture(e.pointerId); handleZoomPanStart('gen', e); } }}
                        onPointerMove={(e) => handleZoomPanMove('gen', e, zoomGen)}
                        onPointerUp={handleZoomPanEnd}
                        onPointerCancel={handleZoomPanEnd}
                        onTouchStart={(e) => { if (zoomGen > 1) handleZoomPanStart('gen', e); }}
                        onTouchMove={(e) => handleZoomPanMove('gen', e, zoomGen)}
                        onTouchEnd={handleZoomPanEnd}
                    >
                        <img
                            src={resultDisplayUrl}
                            alt="AI Result"
                            draggable={false}
                            style={{
                                position: 'absolute', top: '50%', left: '50%',
                                width: '100%', height: '100%',
                                objectFit: 'contain',
                                transform: `translate(calc(-50% + ${panGen.x}px), calc(-50% + ${panGen.y}px)) scale(${zoomGen})`,
                                transformOrigin: 'center center',
                                transition: dragRef.current?.panel === 'gen' ? 'none' : 'transform 0.15s ease',
                                userSelect: 'none', pointerEvents: 'none',
                            }}
                        />
                        <div style={{ position: 'absolute', top: 10, left: '50%', transform: 'translateX(-50%)', background: 'rgba(0,0,0,0.6)', borderRadius: 6, padding: '3px 10px', zIndex: 2, whiteSpace: 'nowrap' }}>
                            <span>{t('imageGeneration.afterLabel') || 'AI Result'}</span>
                        </div>
                        <ZoomControls zoom={zoomGen} onIn={zoomBothIn} onOut={zoomBothOut} onReset={zoomBothReset} />
                    </div>
                </div>

                {/* Adjustment Controls Panel */}
                <div style={{
                    width: isMobile ? '100%' : '260px',
                    flexShrink: 0,
                    backgroundColor: 'var(--color-body, #ffffff)',
                    display: 'flex',
                    flexDirection: 'column',
                    borderLeft: '1px solid #e5e7eb',
                    overflow: 'hidden',
                }}>
                    {/* The edits are made in the photo's edit drawer (ছবি এডিটিং) — shown here as they will be printed */}
                    <div style={{ flex: 1 }} />

                    {/* Fixed bottom buttons */}
                    <div style={{ flexShrink: 0, padding: '10px 16px', borderTop: '1px solid #e5e7eb', display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {/* Reset Adjustments */}
                        <button
                            className="inline-flex items-center gap-1.5 font-semibold px-4 py-2 text-sm rounded-md transition-colors disabled:opacity-50 w-full justify-center"
                            style={{background:"#2563eb22",color:"#2563eb"}}
                            disabled={!hasAdjustmentsChanged}
                            onClick={() => setEdits(NEUTRAL_EDITS)}
                        >
                            {t('imageGeneration.resetAdjustments')}
                        </button>

                        {/* Print Photo */}
                        <button
                            className="inline-flex items-center gap-1.5 font-semibold px-4 py-2 text-sm rounded-md transition-colors disabled:opacity-50 w-full justify-center"
                            style={{background:"#0891b2",color:"white"}}
                            onClick={async () => {
                                const editedUrl = await getEditedImageUrl();
                                setPrintImageUrl(editedUrl || generatedImage);
                                setPrintImageId(generatedImageId);
                                setShowPrintModal(true);
                                setShowZoomModal(false);
                            }}
                        >
                            <IconPrinter size={18} />
                            {t('common.print') || 'প্রিন্ট করুন'}
                        </button>
                    </div>
                </div>
            </Modal>
                );
            })()}

            {/* Limit Reached Modal — hidden */}

            {/* Print Modal — conditional render ensures fresh state on every open */}
            {showPrintModal && (
                <PrintModal
                    opened={showPrintModal}
                    onClose={() => setShowPrintModal(false)}
                    imageUrl={printImageUrl || generatedImage}
                    imageId={printImageId}
                    isDualImage={isMergedImage}
                    imageHistory={imageHistory}
                />
            )}

            {/* No credit modal */}
            <LimitReachedModal
                opened={noCreditModalOpen}
                onClose={() => setNoCreditModalOpen(false)}
                isOperator={isOperator}
            />

            {/* Eraser / image editing modal */}
            <EraserModal
                open={eraserModalOpen}
                imageUrl={erasedImageUrl || generatedImage}
                exportW={600}
                exportH={600}
                bgColor={bg === 'custom' ? customColor : (backgrounds.find(b => b.id === bg)?.color || '#ffffff')}
                onClose={() => setEraserModalOpen(false)}
                onApply={(newUrl) => {
                    setErasedImageUrl(newUrl);
                    setEraserModalOpen(false);
                }}
            />

            {/* Crop / Rotate modal — full-screen editor (light + dark) */}
            <Modal
                opened={showCropModal}
                onClose={closeCropModal}
                title={null}
                centered
                fullScreen
                styles={{
                    // CropStage renders its own header + footer; hide the wrapper chrome.
                    header: { display: 'none' },
                    body: { padding: 0, display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 },
                    inner: { padding: 0 },
                    content: { display: 'flex', flexDirection: 'column', height: '100%', background: 'var(--color-body)' },
                }}
            >
                <CropStage
                    ref={cropStageRef}
                    imageSrc={cropImageSrc}
                    fileName={pendingCropFile?.name}
                    isMobile={isMobile}
                    compact={isTabletView}
                    t={t}
                    aspect={cropAspect.ratio}
                    outWidth={cropAspect.outW}
                    outHeight={cropAspect.outH}
                    onClose={closeCropModal}
                    onApply={handleCropApply}
                    onSkip={skipCropAndProcess}
                    onError={handleCropError}
                />
            </Modal>

            {/* ── Mobile bottom navigation bar + Sheets ──────────────────── */}
            {isMobile && !showCropModal && (
                <>
                    {/* Nav bar — same design as site-wide bottom nav */}
                    <nav className="fixed bottom-0 left-0 right-0 z-40 bg-white/95 dark:bg-zinc-900/95 backdrop-blur-xl border-t border-zinc-200 dark:border-zinc-700 shadow-[0_-4px_24px_rgba(0,0,0,0.12)]">
                        <div className="grid grid-cols-5 items-center px-1 pt-1 pb-2">

                            {/* Photo Size */}
                            <button onClick={() => setActiveDrawer(activeDrawer === 'photo' ? null : 'photo')}
                                className={`relative flex flex-col items-center gap-1 py-0.5 px-0.5 transition-colors ${activeDrawer === 'photo' ? 'text-orange-500' : 'text-zinc-400 dark:text-zinc-500'}`}>
                                {size !== 'bd_passport' && <span className={styles.navCheck}><IconCheck size={7} color="white" strokeWidth={3.5} /></span>}
                                <NavIcon Icon={IconAspectRatio} className="text-sky-500" active={activeDrawer === 'photo'} />
                                <span className="text-[9px] font-bold leading-none">{t('imageGeneration.photoSize')}</span>
                            </button>

                            {/* Dress + Background */}
                            <button onClick={() => setActiveDrawer(activeDrawer === 'style' ? null : 'style')}
                                className={`relative flex flex-col items-center gap-1 py-0.5 px-0.5 transition-colors ${activeDrawer === 'style' ? 'text-orange-500' : 'text-zinc-400 dark:text-zinc-500'}`}>
                                {(dress !== 'original' || bg !== 'white') && <span className={styles.navCheck}><IconCheck size={7} color="white" strokeWidth={3.5} /></span>}
                                <NavIcon Icon={IconShirt} className="text-pink-500" active={activeDrawer === 'style'} />
                                <span className="whitespace-nowrap text-[9px] font-bold leading-none">{t('imageGeneration.navDressBg')}</span>
                            </button>

                            {/* Generate FAB */}
                            <div className="flex justify-center">
                                <button
                                    onClick={handleGenerate}
                                    disabled={isGenerating || (showExtraGuide && (extraEditingGuide.length > 150 || extraEditingGuide.trim().split(/\s+/).filter(Boolean).length > GUIDE_MAX_WORDS)) || (generatedImage ? !settingsChanged : (!size || !dress || !bg))}
                                    className={`w-14 h-14 -mt-5 rounded-full flex items-center justify-center text-white disabled:opacity-50 border-[3px] border-white dark:border-zinc-900 ${previewUrl ? 'bg-[linear-gradient(135deg,#FBA53A_0%,#F4791A_52%,#EB5E10_100%)] shadow-[0_8px_28px_-6px_rgba(244,123,22,0.7)]' : 'bg-zinc-400 dark:bg-zinc-600 shadow-[0_4px_12px_-4px_rgba(0,0,0,0.3)]'}`}
                                >
                                    {isGenerating
                                        ? <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                        : generatedImage
                                            ? <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M4 12a8 8 0 018-8v2M20 12a8 8 0 01-8 8v-2" stroke="white" strokeWidth="2.2" strokeLinecap="round"/><path d="M12 4l-2 2 2 2M12 20l2-2-2-2" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                            : <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M12 2l1.8 5.5L19 9l-5.2 1.8L12 16l-1.8-5.2L5 9l5.2-1.5L12 2z" fill="white" stroke="white" strokeWidth="0.5" strokeLinejoin="round"/><path d="M19 15l.9 2.6L22.5 19l-2.6.9L19 22.5l-.9-2.6L16 19l2.6-.9L19 15z" fill="white" stroke="white" strokeWidth="0.3" strokeLinejoin="round"/></svg>}
                                </button>
                            </div>

                            {/* Extra Tools */}
                            <button onClick={() => setActiveDrawer(activeDrawer === 'extra' ? null : 'extra')}
                                className={`relative flex flex-col items-center gap-1 py-0.5 px-0.5 transition-colors ${activeDrawer === 'extra' ? 'text-orange-500' : 'text-zinc-400 dark:text-zinc-500'}`}>
                                {anyToolOn && <span className={styles.navCheck}><IconCheck size={7} color="white" strokeWidth={3.5} /></span>}
                                <NavIcon Icon={IconWand} className="text-violet-500" active={activeDrawer === 'extra'} />
                                <span className="text-[9px] font-bold leading-none">নির্দেশনা</span>
                            </button>

                            {/* Adjust / Settings */}
                            <button
                                disabled={!generatedImage} // nothing to set until there is a result
                                onClick={() => {
                                    if (customInline) closeCustomDownload(); // back to the result and its actions
                                    else if (dockOpen) { setActiveDrawer(null); setResultActionsOpen(true); }
                                    else setResultActionsOpen((o) => !o);
                                }}
                                className={`relative flex flex-col items-center gap-1 py-0.5 px-0.5 ${generatedImage && resultActionsOpen && !dockOpen ? 'text-orange-500' : 'text-zinc-400 dark:text-zinc-500'}`}>
                                {generatedImage && hasAdjustmentsChanged && <span className={styles.navCheck}><IconCheck size={7} color="white" strokeWidth={3.5} /></span>}
                                <NavIcon Icon={IconAdjustmentsHorizontal} className="text-emerald-500" active={!!generatedImage && resultActionsOpen && !dockOpen} />
                                <span className="text-[9px] font-bold leading-none">সেটিং</span>
                            </button>
                        </div>
                    </nav>

                    {/* ── Recent photos — the recent strip is hidden on a phone: a pill above the tabs (only before a result, where the
                        action bar takes that spot) opens them as a dock under the photo. ── */}
                    {(imageHistory.length > 0 || bgJobs.length > 0) && !generatedImage && !isGenerating && activeDrawer === null && (
                        <button
                            type="button"
                            onClick={() => setActiveDrawer('recent')}
                            className="fixed left-3 z-40 inline-flex items-center gap-2 rounded-full border border-zinc-200 bg-white/95 py-1 pl-1 pr-3 shadow-[0_6px_20px_-4px_rgba(0,0,0,0.25)] backdrop-blur-xl dark:border-zinc-700 dark:bg-zinc-800/95"
                            style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 74px)' }}
                        >
                            <span className="relative h-7 w-7 flex-none overflow-hidden rounded-full bg-zinc-200 dark:bg-zinc-700" style={bgJobs[0] || !imageHistory[0] ? undefined : thumbBgStyle(imageHistory[0])}>
                                {(bgJobs[0]?.previewUrl || imageHistory[0]?.aiImage) && <img src={bgJobs[0]?.previewUrl || imageHistory[0].aiImage} alt="" className="h-full w-full object-cover" />}
                                {bgJobs.some((job) => job.status === 'processing') && (
                                    <span className="absolute inset-0 flex items-center justify-center bg-black/45 text-white"><IconLoader2 size={13} className="animate-spin" /></span>
                                )}
                            </span>
                            <span className="text-[12px] font-bold leading-none text-zinc-800 dark:text-zinc-100">{t('imageGeneration.recentPhotos')}</span>
                        </button>
                    )}

                    {/* ── Photo Booth Sheet ── */}
                    <Sheet open={activeDrawer === 'booth'} onOpenChange={(o) => { if (!o) setActiveDrawer(null); }}>
                        <SheetContent side="bottom" className="p-0 rounded-t-[28px] z-[99999] max-h-[88vh] overflow-hidden flex flex-col bg-zinc-100 dark:bg-zinc-900" showClose={false}>
                            <SheetTitle className="sr-only">{t('photoCollectorPanel.title')}</SheetTitle>
                            <div className="flex items-center justify-between px-4 pt-3 pb-2.5">
                                <div>
                                    <h2 className="text-base font-bold text-zinc-900 dark:text-zinc-100">{t('photoCollectorPanel.title')}</h2>
                                    <p className="text-[11px] text-zinc-500 mt-0.5">{t('photoCollectorPanel.tapToLoad')}</p>
                                </div>
                                <button onClick={() => setActiveDrawer(null)} className="w-8 h-8 flex items-center justify-center rounded-xl bg-white/70 dark:bg-zinc-700 text-zinc-500"><IconX size={15} /></button>
                            </div>
                            <div className="overflow-y-auto px-3 pb-8" style={{ WebkitOverflowScrolling: 'touch' }}>
                                <div className="bg-white dark:bg-zinc-800 rounded-2xl shadow-[0_1px_3px_rgba(0,0,0,0.07)] p-3">
                                    <PhotoCollectorPanel
                                        officeSpaceId={selectedOfficeSpace?.id}
                                        shopName={selectedOfficeSpace?.businessProfile?.shopName || selectedOfficeSpace?.name}
                                        shopLogo={selectedOfficeSpace?.businessProfile?.shopLogo}
                                        shopPhone={selectedOfficeSpace?.businessProfile?.shopPhone}
                                        shopAddress={selectedOfficeSpace?.businessProfile?.businessAddress}
                                        onPick={async (id, item) => { await loadQueuePhoto(id, item); setActiveDrawer(null); }}
                                        refreshKey={pcRefreshKey}
                                    />
                                </div>
                            </div>
                        </SheetContent>
                    </Sheet>

                    {/* ── Photo Size Sheet ── */}
                </>
            )}

            {/* ── Mobile upload choice Sheet (Gallery vs Camera) ── */}
            {isMobile && (
                <Sheet open={showUploadSheet} onOpenChange={(o) => { if (!o) setShowUploadSheet(false); }}>
                    <SheetContent side="bottom" className="p-0 rounded-t-[28px] z-[99999] overflow-hidden flex flex-col bg-zinc-100 dark:bg-zinc-900" showClose={false}>
                        <SheetTitle className="sr-only">Upload Photo</SheetTitle>
                                                <div className="flex items-center justify-between px-4 pt-3 pb-2">
                            <div>
                                <h2 className="text-base font-bold text-zinc-900 dark:text-zinc-100">{t('imageGeneration.uploadChoiceTitle')}</h2>
                                <p className="text-[11px] text-zinc-500 dark:text-zinc-400 mt-0.5">{t('imageGeneration.uploadChoiceSubtitle')}</p>
                            </div>
                            <button onClick={() => setShowUploadSheet(false)} className="w-8 h-8 flex items-center justify-center rounded-xl bg-white/70 dark:bg-zinc-700 text-zinc-500 border-none cursor-pointer">
                                <IconX size={15} />
                            </button>
                        </div>
                        <div className="px-3 pb-6 flex flex-col gap-3">
                            {/* Gallery option */}
                            <button
                                onClick={() => { setShowUploadSheet(false); setTimeout(() => fileInputRef.current?.click(), 100); }}
                                className="flex items-center gap-4 w-full px-4 py-4 rounded-2xl bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 text-left cursor-pointer"
                                style={{ border: 'none' }}
                            >
                                <div className="w-12 h-12 rounded-xl bg-orange-50 dark:bg-orange-900/20 flex items-center justify-center shrink-0">
                                    <IconPhotoUp size={24} className="text-orange-500" />
                                </div>
                                <div>
                                    <p className="text-sm font-bold text-zinc-900 dark:text-zinc-100 m-0">{t('imageGeneration.uploadFromGallery')}</p>
                                    <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5 m-0">{t('imageGeneration.uploadFromGalleryHint')}</p>
                                </div>
                            </button>

                            {/* Camera option */}
                            <button
                                onClick={() => { setShowUploadSheet(false); setTimeout(() => cameraInputRef.current?.click(), 100); }}
                                className="flex items-center gap-4 w-full px-4 py-4 rounded-2xl bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 text-left cursor-pointer"
                                style={{ border: 'none' }}
                            >
                                <div className="w-12 h-12 rounded-xl bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center shrink-0">
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="text-blue-500" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z"/>
                                        <circle cx="12" cy="13" r="3"/>
                                    </svg>
                                </div>
                                <div>
                                    <p className="text-sm font-bold text-zinc-900 dark:text-zinc-100 m-0">{t('imageGeneration.uploadFromCamera')}</p>
                                    <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5 m-0">{t('imageGeneration.uploadFromCameraHint')}</p>
                                </div>
                            </button>
                        </div>
                    </SheetContent>
                </Sheet>
            )}

            {/* ── Mobile dual-mode upload choice Sheet (Gallery vs Camera for left/right) ── */}
            {isMobile && (
                <Sheet open={dualUploadSide !== null} onOpenChange={(o) => { if (!o) setDualUploadSide(null); }}>
                    <SheetContent side="bottom" className="p-0 rounded-t-[28px] z-[99999] overflow-hidden flex flex-col bg-zinc-100 dark:bg-zinc-900" showClose={false}>
                        <SheetTitle className="sr-only">Upload Photo</SheetTitle>
                                                <div className="flex items-center justify-between px-4 pt-3 pb-2">
                            <div>
                                <h2 className="text-base font-bold text-zinc-900 dark:text-zinc-100">{t('imageGeneration.uploadChoiceTitle')}</h2>
                                <p className="text-[11px] text-zinc-500 dark:text-zinc-400 mt-0.5">{t('imageGeneration.uploadChoiceSubtitle')}</p>
                            </div>
                            <button onClick={() => setDualUploadSide(null)} className="w-8 h-8 flex items-center justify-center rounded-xl bg-white/70 dark:bg-zinc-700 text-zinc-500 border-none cursor-pointer">
                                <IconX size={15} />
                            </button>
                        </div>
                        <div className="px-3 pb-6 flex flex-col gap-3">
                            {/* Gallery option */}
                            <button
                                onClick={() => {
                                    const side = dualUploadSide;
                                    setDualUploadSide(null);
                                    setTimeout(() => { (side === 'left' ? inlineMergeLeftRef : inlineMergeRightRef).current?.click(); }, 100);
                                }}
                                className="flex items-center gap-4 w-full px-4 py-4 rounded-2xl bg-white dark:bg-zinc-800 text-left cursor-pointer"
                                style={{ border: 'none' }}
                            >
                                <div className="w-12 h-12 rounded-xl bg-orange-50 dark:bg-orange-900/20 flex items-center justify-center shrink-0">
                                    <IconPhotoUp size={24} className="text-orange-500" />
                                </div>
                                <div>
                                    <p className="text-sm font-bold text-zinc-900 dark:text-zinc-100 m-0">{t('imageGeneration.uploadFromGallery')}</p>
                                    <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5 m-0">{t('imageGeneration.uploadFromGalleryHint')}</p>
                                </div>
                            </button>

                            {/* Camera option */}
                            <button
                                onClick={() => {
                                    const side = dualUploadSide;
                                    setDualUploadSide(null);
                                    setTimeout(() => { (side === 'left' ? cameraMergeLeftRef : cameraMergeRightRef).current?.click(); }, 100);
                                }}
                                className="flex items-center gap-4 w-full px-4 py-4 rounded-2xl bg-white dark:bg-zinc-800 text-left cursor-pointer"
                                style={{ border: 'none' }}
                            >
                                <div className="w-12 h-12 rounded-xl bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center shrink-0">
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="text-blue-500" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z"/>
                                        <circle cx="12" cy="13" r="3"/>
                                    </svg>
                                </div>
                                <div>
                                    <p className="text-sm font-bold text-zinc-900 dark:text-zinc-100 m-0">{t('imageGeneration.uploadFromCamera')}</p>
                                    <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5 m-0">{t('imageGeneration.uploadFromCameraHint')}</p>
                                </div>
                            </button>
                        </div>
                    </SheetContent>
                </Sheet>
            )}

            {/* Dislike feedback — reuses the AI-template modal (preset reasons + sanitized
                "Other" note). Message is stored on GeneratedImage.feedbackNote. */}
            <AiTemplateFeedbackModal
                open={showFeedbackModal}
                onOpenChange={setShowFeedbackModal}
                onSubmit={(reason, note) => handleFeedback(pendingFeedbackId, 'negative', reason, note)}
                submitting={feedbackLoading === 'negative'}
                reasons={IMAGE_FEEDBACK_REASONS}
                maxWords={10}
                strict
            />

            {/* HD / Ultra HD one-time info modal — explains the tier, then generates. */}
            <Dialog open={!!qualityInfoTier} onOpenChange={(o) => { if (!o) setQualityInfoTier(null); }}>
                <DialogContent showClose={false} className="max-w-[560px] p-0 rounded-2xl overflow-hidden font-hind border border-zinc-200 dark:border-zinc-700">
                    {(() => {
                        const tier = qualityInfoTier;
                        if (!tier) return null;
                        const isUltra = tier === 'ultra';
                        const proceed = async (dontShow) => {
                            const chosen = tier;
                            if (dontShow) {
                                try { await updateSettings(isUltra ? { hideUltraInfo: true } : { hideHdInfo: true }); } catch { /* non-blocking */ }
                            }
                            setQualityInfoTier(null);
                            handleGenerate(chosen);
                        };
                        const iconGrad = isUltra ? 'from-red-600 to-rose-700' : 'from-violet-600 to-indigo-600';
                        return (
                            <>
                                {/* Header — icon square + title/subtitle + red close (bulk-edit style) */}
                                <div className="px-6 py-5 border-b border-zinc-100 dark:border-zinc-800 flex items-center gap-3.5">
                                    <span className={`w-[46px] h-[46px] flex-shrink-0 rounded-xl bg-gradient-to-br ${iconGrad} text-white flex items-center justify-center`}>
                                        {isUltra ? <IconCrown size={24} stroke={1.9} /> : <IconSparkles size={24} stroke={1.9} />}
                                    </span>
                                    <div className="min-w-0 flex-1">
                                        <DialogTitle className="text-[19px] font-bold text-zinc-900 dark:text-zinc-100 leading-snug">
                                            {t(isUltra ? 'imageGeneration.quality.ultraModalTitle' : 'imageGeneration.quality.hdModalTitle')}
                                        </DialogTitle>
                                        <p className="text-[13px] text-zinc-400 dark:text-zinc-500 mt-0.5 leading-snug">{t('imageGeneration.quality.modalSubtitle')}</p>
                                    </div>
                                    <button
                                        onClick={() => setQualityInfoTier(null)}
                                        aria-label={t('common.close') || 'বন্ধ করুন'}
                                        className="flex-shrink-0 self-center w-9 h-9 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center transition-colors"
                                    >
                                        <IconX size={18} stroke={2.5} />
                                    </button>
                                </div>

                                {/* Body */}
                                <div className="px-6 py-5">
                                    <DialogDescription className="text-[15px] text-zinc-600 dark:text-zinc-300 leading-relaxed">
                                        {t(isUltra ? 'imageGeneration.quality.ultraModalBody' : 'imageGeneration.quality.hdModalBody')}
                                    </DialogDescription>
                                    {isUltra && (
                                        <div className="mt-4 flex items-center gap-2.5 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 px-4 py-3.5">
                                            <IconCrown size={19} className="text-amber-600 dark:text-amber-400 flex-shrink-0" stroke={2} />
                                            <p className="text-[14px] font-semibold leading-relaxed text-amber-700 dark:text-amber-300">{t('imageGeneration.quality.ultraOutputSize')}</p>
                                        </div>
                                    )}
                                </div>

                                {/* Actions */}
                                <div className="px-6 pb-6 pt-1 flex gap-3">
                                    <button
                                        onClick={() => proceed(true)}
                                        className="flex-shrink-0 h-[52px] px-5 rounded-xl border border-zinc-200 dark:border-zinc-700 text-zinc-600 dark:text-zinc-300 text-[14px] font-bold hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors"
                                    >
                                        {t('imageGeneration.quality.okDontShow')}
                                    </button>
                                    <button
                                        onClick={() => proceed(false)}
                                        className="flex-1 h-[52px] rounded-xl bg-primary hover:bg-primary/90 text-white text-[16px] font-bold inline-flex items-center justify-center gap-2 shadow-md shadow-primary/30 transition-colors"
                                    >
                                        {t('imageGeneration.quality.ok')}
                                    </button>
                                </div>
                            </>
                        );
                    })()}
                </DialogContent>
            </Dialog>
        </>
    );
}
